import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import cn from 'classnames';
import * as React from 'react';
import React__default, { createContext, useContext, useRef, useMemo, forwardRef, useState, useEffect, Fragment as Fragment$1, Children, cloneElement, useCallback, Component, createRef, useLayoutEffect, useReducer, createElement, memo, PureComponent } from 'react';
import { throttle, debounce as debounce$2 } from 'throttle-debounce';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleRight, faTimes, faAngleDown, faCheck as faCheck$2, faSearch as faSearch$1, faCheckCircle as faCheckCircle$1, faBan, faExclamationTriangle as faExclamationTriangle$1, faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import { faMinus, faCheck, faFrown, faExclamationCircle as faExclamationCircle$1, faCheckCircle, faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { faCircle } from '@fortawesome/pro-solid-svg-icons/faCircle';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons/faExclamationCircle';
import ReactModal from 'react-modal';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { faCheck as faCheck$1 } from '@fortawesome/free-solid-svg-icons';
import { v4 } from 'uuid';
import { faRedo, faEye, faEyeSlash, faTimesCircle, faSearch, faFileSearch, faChevronLeft, faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import throttle$1 from 'lodash.throttle';
import mitt from 'mitt';
import { faFileAlt } from '@fortawesome/pro-regular-svg-icons/faFileAlt';
import { TabPanel as TabPanel$1, Tabs as Tabs$1, TabList, Tab } from 'react-tabs';
import { toast, ToastContainer as ToastContainer$1, Slide } from 'react-toastify';
import Tippy from '@tippyjs/react';
import xss, { escapeAttrValue } from 'xss';

const createBemBlockBuilder$1 = (blocks)=>{
    const cleanedBlocks = blocks.reduce((reviewedBlocks, block)=>block ? reviewedBlocks.concat(block.trim().split(" ")) : reviewedBlocks, []);
    return (elementOrModifier = "")=>cleanedBlocks.reduce((classList, block)=>cn(classList, `${block}${elementOrModifier}`), "");
};

function createGenericContext$1({ initialContext, errorMessage }) {
    const GenericContext = /*#__PURE__*/ createContext(initialContext);
    const useGenericContext = ()=>{
        const expectedContext = useContext(GenericContext);
        if (!expectedContext) {
            const message = errorMessage ? errorMessage : "useGenericContext - Can not provide a context to use. Please make sure you have defined a context to be consumed.";
            throw new Error(message);
        }
        return expectedContext;
    };
    const GenericContextConsumer = ({ children })=>{
        const expectedContext = useGenericContext();
        return children(expectedContext);
    };
    return {
        useGenericContext,
        GenericContextProvider: GenericContext.Provider,
        GenericContextConsumer
    };
}

const DEFAULT_DELAY = 300;
const DEFAULT_INPUT_MIN_LENGTH = 7;
const DEFAULT_SHOULD_THROTTLE = (inputValue)=>inputValue.length < DEFAULT_INPUT_MIN_LENGTH || inputValue.endsWith(" ");
const throttleDebounceInput = (ref)=>{
    const { delay = DEFAULT_DELAY, shouldThrottle = DEFAULT_SHOULD_THROTTLE } = ref.current[1];
    const throttledCallback = throttle(delay, (inputValue)=>ref.current[0](inputValue));
    const debouncedCallback = debounce$2(delay, (inputValue)=>ref.current[0](inputValue));
    return (inputValue)=>{
        const condition = typeof shouldThrottle === "function" ? shouldThrottle(inputValue) : shouldThrottle;
        if (condition) {
            throttledCallback(inputValue);
        } else {
            debouncedCallback(inputValue);
        }
    };
};
/**
 * @param func Callback function to be debounced or throttled
 * @param options options.delay: amount of time to delay the throttle or debounce,
 *  options.shouldThrottle: boolean deciding to use throttle or debouncing.
 *  _Note: This cannot currently be changed after initial render due to memoization._
 * @returns Memoized version of the func callback wrapped in debounce/throttle methods
 * @description Within this function a ref is utilized to ensure that every call to useThrottledDebouncedInput does not re-memoize.
 *  We memoize a reference to the func and options properties being passed as parameters.
 *  Therefore, we do not want to re-memoize if the func changes, but the reference will always point
 *  to the current func value.
 */ const useThrottledDebouncedInput = (func, options = {})=>{
    const paramsRef = useRef([
        func,
        options
    ]);
    paramsRef.current = [
        func,
        options
    ];
    return useMemo(()=>throttleDebounceInput(paramsRef), []);
};

const AccordionSection = ({ children, collapseTitle, disableAnimations = false, expandTitle, getBlocksWith, isOpen, isSelected = false, onClick, renderCollapseTitle, renderExpandTitle, renderSection, section, showToggleButtons, topLevel = false })=>{
    const toggleButtons = showToggleButtons && children;
    const handleClick = ()=>{
        onClick(section);
    };
    const toggleAccordionTitle = isOpen ? collapseTitle || renderCollapseTitle(section) : expandTitle || renderExpandTitle(section);
    return /*#__PURE__*/ jsxs("section", {
        id: `section-${section.id}`,
        className: cn(getBlocksWith("__section"), disableAnimations && getBlocksWith("__section--disable-animations"), isOpen && getBlocksWith("__section--open"), topLevel && getBlocksWith("__section--top-level")),
        children: [
            /*#__PURE__*/ jsxs("div", {
                className: cn(getBlocksWith("__header"), isSelected && getBlocksWith("__header--selected")),
                children: [
                    /*#__PURE__*/ jsx("div", {
                        className: getBlocksWith("__toggle-button"),
                        children: toggleButtons && /*#__PURE__*/ jsx("button", {
                            "aria-label": toggleAccordionTitle,
                            onClick: handleClick,
                            "aria-expanded": isOpen,
                            children: /*#__PURE__*/ jsx(FontAwesomeIcon, {
                                className: cn(isOpen ? getBlocksWith("__toggle-button--open") : getBlocksWith("__toggle-button--closed")),
                                size: "2x",
                                icon: faAngleRight,
                                title: toggleAccordionTitle
                            })
                        })
                    }),
                    /*#__PURE__*/ jsx("section", {
                        className: cn(getBlocksWith("__title")),
                        children: renderSection(section)
                    })
                ]
            }),
            /*#__PURE__*/ jsx("div", {
                hidden: !isOpen,
                className: cn(getBlocksWith("__children"), isOpen && getBlocksWith("__children--open")),
                children: children
            })
        ]
    });
};

// Ripped from here https://alligator.io/react/react-accordion-component/
// and here https://codesandbox.io/s/p51vxkr7j
const Accordion = ({ className, collapseTitle = "", disableAnimations = false, expandAllSections = false, expandTitle = "", items, onClick = ()=>{}, openSections = {}, renderCollapseTitle = ()=>"", renderExpandTitle = ()=>"", renderSection, selectedId = "", topLevel = false })=>{
    const getBlocksWith = createBemBlockBuilder$1([
        className,
        "eb-accordion"
    ]);
    const renderSubSections = (subSections)=>{
        return subSections && subSections.length > 0 && /*#__PURE__*/ jsx(Accordion, {
            className: className,
            renderCollapseTitle: renderCollapseTitle,
            collapseTitle: collapseTitle,
            disableAnimations: disableAnimations,
            expandAllSections: expandAllSections,
            renderExpandTitle: renderExpandTitle,
            expandTitle: expandTitle,
            items: subSections,
            onClick: onClick,
            openSections: openSections,
            renderSection: renderSection,
            selectedId: selectedId
        });
    };
    return /*#__PURE__*/ jsx("div", {
        className: cn(getBlocksWith(), topLevel && getBlocksWith("--top-level")),
        children: items && items.length && items.map((section)=>{
            const { id, subSections } = section;
            return /*#__PURE__*/ jsx(AccordionSection, {
                getBlocksWith: getBlocksWith,
                renderCollapseTitle: renderCollapseTitle,
                collapseTitle: collapseTitle,
                disableAnimations: disableAnimations,
                renderExpandTitle: renderExpandTitle,
                expandTitle: expandTitle,
                isOpen: expandAllSections || !!openSections[id],
                isSelected: id === selectedId,
                onClick: onClick,
                renderSection: renderSection,
                section: section,
                showToggleButtons: !expandAllSections,
                topLevel: topLevel,
                children: renderSubSections(subSections)
            }, id);
        })
    });
};

function _define_property$12(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$12(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$12(target, key, source[key]);
        });
    }
    return target;
}
function _object_without_properties$V(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$V(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$V(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const Button$1 = /*#__PURE__*/ forwardRef((_param, forwardedRef)=>{
    var { disabled = false, className = "", styleType = "default", type = "button" } = _param, rest = _object_without_properties$V(_param, [
        "disabled",
        "className",
        "styleType",
        "type"
    ]);
    return /*#__PURE__*/ jsx("button", _object_spread$12({
        ref: forwardedRef,
        "data-auto": "button",
        className: cn("eb-button", `eb-button--${styleType}`, className),
        disabled: disabled,
        type: type
    }, rest));
});
Button$1.displayName = "Button";

const { useGenericContext: useCardContext, GenericContextConsumer: CardContextConsumer, GenericContextProvider: CardContextProvider } = createGenericContext$1({
    errorMessage: "Card compound components cannot be rendered outside the Card component"
});

const CardHeader = ({ children })=>{
    const { getBemBlocksWith } = useCardContext();
    return /*#__PURE__*/ jsx("div", {
        className: cn(getBemBlocksWith("__header")),
        "data-auto": "card-header",
        children: children
    });
};

const CardFooter = ({ children })=>{
    const { getBemBlocksWith } = useCardContext();
    return /*#__PURE__*/ jsx("div", {
        className: cn(getBemBlocksWith("__footer")),
        "data-auto": "card-footer",
        children: children
    });
};

const Card = /*#__PURE__*/ forwardRef(({ className, isRounded = false, isTransparent = true, isRaised = false, isHoverable = false, ariaLabel, children, id, border }, ref)=>{
    const getBemBlocksWith = createBemBlockBuilder$1([
        "eb-card",
        className
    ]);
    const classNames = cn(getBemBlocksWith(), border === "all" && getBemBlocksWith("--border"), border && border !== "all" && getBemBlocksWith(`--border-${border}`), isHoverable && getBemBlocksWith("--hoverable"), isRounded && border === "all" && getBemBlocksWith("--rounded"), isRaised && getBemBlocksWith("--raised"), isTransparent && getBemBlocksWith("--transparent"));
    const cardContext = {
        getBemBlocksWith
    };
    return /*#__PURE__*/ jsx(CardContextProvider, {
        value: cardContext,
        children: /*#__PURE__*/ jsx("section", {
            ref: ref,
            id: id,
            className: classNames,
            "aria-label": ariaLabel,
            "data-auto": "card",
            children: children
        })
    });
});
Card.Header = CardHeader;
Card.Header.displayName = "Card.Header";
Card.Footer = CardFooter;
Card.Footer.displayName = "Card.Footer";
Card.displayName = "Card";

function _define_property$11(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$11(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$11(target, key, source[key]);
        });
    }
    return target;
}
function _object_without_properties$U(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$U(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$U(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const Control = /*#__PURE__*/ forwardRef((_param, forwardedRef)=>{
    var { icon, label, type, size = "base", className, disabled = false, error = false } = _param, rest = _object_without_properties$U(_param, [
        "icon",
        "label",
        "type",
        "size",
        "className",
        "disabled",
        "error"
    ]);
    const getBlocksWith = createBemBlockBuilder$1([
        "eb-control",
        className
    ]);
    const getModifier = (blockElement)=>{
        const modifiedBlockElement = disabled ? `${blockElement}--disabled` : error ? `${blockElement}--error` : blockElement;
        return getBlocksWith(modifiedBlockElement);
    };
    return /*#__PURE__*/ jsx("span", {
        className: getBlocksWith(),
        "data-auto": "control",
        children: /*#__PURE__*/ jsxs("label", {
            className: cn(getBlocksWith("__label"), getModifier("__label")),
            "data-auto": "control-label",
            children: [
                /*#__PURE__*/ jsx("input", _object_spread$11({
                    ref: forwardedRef,
                    type: type,
                    className: cn(getBlocksWith("__input"), getModifier("__input"), getBlocksWith(`__input--${size}`)),
                    "data-auto": "control-input",
                    disabled: disabled
                }, rest)),
                /*#__PURE__*/ jsx("span", {
                    className: cn(getBlocksWith("__icon-wrapper"), getModifier("__icon-wrapper")),
                    "data-auto": "control-label-icon-wrapper",
                    children: /*#__PURE__*/ jsx(FontAwesomeIcon, {
                        "data-auto": "font-awesome-icon",
                        className: cn(getBlocksWith("__icon"), getModifier("__icon"), getBlocksWith(`__icon--${size}`)),
                        icon: icon
                    })
                }),
                /*#__PURE__*/ jsx("span", {
                    className: cn(getBlocksWith("__label-text"), getBlocksWith(`__label-text--${size}`)),
                    "data-auto": "control-label-text",
                    children: label
                })
            ]
        })
    });
});
Control.displayName = "Control";

/**
 * @example
 * Wrapper class name as element:
 * getWrapperClassNames("base__wrapper", "base", ["--open"]) => "base__wrapper base__wrapper--open"
 *
 * Wrapper class name as block:
 * getWrapperClassNames(true, undefined, "base", ["--open"]) => "base base--open"
 *
 * Optional modifier:
 * getWrapperClassNames(true, undefined, "base", [isOpen && "--open"]) => "base base--open" or "base"
 */ const getWrapperClassNames$1 = ({ consumerWrapperClassName, consumerBaseClassName, modifiers, defaultBaseClassName })=>{
    const getBEMBlocksWith = createBemBlockBuilder$1([
        defaultBaseClassName,
        consumerWrapperClassName !== null && consumerWrapperClassName !== void 0 ? consumerWrapperClassName : consumerBaseClassName
    ]);
    return cn(getBEMBlocksWith(), modifiers === null || modifiers === void 0 ? void 0 : modifiers.map((modifier)=>typeof modifier === "string" && getBEMBlocksWith(modifier)));
};

/** Takes multiple refs and returns a single ref-function that binds element instance to each ref */ const combineRefs$1 = (...refs)=>(component)=>{
        refs.forEach((ref)=>{
            if (typeof ref === "function") {
                ref(component);
            }
            if (typeof ref === "object" && ref) {
                ref.current = component;
            }
        });
    };

const useControlledValue = (externalValue, defaultValue)=>{
    const [value, setValue] = useState(defaultValue);
    return [
        externalValue !== null && externalValue !== void 0 ? externalValue : value,
        setValue
    ];
};

function _define_property$10(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$10(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$10(target, key, source[key]);
        });
    }
    return target;
}
function _object_without_properties$T(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$T(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$T(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const Checkbox = /*#__PURE__*/ forwardRef((_param, forwardedRef)=>{
    var { className, isIndeterminate = false, onChange: handleChange, checked: externalChecked } = _param, rest = _object_without_properties$T(_param, [
        "className",
        "isIndeterminate",
        "onChange",
        "checked"
    ]);
    const [checked, setChecked] = useControlledValue(externalChecked, false);
    const checkboxRef = useRef(null);
    useEffect(()=>{
        if (checkboxRef && checkboxRef.current) {
            checkboxRef.current.indeterminate = isIndeterminate;
        }
    }, [
        isIndeterminate
    ]);
    const mergedRefs = combineRefs$1(forwardedRef, checkboxRef);
    const icon = useMemo(()=>{
        if (isIndeterminate && !checked) {
            return faMinus;
        }
        return faCheck;
    }, [
        checked,
        isIndeterminate
    ]);
    return /*#__PURE__*/ jsx(Control, _object_spread$10({
        checked: checked,
        className: cn("eb-checkbox", className),
        icon: icon,
        onChange: (e)=>{
            handleChange && handleChange(e);
            setChecked((prevChecked)=>!prevChecked);
        },
        ref: mergedRefs,
        type: "checkbox"
    }, rest));
});
Checkbox.displayName = "Checkbox";

function _define_property$$(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$$(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$$(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$R(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$R(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$R(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$S(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$S(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$S(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const CloseButton$1 = (_param)=>{
    var { title, onClick, autoFocus, className } = _param, rest = _object_without_properties$S(_param, [
        "title",
        "onClick",
        "autoFocus",
        "className"
    ]);
    return /*#__PURE__*/ jsx(Button$1, _object_spread_props$R(_object_spread$$({
        className: cn("close-button", className),
        styleType: "flat-icon",
        onClick: onClick,
        title: title,
        "data-auto": "close-button",
        autoFocus: autoFocus
    }, rest), {
        children: /*#__PURE__*/ jsx(FontAwesomeIcon, {
            icon: faTimes
        })
    }));
};

const SIZE = 50;
const THICKNESS = 3;
const RADIUS = (SIZE - THICKNESS) / 2;
const IndeterminateSpinner = /*#__PURE__*/ forwardRef(({ className, level }, ref)=>{
    const isIe = typeof window !== "undefined" && (navigator.userAgent.indexOf("MSIE") !== -1 || navigator.appVersion.indexOf("Trident/") > -1);
    const browserClassName = isIe ? "eb-indeterminate-spinner-ie-11" : "eb-indeterminate-spinner";
    const getBlocksWith = createBemBlockBuilder$1([
        browserClassName,
        className
    ]);
    return /*#__PURE__*/ jsx("div", {
        className: getBlocksWith(),
        "data-auto": "indeterminate-spinner",
        ref: ref,
        children: isIe ? /*#__PURE__*/ jsxs("div", {
            className: cn(getBlocksWith("__ring"), getBlocksWith(`__ring--${level}`)),
            children: [
                /*#__PURE__*/ jsx("div", {}),
                /*#__PURE__*/ jsx("div", {}),
                /*#__PURE__*/ jsx("div", {}),
                /*#__PURE__*/ jsx("div", {})
            ]
        }) : /*#__PURE__*/ jsx("svg", {
            className: cn(getBlocksWith("__svg"), getBlocksWith(`__svg--${level}`)),
            viewBox: `${SIZE / 2} ${SIZE / 2} ${SIZE} ${SIZE}`,
            children: /*#__PURE__*/ jsx("circle", {
                cx: SIZE,
                cy: SIZE,
                r: RADIUS
            })
        })
    });
});
IndeterminateSpinner.displayName = "IndeterminateSpinner";

function _define_property$_(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$_(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$_(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$Q(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$Q(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$Q(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$R(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$R(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$R(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const extendedStyleType = [
    "standard"
];
const getStyleType = (styleType)=>extendedStyleType.includes(styleType) ? "default" : styleType;
const baseClassName$6 = "eb-loading-button";
const LoadingButton = /*#__PURE__*/ forwardRef((_param, forwardedRef)=>{
    var { isLoading = false, disabled = false, styleType = "default", type = "button", announceLoadingStart, announceLoadingEnd, loadingPlaceholder, className, children } = _param, rest = _object_without_properties$R(_param, [
        "isLoading",
        "disabled",
        "styleType",
        "type",
        "announceLoadingStart",
        "announceLoadingEnd",
        "loadingPlaceholder",
        "className",
        "children"
    ]);
    const getBlocksWith = createBemBlockBuilder$1([
        baseClassName$6,
        className
    ]);
    // This is required to prevent announcement
    // of the loading state on initial component render
    const [shouldAnnounce, setShouldAnnounce] = useState(false);
    useEffect(()=>{
        isLoading && setShouldAnnounce(true);
    }, [
        isLoading
    ]);
    return /*#__PURE__*/ jsxs(Fragment, {
        children: [
            shouldAnnounce && /*#__PURE__*/ jsx("div", {
                role: "alert",
                className: "screen-reader-text",
                "aria-live": "polite",
                children: isLoading ? announceLoadingStart : announceLoadingEnd
            }),
            /*#__PURE__*/ jsxs(Button$1, _object_spread_props$Q(_object_spread$_({
                ref: forwardedRef,
                className: cn(getBlocksWith(), getBlocksWith(`--${styleType}`), loadingPlaceholder && isLoading && getBlocksWith("--inline"), disabled && getBlocksWith("--disabled"), isLoading && getBlocksWith("--loading")),
                "data-auto": baseClassName$6,
                disabled: disabled || isLoading,
                styleType: getStyleType(styleType),
                type: type
            }, rest), {
                children: [
                    children && /*#__PURE__*/ jsx("div", {
                        "data-auto": `${baseClassName$6}-content`,
                        className: cn(getBlocksWith("__content"), isLoading && getBlocksWith("__content--hidden")),
                        children: children
                    }),
                    isLoading && /*#__PURE__*/ jsxs("div", {
                        "data-auto": `${baseClassName$6}-loader`,
                        className: getBlocksWith("__loader"),
                        children: [
                            loadingPlaceholder,
                            /*#__PURE__*/ jsx(IndeterminateSpinner, {
                                className: getBlocksWith("__spinner"),
                                level: "component",
                                "aria-hidden": "true"
                            })
                        ]
                    })
                ]
            }))
        ]
    });
});

const DataTableColumn = ()=>null;
DataTableColumn.displayName = "DataTable.Column";

function _object_without_properties$Q(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$Q(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$Q(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const DataTableToggle = (_param)=>{
    var { className, open = false, id, onClick } = _param, props = _object_without_properties$Q(_param, [
        "className",
        "open",
        "id",
        "onClick"
    ]);
    const getBlocksWith = createBemBlockBuilder$1([
        "eb-data-table",
        className
    ]);
    const defaultTitle = open ? "Collapse" : "Expand";
    return /*#__PURE__*/ jsx(Button$1, {
        "aria-label": props.ariaLabel,
        "aria-describedby": props.ariaDescribedBy,
        "aria-controls": `result${id}`,
        "aria-expanded": open ? true : false,
        className: cn(getBlocksWith("__toggle-btn"), open ? getBlocksWith("__toggle-btn--is-open") : getBlocksWith("__toggle-btn--is-closed")),
        onClick: onClick,
        styleType: "flat-icon",
        children: /*#__PURE__*/ jsx(FontAwesomeIcon, {
            size: "2x",
            icon: faAngleDown,
            title: props.title || defaultTitle,
            "data-auto": "toggle-icon"
        })
    });
};

const DataTableToggleRowFormatter = (props)=>{
    const { index, cells, item, getBlocksWith } = props;
    return /*#__PURE__*/ jsxs(Fragment$1, {
        children: [
            /*#__PURE__*/ jsx("tr", {
                className: getBlocksWith("__body-row"),
                "data-auto": "table-row",
                children: cells
            }, `data_row_${index}`),
            item.open && /*#__PURE__*/ jsx("tr", {
                id: `result${index}`,
                className: getBlocksWith("__body-row"),
                "data-auto": "table-expanded-row",
                children: /*#__PURE__*/ jsx("td", {
                    className: cn(getBlocksWith("__cell"), getBlocksWith("__cell--expanded")),
                    colSpan: 99,
                    "data-auto": "table-expanded-data",
                    children: item.renderRowContent(index)
                })
            }, `data_row_2_${index}`)
        ]
    }, `data_${index}`);
};

const DefaultRowFormatter = ({ index, cells, getBlocksWith })=>/*#__PURE__*/ jsx("tr", {
        className: getBlocksWith("__body-row"),
        "data-auto": "table-row",
        children: cells
    }, `data_row_${index}`);
const DataTable = /*#__PURE__*/ React__default.forwardRef(function DataTable({ dataGetter, children, rowFormatter = DefaultRowFormatter, caption, className, isFocusable = true }, ref) {
    const getBlocksWith = createBemBlockBuilder$1([
        "eb-data-table",
        className
    ]);
    const renderRow = (item, index)=>{
        const cells = React__default.Children.map(children, (child)=>{
            if (!child) {
                return null;
            }
            const { tagName, fieldName } = child.props;
            const getCellBlocksWith = createBemBlockBuilder$1([
                child.props.className
            ]);
            const dataClasses = cn(getBlocksWith("__cell"), getCellBlocksWith("__cell"));
            // @ts-expect-error c.type.displayName is not a member of the React.Element type.
            if (child.type.displayName !== "DataTable.Column") {
                console.warn("DataTable only uses children of type DataTable.Column. Other component types will be ignored.");
                return null;
            }
            const Tag = tagName || "td";
            return /*#__PURE__*/ jsx(Tag, {
                className: dataClasses,
                "data-auto": "table-data",
                children: child.props.formatter(item)
            }, fieldName);
        });
        return rowFormatter({
            item,
            cells,
            index,
            getBlocksWith
        });
    };
    const renderRows = ()=>{
        let index = 0;
        const rows = [];
        let res;
        let row = true;
        while(row){
            row = dataGetter(index);
            res = row ? renderRow(row, index) : row;
            if (row) {
                rows.push(res);
            }
            index += 1;
        }
        return rows;
    };
    // render headers, account for sorting
    const renderHeader = (render, fieldName)=>{
        return render({
            fieldName
        });
    };
    let hasHeaders = false;
    const headers = React__default.Children.map(children, (child)=>{
        if (!child) {
            return null;
        }
        const { header, headerClassName, fieldName } = child.props;
        const getHeaderBlocksWith = createBemBlockBuilder$1([
            headerClassName
        ]);
        const headerClasses = cn(getBlocksWith("__header-cell"), getHeaderBlocksWith("__header-cell"));
        if (!header) {
            return /*#__PURE__*/ jsx("th", {}, fieldName);
        }
        hasHeaders = true;
        return /*#__PURE__*/ jsx("th", {
            className: headerClasses,
            "data-auto": "table-column-header",
            children: renderHeader(header, fieldName)
        }, fieldName);
    });
    //render footers
    const renderFooter = (render, fieldName)=>{
        return render({
            fieldName
        });
    };
    let hasFooters = false;
    const footers = React__default.Children.map(children, (child)=>{
        if (!child) {
            return null;
        }
        const { footer, fieldName, footerClassName } = child.props;
        const getFooterBlocksWith = createBemBlockBuilder$1([
            footerClassName
        ]);
        const footerClasses = cn(getBlocksWith("__footer-cell"), getFooterBlocksWith("__footer-cell"));
        if (!footer) {
            return /*#__PURE__*/ jsx("td", {}, fieldName);
        }
        hasFooters = true;
        return /*#__PURE__*/ jsx("td", {
            className: footerClasses,
            "data-auto": "table-column-footer",
            children: renderFooter(footer, fieldName)
        }, fieldName);
    });
    return /*#__PURE__*/ jsx("div", {
        className: getBlocksWith(),
        tabIndex: isFocusable ? 0 : undefined,
        "data-auto": "table-wrapper",
        children: /*#__PURE__*/ jsxs("table", {
            className: getBlocksWith("__table"),
            "data-auto": "table-self",
            ref: ref,
            children: [
                caption && /*#__PURE__*/ jsx("caption", {
                    className: getBlocksWith("__caption"),
                    "data-auto": "table-caption",
                    children: caption
                }),
                hasHeaders && /*#__PURE__*/ jsx("thead", {
                    className: getBlocksWith("__header-section"),
                    "data-auto": "table-headers",
                    children: /*#__PURE__*/ jsx("tr", {
                        className: getBlocksWith("__header-row"),
                        children: headers
                    })
                }),
                /*#__PURE__*/ jsx("tbody", {
                    className: getBlocksWith("__body"),
                    "data-auto": "table-body",
                    children: renderRows()
                }),
                hasFooters && /*#__PURE__*/ jsx("tfoot", {
                    className: getBlocksWith("__footer-section"),
                    "data-auto": "table-footers",
                    children: /*#__PURE__*/ jsx("tr", {
                        className: getBlocksWith("__footer-row"),
                        children: footers
                    })
                })
            ]
        })
    });
});
DataTable.displayName = "DataTable";
DataTable.Column = DataTableColumn;
DataTable.Column.displayName = "DataTable.Column";
DataTable.Toggle = DataTableToggle;
DataTable.Toggle.displayName = "DataTable.Toggle";
DataTable.ToggleRowFormatter = DataTableToggleRowFormatter;

function _define_property$Z(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$Z(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$Z(target, key, source[key]);
        });
    }
    return target;
}
function _object_without_properties$P(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$P(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$P(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const RadioButton = /*#__PURE__*/ forwardRef((_param, forwardedRef)=>{
    var { className } = _param, rest = _object_without_properties$P(_param, [
        "className"
    ]);
    return /*#__PURE__*/ jsx(Control, _object_spread$Z({
        ref: forwardedRef,
        icon: faCircle,
        type: "radio",
        className: cn("eb-radio", className)
    }, rest));
});
RadioButton.displayName = "RadioButton";

const TextControl = ({ label, error, description, className, children, labelButton })=>{
    const getBlocksWith = createBemBlockBuilder$1([
        "eb-text-control",
        className
    ]);
    const child = Children.only(children);
    let inputId;
    if (child.props.id) {
        inputId = child.props.id;
    } else {
        console.error("Please provide an id for your input. This is necessary to help describe the purpose of the control.");
    }
    const childWithClass = /*#__PURE__*/ cloneElement(child, {
        className: cn(child.props.className, getBlocksWith("__control"))
    });
    return /*#__PURE__*/ jsxs("div", {
        className: cn(getBlocksWith(), error && getBlocksWith("--invalid")),
        children: [
            /*#__PURE__*/ jsxs("div", {
                className: getBlocksWith("__label-wrapper"),
                children: [
                    /*#__PURE__*/ jsx("label", {
                        htmlFor: inputId,
                        className: getBlocksWith("__label"),
                        children: label
                    }),
                    labelButton
                ]
            }),
            description && /*#__PURE__*/ jsx("div", {
                "data-auto": "text-control-description",
                className: getBlocksWith("__description"),
                id: cn(inputId && `${inputId}-description`),
                children: description
            }),
            childWithClass,
            /*#__PURE__*/ jsx("p", {
                className: getBlocksWith("__error"),
                id: cn(inputId && `${inputId}-error`),
                children: error && /*#__PURE__*/ jsxs(Fragment, {
                    children: [
                        /*#__PURE__*/ jsx(FontAwesomeIcon, {
                            className: getBlocksWith("__error-icon"),
                            icon: faExclamationCircle,
                            "aria-hidden": "true"
                        }),
                        error
                    ]
                })
            })
        ]
    });
};

function _define_property$Y(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$Y(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$Y(target, key, source[key]);
        });
    }
    return target;
}
function _object_without_properties$O(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$O(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$O(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const TextInput = /*#__PURE__*/ forwardRef((_param, ref)=>{
    var { id, className, label, error, description, disabled = false } = _param, rest = _object_without_properties$O(_param, [
        "id",
        "className",
        "label",
        "error",
        "description",
        "disabled"
    ]);
    const baseClassName = "eb-text-input";
    return /*#__PURE__*/ jsx(TextControl, {
        className: cn(baseClassName, className),
        label: label,
        error: error,
        description: description,
        children: /*#__PURE__*/ jsx("input", _object_spread$Y({
            type: "text",
            id: id,
            disabled: disabled,
            className: `${baseClassName}__input`,
            "aria-invalid": Boolean(error),
            "aria-describedby": cn(description && `${id}-description`, error && `${id}-error`),
            ref: ref
        }, rest))
    });
});
TextInput.displayName = "TextInput";

const baseClassName$5 = "eb-date-range";
const DateRange = (props)=>{
    const { minDate, maxDate, onChange, uniqueName, i18n: { minDateLabel, maxDateLabel }, isSharedError, placeholder, className } = props;
    const getBlocksWith = createBemBlockBuilder$1([
        baseClassName$5,
        className
    ]);
    const inputBlockClassName = getBlocksWith("-input");
    const handleMinDateChange = ({ target: { value: newMinDate } })=>{
        onChange(newMinDate, maxDate.value);
    };
    const handleMaxDateChange = ({ target: { value: newMaxDate } })=>{
        onChange(minDate.value, newMaxDate);
    };
    return /*#__PURE__*/ jsxs("div", {
        className: cn(getBlocksWith(), // we need error state to be present, but without message, to avoid
        // message duplication for errors, related to both inputs.
        isSharedError && getBlocksWith("--shared-error")),
        "data-auto": "date-range",
        children: [
            /*#__PURE__*/ jsx(TextInput, {
                id: `${uniqueName}MinDateInput`,
                label: minDateLabel,
                value: minDate.value,
                error: minDate.error,
                onChange: handleMinDateChange,
                placeholder: placeholder,
                "data-auto": "date-range-min-date",
                className: inputBlockClassName
            }),
            /*#__PURE__*/ jsx(TextInput, {
                id: `${uniqueName}MaxDateInput`,
                label: maxDateLabel,
                value: maxDate.value,
                error: maxDate.error,
                onChange: handleMaxDateChange,
                placeholder: placeholder,
                "data-auto": "date-range-max-date",
                className: inputBlockClassName
            })
        ]
    });
};

function _define_property$X(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$X(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$X(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$P(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$P(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$P(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$N(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$N(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$N(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const baseClassName$4 = "eb-date-filters";
const DateFilters = (props)=>{
    const { className, dateRange, i18n: { groupTitle }, isSharedError, quickFilters, placeholder, selectedFilter, showDateRange, uniqueName = "date-filter", onChange } = props, dateRangeI18n = _object_without_properties$N(props.i18n, [
        "groupTitle"
    ]), rest = _object_without_properties$N(props, [
        "className",
        "dateRange",
        "i18n",
        "isSharedError",
        "quickFilters",
        "placeholder",
        "selectedFilter",
        "showDateRange",
        "uniqueName",
        "onChange"
    ]);
    const getBlocksWith = createBemBlockBuilder$1([
        baseClassName$4,
        className
    ]);
    const handleChange = (value)=>{
        onChange({
            selectedFilter: value,
            // send back for consistency
            customRange: {
                minDate: dateRange.minDate.value,
                maxDate: dateRange.maxDate.value
            }
        });
    };
    const handleCustomRangeChange = (minDate, maxDate)=>{
        onChange({
            selectedFilter,
            customRange: {
                minDate,
                maxDate
            }
        });
    };
    const dateRangeUniqueName = `${uniqueName}-custom-range`;
    return /*#__PURE__*/ jsxs("div", {
        className: getBlocksWith(),
        children: [
            /*#__PURE__*/ jsx("div", _object_spread_props$P(_object_spread$X({
                className: getBlocksWith("__group"),
                /* Keep role and aria-label as defaults for backward compatibility */ role: "group",
                "aria-label": groupTitle,
                "data-auto": "quick-date-filters-group"
            }, rest), {
                children: quickFilters.map(({ label, value })=>/*#__PURE__*/ jsx(RadioButton, {
                        label: label,
                        name: uniqueName,
                        value: value,
                        onChange: (ev)=>handleChange(// input converts all values to string, we have to convert it back to number to make the component work correctly
                            typeof value === "number" ? Number(ev.target.value) : ev.target.value),
                        checked: selectedFilter === value,
                        className: getBlocksWith("__filter"),
                        "data-auto": "quick-date-filter"
                    }, value))
            })),
            showDateRange && /*#__PURE__*/ jsx(DateRange, {
                i18n: dateRangeI18n,
                minDate: dateRange.minDate,
                maxDate: dateRange.maxDate,
                isSharedError: isSharedError,
                onChange: handleCustomRangeChange,
                placeholder: placeholder,
                uniqueName: dateRangeUniqueName,
                className: getBlocksWith("-date-range")
            })
        ]
    });
};

const { useGenericContext: useModalContext, GenericContextConsumer: ModalContextConsumer, GenericContextProvider: ModalContextProvider } = createGenericContext$1({
    errorMessage: "Modal compound components cannot be rendered outside the Modal component"
});

function _define_property$W(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$W(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$W(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$O(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$O(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$O(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$M(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$M(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$M(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const ModalHeader = /*#__PURE__*/ forwardRef((_param, ref)=>{
    var { children, className: consumerClassname, closeButtonProps } = _param, rest = _object_without_properties$M(_param, [
        "children",
        "className",
        "closeButtonProps"
    ]);
    const { getBlocksWith } = useModalContext();
    return /*#__PURE__*/ jsxs("div", _object_spread_props$O(_object_spread$W({
        className: cn(consumerClassname, getBlocksWith("__header")),
        "data-auto": "modal-header",
        ref: ref
    }, rest), {
        children: [
            /*#__PURE__*/ jsx("div", {
                className: getBlocksWith("__header-children"),
                children: children
            }),
            closeButtonProps && /*#__PURE__*/ jsx(CloseButton$1, _object_spread_props$O(_object_spread$W({}, closeButtonProps), {
                className: cn(closeButtonProps.className, "close-button", getBlocksWith("__close-button")),
                onClick: (ev)=>{
                    closeButtonProps.onClick && closeButtonProps.onClick(ev);
                }
            }))
        ]
    }));
});

function _define_property$V(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$V(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$V(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$N(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$N(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$N(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$L(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$L(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$L(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const ModalBody = /*#__PURE__*/ forwardRef((_param, ref)=>{
    var { children, className } = _param, rest = _object_without_properties$L(_param, [
        "children",
        "className"
    ]);
    const { getBlocksWith } = useModalContext();
    return /*#__PURE__*/ jsx("div", _object_spread_props$N(_object_spread$V({
        className: cn(className, getBlocksWith("__body")),
        "data-auto": "modal-body",
        ref: ref
    }, rest), {
        children: children
    }));
});

function _define_property$U(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$U(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$U(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$M(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$M(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$M(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$K(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$K(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$K(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const ModalFooter = /*#__PURE__*/ forwardRef((_param, ref)=>{
    var { children, className } = _param, rest = _object_without_properties$K(_param, [
        "children",
        "className"
    ]);
    const { getBlocksWith } = useModalContext();
    return /*#__PURE__*/ jsx("div", _object_spread_props$M(_object_spread$U({
        className: cn(className, getBlocksWith("__footer")),
        "data-auto": "modal-footer",
        ref: ref
    }, rest), {
        children: children
    }));
});

function _define_property$T(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$T(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$T(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$L(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$L(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$L(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$J(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$J(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$J(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
var ANIMATION_PROPS;
/**
 *
 * NOTE: the closeDuration and openDurations are references to duration
 * constants outlined in core/maps/_animation-durations.scss (the openDuration
 * value is not used within the component but is here for reference).
 *
 * closeDuration and openDuration should match the "snap" and "fast" values
 * stipulated in the core/maps/_animation-durations.scss file
 *
 */ (function(ANIMATION_PROPS) {
    // closeDuration is used to set ReactModal close in milliseconds
    ANIMATION_PROPS[ANIMATION_PROPS["closeDuration"] = 100] = "closeDuration";
    // className that removes all transitions
    ANIMATION_PROPS["disableAnimation"] = "disable-animations";
    ANIMATION_PROPS["forwardAnimate"] = "forward-animate";
    // openDuration is not used in the component, it is here to illuminate the scss mappings being used
    ANIMATION_PROPS[ANIMATION_PROPS["openDuration"] = 200] = "openDuration";
    ANIMATION_PROPS["reverseAnimate"] = "reverse-animate";
})(ANIMATION_PROPS || (ANIMATION_PROPS = {}));
const Modal = /*#__PURE__*/ forwardRef((_param, ref)=>{
    var { disableAnimations = false, appElement = "#root", children, className = "", shouldCloseOnOverlayClick = true, isOpen, onClose, onAfterClose: consumerOnAfterClose, modalTitle, parentSelector = undefined, toggleButtonRef } = _param, rest = _object_without_properties$J(_param, [
        "disableAnimations",
        "appElement",
        "children",
        "className",
        "shouldCloseOnOverlayClick",
        "isOpen",
        "onClose",
        "onAfterClose",
        "modalTitle",
        "parentSelector",
        "toggleButtonRef"
    ]);
    const contentRefCallback = useCallback((node)=>{
        if (ref) {
            if (typeof ref === "function") {
                ref(node);
            } else {
                ref.current = node;
            }
        }
    }, [
        ref
    ]);
    const baseClassName = "eb-modal";
    const getBlocksWith = createBemBlockBuilder$1([
        className,
        baseClassName
    ]);
    const closeDuration = disableAnimations ? 0 : 100;
    ReactModal.setAppElement(appElement);
    if (appElement === "body") {
        console.error('Applying the aria-hidden="true" attribute to your <body> element hides your entire web page from assistive technology users!');
    }
    const modalContext = {
        getBlocksWith,
        onClose
    };
    return /*#__PURE__*/ jsx(ModalContextProvider, {
        value: modalContext,
        children: /*#__PURE__*/ jsx(ReactModal, _object_spread_props$L(_object_spread$T({
            bodyOpenClassName: getBlocksWith("--open"),
            className: {
                base: cn(getBlocksWith(), disableAnimations && getBlocksWith(`--${"disable-animations"}`)),
                afterOpen: cn(getBlocksWith(`--${"forward-animate"}`)),
                beforeClose: cn(getBlocksWith(`--${"reverse-animate"}`))
            },
            closeTimeoutMS: closeDuration,
            contentLabel: modalTitle,
            isOpen: isOpen,
            onRequestClose: onClose,
            overlayClassName: {
                base: cn(getBlocksWith("__overlay"), disableAnimations && getBlocksWith(`__overlay--${"disable-animations"}`)),
                afterOpen: getBlocksWith(`__overlay--${"forward-animate"}`),
                beforeClose: getBlocksWith(`__overlay--${"reverse-animate"}`)
            },
            shouldCloseOnOverlayClick: shouldCloseOnOverlayClick,
            parentSelector: parentSelector,
            data: {
                auto: "modal"
            },
            contentRef: contentRefCallback,
            onAfterClose: ()=>{
                toggleButtonRef && toggleButtonRef.current && toggleButtonRef.current.focus();
                typeof consumerOnAfterClose === "function" && consumerOnAfterClose();
            }
        }, rest), {
            children: children
        }))
    });
});
Modal.displayName = "Modal";
Modal.Header = ModalHeader;
Modal.Header.displayName = "Modal.Header";
Modal.Body = ModalBody;
Modal.Body.displayName = "Modal.Body";
Modal.Footer = ModalFooter;
Modal.Footer.displayName = "Modal.Footer";

function _define_property$S(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$S(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$S(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$K(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$K(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$K(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$I(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$I(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$I(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const DialogNotification = /*#__PURE__*/ forwardRef((_param, ref)=>{
    var { acceptButtonStyleType = "default", acceptButtonText, acceptButtonIcon, children, className, title = null, onAccept, onReject, rejectButtonStyleType = "text", rejectButtonText } = _param, rest = _object_without_properties$I(_param, [
        "acceptButtonStyleType",
        "acceptButtonText",
        "acceptButtonIcon",
        "children",
        "className",
        "title",
        "onAccept",
        "onReject",
        "rejectButtonStyleType",
        "rejectButtonText"
    ]);
    const getBlocksWith = createBemBlockBuilder$1([
        "eb-dialog-notification",
        className
    ]);
    return /*#__PURE__*/ jsxs(Modal, _object_spread_props$K(_object_spread$S({}, rest), {
        className: getBlocksWith(),
        onClose: onReject,
        ref: ref,
        children: [
            /*#__PURE__*/ jsxs("div", {
                "data-auto": "dialog-notification-wrapper",
                className: getBlocksWith("__wrapper"),
                children: [
                    /*#__PURE__*/ jsx(Modal.Header, {
                        closeButtonProps: {
                            title: rejectButtonText,
                            onClick: onReject
                        },
                        children: /*#__PURE__*/ jsx("h2", {
                            className: getBlocksWith("__title"),
                            children: title
                        })
                    }),
                    /*#__PURE__*/ jsx(Modal.Body, {
                        children: /*#__PURE__*/ jsx("section", {
                            className: cn(className, getBlocksWith("__body-content")),
                            children: /*#__PURE__*/ jsx("p", {
                                "data-auto": "dialog-notification-body-description",
                                className: cn(getBlocksWith("__body-description"), title && getBlocksWith("__body-description--secondary")),
                                children: children
                            })
                        })
                    })
                ]
            }),
            /*#__PURE__*/ jsxs(Modal.Footer, {
                children: [
                    /*#__PURE__*/ jsx(Button$1, {
                        className: getBlocksWith("__reject-button"),
                        onClick: onReject,
                        styleType: rejectButtonStyleType,
                        children: rejectButtonText
                    }),
                    /*#__PURE__*/ jsxs(Button$1, {
                        className: getBlocksWith("__accept-button"),
                        onClick: onAccept,
                        styleType: acceptButtonStyleType,
                        children: [
                            acceptButtonIcon && /*#__PURE__*/ jsx(FontAwesomeIcon, {
                                className: getBlocksWith("__accept-button-icon"),
                                icon: acceptButtonIcon
                            }),
                            acceptButtonText
                        ]
                    })
                ]
            })
        ]
    }));
});

function _define_property$R(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$R(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$R(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$J(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$J(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$J(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
var INTERACTIONS_AFFORDANCE;
(function(INTERACTIONS_AFFORDANCE) {
    INTERACTIONS_AFFORDANCE["ESC"] = "Escape key";
    INTERACTIONS_AFFORDANCE["OVERLAY"] = "Overlay";
})(INTERACTIONS_AFFORDANCE || (INTERACTIONS_AFFORDANCE = {}));
var POSITIONS;
(function(POSITIONS) {
    POSITIONS["start"] = "start";
    POSITIONS["end"] = "end";
})(POSITIONS || (POSITIONS = {}));
const FOCUSABLE_ELEMENTS_SELECTORS = [
    "button",
    "[href]",
    "input",
    "select",
    "textarea",
    "[tabindex]:not([tabindex='-1'])"
].join(", ");
var KEYS;
(function(KEYS) {
    KEYS["ESCAPE"] = "Escape";
    KEYS["TAB"] = "Tab";
})(KEYS || (KEYS = {}));
const baseClassName$3 = "eb-drawer";
const defaultZIndex = 9000;
class DrawerNoRef extends Component {
    componentDidUpdate(prevProps) {
        const isOpening = !prevProps.isOpen && this.props.isOpen;
        const isClosing = prevProps.isOpen && !this.props.isOpen;
        if (isOpening) {
            this.handleOpen();
        }
        if (isClosing) {
            this.handleClose(true);
        }
    }
    componentWillUnmount() {
        document.removeEventListener("keydown", this.handleKeyDown);
    }
    render() {
        const { isGlobal } = this.props;
        // It appears that document can be undefined initially in case of server-side rendering.
        // Would appreciate any suggestions for more elegant workaround
        if (typeof document === "undefined") {
            return null;
        }
        const drawer = /*#__PURE__*/ jsxs(Fragment, {
            children: [
                this.renderOverlay(),
                this.renderDrawer()
            ]
        });
        return isGlobal ? /*#__PURE__*/ ReactDOM.createPortal(drawer, document.body) : drawer;
    }
    constructor(props){
        super(props);
        _define_property$R(this, "drawerRef", void 0);
        _define_property$R(this, "getBemBlocksWith", void 0);
        _define_property$R(this, "initiallyFocusedElement", void 0);
        _define_property$R(this, "drawerRefCallback", (node)=>{
            const { forwardedRef } = this.props;
            if (forwardedRef) {
                if (typeof forwardedRef === "function") {
                    forwardedRef(node);
                } else {
                    forwardedRef.current = node;
                }
            }
            this.drawerRef.current = node;
        });
        _define_property$R(this, "focusOnContent", ()=>{
            let focusable;
            if (this.drawerRef.current) {
                focusable = this.drawerRef.current.querySelector(FOCUSABLE_ELEMENTS_SELECTORS);
            }
            if (focusable) {
                focusable.focus();
            }
        });
        _define_property$R(this, "handleKeyDown", (event)=>{
            if (!this.props.isGlobal) {
                return;
            }
            const focusableElements = this.drawerRef.current ? this.drawerRef.current.querySelectorAll(FOCUSABLE_ELEMENTS_SELECTORS) : [];
            const firstFocusable = focusableElements[0];
            const lastFocusable = focusableElements[focusableElements.length - 1];
            const activeElement = document.activeElement;
            if (event.key === "Escape") {
                this.handleClose(false, "Escape key");
            } else if (event.key === "Tab" && event.shiftKey) {
                if (activeElement === firstFocusable) {
                    lastFocusable.focus();
                    event.preventDefault();
                }
            } else if (event.key === "Tab" && activeElement === lastFocusable) {
                firstFocusable.focus();
                event.preventDefault();
            }
        });
        _define_property$R(this, "handleOpen", ()=>{
            const { setFocusOnOpen } = this.props;
            document.addEventListener("keydown", this.handleKeyDown);
            this.initiallyFocusedElement = document.activeElement;
            if (setFocusOnOpen) {
                /* With styles we have it requires waiting two animation frames (requestAnimationFrame cycles)
      before you can focus on an element, so without timeout focus won't be set.
      When delay is less when 400 content will glitch in some cases. See US574068 for issue description.*/ const ANIMATION_DELAY = 400;
                setTimeout(this.focusOnContent, ANIMATION_DELAY);
            }
        });
        _define_property$R(this, "handleClose", (propsChanged, affordance)=>{
            const { returnFocusSelector, setFocusOnClose, onClose } = this.props;
            if (setFocusOnClose) {
                const elementToFocus = returnFocusSelector ? document.querySelector(returnFocusSelector) : this.initiallyFocusedElement;
                if (elementToFocus) {
                    elementToFocus.focus();
                }
            }
            document.removeEventListener("keydown", this.handleKeyDown);
            if (!propsChanged) {
                onClose(affordance);
            }
        });
        _define_property$R(this, "handleOverlayClick", ()=>{
            this.handleClose(false, "Overlay");
        });
        _define_property$R(this, "renderOverlay", ()=>{
            const { disableAnimations, isOpen, isGlobal, showOverlay, zIndex = defaultZIndex } = this.props;
            if (!showOverlay) {
                return null;
            }
            const overlayClassNames = cn(this.getBemBlocksWith("__overlay"), disableAnimations && this.getBemBlocksWith("__overlay--disable-animations"), !isGlobal && this.getBemBlocksWith("__overlay--contained"), isOpen && this.getBemBlocksWith("__overlay--open"));
            return /*#__PURE__*/ jsx("div", {
                className: overlayClassNames,
                "data-auto": "drawer-overlay",
                onClick: this.handleOverlayClick,
                style: {
                    zIndex
                }
            });
        });
        _define_property$R(this, "renderDrawer", ()=>{
            const { disableAnimations, isOpen, isGlobal, position, className, children, titleElementId, drawerTag: DrawerTag = "div", zIndex = defaultZIndex, role } = this.props;
            const drawerClassNames = cn(this.getBemBlocksWith(), disableAnimations && this.getBemBlocksWith("--disable-animations"), className, !isGlobal && this.getBemBlocksWith("--contained"), isOpen && this.getBemBlocksWith("--open"), position === "start" && this.getBemBlocksWith("--start-positioned"), position === "end" && this.getBemBlocksWith("--end-positioned"));
            return /*#__PURE__*/ jsx(DrawerTag, {
                className: drawerClassNames,
                style: {
                    zIndex: zIndex + 1
                },
                ref: this.drawerRefCallback,
                "aria-hidden": !isOpen,
                "aria-labelledby": titleElementId,
                "data-auto": "drawer",
                tabIndex: -1,
                role: role,
                children: children
            });
        });
        this.drawerRef = /*#__PURE__*/ createRef();
        if (props.isOpen) {
            this.handleOpen();
        }
        this.getBemBlocksWith = createBemBlockBuilder$1([
            baseClassName$3,
            this.props.className
        ]);
    }
}
_define_property$R(DrawerNoRef, "defaultProps", {
    disableAnimations: false,
    isOpen: false,
    setFocusOnOpen: true,
    setFocusOnClose: true,
    showOverlay: true,
    isGlobal: true,
    className: "",
    position: "start",
    returnFocusSelector: ""
});
const Drawer = /*#__PURE__*/ forwardRef((props, ref)=>{
    return /*#__PURE__*/ jsx(DrawerNoRef, _object_spread_props$J(_object_spread$R({}, props), {
        forwardedRef: ref
    }));
});
Drawer.displayName = "Drawer";

var reactIs$3 = {exports: {}};

var reactIs_production_min$2 = {};

/** @license React v17.0.2
 * react-is.production.min.js
 *
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

var hasRequiredReactIs_production_min$2;

function requireReactIs_production_min$2 () {
	if (hasRequiredReactIs_production_min$2) return reactIs_production_min$2;
	hasRequiredReactIs_production_min$2 = 1;
var b=60103,c=60106,d=60107,e=60108,f=60114,g=60109,h=60110,k=60112,l=60113,m=60120,n=60115,p=60116,q=60121,r=60122,u=60117,v=60129,w=60131;
	if("function"===typeof Symbol&&Symbol.for){var x=Symbol.for;b=x("react.element");c=x("react.portal");d=x("react.fragment");e=x("react.strict_mode");f=x("react.profiler");g=x("react.provider");h=x("react.context");k=x("react.forward_ref");l=x("react.suspense");m=x("react.suspense_list");n=x("react.memo");p=x("react.lazy");q=x("react.block");r=x("react.server.block");u=x("react.fundamental");v=x("react.debug_trace_mode");w=x("react.legacy_hidden");}
	function y(a){if("object"===typeof a&&null!==a){var t=a.$$typeof;switch(t){case b:switch(a=a.type,a){case d:case f:case e:case l:case m:return a;default:switch(a=a&&a.$$typeof,a){case h:case k:case p:case n:case g:return a;default:return t}}case c:return t}}}var z=g,A=b,B=k,C=d,D=p,E=n,F=c,G=f,H=e,I=l;reactIs_production_min$2.ContextConsumer=h;reactIs_production_min$2.ContextProvider=z;reactIs_production_min$2.Element=A;reactIs_production_min$2.ForwardRef=B;reactIs_production_min$2.Fragment=C;reactIs_production_min$2.Lazy=D;reactIs_production_min$2.Memo=E;reactIs_production_min$2.Portal=F;reactIs_production_min$2.Profiler=G;reactIs_production_min$2.StrictMode=H;
	reactIs_production_min$2.Suspense=I;reactIs_production_min$2.isAsyncMode=function(){return !1};reactIs_production_min$2.isConcurrentMode=function(){return !1};reactIs_production_min$2.isContextConsumer=function(a){return y(a)===h};reactIs_production_min$2.isContextProvider=function(a){return y(a)===g};reactIs_production_min$2.isElement=function(a){return "object"===typeof a&&null!==a&&a.$$typeof===b};reactIs_production_min$2.isForwardRef=function(a){return y(a)===k};reactIs_production_min$2.isFragment=function(a){return y(a)===d};reactIs_production_min$2.isLazy=function(a){return y(a)===p};reactIs_production_min$2.isMemo=function(a){return y(a)===n};
	reactIs_production_min$2.isPortal=function(a){return y(a)===c};reactIs_production_min$2.isProfiler=function(a){return y(a)===f};reactIs_production_min$2.isStrictMode=function(a){return y(a)===e};reactIs_production_min$2.isSuspense=function(a){return y(a)===l};reactIs_production_min$2.isValidElementType=function(a){return "string"===typeof a||"function"===typeof a||a===d||a===f||a===v||a===e||a===l||a===m||a===w||"object"===typeof a&&null!==a&&(a.$$typeof===p||a.$$typeof===n||a.$$typeof===g||a.$$typeof===h||a.$$typeof===k||a.$$typeof===u||a.$$typeof===q||a[0]===r)?!0:!1};
	reactIs_production_min$2.typeOf=y;
	return reactIs_production_min$2;
}

var reactIs_development$2 = {};

/** @license React v17.0.2
 * react-is.development.js
 *
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

var hasRequiredReactIs_development$2;

function requireReactIs_development$2 () {
	if (hasRequiredReactIs_development$2) return reactIs_development$2;
	hasRequiredReactIs_development$2 = 1;

	if (process.env.NODE_ENV !== "production") {
	  (function() {

	// ATTENTION
	// When adding new symbols to this file,
	// Please consider also adding to 'react-devtools-shared/src/backend/ReactSymbols'
	// The Symbol used to tag the ReactElement-like types. If there is no native Symbol
	// nor polyfill, then a plain number is used for performance.
	var REACT_ELEMENT_TYPE = 0xeac7;
	var REACT_PORTAL_TYPE = 0xeaca;
	var REACT_FRAGMENT_TYPE = 0xeacb;
	var REACT_STRICT_MODE_TYPE = 0xeacc;
	var REACT_PROFILER_TYPE = 0xead2;
	var REACT_PROVIDER_TYPE = 0xeacd;
	var REACT_CONTEXT_TYPE = 0xeace;
	var REACT_FORWARD_REF_TYPE = 0xead0;
	var REACT_SUSPENSE_TYPE = 0xead1;
	var REACT_SUSPENSE_LIST_TYPE = 0xead8;
	var REACT_MEMO_TYPE = 0xead3;
	var REACT_LAZY_TYPE = 0xead4;
	var REACT_BLOCK_TYPE = 0xead9;
	var REACT_SERVER_BLOCK_TYPE = 0xeada;
	var REACT_FUNDAMENTAL_TYPE = 0xead5;
	var REACT_DEBUG_TRACING_MODE_TYPE = 0xeae1;
	var REACT_LEGACY_HIDDEN_TYPE = 0xeae3;

	if (typeof Symbol === 'function' && Symbol.for) {
	  var symbolFor = Symbol.for;
	  REACT_ELEMENT_TYPE = symbolFor('react.element');
	  REACT_PORTAL_TYPE = symbolFor('react.portal');
	  REACT_FRAGMENT_TYPE = symbolFor('react.fragment');
	  REACT_STRICT_MODE_TYPE = symbolFor('react.strict_mode');
	  REACT_PROFILER_TYPE = symbolFor('react.profiler');
	  REACT_PROVIDER_TYPE = symbolFor('react.provider');
	  REACT_CONTEXT_TYPE = symbolFor('react.context');
	  REACT_FORWARD_REF_TYPE = symbolFor('react.forward_ref');
	  REACT_SUSPENSE_TYPE = symbolFor('react.suspense');
	  REACT_SUSPENSE_LIST_TYPE = symbolFor('react.suspense_list');
	  REACT_MEMO_TYPE = symbolFor('react.memo');
	  REACT_LAZY_TYPE = symbolFor('react.lazy');
	  REACT_BLOCK_TYPE = symbolFor('react.block');
	  REACT_SERVER_BLOCK_TYPE = symbolFor('react.server.block');
	  REACT_FUNDAMENTAL_TYPE = symbolFor('react.fundamental');
	  symbolFor('react.scope');
	  symbolFor('react.opaque.id');
	  REACT_DEBUG_TRACING_MODE_TYPE = symbolFor('react.debug_trace_mode');
	  symbolFor('react.offscreen');
	  REACT_LEGACY_HIDDEN_TYPE = symbolFor('react.legacy_hidden');
	}

	// Filter certain DOM attributes (e.g. src, href) if their values are empty strings.

	var enableScopeAPI = false; // Experimental Create Event Handle API.

	function isValidElementType(type) {
	  if (typeof type === 'string' || typeof type === 'function') {
	    return true;
	  } // Note: typeof might be other than 'symbol' or 'number' (e.g. if it's a polyfill).


	  if (type === REACT_FRAGMENT_TYPE || type === REACT_PROFILER_TYPE || type === REACT_DEBUG_TRACING_MODE_TYPE || type === REACT_STRICT_MODE_TYPE || type === REACT_SUSPENSE_TYPE || type === REACT_SUSPENSE_LIST_TYPE || type === REACT_LEGACY_HIDDEN_TYPE || enableScopeAPI ) {
	    return true;
	  }

	  if (typeof type === 'object' && type !== null) {
	    if (type.$$typeof === REACT_LAZY_TYPE || type.$$typeof === REACT_MEMO_TYPE || type.$$typeof === REACT_PROVIDER_TYPE || type.$$typeof === REACT_CONTEXT_TYPE || type.$$typeof === REACT_FORWARD_REF_TYPE || type.$$typeof === REACT_FUNDAMENTAL_TYPE || type.$$typeof === REACT_BLOCK_TYPE || type[0] === REACT_SERVER_BLOCK_TYPE) {
	      return true;
	    }
	  }

	  return false;
	}

	function typeOf(object) {
	  if (typeof object === 'object' && object !== null) {
	    var $$typeof = object.$$typeof;

	    switch ($$typeof) {
	      case REACT_ELEMENT_TYPE:
	        var type = object.type;

	        switch (type) {
	          case REACT_FRAGMENT_TYPE:
	          case REACT_PROFILER_TYPE:
	          case REACT_STRICT_MODE_TYPE:
	          case REACT_SUSPENSE_TYPE:
	          case REACT_SUSPENSE_LIST_TYPE:
	            return type;

	          default:
	            var $$typeofType = type && type.$$typeof;

	            switch ($$typeofType) {
	              case REACT_CONTEXT_TYPE:
	              case REACT_FORWARD_REF_TYPE:
	              case REACT_LAZY_TYPE:
	              case REACT_MEMO_TYPE:
	              case REACT_PROVIDER_TYPE:
	                return $$typeofType;

	              default:
	                return $$typeof;
	            }

	        }

	      case REACT_PORTAL_TYPE:
	        return $$typeof;
	    }
	  }

	  return undefined;
	}
	var ContextConsumer = REACT_CONTEXT_TYPE;
	var ContextProvider = REACT_PROVIDER_TYPE;
	var Element = REACT_ELEMENT_TYPE;
	var ForwardRef = REACT_FORWARD_REF_TYPE;
	var Fragment = REACT_FRAGMENT_TYPE;
	var Lazy = REACT_LAZY_TYPE;
	var Memo = REACT_MEMO_TYPE;
	var Portal = REACT_PORTAL_TYPE;
	var Profiler = REACT_PROFILER_TYPE;
	var StrictMode = REACT_STRICT_MODE_TYPE;
	var Suspense = REACT_SUSPENSE_TYPE;
	var hasWarnedAboutDeprecatedIsAsyncMode = false;
	var hasWarnedAboutDeprecatedIsConcurrentMode = false; // AsyncMode should be deprecated

	function isAsyncMode(object) {
	  {
	    if (!hasWarnedAboutDeprecatedIsAsyncMode) {
	      hasWarnedAboutDeprecatedIsAsyncMode = true; // Using console['warn'] to evade Babel and ESLint

	      console['warn']('The ReactIs.isAsyncMode() alias has been deprecated, ' + 'and will be removed in React 18+.');
	    }
	  }

	  return false;
	}
	function isConcurrentMode(object) {
	  {
	    if (!hasWarnedAboutDeprecatedIsConcurrentMode) {
	      hasWarnedAboutDeprecatedIsConcurrentMode = true; // Using console['warn'] to evade Babel and ESLint

	      console['warn']('The ReactIs.isConcurrentMode() alias has been deprecated, ' + 'and will be removed in React 18+.');
	    }
	  }

	  return false;
	}
	function isContextConsumer(object) {
	  return typeOf(object) === REACT_CONTEXT_TYPE;
	}
	function isContextProvider(object) {
	  return typeOf(object) === REACT_PROVIDER_TYPE;
	}
	function isElement(object) {
	  return typeof object === 'object' && object !== null && object.$$typeof === REACT_ELEMENT_TYPE;
	}
	function isForwardRef(object) {
	  return typeOf(object) === REACT_FORWARD_REF_TYPE;
	}
	function isFragment(object) {
	  return typeOf(object) === REACT_FRAGMENT_TYPE;
	}
	function isLazy(object) {
	  return typeOf(object) === REACT_LAZY_TYPE;
	}
	function isMemo(object) {
	  return typeOf(object) === REACT_MEMO_TYPE;
	}
	function isPortal(object) {
	  return typeOf(object) === REACT_PORTAL_TYPE;
	}
	function isProfiler(object) {
	  return typeOf(object) === REACT_PROFILER_TYPE;
	}
	function isStrictMode(object) {
	  return typeOf(object) === REACT_STRICT_MODE_TYPE;
	}
	function isSuspense(object) {
	  return typeOf(object) === REACT_SUSPENSE_TYPE;
	}

	reactIs_development$2.ContextConsumer = ContextConsumer;
	reactIs_development$2.ContextProvider = ContextProvider;
	reactIs_development$2.Element = Element;
	reactIs_development$2.ForwardRef = ForwardRef;
	reactIs_development$2.Fragment = Fragment;
	reactIs_development$2.Lazy = Lazy;
	reactIs_development$2.Memo = Memo;
	reactIs_development$2.Portal = Portal;
	reactIs_development$2.Profiler = Profiler;
	reactIs_development$2.StrictMode = StrictMode;
	reactIs_development$2.Suspense = Suspense;
	reactIs_development$2.isAsyncMode = isAsyncMode;
	reactIs_development$2.isConcurrentMode = isConcurrentMode;
	reactIs_development$2.isContextConsumer = isContextConsumer;
	reactIs_development$2.isContextProvider = isContextProvider;
	reactIs_development$2.isElement = isElement;
	reactIs_development$2.isForwardRef = isForwardRef;
	reactIs_development$2.isFragment = isFragment;
	reactIs_development$2.isLazy = isLazy;
	reactIs_development$2.isMemo = isMemo;
	reactIs_development$2.isPortal = isPortal;
	reactIs_development$2.isProfiler = isProfiler;
	reactIs_development$2.isStrictMode = isStrictMode;
	reactIs_development$2.isSuspense = isSuspense;
	reactIs_development$2.isValidElementType = isValidElementType;
	reactIs_development$2.typeOf = typeOf;
	  })();
	}
	return reactIs_development$2;
}

if (process.env.NODE_ENV === 'production') {
  reactIs$3.exports = requireReactIs_production_min$2();
} else {
  reactIs$3.exports = requireReactIs_development$2();
}

var reactIsExports$2 = reactIs$3.exports;

function t$1(t){return "object"==typeof t&&null!=t&&1===t.nodeType}function e$1(t,e){return (!e||"hidden"!==t)&&"visible"!==t&&"clip"!==t}function n$1(t,n){if(t.clientHeight<t.scrollHeight||t.clientWidth<t.scrollWidth){var r=getComputedStyle(t,null);return e$1(r.overflowY,n)||e$1(r.overflowX,n)||function(t){var e=function(t){if(!t.ownerDocument||!t.ownerDocument.defaultView)return null;try{return t.ownerDocument.defaultView.frameElement}catch(t){return null}}(t);return !!e&&(e.clientHeight<t.scrollHeight||e.clientWidth<t.scrollWidth)}(t)}return !1}function r$1(t,e,n,r,i,o,l,d){return o<t&&l>e||o>t&&l<e?0:o<=t&&d<=n||l>=e&&d>=n?o-t-r:l>e&&d<n||o<t&&d>n?l-e+i:0}var i$1=function(e,i){var o=window,l=i.scrollMode,d=i.block,f=i.inline,h=i.boundary,u=i.skipOverflowHiddenElements,s="function"==typeof h?h:function(t){return t!==h};if(!t$1(e))throw new TypeError("Invalid target");for(var a,c,g=document.scrollingElement||document.documentElement,p=[],m=e;t$1(m)&&s(m);){if((m=null==(c=(a=m).parentElement)?a.getRootNode().host||null:c)===g){p.push(m);break}null!=m&&m===document.body&&n$1(m)&&!n$1(document.documentElement)||null!=m&&n$1(m,u)&&p.push(m);}for(var w=o.visualViewport?o.visualViewport.width:innerWidth,v=o.visualViewport?o.visualViewport.height:innerHeight,W=window.scrollX||pageXOffset,H=window.scrollY||pageYOffset,b=e.getBoundingClientRect(),y=b.height,E=b.width,M=b.top,V=b.right,x=b.bottom,I=b.left,C="start"===d||"nearest"===d?M:"end"===d?x:M+y/2,R="center"===f?I+E/2:"end"===f?V:I,T=[],k=0;k<p.length;k++){var B=p[k],D=B.getBoundingClientRect(),O=D.height,X=D.width,Y=D.top,L=D.right,S=D.bottom,j=D.left;if("if-needed"===l&&M>=0&&I>=0&&x<=v&&V<=w&&M>=Y&&x<=S&&I>=j&&V<=L)return T;var N=getComputedStyle(B),q=parseInt(N.borderLeftWidth,10),z=parseInt(N.borderTopWidth,10),A=parseInt(N.borderRightWidth,10),F=parseInt(N.borderBottomWidth,10),G=0,J=0,K="offsetWidth"in B?B.offsetWidth-B.clientWidth-q-A:0,P="offsetHeight"in B?B.offsetHeight-B.clientHeight-z-F:0,Q="offsetWidth"in B?0===B.offsetWidth?0:X/B.offsetWidth:0,U="offsetHeight"in B?0===B.offsetHeight?0:O/B.offsetHeight:0;if(g===B)G="start"===d?C:"end"===d?C-v:"nearest"===d?r$1(H,H+v,v,z,F,H+C,H+C+y,y):C-v/2,J="start"===f?R:"center"===f?R-w/2:"end"===f?R-w:r$1(W,W+w,w,q,A,W+R,W+R+E,E),G=Math.max(0,G+H),J=Math.max(0,J+W);else {G="start"===d?C-Y-z:"end"===d?C-S+F+P:"nearest"===d?r$1(Y,S,O,z,F+P,C,C+y,y):C-(Y+O/2)+P/2,J="start"===f?R-j-q:"center"===f?R-(j+X/2)+K/2:"end"===f?R-L+A+K:r$1(j,L,X,q,A+K,R,R+E,E);var Z=B.scrollLeft,$=B.scrollTop;C+=$-(G=Math.max(0,Math.min($+G/U,B.scrollHeight-O/U+P))),R+=Z-(J=Math.max(0,Math.min(Z+J/Q,B.scrollWidth-X/Q+K)));}T.push({el:B,top:G,left:J});}return T};

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol */

var extendStatics = function(d, b) {
  extendStatics = Object.setPrototypeOf ||
      ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
      function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
  return extendStatics(d, b);
};

function __extends(d, b) {
  if (typeof b !== "function" && b !== null)
      throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
  extendStatics(d, b);
  function __() { this.constructor = d; }
  d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}

var __assign$1 = function() {
  __assign$1 = Object.assign || function __assign(t) {
      for (var s, i = 1, n = arguments.length; i < n; i++) {
          s = arguments[i];
          for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
      return t;
  };
  return __assign$1.apply(this, arguments);
};

function __rest(s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
      t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function")
      for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
          if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
              t[p[i]] = s[p[i]];
      }
  return t;
}

function __spreadArray(to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
      if (ar || !(i in from)) {
          if (!ar) ar = Array.prototype.slice.call(from, 0, i);
          ar[i] = from[i];
      }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
  var e = new Error(message);
  return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

let idCounter$1 = 0;
/**
 * Accepts a parameter and returns it if it's a function
 * or a noop function if it's not. This allows us to
 * accept a callback, but not worry about it if it's not
 * passed.
 * @param {Function} cb the callback
 * @return {Function} a function
 */

function cbToCb$1(cb) {
  return typeof cb === 'function' ? cb : noop$1;
}

function noop$1() {}
/**
 * Scroll node into view if necessary
 * @param {HTMLElement} node the element that should scroll into view
 * @param {HTMLElement} menuNode the menu element of the component
 */


function scrollIntoView$1(node, menuNode) {
  if (!node) {
    return;
  }

  const actions = i$1(node, {
    boundary: menuNode,
    block: 'nearest',
    scrollMode: 'if-needed'
  });
  actions.forEach(_ref => {
    let {
      el,
      top,
      left
    } = _ref;
    el.scrollTop = top;
    el.scrollLeft = left;
  });
}
/**
 * @param {HTMLElement} parent the parent node
 * @param {HTMLElement} child the child node
 * @param {Window} environment The window context where downshift renders.
 * @return {Boolean} whether the parent is the child or the child is in the parent
 */


function isOrContainsNode$1(parent, child, environment) {
  const result = parent === child || child instanceof environment.Node && parent.contains && parent.contains(child);
  return result;
}
/**
 * Simple debounce implementation. Will call the given
 * function once after the time given has passed since
 * it was last called.
 * @param {Function} fn the function to call after the time
 * @param {Number} time the time to wait
 * @return {Function} the debounced function
 */


function debounce$1(fn, time) {
  let timeoutId;

  function cancel() {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }

  function wrapper() {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    cancel();
    timeoutId = setTimeout(() => {
      timeoutId = null;
      fn(...args);
    }, time);
  }

  wrapper.cancel = cancel;
  return wrapper;
}
/**
 * This is intended to be used to compose event handlers.
 * They are executed in order until one of them sets
 * `event.preventDownshiftDefault = true`.
 * @param {...Function} fns the event handler functions
 * @return {Function} the event handler to add to an element
 */


function callAllEventHandlers$1() {
  for (var _len2 = arguments.length, fns = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
    fns[_key2] = arguments[_key2];
  }

  return function (event) {
    for (var _len3 = arguments.length, args = new Array(_len3 > 1 ? _len3 - 1 : 0), _key3 = 1; _key3 < _len3; _key3++) {
      args[_key3 - 1] = arguments[_key3];
    }

    return fns.some(fn => {
      if (fn) {
        fn(event, ...args);
      }

      return event.preventDownshiftDefault || event.hasOwnProperty('nativeEvent') && event.nativeEvent.preventDownshiftDefault;
    });
  };
}

function handleRefs$1() {
  for (var _len4 = arguments.length, refs = new Array(_len4), _key4 = 0; _key4 < _len4; _key4++) {
    refs[_key4] = arguments[_key4];
  }

  return node => {
    refs.forEach(ref => {
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    });
  };
}
/**
 * This generates a unique ID for an instance of Downshift
 * @return {String} the unique ID
 */


function generateId$1() {
  return String(idCounter$1++);
}
/**
 * Default implementation for status message. Only added when menu is open.
 * Will specify if there are results in the list, and if so, how many,
 * and what keys are relevant.
 *
 * @param {Object} param the downshift state and other relevant properties
 * @return {String} the a11y status message
 */


function getA11yStatusMessage$1$1(_ref2) {
  let {
    isOpen,
    resultCount,
    previousResultCount
  } = _ref2;

  if (!isOpen) {
    return '';
  }

  if (!resultCount) {
    return 'No results are available.';
  }

  if (resultCount !== previousResultCount) {
    return `${resultCount} result${resultCount === 1 ? ' is' : 's are'} available, use up and down arrow keys to navigate. Press Enter key to select.`;
  }

  return '';
}
/**
 * Takes an argument and if it's an array, returns the first item in the array
 * otherwise returns the argument
 * @param {*} arg the maybe-array
 * @param {*} defaultValue the value if arg is falsey not defined
 * @return {*} the arg or it's first item
 */


function unwrapArray$1(arg, defaultValue) {
  arg = Array.isArray(arg) ?
  /* istanbul ignore next (preact) */
  arg[0] : arg;

  if (!arg && defaultValue) {
    return defaultValue;
  } else {
    return arg;
  }
}
/**
 * @param {Object} element (P)react element
 * @return {Boolean} whether it's a DOM element
 */


function isDOMElement$1(element) {


  return typeof element.type === 'string';
}
/**
 * @param {Object} element (P)react element
 * @return {Object} the props
 */


function getElementProps$1(element) {

  return element.props;
}
/**
 * Throws a helpful error message for required properties. Useful
 * to be used as a default in destructuring or object params.
 * @param {String} fnName the function name
 * @param {String} propName the prop name
 */


function requiredProp$1(fnName, propName) {
  // eslint-disable-next-line no-console
  console.error(`The property "${propName}" is required in "${fnName}"`);
}

const stateKeys$1 = ['highlightedIndex', 'inputValue', 'isOpen', 'selectedItem', 'type'];
/**
 * @param {Object} state the state object
 * @return {Object} state that is relevant to downshift
 */

function pickState$1(state) {
  if (state === void 0) {
    state = {};
  }

  const result = {};
  stateKeys$1.forEach(k => {
    if (state.hasOwnProperty(k)) {
      result[k] = state[k];
    }
  });
  return result;
}
/**
 * This will perform a shallow merge of the given state object
 * with the state coming from props
 * (for the controlled component scenario)
 * This is used in state updater functions so they're referencing
 * the right state regardless of where it comes from.
 *
 * @param {Object} state The state of the component/hook.
 * @param {Object} props The props that may contain controlled values.
 * @returns {Object} The merged controlled state.
 */


function getState$1(state, props) {
  return Object.keys(state).reduce((prevState, key) => {
    prevState[key] = isControlledProp$1(props, key) ? props[key] : state[key];
    return prevState;
  }, {});
}
/**
 * This determines whether a prop is a "controlled prop" meaning it is
 * state which is controlled by the outside of this component rather
 * than within this component.
 *
 * @param {Object} props The props that may contain controlled values.
 * @param {String} key the key to check
 * @return {Boolean} whether it is a controlled controlled prop
 */


function isControlledProp$1(props, key) {
  return props[key] !== undefined;
}
/**
 * Normalizes the 'key' property of a KeyboardEvent in IE/Edge
 * @param {Object} event a keyboardEvent object
 * @return {String} keyboard key
 */


function normalizeArrowKey$1(event) {
  const {
    key,
    keyCode
  } = event;
  /* istanbul ignore next (ie) */

  if (keyCode >= 37 && keyCode <= 40 && key.indexOf('Arrow') !== 0) {
    return `Arrow${key}`;
  }

  return key;
}
/**
 * Simple check if the value passed is object literal
 * @param {*} obj any things
 * @return {Boolean} whether it's object literal
 */


function isPlainObject$1(obj) {
  return Object.prototype.toString.call(obj) === '[object Object]';
}
/**
 * Returns the new index in the list, in a circular way. If next value is out of bonds from the total,
 * it will wrap to either 0 or itemCount - 1.
 *
 * @param {number} moveAmount Number of positions to move. Negative to move backwards, positive forwards.
 * @param {number} baseIndex The initial position to move from.
 * @param {number} itemCount The total number of items.
 * @param {Function} getItemNodeFromIndex Used to check if item is disabled.
 * @param {boolean} circular Specify if navigation is circular. Default is true.
 * @returns {number} The new index after the move.
 */


function getNextWrappingIndex$1(moveAmount, baseIndex, itemCount, getItemNodeFromIndex, circular) {
  if (circular === void 0) {
    circular = true;
  }

  if (itemCount === 0) {
    return -1;
  }

  const itemsLastIndex = itemCount - 1;

  if (typeof baseIndex !== 'number' || baseIndex < 0 || baseIndex >= itemCount) {
    baseIndex = moveAmount > 0 ? -1 : itemsLastIndex + 1;
  }

  let newIndex = baseIndex + moveAmount;

  if (newIndex < 0) {
    newIndex = circular ? itemsLastIndex : 0;
  } else if (newIndex > itemsLastIndex) {
    newIndex = circular ? 0 : itemsLastIndex;
  }

  const nonDisabledNewIndex = getNextNonDisabledIndex$1(moveAmount, newIndex, itemCount, getItemNodeFromIndex, circular);

  if (nonDisabledNewIndex === -1) {
    return baseIndex >= itemCount ? -1 : baseIndex;
  }

  return nonDisabledNewIndex;
}
/**
 * Returns the next index in the list of an item that is not disabled.
 *
 * @param {number} moveAmount Number of positions to move. Negative to move backwards, positive forwards.
 * @param {number} baseIndex The initial position to move from.
 * @param {number} itemCount The total number of items.
 * @param {Function} getItemNodeFromIndex Used to check if item is disabled.
 * @param {boolean} circular Specify if navigation is circular. Default is true.
 * @returns {number} The new index. Returns baseIndex if item is not disabled. Returns next non-disabled item otherwise. If no non-disabled found it will return -1.
 */


function getNextNonDisabledIndex$1(moveAmount, baseIndex, itemCount, getItemNodeFromIndex, circular) {
  const currentElementNode = getItemNodeFromIndex(baseIndex);

  if (!currentElementNode || !currentElementNode.hasAttribute('disabled')) {
    return baseIndex;
  }

  if (moveAmount > 0) {
    for (let index = baseIndex + 1; index < itemCount; index++) {
      if (!getItemNodeFromIndex(index).hasAttribute('disabled')) {
        return index;
      }
    }
  } else {
    for (let index = baseIndex - 1; index >= 0; index--) {
      if (!getItemNodeFromIndex(index).hasAttribute('disabled')) {
        return index;
      }
    }
  }

  if (circular) {
    return moveAmount > 0 ? getNextNonDisabledIndex$1(1, 0, itemCount, getItemNodeFromIndex, false) : getNextNonDisabledIndex$1(-1, itemCount - 1, itemCount, getItemNodeFromIndex, false);
  }

  return -1;
}
/**
 * Checks if event target is within the downshift elements.
 *
 * @param {EventTarget} target Target to check.
 * @param {HTMLElement[]} downshiftElements The elements that form downshift (list, toggle button etc).
 * @param {Window} environment The window context where downshift renders.
 * @param {boolean} checkActiveElement Whether to also check activeElement.
 *
 * @returns {boolean} Whether or not the target is within downshift elements.
 */


function targetWithinDownshift$1(target, downshiftElements, environment, checkActiveElement) {
  if (checkActiveElement === void 0) {
    checkActiveElement = true;
  }

  return downshiftElements.some(contextNode => contextNode && (isOrContainsNode$1(contextNode, target, environment) || checkActiveElement && isOrContainsNode$1(contextNode, environment.document.activeElement, environment)));
} // eslint-disable-next-line import/no-mutable-exports


let validateControlledUnchanged$1 = noop$1;
/* istanbul ignore next */

if (process.env.NODE_ENV !== 'production') {
  validateControlledUnchanged$1 = (state, prevProps, nextProps) => {
    const warningDescription = `This prop should not switch from controlled to uncontrolled (or vice versa). Decide between using a controlled or uncontrolled Downshift element for the lifetime of the component. More info: https://github.com/downshift-js/downshift#control-props`;
    Object.keys(state).forEach(propKey => {
      if (prevProps[propKey] !== undefined && nextProps[propKey] === undefined) {
        // eslint-disable-next-line no-console
        console.error(`downshift: A component has changed the controlled prop "${propKey}" to be uncontrolled. ${warningDescription}`);
      } else if (prevProps[propKey] === undefined && nextProps[propKey] !== undefined) {
        // eslint-disable-next-line no-console
        console.error(`downshift: A component has changed the uncontrolled prop "${propKey}" to be controlled. ${warningDescription}`);
      }
    });
  };
}

const cleanupStatus$1 = debounce$1(documentProp => {
  getStatusDiv$1(documentProp).textContent = '';
}, 500);
/**
 * @param {String} status the status message
 * @param {Object} documentProp document passed by the user.
 */

function setStatus$1(status, documentProp) {
  const div = getStatusDiv$1(documentProp);

  if (!status) {
    return;
  }

  div.textContent = status;
  cleanupStatus$1(documentProp);
}
/**
 * Get the status node or create it if it does not already exist.
 * @param {Object} documentProp document passed by the user.
 * @return {HTMLElement} the status node.
 */


function getStatusDiv$1(documentProp) {
  if (documentProp === void 0) {
    documentProp = document;
  }

  let statusDiv = documentProp.getElementById('a11y-status-message');

  if (statusDiv) {
    return statusDiv;
  }

  statusDiv = documentProp.createElement('div');
  statusDiv.setAttribute('id', 'a11y-status-message');
  statusDiv.setAttribute('role', 'status');
  statusDiv.setAttribute('aria-live', 'polite');
  statusDiv.setAttribute('aria-relevant', 'additions text');
  Object.assign(statusDiv.style, {
    border: '0',
    clip: 'rect(0 0 0 0)',
    height: '1px',
    margin: '-1px',
    overflow: 'hidden',
    padding: '0',
    position: 'absolute',
    width: '1px'
  });
  documentProp.body.appendChild(statusDiv);
  return statusDiv;
}

const unknown$1 = process.env.NODE_ENV !== "production" ? '__autocomplete_unknown__' : 0;
const mouseUp$1 = process.env.NODE_ENV !== "production" ? '__autocomplete_mouseup__' : 1;
const itemMouseEnter$1 = process.env.NODE_ENV !== "production" ? '__autocomplete_item_mouseenter__' : 2;
const keyDownArrowUp$1 = process.env.NODE_ENV !== "production" ? '__autocomplete_keydown_arrow_up__' : 3;
const keyDownArrowDown$1 = process.env.NODE_ENV !== "production" ? '__autocomplete_keydown_arrow_down__' : 4;
const keyDownEscape$1 = process.env.NODE_ENV !== "production" ? '__autocomplete_keydown_escape__' : 5;
const keyDownEnter$1 = process.env.NODE_ENV !== "production" ? '__autocomplete_keydown_enter__' : 6;
const keyDownHome$1 = process.env.NODE_ENV !== "production" ? '__autocomplete_keydown_home__' : 7;
const keyDownEnd$1 = process.env.NODE_ENV !== "production" ? '__autocomplete_keydown_end__' : 8;
const clickItem$1 = process.env.NODE_ENV !== "production" ? '__autocomplete_click_item__' : 9;
const blurInput$1 = process.env.NODE_ENV !== "production" ? '__autocomplete_blur_input__' : 10;
const changeInput$1 = process.env.NODE_ENV !== "production" ? '__autocomplete_change_input__' : 11;
const keyDownSpaceButton$1 = process.env.NODE_ENV !== "production" ? '__autocomplete_keydown_space_button__' : 12;
const clickButton$1 = process.env.NODE_ENV !== "production" ? '__autocomplete_click_button__' : 13;
const blurButton$1 = process.env.NODE_ENV !== "production" ? '__autocomplete_blur_button__' : 14;
const controlledPropUpdatedSelectedItem$1 = process.env.NODE_ENV !== "production" ? '__autocomplete_controlled_prop_updated_selected_item__' : 15;
const touchEnd$1 = process.env.NODE_ENV !== "production" ? '__autocomplete_touchend__' : 16;

var stateChangeTypes$3$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  unknown: unknown$1,
  mouseUp: mouseUp$1,
  itemMouseEnter: itemMouseEnter$1,
  keyDownArrowUp: keyDownArrowUp$1,
  keyDownArrowDown: keyDownArrowDown$1,
  keyDownEscape: keyDownEscape$1,
  keyDownEnter: keyDownEnter$1,
  keyDownHome: keyDownHome$1,
  keyDownEnd: keyDownEnd$1,
  clickItem: clickItem$1,
  blurInput: blurInput$1,
  changeInput: changeInput$1,
  keyDownSpaceButton: keyDownSpaceButton$1,
  clickButton: clickButton$1,
  blurButton: blurButton$1,
  controlledPropUpdatedSelectedItem: controlledPropUpdatedSelectedItem$1,
  touchEnd: touchEnd$1
});

/* eslint camelcase:0 */

const Downshift$1 = /*#__PURE__*/(() => {
  class Downshift extends Component {
    constructor(_props) {
      var _this;

      super(_props);
      _this = this;
      this.id = this.props.id || `downshift-${generateId$1()}`;
      this.menuId = this.props.menuId || `${this.id}-menu`;
      this.labelId = this.props.labelId || `${this.id}-label`;
      this.inputId = this.props.inputId || `${this.id}-input`;

      this.getItemId = this.props.getItemId || (index => `${this.id}-item-${index}`);

      this.input = null;
      this.items = [];
      this.itemCount = null;
      this.previousResultCount = 0;
      this.timeoutIds = [];

      this.internalSetTimeout = (fn, time) => {
        const id = setTimeout(() => {
          this.timeoutIds = this.timeoutIds.filter(i => i !== id);
          fn();
        }, time);
        this.timeoutIds.push(id);
      };

      this.setItemCount = count => {
        this.itemCount = count;
      };

      this.unsetItemCount = () => {
        this.itemCount = null;
      };

      this.setHighlightedIndex = function (highlightedIndex, otherStateToSet) {
        if (highlightedIndex === void 0) {
          highlightedIndex = _this.props.defaultHighlightedIndex;
        }

        if (otherStateToSet === void 0) {
          otherStateToSet = {};
        }

        otherStateToSet = pickState$1(otherStateToSet);

        _this.internalSetState({
          highlightedIndex,
          ...otherStateToSet
        });
      };

      this.clearSelection = cb => {
        this.internalSetState({
          selectedItem: null,
          inputValue: '',
          highlightedIndex: this.props.defaultHighlightedIndex,
          isOpen: this.props.defaultIsOpen
        }, cb);
      };

      this.selectItem = (item, otherStateToSet, cb) => {
        otherStateToSet = pickState$1(otherStateToSet);
        this.internalSetState({
          isOpen: this.props.defaultIsOpen,
          highlightedIndex: this.props.defaultHighlightedIndex,
          selectedItem: item,
          inputValue: this.props.itemToString(item),
          ...otherStateToSet
        }, cb);
      };

      this.selectItemAtIndex = (itemIndex, otherStateToSet, cb) => {
        const item = this.items[itemIndex];

        if (item == null) {
          return;
        }

        this.selectItem(item, otherStateToSet, cb);
      };

      this.selectHighlightedItem = (otherStateToSet, cb) => {
        return this.selectItemAtIndex(this.getState().highlightedIndex, otherStateToSet, cb);
      };

      this.internalSetState = (stateToSet, cb) => {
        let isItemSelected, onChangeArg;
        const onStateChangeArg = {};
        const isStateToSetFunction = typeof stateToSet === 'function'; // we want to call `onInputValueChange` before the `setState` call
        // so someone controlling the `inputValue` state gets notified of
        // the input change as soon as possible. This avoids issues with
        // preserving the cursor position.
        // See https://github.com/downshift-js/downshift/issues/217 for more info.

        if (!isStateToSetFunction && stateToSet.hasOwnProperty('inputValue')) {
          this.props.onInputValueChange(stateToSet.inputValue, { ...this.getStateAndHelpers(),
            ...stateToSet
          });
        }

        return this.setState(state => {
          state = this.getState(state);
          let newStateToSet = isStateToSetFunction ? stateToSet(state) : stateToSet; // Your own function that could modify the state that will be set.

          newStateToSet = this.props.stateReducer(state, newStateToSet); // checks if an item is selected, regardless of if it's different from
          // what was selected before
          // used to determine if onSelect and onChange callbacks should be called

          isItemSelected = newStateToSet.hasOwnProperty('selectedItem'); // this keeps track of the object we want to call with setState

          const nextState = {}; // this is just used to tell whether the state changed
          // and we're trying to update that state. OR if the selection has changed and we're
          // trying to update the selection

          if (isItemSelected && newStateToSet.selectedItem !== state.selectedItem) {
            onChangeArg = newStateToSet.selectedItem;
          }

          newStateToSet.type = newStateToSet.type || unknown$1;
          Object.keys(newStateToSet).forEach(key => {
            // onStateChangeArg should only have the state that is
            // actually changing
            if (state[key] !== newStateToSet[key]) {
              onStateChangeArg[key] = newStateToSet[key];
            } // the type is useful for the onStateChangeArg
            // but we don't actually want to set it in internal state.
            // this is an undocumented feature for now... Not all internalSetState
            // calls support it and I'm not certain we want them to yet.
            // But it enables users controlling the isOpen state to know when
            // the isOpen state changes due to mouseup events which is quite handy.


            if (key === 'type') {
              return;
            }

            newStateToSet[key]; // if it's coming from props, then we don't care to set it internally

            if (!isControlledProp$1(this.props, key)) {
              nextState[key] = newStateToSet[key];
            }
          }); // if stateToSet is a function, then we weren't able to call onInputValueChange
          // earlier, so we'll call it now that we know what the inputValue state will be.

          if (isStateToSetFunction && newStateToSet.hasOwnProperty('inputValue')) {
            this.props.onInputValueChange(newStateToSet.inputValue, { ...this.getStateAndHelpers(),
              ...newStateToSet
            });
          }

          return nextState;
        }, () => {
          // call the provided callback if it's a function
          cbToCb$1(cb)(); // only call the onStateChange and onChange callbacks if
          // we have relevant information to pass them.

          const hasMoreStateThanType = Object.keys(onStateChangeArg).length > 1;

          if (hasMoreStateThanType) {
            this.props.onStateChange(onStateChangeArg, this.getStateAndHelpers());
          }

          if (isItemSelected) {
            this.props.onSelect(stateToSet.selectedItem, this.getStateAndHelpers());
          }

          if (onChangeArg !== undefined) {
            this.props.onChange(onChangeArg, this.getStateAndHelpers());
          } // this is currently undocumented and therefore subject to change
          // We'll try to not break it, but just be warned.


          this.props.onUserAction(onStateChangeArg, this.getStateAndHelpers());
        });
      };

      this.rootRef = node => this._rootNode = node;

      this.getRootProps = function (_temp, _temp2) {
        let {
          refKey = 'ref',
          ref,
          ...rest
        } = _temp === void 0 ? {} : _temp;
        let {
          suppressRefError = false
        } = _temp2 === void 0 ? {} : _temp2;
        // this is used in the render to know whether the user has called getRootProps.
        // It uses that to know whether to apply the props automatically
        _this.getRootProps.called = true;
        _this.getRootProps.refKey = refKey;
        _this.getRootProps.suppressRefError = suppressRefError;

        const {
          isOpen
        } = _this.getState();

        return {
          [refKey]: handleRefs$1(ref, _this.rootRef),
          role: 'combobox',
          'aria-expanded': isOpen,
          'aria-haspopup': 'listbox',
          'aria-owns': isOpen ? _this.menuId : null,
          'aria-labelledby': _this.labelId,
          ...rest
        };
      };

      this.keyDownHandlers = {
        ArrowDown(event) {
          event.preventDefault();

          if (this.getState().isOpen) {
            const amount = event.shiftKey ? 5 : 1;
            this.moveHighlightedIndex(amount, {
              type: keyDownArrowDown$1
            });
          } else {
            this.internalSetState({
              isOpen: true,
              type: keyDownArrowDown$1
            }, () => {
              const itemCount = this.getItemCount();

              if (itemCount > 0) {
                const {
                  highlightedIndex
                } = this.getState();
                const nextHighlightedIndex = getNextWrappingIndex$1(1, highlightedIndex, itemCount, index => this.getItemNodeFromIndex(index));
                this.setHighlightedIndex(nextHighlightedIndex, {
                  type: keyDownArrowDown$1
                });
              }
            });
          }
        },

        ArrowUp(event) {
          event.preventDefault();

          if (this.getState().isOpen) {
            const amount = event.shiftKey ? -5 : -1;
            this.moveHighlightedIndex(amount, {
              type: keyDownArrowUp$1
            });
          } else {
            this.internalSetState({
              isOpen: true,
              type: keyDownArrowUp$1
            }, () => {
              const itemCount = this.getItemCount();

              if (itemCount > 0) {
                const {
                  highlightedIndex
                } = this.getState();
                const nextHighlightedIndex = getNextWrappingIndex$1(-1, highlightedIndex, itemCount, index => this.getItemNodeFromIndex(index));
                this.setHighlightedIndex(nextHighlightedIndex, {
                  type: keyDownArrowUp$1
                });
              }
            });
          }
        },

        Enter(event) {
          if (event.which === 229) {
            return;
          }

          const {
            isOpen,
            highlightedIndex
          } = this.getState();

          if (isOpen && highlightedIndex != null) {
            event.preventDefault();
            const item = this.items[highlightedIndex];
            const itemNode = this.getItemNodeFromIndex(highlightedIndex);

            if (item == null || itemNode && itemNode.hasAttribute('disabled')) {
              return;
            }

            this.selectHighlightedItem({
              type: keyDownEnter$1
            });
          }
        },

        Escape(event) {
          event.preventDefault();
          this.reset({
            type: keyDownEscape$1,
            ...(!this.state.isOpen && {
              selectedItem: null,
              inputValue: ''
            })
          });
        }

      };
      this.buttonKeyDownHandlers = { ...this.keyDownHandlers,

        ' '(event) {
          event.preventDefault();
          this.toggleMenu({
            type: keyDownSpaceButton$1
          });
        }

      };
      this.inputKeyDownHandlers = { ...this.keyDownHandlers,

        Home(event) {
          const {
            isOpen
          } = this.getState();

          if (!isOpen) {
            return;
          }

          event.preventDefault();
          const itemCount = this.getItemCount();

          if (itemCount <= 0 || !isOpen) {
            return;
          } // get next non-disabled starting downwards from 0 if that's disabled.


          const newHighlightedIndex = getNextNonDisabledIndex$1(1, 0, itemCount, index => this.getItemNodeFromIndex(index), false);
          this.setHighlightedIndex(newHighlightedIndex, {
            type: keyDownHome$1
          });
        },

        End(event) {
          const {
            isOpen
          } = this.getState();

          if (!isOpen) {
            return;
          }

          event.preventDefault();
          const itemCount = this.getItemCount();

          if (itemCount <= 0 || !isOpen) {
            return;
          } // get next non-disabled starting upwards from last index if that's disabled.


          const newHighlightedIndex = getNextNonDisabledIndex$1(-1, itemCount - 1, itemCount, index => this.getItemNodeFromIndex(index), false);
          this.setHighlightedIndex(newHighlightedIndex, {
            type: keyDownEnd$1
          });
        }

      };

      this.getToggleButtonProps = function (_temp3) {
        let {
          onClick,
          onPress,
          onKeyDown,
          onKeyUp,
          onBlur,
          ...rest
        } = _temp3 === void 0 ? {} : _temp3;

        const {
          isOpen
        } = _this.getState();

        const enabledEventHandlers = {
          onClick: callAllEventHandlers$1(onClick, _this.buttonHandleClick),
          onKeyDown: callAllEventHandlers$1(onKeyDown, _this.buttonHandleKeyDown),
          onKeyUp: callAllEventHandlers$1(onKeyUp, _this.buttonHandleKeyUp),
          onBlur: callAllEventHandlers$1(onBlur, _this.buttonHandleBlur)
        };
        const eventHandlers = rest.disabled ? {} : enabledEventHandlers;
        return {
          type: 'button',
          role: 'button',
          'aria-label': isOpen ? 'close menu' : 'open menu',
          'aria-haspopup': true,
          'data-toggle': true,
          ...eventHandlers,
          ...rest
        };
      };

      this.buttonHandleKeyUp = event => {
        // Prevent click event from emitting in Firefox
        event.preventDefault();
      };

      this.buttonHandleKeyDown = event => {
        const key = normalizeArrowKey$1(event);

        if (this.buttonKeyDownHandlers[key]) {
          this.buttonKeyDownHandlers[key].call(this, event);
        }
      };

      this.buttonHandleClick = event => {
        event.preventDefault(); // handle odd case for Safari and Firefox which
        // don't give the button the focus properly.

        /* istanbul ignore if (can't reasonably test this) */

        if (this.props.environment.document.activeElement === this.props.environment.document.body) {
          event.target.focus();
        } // to simplify testing components that use downshift, we'll not wrap this in a setTimeout
        // if the NODE_ENV is test. With the proper build system, this should be dead code eliminated
        // when building for production and should therefore have no impact on production code.


        if (process.env.NODE_ENV === 'test') {
          this.toggleMenu({
            type: clickButton$1
          });
        } else {
          // Ensure that toggle of menu occurs after the potential blur event in iOS
          this.internalSetTimeout(() => this.toggleMenu({
            type: clickButton$1
          }));
        }
      };

      this.buttonHandleBlur = event => {
        const blurTarget = event.target; // Save blur target for comparison with activeElement later
        // Need setTimeout, so that when the user presses Tab, the activeElement is the next focused element, not body element

        this.internalSetTimeout(() => {
          if (!this.isMouseDown && (this.props.environment.document.activeElement == null || this.props.environment.document.activeElement.id !== this.inputId) && this.props.environment.document.activeElement !== blurTarget // Do nothing if we refocus the same element again (to solve issue in Safari on iOS)
          ) {
            this.reset({
              type: blurButton$1
            });
          }
        });
      };

      this.getLabelProps = props => {
        return {
          htmlFor: this.inputId,
          id: this.labelId,
          ...props
        };
      };

      this.getInputProps = function (_temp4) {
        let {
          onKeyDown,
          onBlur,
          onChange,
          onInput,
          onChangeText,
          ...rest
        } = _temp4 === void 0 ? {} : _temp4;
        let onChangeKey;
        let eventHandlers = {};
        /* istanbul ignore next (preact) */

        {
          onChangeKey = 'onChange';
        }

        const {
          inputValue,
          isOpen,
          highlightedIndex
        } = _this.getState();

        if (!rest.disabled) {
          eventHandlers = {
            [onChangeKey]: callAllEventHandlers$1(onChange, onInput, _this.inputHandleChange),
            onKeyDown: callAllEventHandlers$1(onKeyDown, _this.inputHandleKeyDown),
            onBlur: callAllEventHandlers$1(onBlur, _this.inputHandleBlur)
          };
        }

        return {
          'aria-autocomplete': 'list',
          'aria-activedescendant': isOpen && typeof highlightedIndex === 'number' && highlightedIndex >= 0 ? _this.getItemId(highlightedIndex) : null,
          'aria-controls': isOpen ? _this.menuId : null,
          'aria-labelledby': _this.labelId,
          // https://developer.mozilla.org/en-US/docs/Web/Security/Securing_your_site/Turning_off_form_autocompletion
          // revert back since autocomplete="nope" is ignored on latest Chrome and Opera
          autoComplete: 'off',
          value: inputValue,
          id: _this.inputId,
          ...eventHandlers,
          ...rest
        };
      };

      this.inputHandleKeyDown = event => {
        const key = normalizeArrowKey$1(event);

        if (key && this.inputKeyDownHandlers[key]) {
          this.inputKeyDownHandlers[key].call(this, event);
        }
      };

      this.inputHandleChange = event => {
        this.internalSetState({
          type: changeInput$1,
          isOpen: true,
          inputValue: event.target.value,
          highlightedIndex: this.props.defaultHighlightedIndex
        });
      };

      this.inputHandleBlur = () => {
        // Need setTimeout, so that when the user presses Tab, the activeElement is the next focused element, not the body element
        this.internalSetTimeout(() => {
          const downshiftButtonIsActive = this.props.environment.document && !!this.props.environment.document.activeElement && !!this.props.environment.document.activeElement.dataset && this.props.environment.document.activeElement.dataset.toggle && this._rootNode && this._rootNode.contains(this.props.environment.document.activeElement);

          if (!this.isMouseDown && !downshiftButtonIsActive) {
            this.reset({
              type: blurInput$1
            });
          }
        });
      };

      this.menuRef = node => {
        this._menuNode = node;
      };

      this.getMenuProps = function (_temp5, _temp6) {
        let {
          refKey = 'ref',
          ref,
          ...props
        } = _temp5 === void 0 ? {} : _temp5;
        let {
          suppressRefError = false
        } = _temp6 === void 0 ? {} : _temp6;
        _this.getMenuProps.called = true;
        _this.getMenuProps.refKey = refKey;
        _this.getMenuProps.suppressRefError = suppressRefError;
        return {
          [refKey]: handleRefs$1(ref, _this.menuRef),
          role: 'listbox',
          'aria-labelledby': props && props['aria-label'] ? null : _this.labelId,
          id: _this.menuId,
          ...props
        };
      };

      this.getItemProps = function (_temp7) {
        let {
          onMouseMove,
          onMouseDown,
          onClick,
          onPress,
          index,
          item = process.env.NODE_ENV === 'production' ?
          /* istanbul ignore next */
          undefined : requiredProp$1('getItemProps', 'item'),
          ...rest
        } = _temp7 === void 0 ? {} : _temp7;

        if (index === undefined) {
          _this.items.push(item);

          index = _this.items.indexOf(item);
        } else {
          _this.items[index] = item;
        }

        const onSelectKey = 'onClick';
        const customClickHandler = onClick;
        const enabledEventHandlers = {
          // onMouseMove is used over onMouseEnter here. onMouseMove
          // is only triggered on actual mouse movement while onMouseEnter
          // can fire on DOM changes, interrupting keyboard navigation
          onMouseMove: callAllEventHandlers$1(onMouseMove, () => {
            if (index === _this.getState().highlightedIndex) {
              return;
            }

            _this.setHighlightedIndex(index, {
              type: itemMouseEnter$1
            }); // We never want to manually scroll when changing state based
            // on `onMouseMove` because we will be moving the element out
            // from under the user which is currently scrolling/moving the
            // cursor


            _this.avoidScrolling = true;

            _this.internalSetTimeout(() => _this.avoidScrolling = false, 250);
          }),
          onMouseDown: callAllEventHandlers$1(onMouseDown, event => {
            // This prevents the activeElement from being changed
            // to the item so it can remain with the current activeElement
            // which is a more common use case.
            event.preventDefault();
          }),
          [onSelectKey]: callAllEventHandlers$1(customClickHandler, () => {
            _this.selectItemAtIndex(index, {
              type: clickItem$1
            });
          })
        }; // Passing down the onMouseDown handler to prevent redirect
        // of the activeElement if clicking on disabled items

        const eventHandlers = rest.disabled ? {
          onMouseDown: enabledEventHandlers.onMouseDown
        } : enabledEventHandlers;
        return {
          id: _this.getItemId(index),
          role: 'option',
          'aria-selected': _this.getState().highlightedIndex === index,
          ...eventHandlers,
          ...rest
        };
      };

      this.clearItems = () => {
        this.items = [];
      };

      this.reset = function (otherStateToSet, cb) {
        if (otherStateToSet === void 0) {
          otherStateToSet = {};
        }

        otherStateToSet = pickState$1(otherStateToSet);

        _this.internalSetState(_ref => {
          let {
            selectedItem
          } = _ref;
          return {
            isOpen: _this.props.defaultIsOpen,
            highlightedIndex: _this.props.defaultHighlightedIndex,
            inputValue: _this.props.itemToString(selectedItem),
            ...otherStateToSet
          };
        }, cb);
      };

      this.toggleMenu = function (otherStateToSet, cb) {
        if (otherStateToSet === void 0) {
          otherStateToSet = {};
        }

        otherStateToSet = pickState$1(otherStateToSet);

        _this.internalSetState(_ref2 => {
          let {
            isOpen
          } = _ref2;
          return {
            isOpen: !isOpen,
            ...(isOpen && {
              highlightedIndex: _this.props.defaultHighlightedIndex
            }),
            ...otherStateToSet
          };
        }, () => {
          const {
            isOpen,
            highlightedIndex
          } = _this.getState();

          if (isOpen) {
            if (_this.getItemCount() > 0 && typeof highlightedIndex === 'number') {
              _this.setHighlightedIndex(highlightedIndex, otherStateToSet);
            }
          }

          cbToCb$1(cb)();
        });
      };

      this.openMenu = cb => {
        this.internalSetState({
          isOpen: true
        }, cb);
      };

      this.closeMenu = cb => {
        this.internalSetState({
          isOpen: false
        }, cb);
      };

      this.updateStatus = debounce$1(() => {
        const state = this.getState();
        const item = this.items[state.highlightedIndex];
        const resultCount = this.getItemCount();
        const status = this.props.getA11yStatusMessage({
          itemToString: this.props.itemToString,
          previousResultCount: this.previousResultCount,
          resultCount,
          highlightedItem: item,
          ...state
        });
        this.previousResultCount = resultCount;
        setStatus$1(status, this.props.environment.document);
      }, 200);
      // fancy destructuring + defaults + aliases
      // this basically says each value of state should either be set to
      // the initial value or the default value if the initial value is not provided
      const {
        defaultHighlightedIndex,
        initialHighlightedIndex: _highlightedIndex = defaultHighlightedIndex,
        defaultIsOpen,
        initialIsOpen: _isOpen = defaultIsOpen,
        initialInputValue: _inputValue = '',
        initialSelectedItem: _selectedItem = null
      } = this.props;

      const _state = this.getState({
        highlightedIndex: _highlightedIndex,
        isOpen: _isOpen,
        inputValue: _inputValue,
        selectedItem: _selectedItem
      });

      if (_state.selectedItem != null && this.props.initialInputValue === undefined) {
        _state.inputValue = this.props.itemToString(_state.selectedItem);
      }

      this.state = _state;
    }

    /**
     * Clear all running timeouts
     */
    internalClearTimeouts() {
      this.timeoutIds.forEach(id => {
        clearTimeout(id);
      });
      this.timeoutIds = [];
    }
    /**
     * Gets the state based on internal state or props
     * If a state value is passed via props, then that
     * is the value given, otherwise it's retrieved from
     * stateToMerge
     *
     * @param {Object} stateToMerge defaults to this.state
     * @return {Object} the state
     */


    getState(stateToMerge) {
      if (stateToMerge === void 0) {
        stateToMerge = this.state;
      }

      return getState$1(stateToMerge, this.props);
    }

    getItemCount() {
      // things read better this way. They're in priority order:
      // 1. `this.itemCount`
      // 2. `this.props.itemCount`
      // 3. `this.items.length`
      let itemCount = this.items.length;

      if (this.itemCount != null) {
        itemCount = this.itemCount;
      } else if (this.props.itemCount !== undefined) {
        itemCount = this.props.itemCount;
      }

      return itemCount;
    }

    getItemNodeFromIndex(index) {
      return this.props.environment.document.getElementById(this.getItemId(index));
    }

    scrollHighlightedItemIntoView() {
      /* istanbul ignore else (react-native) */
      {
        const node = this.getItemNodeFromIndex(this.getState().highlightedIndex);
        this.props.scrollIntoView(node, this._menuNode);
      }
    }

    moveHighlightedIndex(amount, otherStateToSet) {
      const itemCount = this.getItemCount();
      const {
        highlightedIndex
      } = this.getState();

      if (itemCount > 0) {
        const nextHighlightedIndex = getNextWrappingIndex$1(amount, highlightedIndex, itemCount, index => this.getItemNodeFromIndex(index));
        this.setHighlightedIndex(nextHighlightedIndex, otherStateToSet);
      }
    }

    getStateAndHelpers() {
      const {
        highlightedIndex,
        inputValue,
        selectedItem,
        isOpen
      } = this.getState();
      const {
        itemToString
      } = this.props;
      const {
        id
      } = this;
      const {
        getRootProps,
        getToggleButtonProps,
        getLabelProps,
        getMenuProps,
        getInputProps,
        getItemProps,
        openMenu,
        closeMenu,
        toggleMenu,
        selectItem,
        selectItemAtIndex,
        selectHighlightedItem,
        setHighlightedIndex,
        clearSelection,
        clearItems,
        reset,
        setItemCount,
        unsetItemCount,
        internalSetState: setState
      } = this;
      return {
        // prop getters
        getRootProps,
        getToggleButtonProps,
        getLabelProps,
        getMenuProps,
        getInputProps,
        getItemProps,
        // actions
        reset,
        openMenu,
        closeMenu,
        toggleMenu,
        selectItem,
        selectItemAtIndex,
        selectHighlightedItem,
        setHighlightedIndex,
        clearSelection,
        clearItems,
        setItemCount,
        unsetItemCount,
        setState,
        // props
        itemToString,
        // derived
        id,
        // state
        highlightedIndex,
        inputValue,
        isOpen,
        selectedItem
      };
    } //////////////////////////// ROOT


    componentDidMount() {
      /* istanbul ignore if (react-native) */
      if (process.env.NODE_ENV !== 'production' && !false && this.getMenuProps.called && !this.getMenuProps.suppressRefError) {
        validateGetMenuPropsCalledCorrectly$1(this._menuNode, this.getMenuProps);
      }
      /* istanbul ignore if (react-native) */


      {
        // this.isMouseDown helps us track whether the mouse is currently held down.
        // This is useful when the user clicks on an item in the list, but holds the mouse
        // down long enough for the list to disappear (because the blur event fires on the input)
        // this.isMouseDown is used in the blur handler on the input to determine whether the blur event should
        // trigger hiding the menu.
        const onMouseDown = () => {
          this.isMouseDown = true;
        };

        const onMouseUp = event => {
          this.isMouseDown = false; // if the target element or the activeElement is within a downshift node
          // then we don't want to reset downshift

          const contextWithinDownshift = targetWithinDownshift$1(event.target, [this._rootNode, this._menuNode], this.props.environment);

          if (!contextWithinDownshift && this.getState().isOpen) {
            this.reset({
              type: mouseUp$1
            }, () => this.props.onOuterClick(this.getStateAndHelpers()));
          }
        }; // Touching an element in iOS gives focus and hover states, but touching out of
        // the element will remove hover, and persist the focus state, resulting in the
        // blur event not being triggered.
        // this.isTouchMove helps us track whether the user is tapping or swiping on a touch screen.
        // If the user taps outside of Downshift, the component should be reset,
        // but not if the user is swiping


        const onTouchStart = () => {
          this.isTouchMove = false;
        };

        const onTouchMove = () => {
          this.isTouchMove = true;
        };

        const onTouchEnd = event => {
          const contextWithinDownshift = targetWithinDownshift$1(event.target, [this._rootNode, this._menuNode], this.props.environment, false);

          if (!this.isTouchMove && !contextWithinDownshift && this.getState().isOpen) {
            this.reset({
              type: touchEnd$1
            }, () => this.props.onOuterClick(this.getStateAndHelpers()));
          }
        };

        const {
          environment
        } = this.props;
        environment.addEventListener('mousedown', onMouseDown);
        environment.addEventListener('mouseup', onMouseUp);
        environment.addEventListener('touchstart', onTouchStart);
        environment.addEventListener('touchmove', onTouchMove);
        environment.addEventListener('touchend', onTouchEnd);

        this.cleanup = () => {
          this.internalClearTimeouts();
          this.updateStatus.cancel();
          environment.removeEventListener('mousedown', onMouseDown);
          environment.removeEventListener('mouseup', onMouseUp);
          environment.removeEventListener('touchstart', onTouchStart);
          environment.removeEventListener('touchmove', onTouchMove);
          environment.removeEventListener('touchend', onTouchEnd);
        };
      }
    }

    shouldScroll(prevState, prevProps) {
      const {
        highlightedIndex: currentHighlightedIndex
      } = this.props.highlightedIndex === undefined ? this.getState() : this.props;
      const {
        highlightedIndex: prevHighlightedIndex
      } = prevProps.highlightedIndex === undefined ? prevState : prevProps;
      const scrollWhenOpen = currentHighlightedIndex && this.getState().isOpen && !prevState.isOpen;
      const scrollWhenNavigating = currentHighlightedIndex !== prevHighlightedIndex;
      return scrollWhenOpen || scrollWhenNavigating;
    }

    componentDidUpdate(prevProps, prevState) {
      if (process.env.NODE_ENV !== 'production') {
        validateControlledUnchanged$1(this.state, prevProps, this.props);
        /* istanbul ignore if (react-native) */

        if (this.getMenuProps.called && !this.getMenuProps.suppressRefError) {
          validateGetMenuPropsCalledCorrectly$1(this._menuNode, this.getMenuProps);
        }
      }

      if (isControlledProp$1(this.props, 'selectedItem') && this.props.selectedItemChanged(prevProps.selectedItem, this.props.selectedItem)) {
        this.internalSetState({
          type: controlledPropUpdatedSelectedItem$1,
          inputValue: this.props.itemToString(this.props.selectedItem)
        });
      }

      if (!this.avoidScrolling && this.shouldScroll(prevState, prevProps)) {
        this.scrollHighlightedItemIntoView();
      }
      /* istanbul ignore else (react-native) */


      {
        this.updateStatus();
      }
    }

    componentWillUnmount() {
      this.cleanup(); // avoids memory leak
    }

    render() {
      const children = unwrapArray$1(this.props.children, noop$1); // because the items are rerendered every time we call the children
      // we clear this out each render and it will be populated again as
      // getItemProps is called.

      this.clearItems(); // we reset this so we know whether the user calls getRootProps during
      // this render. If they do then we don't need to do anything,
      // if they don't then we need to clone the element they return and
      // apply the props for them.

      this.getRootProps.called = false;
      this.getRootProps.refKey = undefined;
      this.getRootProps.suppressRefError = undefined; // we do something similar for getMenuProps

      this.getMenuProps.called = false;
      this.getMenuProps.refKey = undefined;
      this.getMenuProps.suppressRefError = undefined; // we do something similar for getLabelProps

      this.getLabelProps.called = false; // and something similar for getInputProps

      this.getInputProps.called = false;
      const element = unwrapArray$1(children(this.getStateAndHelpers()));

      if (!element) {
        return null;
      }

      if (this.getRootProps.called || this.props.suppressRefError) {
        if (process.env.NODE_ENV !== 'production' && !this.getRootProps.suppressRefError && !this.props.suppressRefError) {
          validateGetRootPropsCalledCorrectly$1(element, this.getRootProps);
        }

        return element;
      } else if (isDOMElement$1(element)) {
        // they didn't apply the root props, but we can clone
        // this and apply the props ourselves
        return /*#__PURE__*/cloneElement(element, this.getRootProps(getElementProps$1(element)));
      }
      /* istanbul ignore else */


      if (process.env.NODE_ENV !== 'production') {
        // they didn't apply the root props, but they need to
        // otherwise we can't query around the autocomplete
        throw new Error('downshift: If you return a non-DOM element, you must apply the getRootProps function');
      }
      /* istanbul ignore next */


      return undefined;
    }

  }

  Downshift.defaultProps = {
    defaultHighlightedIndex: null,
    defaultIsOpen: false,
    getA11yStatusMessage: getA11yStatusMessage$1$1,
    itemToString: i => {
      if (i == null) {
        return '';
      }

      if (process.env.NODE_ENV !== 'production' && isPlainObject$1(i) && !i.hasOwnProperty('toString')) {
        // eslint-disable-next-line no-console
        console.warn('downshift: An object was passed to the default implementation of `itemToString`. You should probably provide your own `itemToString` implementation. Please refer to the `itemToString` API documentation.', 'The object that was passed:', i);
      }

      return String(i);
    },
    onStateChange: noop$1,
    onInputValueChange: noop$1,
    onUserAction: noop$1,
    onChange: noop$1,
    onSelect: noop$1,
    onOuterClick: noop$1,
    selectedItemChanged: (prevItem, item) => prevItem !== item,
    environment:
    /* istanbul ignore next (ssr) */
    typeof window === 'undefined' ? {} : window,
    stateReducer: (state, stateToSet) => stateToSet,
    suppressRefError: false,
    scrollIntoView: scrollIntoView$1
  };
  Downshift.stateChangeTypes = stateChangeTypes$3$1;
  return Downshift;
})();

process.env.NODE_ENV !== "production" ? Downshift$1.propTypes = {
  children: PropTypes.func,
  defaultHighlightedIndex: PropTypes.number,
  defaultIsOpen: PropTypes.bool,
  initialHighlightedIndex: PropTypes.number,
  initialSelectedItem: PropTypes.any,
  initialInputValue: PropTypes.string,
  initialIsOpen: PropTypes.bool,
  getA11yStatusMessage: PropTypes.func,
  itemToString: PropTypes.func,
  onChange: PropTypes.func,
  onSelect: PropTypes.func,
  onStateChange: PropTypes.func,
  onInputValueChange: PropTypes.func,
  onUserAction: PropTypes.func,
  onOuterClick: PropTypes.func,
  selectedItemChanged: PropTypes.func,
  stateReducer: PropTypes.func,
  itemCount: PropTypes.number,
  id: PropTypes.string,
  environment: PropTypes.shape({
    addEventListener: PropTypes.func,
    removeEventListener: PropTypes.func,
    document: PropTypes.shape({
      getElementById: PropTypes.func,
      activeElement: PropTypes.any,
      body: PropTypes.any
    })
  }),
  suppressRefError: PropTypes.bool,
  scrollIntoView: PropTypes.func,
  // things we keep in state for uncontrolled components
  // but can accept as props for controlled components

  /* eslint-disable react/no-unused-prop-types */
  selectedItem: PropTypes.any,
  isOpen: PropTypes.bool,
  inputValue: PropTypes.string,
  highlightedIndex: PropTypes.number,
  labelId: PropTypes.string,
  inputId: PropTypes.string,
  menuId: PropTypes.string,
  getItemId: PropTypes.func
  /* eslint-enable react/no-unused-prop-types */

} : void 0;

function validateGetMenuPropsCalledCorrectly$1(node, _ref3) {
  let {
    refKey
  } = _ref3;

  if (!node) {
    // eslint-disable-next-line no-console
    console.error(`downshift: The ref prop "${refKey}" from getMenuProps was not applied correctly on your menu element.`);
  }
}

function validateGetRootPropsCalledCorrectly$1(element, _ref4) {
  let {
    refKey
  } = _ref4;
  const refKeySpecified = refKey !== 'ref';
  const isComposite = !isDOMElement$1(element);

  if (isComposite && !refKeySpecified && !reactIsExports$2.isForwardRef(element)) {
    // eslint-disable-next-line no-console
    console.error('downshift: You returned a non-DOM element. You must specify a refKey in getRootProps');
  } else if (!isComposite && refKeySpecified) {
    // eslint-disable-next-line no-console
    console.error(`downshift: You returned a DOM element. You should not specify a refKey in getRootProps. You specified "${refKey}"`);
  }

  if (!reactIsExports$2.isForwardRef(element) && !getElementProps$1(element)[refKey]) {
    // eslint-disable-next-line no-console
    console.error(`downshift: You must apply the ref prop "${refKey}" from getRootProps onto your root element.`);
  }
}

const dropdownDefaultStateValues$1 = {
  highlightedIndex: -1,
  isOpen: false,
  selectedItem: null,
  inputValue: ''
};

function callOnChangeProps$1(action, state, newState) {
  const {
    props,
    type
  } = action;
  const changes = {};
  Object.keys(state).forEach(key => {
    invokeOnChangeHandler$1(key, action, state, newState);

    if (newState[key] !== state[key]) {
      changes[key] = newState[key];
    }
  });

  if (props.onStateChange && Object.keys(changes).length) {
    props.onStateChange({
      type,
      ...changes
    });
  }
}

function invokeOnChangeHandler$1(key, action, state, newState) {
  const {
    props,
    type
  } = action;
  const handler = `on${capitalizeString$1(key)}Change`;

  if (props[handler] && newState[key] !== undefined && newState[key] !== state[key]) {
    props[handler]({
      type,
      ...newState
    });
  }
}
/**
 * Default state reducer that returns the changes.
 *
 * @param {Object} s state.
 * @param {Object} a action with changes.
 * @returns {Object} changes.
 */


function stateReducer$1(s, a) {
  return a.changes;
}
/**
 * Returns a message to be added to aria-live region when item is selected.
 *
 * @param {Object} selectionParameters Parameters required to build the message.
 * @returns {string} The a11y message.
 */


function getA11ySelectionMessage$1(selectionParameters) {
  const {
    selectedItem,
    itemToString: itemToStringLocal
  } = selectionParameters;
  return selectedItem ? `${itemToStringLocal(selectedItem)} has been selected.` : '';
}
/**
 * Debounced call for updating the a11y message.
 */


const updateA11yStatus$1 = debounce$1((getA11yMessage, document) => {
  setStatus$1(getA11yMessage(), document);
}, 200); // istanbul ignore next

const useIsomorphicLayoutEffect$1 = typeof window !== 'undefined' && typeof window.document !== 'undefined' && typeof window.document.createElement !== 'undefined' ? useLayoutEffect : useEffect;

function useElementIds$1(_ref) {
  let {
    id = `downshift-${generateId$1()}`,
    labelId,
    menuId,
    getItemId,
    toggleButtonId,
    inputId
  } = _ref;
  const elementIdsRef = useRef({
    labelId: labelId || `${id}-label`,
    menuId: menuId || `${id}-menu`,
    getItemId: getItemId || (index => `${id}-item-${index}`),
    toggleButtonId: toggleButtonId || `${id}-toggle-button`,
    inputId: inputId || `${id}-input`
  });
  return elementIdsRef.current;
}

function getItemIndex$1(index, item, items) {
  if (index !== undefined) {
    return index;
  }

  if (items.length === 0) {
    return -1;
  }

  return items.indexOf(item);
}

function itemToString$1(item) {
  return item ? String(item) : '';
}

function isAcceptedCharacterKey(key) {
  return /^\S{1}$/.test(key);
}

function capitalizeString$1(string) {
  return `${string.slice(0, 1).toUpperCase()}${string.slice(1)}`;
}

function useLatestRef$1(val) {
  const ref = useRef(val); // technically this is not "concurrent mode safe" because we're manipulating
  // the value during render (so it's not idempotent). However, the places this
  // hook is used is to support memoizing callbacks which will be called
  // *during* render, so we need the latest values *during* render.
  // If not for this, then we'd probably want to use useLayoutEffect instead.

  ref.current = val;
  return ref;
}
/**
 * Computes the controlled state using a the previous state, props,
 * two reducers, one from downshift and an optional one from the user.
 * Also calls the onChange handlers for state values that have changed.
 *
 * @param {Function} reducer Reducer function from downshift.
 * @param {Object} initialState Initial state of the hook.
 * @param {Object} props The hook props.
 * @returns {Array} An array with the state and an action dispatcher.
 */


function useEnhancedReducer$1(reducer, initialState, props) {
  const prevStateRef = useRef();
  const actionRef = useRef();
  const enhancedReducer = useCallback((state, action) => {
    actionRef.current = action;
    state = getState$1(state, action.props);
    const changes = reducer(state, action);
    const newState = action.props.stateReducer(state, { ...action,
      changes
    });
    return newState;
  }, [reducer]);
  const [state, dispatch] = useReducer(enhancedReducer, initialState);
  const propsRef = useLatestRef$1(props);
  const dispatchWithProps = useCallback(action => dispatch({
    props: propsRef.current,
    ...action
  }), [propsRef]);
  const action = actionRef.current;
  useEffect(() => {
    if (action && prevStateRef.current && prevStateRef.current !== state) {
      callOnChangeProps$1(action, getState$1(prevStateRef.current, action.props), state);
    }

    prevStateRef.current = state;
  }, [state, props, action]);
  return [state, dispatchWithProps];
}
/**
 * Wraps the useEnhancedReducer and applies the controlled prop values before
 * returning the new state.
 *
 * @param {Function} reducer Reducer function from downshift.
 * @param {Object} initialState Initial state of the hook.
 * @param {Object} props The hook props.
 * @returns {Array} An array with the state and an action dispatcher.
 */


function useControlledReducer$1(reducer, initialState, props) {
  const [state, dispatch] = useEnhancedReducer$1(reducer, initialState, props);
  return [getState$1(state, props), dispatch];
}

const defaultProps$3$1 = {
  itemToString: itemToString$1,
  stateReducer: stateReducer$1,
  getA11ySelectionMessage: getA11ySelectionMessage$1,
  scrollIntoView: scrollIntoView$1,
  circularNavigation: false,
  environment:
  /* istanbul ignore next (ssr) */
  typeof window === 'undefined' ? {} : window
};

function getDefaultValue$1$1(props, propKey, defaultStateValues) {
  if (defaultStateValues === void 0) {
    defaultStateValues = dropdownDefaultStateValues$1;
  }

  const defaultValue = props[`default${capitalizeString$1(propKey)}`];

  if (defaultValue !== undefined) {
    return defaultValue;
  }

  return defaultStateValues[propKey];
}

function getInitialValue$1$1(props, propKey, defaultStateValues) {
  if (defaultStateValues === void 0) {
    defaultStateValues = dropdownDefaultStateValues$1;
  }

  const value = props[propKey];

  if (value !== undefined) {
    return value;
  }

  const initialValue = props[`initial${capitalizeString$1(propKey)}`];

  if (initialValue !== undefined) {
    return initialValue;
  }

  return getDefaultValue$1$1(props, propKey, defaultStateValues);
}

function getInitialState$2$1(props) {
  const selectedItem = getInitialValue$1$1(props, 'selectedItem');
  const isOpen = getInitialValue$1$1(props, 'isOpen');
  const highlightedIndex = getInitialValue$1$1(props, 'highlightedIndex');
  const inputValue = getInitialValue$1$1(props, 'inputValue');
  return {
    highlightedIndex: highlightedIndex < 0 && selectedItem && isOpen ? props.items.indexOf(selectedItem) : highlightedIndex,
    isOpen,
    selectedItem,
    inputValue
  };
}

function getHighlightedIndexOnOpen$1(props, state, offset, getItemNodeFromIndex) {
  const {
    items,
    initialHighlightedIndex,
    defaultHighlightedIndex
  } = props;
  const {
    selectedItem,
    highlightedIndex
  } = state;

  if (items.length === 0) {
    return -1;
  } // initialHighlightedIndex will give value to highlightedIndex on initial state only.


  if (initialHighlightedIndex !== undefined && highlightedIndex === initialHighlightedIndex) {
    return initialHighlightedIndex;
  }

  if (defaultHighlightedIndex !== undefined) {
    return defaultHighlightedIndex;
  }

  if (selectedItem) {
    if (offset === 0) {
      return items.indexOf(selectedItem);
    }

    return getNextWrappingIndex$1(offset, items.indexOf(selectedItem), items.length, getItemNodeFromIndex, false);
  }

  if (offset === 0) {
    return -1;
  }

  return offset < 0 ? items.length - 1 : 0;
}
/**
 * Reuse the movement tracking of mouse and touch events.
 *
 * @param {boolean} isOpen Whether the dropdown is open or not.
 * @param {Array<Object>} downshiftElementRefs Downshift element refs to track movement (toggleButton, menu etc.)
 * @param {Object} environment Environment where component/hook exists.
 * @param {Function} handleBlur Handler on blur from mouse or touch.
 * @returns {Object} Ref containing whether mouseDown or touchMove event is happening
 */


function useMouseAndTouchTracker$1(isOpen, downshiftElementRefs, environment, handleBlur) {
  const mouseAndTouchTrackersRef = useRef({
    isMouseDown: false,
    isTouchMove: false
  });
  useEffect(() => {
    // The same strategy for checking if a click occurred inside or outside downsift
    // as in downshift.js.
    const onMouseDown = () => {
      mouseAndTouchTrackersRef.current.isMouseDown = true;
    };

    const onMouseUp = event => {
      mouseAndTouchTrackersRef.current.isMouseDown = false;

      if (isOpen && !targetWithinDownshift$1(event.target, downshiftElementRefs.map(ref => ref.current), environment)) {
        handleBlur();
      }
    };

    const onTouchStart = () => {
      mouseAndTouchTrackersRef.current.isTouchMove = false;
    };

    const onTouchMove = () => {
      mouseAndTouchTrackersRef.current.isTouchMove = true;
    };

    const onTouchEnd = event => {
      if (isOpen && !mouseAndTouchTrackersRef.current.isTouchMove && !targetWithinDownshift$1(event.target, downshiftElementRefs.map(ref => ref.current), environment, false)) {
        handleBlur();
      }
    };

    environment.addEventListener('mousedown', onMouseDown);
    environment.addEventListener('mouseup', onMouseUp);
    environment.addEventListener('touchstart', onTouchStart);
    environment.addEventListener('touchmove', onTouchMove);
    environment.addEventListener('touchend', onTouchEnd);
    return function cleanup() {
      environment.removeEventListener('mousedown', onMouseDown);
      environment.removeEventListener('mouseup', onMouseUp);
      environment.removeEventListener('touchstart', onTouchStart);
      environment.removeEventListener('touchmove', onTouchMove);
      environment.removeEventListener('touchend', onTouchEnd);
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, environment]);
  return mouseAndTouchTrackersRef;
}
/* istanbul ignore next */
// eslint-disable-next-line import/no-mutable-exports


let useGetterPropsCalledChecker$1 = () => noop$1;
/**
 * Custom hook that checks if getter props are called correctly.
 *
 * @param  {...any} propKeys Getter prop names to be handled.
 * @returns {Function} Setter function called inside getter props to set call information.
 */

/* istanbul ignore next */


if (process.env.NODE_ENV !== 'production') {
  useGetterPropsCalledChecker$1 = function () {
    const isInitialMountRef = useRef(true);

    for (var _len = arguments.length, propKeys = new Array(_len), _key = 0; _key < _len; _key++) {
      propKeys[_key] = arguments[_key];
    }

    const getterPropsCalledRef = useRef(propKeys.reduce((acc, propKey) => {
      acc[propKey] = {};
      return acc;
    }, {}));
    useEffect(() => {
      Object.keys(getterPropsCalledRef.current).forEach(propKey => {
        const propCallInfo = getterPropsCalledRef.current[propKey];

        if (isInitialMountRef.current) {
          if (!Object.keys(propCallInfo).length) {
            // eslint-disable-next-line no-console
            console.error(`downshift: You forgot to call the ${propKey} getter function on your component / element.`);
            return;
          }
        }

        const {
          suppressRefError,
          refKey,
          elementRef
        } = propCallInfo;

        if ((!elementRef || !elementRef.current) && !suppressRefError) {
          // eslint-disable-next-line no-console
          console.error(`downshift: The ref prop "${refKey}" from ${propKey} was not applied correctly on your element.`);
        }
      });
      isInitialMountRef.current = false;
    });
    const setGetterPropCallInfo = useCallback((propKey, suppressRefError, refKey, elementRef) => {
      getterPropsCalledRef.current[propKey] = {
        suppressRefError,
        refKey,
        elementRef
      };
    }, []);
    return setGetterPropCallInfo;
  };
}

function useA11yMessageSetter$1(getA11yMessage, dependencyArray, _ref2) {
  let {
    isInitialMount,
    highlightedIndex,
    items,
    environment,
    ...rest
  } = _ref2;
  // Sets a11y status message on changes in state.
  useEffect(() => {
    if (isInitialMount || false) {
      return;
    }

    updateA11yStatus$1(() => getA11yMessage({
      highlightedIndex,
      highlightedItem: items[highlightedIndex],
      resultCount: items.length,
      ...rest
    }), environment.document); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencyArray);
}

function useScrollIntoView$1(_ref3) {
  let {
    highlightedIndex,
    isOpen,
    itemRefs,
    getItemNodeFromIndex,
    menuElement,
    scrollIntoView: scrollIntoViewProp
  } = _ref3;
  // used not to scroll on highlight by mouse.
  const shouldScrollRef = useRef(true); // Scroll on highlighted item if change comes from keyboard.

  useIsomorphicLayoutEffect$1(() => {
    if (highlightedIndex < 0 || !isOpen || !Object.keys(itemRefs.current).length) {
      return;
    }

    if (shouldScrollRef.current === false) {
      shouldScrollRef.current = true;
    } else {
      scrollIntoViewProp(getItemNodeFromIndex(highlightedIndex), menuElement);
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [highlightedIndex]);
  return shouldScrollRef;
} // eslint-disable-next-line import/no-mutable-exports


let useControlPropsValidator$1 = noop$1;
/* istanbul ignore next */

if (process.env.NODE_ENV !== 'production') {
  useControlPropsValidator$1 = _ref4 => {
    let {
      isInitialMount,
      props,
      state
    } = _ref4;
    // used for checking when props are moving from controlled to uncontrolled.
    const prevPropsRef = useRef(props);
    useEffect(() => {
      if (isInitialMount) {
        return;
      }

      validateControlledUnchanged$1(state, prevPropsRef.current, props);
      prevPropsRef.current = props;
    }, [state, props, isInitialMount]);
  };
}

/* eslint-disable complexity */

function downshiftCommonReducer$1(state, action, stateChangeTypes) {
  const {
    type,
    props
  } = action;
  let changes;

  switch (type) {
    case stateChangeTypes.ItemMouseMove:
      changes = {
        highlightedIndex: action.disabled ? -1 : action.index
      };
      break;

    case stateChangeTypes.MenuMouseLeave:
      changes = {
        highlightedIndex: -1
      };
      break;

    case stateChangeTypes.ToggleButtonClick:
    case stateChangeTypes.FunctionToggleMenu:
      changes = {
        isOpen: !state.isOpen,
        highlightedIndex: state.isOpen ? -1 : getHighlightedIndexOnOpen$1(props, state, 0)
      };
      break;

    case stateChangeTypes.FunctionOpenMenu:
      changes = {
        isOpen: true,
        highlightedIndex: getHighlightedIndexOnOpen$1(props, state, 0)
      };
      break;

    case stateChangeTypes.FunctionCloseMenu:
      changes = {
        isOpen: false
      };
      break;

    case stateChangeTypes.FunctionSetHighlightedIndex:
      changes = {
        highlightedIndex: action.highlightedIndex
      };
      break;

    case stateChangeTypes.FunctionSetInputValue:
      changes = {
        inputValue: action.inputValue
      };
      break;

    case stateChangeTypes.FunctionReset:
      changes = {
        highlightedIndex: getDefaultValue$1$1(props, 'highlightedIndex'),
        isOpen: getDefaultValue$1$1(props, 'isOpen'),
        selectedItem: getDefaultValue$1$1(props, 'selectedItem'),
        inputValue: getDefaultValue$1$1(props, 'inputValue')
      };
      break;

    default:
      throw new Error('Reducer called without proper action type.');
  }

  return { ...state,
    ...changes
  };
}
/* eslint-enable complexity */

function getItemIndexByCharacterKey(_a) {
    var keysSoFar = _a.keysSoFar, highlightedIndex = _a.highlightedIndex, items = _a.items, itemToString = _a.itemToString, getItemNodeFromIndex = _a.getItemNodeFromIndex;
    var lowerCasedKeysSoFar = keysSoFar.toLowerCase();
    for (var index = 0; index < items.length; index++) {
        var offsetIndex = (index + highlightedIndex + 1) % items.length;
        var item = items[offsetIndex];
        if (item !== undefined &&
            itemToString(item)
                .toLowerCase()
                .startsWith(lowerCasedKeysSoFar)) {
            var element = getItemNodeFromIndex(offsetIndex);
            if (!(element === null || element === void 0 ? void 0 : element.hasAttribute('disabled'))) {
                return offsetIndex;
            }
        }
    }
    return highlightedIndex;
}
var propTypes$2 = {
    items: PropTypes.array.isRequired,
    itemToString: PropTypes.func,
    getA11yStatusMessage: PropTypes.func,
    getA11ySelectionMessage: PropTypes.func,
    circularNavigation: PropTypes.bool,
    highlightedIndex: PropTypes.number,
    defaultHighlightedIndex: PropTypes.number,
    initialHighlightedIndex: PropTypes.number,
    isOpen: PropTypes.bool,
    defaultIsOpen: PropTypes.bool,
    initialIsOpen: PropTypes.bool,
    selectedItem: PropTypes.any,
    initialSelectedItem: PropTypes.any,
    defaultSelectedItem: PropTypes.any,
    id: PropTypes.string,
    labelId: PropTypes.string,
    menuId: PropTypes.string,
    getItemId: PropTypes.func,
    toggleButtonId: PropTypes.string,
    stateReducer: PropTypes.func,
    onSelectedItemChange: PropTypes.func,
    onHighlightedIndexChange: PropTypes.func,
    onStateChange: PropTypes.func,
    onIsOpenChange: PropTypes.func,
    environment: PropTypes.shape({
        addEventListener: PropTypes.func,
        removeEventListener: PropTypes.func,
        document: PropTypes.shape({
            getElementById: PropTypes.func,
            activeElement: PropTypes.any,
            body: PropTypes.any
        })
    })
};
/**
 * Default implementation for status message. Only added when menu is open.
 * Will specift if there are results in the list, and if so, how many,
 * and what keys are relevant.
 *
 * @param {Object} param the downshift state and other relevant properties
 * @return {String} the a11y status message
 */
function getA11yStatusMessage$2(_a) {
    var isOpen = _a.isOpen, resultCount = _a.resultCount, previousResultCount = _a.previousResultCount;
    if (!isOpen) {
        return '';
    }
    if (!resultCount) {
        return 'No results are available.';
    }
    if (resultCount !== previousResultCount) {
        return "".concat(resultCount, " result").concat(resultCount === 1 ? ' is' : 's are', " available, use up and down arrow keys to navigate. Press Enter or Space Bar keys to select.");
    }
    return '';
}
var defaultProps$2 = __assign$1(__assign$1({}, defaultProps$3$1), { getA11yStatusMessage: getA11yStatusMessage$2 });
// eslint-disable-next-line import/no-mutable-exports
var validatePropTypes$2 = noop$1;
/* istanbul ignore next */
if (process.env.NODE_ENV !== 'production') {
    validatePropTypes$2 = function (options, caller) {
        PropTypes.checkPropTypes(propTypes$2, options, 'prop', caller.name);
    };
}

const MenuKeyDownArrowDown = process.env.NODE_ENV !== "production" ? '__menu_keydown_arrow_down__' : 0;
const MenuKeyDownArrowUp = process.env.NODE_ENV !== "production" ? '__menu_keydown_arrow_up__' : 1;
const MenuKeyDownEscape = process.env.NODE_ENV !== "production" ? '__menu_keydown_escape__' : 2;
const MenuKeyDownHome = process.env.NODE_ENV !== "production" ? '__menu_keydown_home__' : 3;
const MenuKeyDownEnd = process.env.NODE_ENV !== "production" ? '__menu_keydown_end__' : 4;
const MenuKeyDownEnter = process.env.NODE_ENV !== "production" ? '__menu_keydown_enter__' : 5;
const MenuKeyDownSpaceButton = process.env.NODE_ENV !== "production" ? '__menu_keydown_space_button__' : 6;
const MenuKeyDownCharacter = process.env.NODE_ENV !== "production" ? '__menu_keydown_character__' : 7;
const MenuBlur = process.env.NODE_ENV !== "production" ? '__menu_blur__' : 8;
const MenuMouseLeave$1 = process.env.NODE_ENV !== "production" ? '__menu_mouse_leave__' : 9;
const ItemMouseMove$1 = process.env.NODE_ENV !== "production" ? '__item_mouse_move__' : 10;
const ItemClick$1 = process.env.NODE_ENV !== "production" ? '__item_click__' : 11;
const ToggleButtonClick$1 = process.env.NODE_ENV !== "production" ? '__togglebutton_click__' : 12;
const ToggleButtonKeyDownArrowDown = process.env.NODE_ENV !== "production" ? '__togglebutton_keydown_arrow_down__' : 13;
const ToggleButtonKeyDownArrowUp = process.env.NODE_ENV !== "production" ? '__togglebutton_keydown_arrow_up__' : 14;
const ToggleButtonKeyDownCharacter = process.env.NODE_ENV !== "production" ? '__togglebutton_keydown_character__' : 15;
const FunctionToggleMenu$1 = process.env.NODE_ENV !== "production" ? '__function_toggle_menu__' : 16;
const FunctionOpenMenu$1 = process.env.NODE_ENV !== "production" ? '__function_open_menu__' : 17;
const FunctionCloseMenu$1 = process.env.NODE_ENV !== "production" ? '__function_close_menu__' : 18;
const FunctionSetHighlightedIndex$1 = process.env.NODE_ENV !== "production" ? '__function_set_highlighted_index__' : 19;
const FunctionSelectItem$1 = process.env.NODE_ENV !== "production" ? '__function_select_item__' : 20;
const FunctionSetInputValue$1 = process.env.NODE_ENV !== "production" ? '__function_set_input_value__' : 21;
const FunctionReset$2 = process.env.NODE_ENV !== "production" ? '__function_reset__' : 22;

var stateChangeTypes$2 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  MenuKeyDownArrowDown: MenuKeyDownArrowDown,
  MenuKeyDownArrowUp: MenuKeyDownArrowUp,
  MenuKeyDownEscape: MenuKeyDownEscape,
  MenuKeyDownHome: MenuKeyDownHome,
  MenuKeyDownEnd: MenuKeyDownEnd,
  MenuKeyDownEnter: MenuKeyDownEnter,
  MenuKeyDownSpaceButton: MenuKeyDownSpaceButton,
  MenuKeyDownCharacter: MenuKeyDownCharacter,
  MenuBlur: MenuBlur,
  MenuMouseLeave: MenuMouseLeave$1,
  ItemMouseMove: ItemMouseMove$1,
  ItemClick: ItemClick$1,
  ToggleButtonClick: ToggleButtonClick$1,
  ToggleButtonKeyDownArrowDown: ToggleButtonKeyDownArrowDown,
  ToggleButtonKeyDownArrowUp: ToggleButtonKeyDownArrowUp,
  ToggleButtonKeyDownCharacter: ToggleButtonKeyDownCharacter,
  FunctionToggleMenu: FunctionToggleMenu$1,
  FunctionOpenMenu: FunctionOpenMenu$1,
  FunctionCloseMenu: FunctionCloseMenu$1,
  FunctionSetHighlightedIndex: FunctionSetHighlightedIndex$1,
  FunctionSelectItem: FunctionSelectItem$1,
  FunctionSetInputValue: FunctionSetInputValue$1,
  FunctionReset: FunctionReset$2
});

/* eslint-disable complexity */

function downshiftSelectReducer(state, action) {
  const {
    type,
    props,
    shiftKey
  } = action;
  let changes;

  switch (type) {
    case ItemClick$1:
      changes = {
        isOpen: getDefaultValue$1$1(props, 'isOpen'),
        highlightedIndex: getDefaultValue$1$1(props, 'highlightedIndex'),
        selectedItem: props.items[action.index]
      };
      break;

    case ToggleButtonKeyDownCharacter:
      {
        const lowercasedKey = action.key;
        const inputValue = `${state.inputValue}${lowercasedKey}`;
        const itemIndex = getItemIndexByCharacterKey({
          keysSoFar: inputValue,
          highlightedIndex: state.selectedItem ? props.items.indexOf(state.selectedItem) : -1,
          items: props.items,
          itemToString: props.itemToString,
          getItemNodeFromIndex: action.getItemNodeFromIndex
        });
        changes = {
          inputValue,
          ...(itemIndex >= 0 && {
            selectedItem: props.items[itemIndex]
          })
        };
      }
      break;

    case ToggleButtonKeyDownArrowDown:
      changes = {
        highlightedIndex: getHighlightedIndexOnOpen$1(props, state, 1, action.getItemNodeFromIndex),
        isOpen: true
      };
      break;

    case ToggleButtonKeyDownArrowUp:
      changes = {
        highlightedIndex: getHighlightedIndexOnOpen$1(props, state, -1, action.getItemNodeFromIndex),
        isOpen: true
      };
      break;

    case MenuKeyDownEnter:
    case MenuKeyDownSpaceButton:
      changes = {
        isOpen: getDefaultValue$1$1(props, 'isOpen'),
        highlightedIndex: getDefaultValue$1$1(props, 'highlightedIndex'),
        ...(state.highlightedIndex >= 0 && {
          selectedItem: props.items[state.highlightedIndex]
        })
      };
      break;

    case MenuKeyDownHome:
      changes = {
        highlightedIndex: getNextNonDisabledIndex$1(1, 0, props.items.length, action.getItemNodeFromIndex, false)
      };
      break;

    case MenuKeyDownEnd:
      changes = {
        highlightedIndex: getNextNonDisabledIndex$1(-1, props.items.length - 1, props.items.length, action.getItemNodeFromIndex, false)
      };
      break;

    case MenuKeyDownEscape:
      changes = {
        isOpen: false,
        highlightedIndex: -1
      };
      break;

    case MenuBlur:
      changes = {
        isOpen: false,
        highlightedIndex: -1
      };
      break;

    case MenuKeyDownCharacter:
      {
        const lowercasedKey = action.key;
        const inputValue = `${state.inputValue}${lowercasedKey}`;
        const highlightedIndex = getItemIndexByCharacterKey({
          keysSoFar: inputValue,
          highlightedIndex: state.highlightedIndex,
          items: props.items,
          itemToString: props.itemToString,
          getItemNodeFromIndex: action.getItemNodeFromIndex
        });
        changes = {
          inputValue,
          ...(highlightedIndex >= 0 && {
            highlightedIndex
          })
        };
      }
      break;

    case MenuKeyDownArrowDown:
      changes = {
        highlightedIndex: getNextWrappingIndex$1(shiftKey ? 5 : 1, state.highlightedIndex, props.items.length, action.getItemNodeFromIndex, props.circularNavigation)
      };
      break;

    case MenuKeyDownArrowUp:
      changes = {
        highlightedIndex: getNextWrappingIndex$1(shiftKey ? -5 : -1, state.highlightedIndex, props.items.length, action.getItemNodeFromIndex, props.circularNavigation)
      };
      break;

    case FunctionSelectItem$1:
      changes = {
        selectedItem: action.selectedItem
      };
      break;

    default:
      return downshiftCommonReducer$1(state, action, stateChangeTypes$2);
  }

  return { ...state,
    ...changes
  };
}
/* eslint-enable complexity */

/* eslint-disable max-statements */
useSelect.stateChangeTypes = stateChangeTypes$2;

function useSelect(userProps) {
  if (userProps === void 0) {
    userProps = {};
  }

  validatePropTypes$2(userProps, useSelect); // Props defaults and destructuring.

  const props = { ...defaultProps$2,
    ...userProps
  };
  const {
    items,
    scrollIntoView,
    environment,
    initialIsOpen,
    defaultIsOpen,
    itemToString,
    getA11ySelectionMessage,
    getA11yStatusMessage
  } = props; // Initial state depending on controlled props.

  const initialState = getInitialState$2$1(props);
  const [state, dispatch] = useControlledReducer$1(downshiftSelectReducer, initialState, props);
  const {
    isOpen,
    highlightedIndex,
    selectedItem,
    inputValue
  } = state; // Element efs.

  const toggleButtonRef = useRef(null);
  const menuRef = useRef(null);
  const itemRefs = useRef({}); // used not to trigger menu blur action in some scenarios.

  const shouldBlurRef = useRef(true); // used to keep the inputValue clearTimeout object between renders.

  const clearTimeoutRef = useRef(null); // prevent id re-generation between renders.

  const elementIds = useElementIds$1(props); // used to keep track of how many items we had on previous cycle.

  const previousResultCountRef = useRef();
  const isInitialMountRef = useRef(true); // utility callback to get item element.

  const latest = useLatestRef$1({
    state,
    props
  }); // Some utils.

  const getItemNodeFromIndex = useCallback(index => itemRefs.current[elementIds.getItemId(index)], [elementIds]); // Effects.
  // Sets a11y status message on changes in state.

  useA11yMessageSetter$1(getA11yStatusMessage, [isOpen, highlightedIndex, inputValue, items], {
    isInitialMount: isInitialMountRef.current,
    previousResultCount: previousResultCountRef.current,
    items,
    environment,
    itemToString,
    ...state
  }); // Sets a11y status message on changes in selectedItem.

  useA11yMessageSetter$1(getA11ySelectionMessage, [selectedItem], {
    isInitialMount: isInitialMountRef.current,
    previousResultCount: previousResultCountRef.current,
    items,
    environment,
    itemToString,
    ...state
  }); // Scroll on highlighted item if change comes from keyboard.

  const shouldScrollRef = useScrollIntoView$1({
    menuElement: menuRef.current,
    highlightedIndex,
    isOpen,
    itemRefs,
    scrollIntoView,
    getItemNodeFromIndex
  }); // Sets cleanup for the keysSoFar callback, debounded after 500ms.

  useEffect(() => {
    // init the clean function here as we need access to dispatch.
    clearTimeoutRef.current = debounce$1(outerDispatch => {
      outerDispatch({
        type: FunctionSetInputValue$1,
        inputValue: ''
      });
    }, 500); // Cancel any pending debounced calls on mount

    return () => {
      clearTimeoutRef.current.cancel();
    };
  }, []); // Invokes the keysSoFar callback set up above.

  useEffect(() => {
    if (!inputValue) {
      return;
    }

    clearTimeoutRef.current(dispatch);
  }, [dispatch, inputValue]);
  useControlPropsValidator$1({
    isInitialMount: isInitialMountRef.current,
    props,
    state
  });
  /* Controls the focus on the menu or the toggle button. */

  useEffect(() => {
    // Don't focus menu on first render.
    if (isInitialMountRef.current) {
      // Unless it was initialised as open.
      if ((initialIsOpen || defaultIsOpen || isOpen) && menuRef.current) {
        menuRef.current.focus();
      }

      return;
    } // Focus menu on open.


    if (isOpen) {
      // istanbul ignore else
      if (menuRef.current) {
        menuRef.current.focus();
      }

      return;
    } // Focus toggleButton on close, but not if it was closed with (Shift+)Tab.


    if (environment.document.activeElement === menuRef.current) {
      // istanbul ignore else
      if (toggleButtonRef.current) {
        shouldBlurRef.current = false;
        toggleButtonRef.current.focus();
      }
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [isOpen]);
  useEffect(() => {
    if (isInitialMountRef.current) {
      return;
    }

    previousResultCountRef.current = items.length;
  }); // Add mouse/touch events to document.

  const mouseAndTouchTrackersRef = useMouseAndTouchTracker$1(isOpen, [menuRef, toggleButtonRef], environment, () => {
    dispatch({
      type: MenuBlur
    });
  });
  const setGetterPropCallInfo = useGetterPropsCalledChecker$1('getMenuProps', 'getToggleButtonProps'); // Make initial ref false.

  useEffect(() => {
    isInitialMountRef.current = false;
  }, []); // Reset itemRefs on close.

  useEffect(() => {
    if (!isOpen) {
      itemRefs.current = {};
    }
  }, [isOpen]); // Event handler functions.

  const toggleButtonKeyDownHandlers = useMemo(() => ({
    ArrowDown(event) {
      event.preventDefault();
      dispatch({
        type: ToggleButtonKeyDownArrowDown,
        getItemNodeFromIndex,
        shiftKey: event.shiftKey
      });
    },

    ArrowUp(event) {
      event.preventDefault();
      dispatch({
        type: ToggleButtonKeyDownArrowUp,
        getItemNodeFromIndex,
        shiftKey: event.shiftKey
      });
    }

  }), [dispatch, getItemNodeFromIndex]);
  const menuKeyDownHandlers = useMemo(() => ({
    ArrowDown(event) {
      event.preventDefault();
      dispatch({
        type: MenuKeyDownArrowDown,
        getItemNodeFromIndex,
        shiftKey: event.shiftKey
      });
    },

    ArrowUp(event) {
      event.preventDefault();
      dispatch({
        type: MenuKeyDownArrowUp,
        getItemNodeFromIndex,
        shiftKey: event.shiftKey
      });
    },

    Home(event) {
      event.preventDefault();
      dispatch({
        type: MenuKeyDownHome,
        getItemNodeFromIndex
      });
    },

    End(event) {
      event.preventDefault();
      dispatch({
        type: MenuKeyDownEnd,
        getItemNodeFromIndex
      });
    },

    Escape() {
      dispatch({
        type: MenuKeyDownEscape
      });
    },

    Enter(event) {
      event.preventDefault();
      dispatch({
        type: MenuKeyDownEnter
      });
    },

    ' '(event) {
      event.preventDefault();
      dispatch({
        type: MenuKeyDownSpaceButton
      });
    }

  }), [dispatch, getItemNodeFromIndex]); // Action functions.

  const toggleMenu = useCallback(() => {
    dispatch({
      type: FunctionToggleMenu$1
    });
  }, [dispatch]);
  const closeMenu = useCallback(() => {
    dispatch({
      type: FunctionCloseMenu$1
    });
  }, [dispatch]);
  const openMenu = useCallback(() => {
    dispatch({
      type: FunctionOpenMenu$1
    });
  }, [dispatch]);
  const setHighlightedIndex = useCallback(newHighlightedIndex => {
    dispatch({
      type: FunctionSetHighlightedIndex$1,
      highlightedIndex: newHighlightedIndex
    });
  }, [dispatch]);
  const selectItem = useCallback(newSelectedItem => {
    dispatch({
      type: FunctionSelectItem$1,
      selectedItem: newSelectedItem
    });
  }, [dispatch]);
  const reset = useCallback(() => {
    dispatch({
      type: FunctionReset$2
    });
  }, [dispatch]);
  const setInputValue = useCallback(newInputValue => {
    dispatch({
      type: FunctionSetInputValue$1,
      inputValue: newInputValue
    });
  }, [dispatch]); // Getter functions.

  const getLabelProps = useCallback(labelProps => ({
    id: elementIds.labelId,
    htmlFor: elementIds.toggleButtonId,
    ...labelProps
  }), [elementIds]);
  const getMenuProps = useCallback(function (_temp, _temp2) {
    let {
      onMouseLeave,
      refKey = 'ref',
      onKeyDown,
      onBlur,
      ref,
      ...rest
    } = _temp === void 0 ? {} : _temp;
    let {
      suppressRefError = false
    } = _temp2 === void 0 ? {} : _temp2;
    const latestState = latest.current.state;

    const menuHandleKeyDown = event => {
      const key = normalizeArrowKey$1(event);

      if (key && menuKeyDownHandlers[key]) {
        menuKeyDownHandlers[key](event);
      } else if (isAcceptedCharacterKey(key)) {
        dispatch({
          type: MenuKeyDownCharacter,
          key,
          getItemNodeFromIndex
        });
      }
    };

    const menuHandleBlur = () => {
      // if the blur was a result of selection, we don't trigger this action.
      if (shouldBlurRef.current === false) {
        shouldBlurRef.current = true;
        return;
      }

      const shouldBlur = !mouseAndTouchTrackersRef.current.isMouseDown;
      /* istanbul ignore else */

      if (shouldBlur) {
        dispatch({
          type: MenuBlur
        });
      }
    };

    const menuHandleMouseLeave = () => {
      dispatch({
        type: MenuMouseLeave$1
      });
    };

    setGetterPropCallInfo('getMenuProps', suppressRefError, refKey, menuRef);
    return {
      [refKey]: handleRefs$1(ref, menuNode => {
        menuRef.current = menuNode;
      }),
      id: elementIds.menuId,
      role: 'listbox',
      'aria-labelledby': elementIds.labelId,
      tabIndex: -1,
      ...(latestState.isOpen && latestState.highlightedIndex > -1 && {
        'aria-activedescendant': elementIds.getItemId(latestState.highlightedIndex)
      }),
      onMouseLeave: callAllEventHandlers$1(onMouseLeave, menuHandleMouseLeave),
      onKeyDown: callAllEventHandlers$1(onKeyDown, menuHandleKeyDown),
      onBlur: callAllEventHandlers$1(onBlur, menuHandleBlur),
      ...rest
    };
  }, [dispatch, latest, menuKeyDownHandlers, mouseAndTouchTrackersRef, setGetterPropCallInfo, elementIds, getItemNodeFromIndex]);
  const getToggleButtonProps = useCallback(function (_temp3, _temp4) {
    let {
      onClick,
      onKeyDown,
      refKey = 'ref',
      ref,
      ...rest
    } = _temp3 === void 0 ? {} : _temp3;
    let {
      suppressRefError = false
    } = _temp4 === void 0 ? {} : _temp4;

    const toggleButtonHandleClick = () => {
      dispatch({
        type: ToggleButtonClick$1
      });
    };

    const toggleButtonHandleKeyDown = event => {
      const key = normalizeArrowKey$1(event);

      if (key && toggleButtonKeyDownHandlers[key]) {
        toggleButtonKeyDownHandlers[key](event);
      } else if (isAcceptedCharacterKey(key)) {
        dispatch({
          type: ToggleButtonKeyDownCharacter,
          key,
          getItemNodeFromIndex
        });
      }
    };

    const toggleProps = {
      [refKey]: handleRefs$1(ref, toggleButtonNode => {
        toggleButtonRef.current = toggleButtonNode;
      }),
      id: elementIds.toggleButtonId,
      'aria-haspopup': 'listbox',
      'aria-expanded': latest.current.state.isOpen,
      'aria-labelledby': `${elementIds.labelId} ${elementIds.toggleButtonId}`,
      ...rest
    };

    if (!rest.disabled) {
      toggleProps.onClick = callAllEventHandlers$1(onClick, toggleButtonHandleClick);
      toggleProps.onKeyDown = callAllEventHandlers$1(onKeyDown, toggleButtonHandleKeyDown);
    }

    setGetterPropCallInfo('getToggleButtonProps', suppressRefError, refKey, toggleButtonRef);
    return toggleProps;
  }, [dispatch, latest, toggleButtonKeyDownHandlers, setGetterPropCallInfo, elementIds, getItemNodeFromIndex]);
  const getItemProps = useCallback(function (_temp5) {
    let {
      item,
      index,
      onMouseMove,
      onClick,
      refKey = 'ref',
      ref,
      disabled,
      ...rest
    } = _temp5 === void 0 ? {} : _temp5;
    const {
      state: latestState,
      props: latestProps
    } = latest.current;

    const itemHandleMouseMove = () => {
      if (index === latestState.highlightedIndex) {
        return;
      }

      shouldScrollRef.current = false;
      dispatch({
        type: ItemMouseMove$1,
        index,
        disabled
      });
    };

    const itemHandleClick = () => {
      dispatch({
        type: ItemClick$1,
        index
      });
    };

    const itemIndex = getItemIndex$1(index, item, latestProps.items);

    if (itemIndex < 0) {
      throw new Error('Pass either item or item index in getItemProps!');
    }

    const itemProps = {
      disabled,
      role: 'option',
      'aria-selected': `${itemIndex === latestState.highlightedIndex}`,
      id: elementIds.getItemId(itemIndex),
      [refKey]: handleRefs$1(ref, itemNode => {
        if (itemNode) {
          itemRefs.current[elementIds.getItemId(itemIndex)] = itemNode;
        }
      }),
      ...rest
    };

    if (!disabled) {
      itemProps.onClick = callAllEventHandlers$1(onClick, itemHandleClick);
    }

    itemProps.onMouseMove = callAllEventHandlers$1(onMouseMove, itemHandleMouseMove);
    return itemProps;
  }, [dispatch, latest, shouldScrollRef, elementIds]);
  return {
    // prop getters.
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getItemProps,
    // actions.
    toggleMenu,
    openMenu,
    closeMenu,
    setHighlightedIndex,
    selectItem,
    reset,
    setInputValue,
    // state.
    highlightedIndex,
    isOpen,
    selectedItem,
    inputValue
  };
}

const InputKeyDownArrowDown$1 = process.env.NODE_ENV !== "production" ? '__input_keydown_arrow_down__' : 0;
const InputKeyDownArrowUp$1 = process.env.NODE_ENV !== "production" ? '__input_keydown_arrow_up__' : 1;
const InputKeyDownEscape$1 = process.env.NODE_ENV !== "production" ? '__input_keydown_escape__' : 2;
const InputKeyDownHome$1 = process.env.NODE_ENV !== "production" ? '__input_keydown_home__' : 3;
const InputKeyDownEnd$1 = process.env.NODE_ENV !== "production" ? '__input_keydown_end__' : 4;
const InputKeyDownEnter$1 = process.env.NODE_ENV !== "production" ? '__input_keydown_enter__' : 5;
const InputChange$1 = process.env.NODE_ENV !== "production" ? '__input_change__' : 6;
const InputBlur$1 = process.env.NODE_ENV !== "production" ? '__input_blur__' : 7;
const MenuMouseLeave$2 = process.env.NODE_ENV !== "production" ? '__menu_mouse_leave__' : 8;
const ItemMouseMove$2 = process.env.NODE_ENV !== "production" ? '__item_mouse_move__' : 9;
const ItemClick$2 = process.env.NODE_ENV !== "production" ? '__item_click__' : 10;
const ToggleButtonClick$2 = process.env.NODE_ENV !== "production" ? '__togglebutton_click__' : 11;
const FunctionToggleMenu$2 = process.env.NODE_ENV !== "production" ? '__function_toggle_menu__' : 12;
const FunctionOpenMenu$2 = process.env.NODE_ENV !== "production" ? '__function_open_menu__' : 13;
const FunctionCloseMenu$2 = process.env.NODE_ENV !== "production" ? '__function_close_menu__' : 14;
const FunctionSetHighlightedIndex$2 = process.env.NODE_ENV !== "production" ? '__function_set_highlighted_index__' : 15;
const FunctionSelectItem$2 = process.env.NODE_ENV !== "production" ? '__function_select_item__' : 16;
const FunctionSetInputValue$2 = process.env.NODE_ENV !== "production" ? '__function_set_input_value__' : 17;
const FunctionReset$1$1 = process.env.NODE_ENV !== "production" ? '__function_reset__' : 18;
const ControlledPropUpdatedSelectedItem$1 = process.env.NODE_ENV !== "production" ? '__controlled_prop_updated_selected_item__' : 19;

var stateChangeTypes$1$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  InputKeyDownArrowDown: InputKeyDownArrowDown$1,
  InputKeyDownArrowUp: InputKeyDownArrowUp$1,
  InputKeyDownEscape: InputKeyDownEscape$1,
  InputKeyDownHome: InputKeyDownHome$1,
  InputKeyDownEnd: InputKeyDownEnd$1,
  InputKeyDownEnter: InputKeyDownEnter$1,
  InputChange: InputChange$1,
  InputBlur: InputBlur$1,
  MenuMouseLeave: MenuMouseLeave$2,
  ItemMouseMove: ItemMouseMove$2,
  ItemClick: ItemClick$2,
  ToggleButtonClick: ToggleButtonClick$2,
  FunctionToggleMenu: FunctionToggleMenu$2,
  FunctionOpenMenu: FunctionOpenMenu$2,
  FunctionCloseMenu: FunctionCloseMenu$2,
  FunctionSetHighlightedIndex: FunctionSetHighlightedIndex$2,
  FunctionSelectItem: FunctionSelectItem$2,
  FunctionSetInputValue: FunctionSetInputValue$2,
  FunctionReset: FunctionReset$1$1,
  ControlledPropUpdatedSelectedItem: ControlledPropUpdatedSelectedItem$1
});

function getInitialState$1$1(props) {
  const initialState = getInitialState$2$1(props);
  const {
    selectedItem
  } = initialState;
  let {
    inputValue
  } = initialState;

  if (inputValue === '' && selectedItem && props.defaultInputValue === undefined && props.initialInputValue === undefined && props.inputValue === undefined) {
    inputValue = props.itemToString(selectedItem);
  }

  return { ...initialState,
    inputValue
  };
}

const propTypes$1$1 = {
  items: PropTypes.array.isRequired,
  itemToString: PropTypes.func,
  getA11yStatusMessage: PropTypes.func,
  getA11ySelectionMessage: PropTypes.func,
  circularNavigation: PropTypes.bool,
  highlightedIndex: PropTypes.number,
  defaultHighlightedIndex: PropTypes.number,
  initialHighlightedIndex: PropTypes.number,
  isOpen: PropTypes.bool,
  defaultIsOpen: PropTypes.bool,
  initialIsOpen: PropTypes.bool,
  selectedItem: PropTypes.any,
  initialSelectedItem: PropTypes.any,
  defaultSelectedItem: PropTypes.any,
  inputValue: PropTypes.string,
  defaultInputValue: PropTypes.string,
  initialInputValue: PropTypes.string,
  id: PropTypes.string,
  labelId: PropTypes.string,
  menuId: PropTypes.string,
  getItemId: PropTypes.func,
  inputId: PropTypes.string,
  toggleButtonId: PropTypes.string,
  stateReducer: PropTypes.func,
  onSelectedItemChange: PropTypes.func,
  onHighlightedIndexChange: PropTypes.func,
  onStateChange: PropTypes.func,
  onIsOpenChange: PropTypes.func,
  onInputValueChange: PropTypes.func,
  environment: PropTypes.shape({
    addEventListener: PropTypes.func,
    removeEventListener: PropTypes.func,
    document: PropTypes.shape({
      getElementById: PropTypes.func,
      activeElement: PropTypes.any,
      body: PropTypes.any
    })
  })
};
/**
 * The useCombobox version of useControlledReducer, which also
 * checks if the controlled prop selectedItem changed between
 * renders. If so, it will also update inputValue with its
 * string equivalent. It uses the common useEnhancedReducer to
 * compute the rest of the state.
 *
 * @param {Function} reducer Reducer function from downshift.
 * @param {Object} initialState Initial state of the hook.
 * @param {Object} props The hook props.
 * @returns {Array} An array with the state and an action dispatcher.
 */

function useControlledReducer$2(reducer, initialState, props) {
  const previousSelectedItemRef = useRef();
  const [state, dispatch] = useEnhancedReducer$1(reducer, initialState, props); // ToDo: if needed, make same approach as selectedItemChanged from Downshift.

  useEffect(() => {
    if (isControlledProp$1(props, 'selectedItem')) {
      if (previousSelectedItemRef.current !== props.selectedItem) {
        dispatch({
          type: ControlledPropUpdatedSelectedItem$1,
          inputValue: props.itemToString(props.selectedItem)
        });
      }

      previousSelectedItemRef.current = state.selectedItem === previousSelectedItemRef.current ? props.selectedItem : state.selectedItem;
    }
  });
  return [getState$1(state, props), dispatch];
} // eslint-disable-next-line import/no-mutable-exports


let validatePropTypes$1$1 = noop$1;
/* istanbul ignore next */

if (process.env.NODE_ENV !== 'production') {
  validatePropTypes$1$1 = (options, caller) => {
    PropTypes.checkPropTypes(propTypes$1$1, options, 'prop', caller.name);
  };
}

const defaultProps$1$1 = { ...defaultProps$3$1,
  getA11yStatusMessage: getA11yStatusMessage$1$1,
  circularNavigation: true
};

/* eslint-disable complexity */

function downshiftUseComboboxReducer$1(state, action) {
  const {
    type,
    props,
    shiftKey
  } = action;
  let changes;

  switch (type) {
    case ItemClick$2:
      changes = {
        isOpen: getDefaultValue$1$1(props, 'isOpen'),
        highlightedIndex: getDefaultValue$1$1(props, 'highlightedIndex'),
        selectedItem: props.items[action.index],
        inputValue: props.itemToString(props.items[action.index])
      };
      break;

    case InputKeyDownArrowDown$1:
      if (state.isOpen) {
        changes = {
          highlightedIndex: getNextWrappingIndex$1(shiftKey ? 5 : 1, state.highlightedIndex, props.items.length, action.getItemNodeFromIndex, props.circularNavigation)
        };
      } else {
        changes = {
          highlightedIndex: getHighlightedIndexOnOpen$1(props, state, 1, action.getItemNodeFromIndex),
          isOpen: props.items.length >= 0
        };
      }

      break;

    case InputKeyDownArrowUp$1:
      if (state.isOpen) {
        changes = {
          highlightedIndex: getNextWrappingIndex$1(shiftKey ? -5 : -1, state.highlightedIndex, props.items.length, action.getItemNodeFromIndex, props.circularNavigation)
        };
      } else {
        changes = {
          highlightedIndex: getHighlightedIndexOnOpen$1(props, state, -1, action.getItemNodeFromIndex),
          isOpen: props.items.length >= 0
        };
      }

      break;

    case InputKeyDownEnter$1:
      changes = { ...(state.isOpen && state.highlightedIndex >= 0 && {
          selectedItem: props.items[state.highlightedIndex],
          isOpen: getDefaultValue$1$1(props, 'isOpen'),
          highlightedIndex: getDefaultValue$1$1(props, 'highlightedIndex'),
          inputValue: props.itemToString(props.items[state.highlightedIndex])
        })
      };
      break;

    case InputKeyDownEscape$1:
      changes = {
        isOpen: false,
        highlightedIndex: -1,
        ...(!state.isOpen && {
          selectedItem: null,
          inputValue: ''
        })
      };
      break;

    case InputKeyDownHome$1:
      changes = {
        highlightedIndex: getNextNonDisabledIndex$1(1, 0, props.items.length, action.getItemNodeFromIndex, false)
      };
      break;

    case InputKeyDownEnd$1:
      changes = {
        highlightedIndex: getNextNonDisabledIndex$1(-1, props.items.length - 1, props.items.length, action.getItemNodeFromIndex, false)
      };
      break;

    case InputBlur$1:
      changes = {
        isOpen: false,
        highlightedIndex: -1,
        ...(state.highlightedIndex >= 0 && action.selectItem && {
          selectedItem: props.items[state.highlightedIndex],
          inputValue: props.itemToString(props.items[state.highlightedIndex])
        })
      };
      break;

    case InputChange$1:
      changes = {
        isOpen: true,
        highlightedIndex: getDefaultValue$1$1(props, 'highlightedIndex'),
        inputValue: action.inputValue
      };
      break;

    case FunctionSelectItem$2:
      changes = {
        selectedItem: action.selectedItem,
        inputValue: props.itemToString(action.selectedItem)
      };
      break;

    case ControlledPropUpdatedSelectedItem$1:
      changes = {
        inputValue: action.inputValue
      };
      break;

    default:
      return downshiftCommonReducer$1(state, action, stateChangeTypes$1$1);
  }

  return { ...state,
    ...changes
  };
}
/* eslint-enable complexity */

/* eslint-disable max-statements */
useCombobox$1.stateChangeTypes = stateChangeTypes$1$1;

function useCombobox$1(userProps) {
  if (userProps === void 0) {
    userProps = {};
  }

  validatePropTypes$1$1(userProps, useCombobox$1); // Props defaults and destructuring.

  const props = { ...defaultProps$1$1,
    ...userProps
  };
  const {
    initialIsOpen,
    defaultIsOpen,
    items,
    scrollIntoView,
    environment,
    getA11yStatusMessage,
    getA11ySelectionMessage,
    itemToString
  } = props; // Initial state depending on controlled props.

  const initialState = getInitialState$1$1(props);
  const [state, dispatch] = useControlledReducer$2(downshiftUseComboboxReducer$1, initialState, props);
  const {
    isOpen,
    highlightedIndex,
    selectedItem,
    inputValue
  } = state; // Element refs.

  const menuRef = useRef(null);
  const itemRefs = useRef({});
  const inputRef = useRef(null);
  const toggleButtonRef = useRef(null);
  const comboboxRef = useRef(null);
  const isInitialMountRef = useRef(true); // prevent id re-generation between renders.

  const elementIds = useElementIds$1(props); // used to keep track of how many items we had on previous cycle.

  const previousResultCountRef = useRef(); // utility callback to get item element.

  const latest = useLatestRef$1({
    state,
    props
  });
  const getItemNodeFromIndex = useCallback(index => itemRefs.current[elementIds.getItemId(index)], [elementIds]); // Effects.
  // Sets a11y status message on changes in state.

  useA11yMessageSetter$1(getA11yStatusMessage, [isOpen, highlightedIndex, inputValue, items], {
    isInitialMount: isInitialMountRef.current,
    previousResultCount: previousResultCountRef.current,
    items,
    environment,
    itemToString,
    ...state
  }); // Sets a11y status message on changes in selectedItem.

  useA11yMessageSetter$1(getA11ySelectionMessage, [selectedItem], {
    isInitialMount: isInitialMountRef.current,
    previousResultCount: previousResultCountRef.current,
    items,
    environment,
    itemToString,
    ...state
  }); // Scroll on highlighted item if change comes from keyboard.

  const shouldScrollRef = useScrollIntoView$1({
    menuElement: menuRef.current,
    highlightedIndex,
    isOpen,
    itemRefs,
    scrollIntoView,
    getItemNodeFromIndex
  });
  useControlPropsValidator$1({
    isInitialMount: isInitialMountRef.current,
    props,
    state
  }); // Focus the input on first render if required.

  useEffect(() => {
    const focusOnOpen = initialIsOpen || defaultIsOpen || isOpen;

    if (focusOnOpen && inputRef.current) {
      inputRef.current.focus();
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, []);
  useEffect(() => {
    if (isInitialMountRef.current) {
      return;
    }

    previousResultCountRef.current = items.length;
  }); // Add mouse/touch events to document.

  const mouseAndTouchTrackersRef = useMouseAndTouchTracker$1(isOpen, [comboboxRef, menuRef, toggleButtonRef], environment, () => {
    dispatch({
      type: InputBlur$1,
      selectItem: false
    });
  });
  const setGetterPropCallInfo = useGetterPropsCalledChecker$1('getInputProps', 'getComboboxProps', 'getMenuProps'); // Make initial ref false.

  useEffect(() => {
    isInitialMountRef.current = false;
  }, []); // Reset itemRefs on close.

  useEffect(() => {
    if (!isOpen) {
      itemRefs.current = {};
    }
  }, [isOpen]);
  /* Event handler functions */

  const inputKeyDownHandlers = useMemo(() => ({
    ArrowDown(event) {
      event.preventDefault();
      dispatch({
        type: InputKeyDownArrowDown$1,
        shiftKey: event.shiftKey,
        getItemNodeFromIndex
      });
    },

    ArrowUp(event) {
      event.preventDefault();
      dispatch({
        type: InputKeyDownArrowUp$1,
        shiftKey: event.shiftKey,
        getItemNodeFromIndex
      });
    },

    Home(event) {
      if (!latest.current.state.isOpen) {
        return;
      }

      event.preventDefault();
      dispatch({
        type: InputKeyDownHome$1,
        getItemNodeFromIndex
      });
    },

    End(event) {
      if (!latest.current.state.isOpen) {
        return;
      }

      event.preventDefault();
      dispatch({
        type: InputKeyDownEnd$1,
        getItemNodeFromIndex
      });
    },

    Escape(event) {
      const latestState = latest.current.state;

      if (latestState.isOpen || latestState.inputValue || latestState.selectedItem || latestState.highlightedIndex > -1) {
        event.preventDefault();
        dispatch({
          type: InputKeyDownEscape$1
        });
      }
    },

    Enter(event) {
      const latestState = latest.current.state; // if closed or no highlighted index, do nothing.

      if (!latestState.isOpen || latestState.highlightedIndex < 0 || event.which === 229 // if IME composing, wait for next Enter keydown event.
      ) {
        return;
      }

      event.preventDefault();
      dispatch({
        type: InputKeyDownEnter$1,
        getItemNodeFromIndex
      });
    }

  }), [dispatch, latest, getItemNodeFromIndex]); // Getter props.

  const getLabelProps = useCallback(labelProps => ({
    id: elementIds.labelId,
    htmlFor: elementIds.inputId,
    ...labelProps
  }), [elementIds]);
  const getMenuProps = useCallback(function (_temp, _temp2) {
    let {
      onMouseLeave,
      refKey = 'ref',
      ref,
      ...rest
    } = _temp === void 0 ? {} : _temp;
    let {
      suppressRefError = false
    } = _temp2 === void 0 ? {} : _temp2;
    setGetterPropCallInfo('getMenuProps', suppressRefError, refKey, menuRef);
    return {
      [refKey]: handleRefs$1(ref, menuNode => {
        menuRef.current = menuNode;
      }),
      id: elementIds.menuId,
      role: 'listbox',
      'aria-labelledby': elementIds.labelId,
      onMouseLeave: callAllEventHandlers$1(onMouseLeave, () => {
        dispatch({
          type: MenuMouseLeave$2
        });
      }),
      ...rest
    };
  }, [dispatch, setGetterPropCallInfo, elementIds]);
  const getItemProps = useCallback(function (_temp3) {
    let {
      item,
      index,
      refKey = 'ref',
      ref,
      onMouseMove,
      onMouseDown,
      onClick,
      onPress,
      disabled,
      ...rest
    } = _temp3 === void 0 ? {} : _temp3;
    const {
      props: latestProps,
      state: latestState
    } = latest.current;
    const itemIndex = getItemIndex$1(index, item, latestProps.items);

    if (itemIndex < 0) {
      throw new Error('Pass either item or item index in getItemProps!');
    }

    const onSelectKey = 'onClick';
    const customClickHandler = onClick;

    const itemHandleMouseMove = () => {
      if (index === latestState.highlightedIndex) {
        return;
      }

      shouldScrollRef.current = false;
      dispatch({
        type: ItemMouseMove$2,
        index,
        disabled
      });
    };

    const itemHandleClick = () => {
      dispatch({
        type: ItemClick$2,
        index
      });
    };

    const itemHandleMouseDown = e => e.preventDefault();

    return {
      [refKey]: handleRefs$1(ref, itemNode => {
        if (itemNode) {
          itemRefs.current[elementIds.getItemId(itemIndex)] = itemNode;
        }
      }),
      disabled,
      role: 'option',
      'aria-selected': `${itemIndex === latestState.highlightedIndex}`,
      id: elementIds.getItemId(itemIndex),
      ...(!disabled && {
        [onSelectKey]: callAllEventHandlers$1(customClickHandler, itemHandleClick)
      }),
      onMouseMove: callAllEventHandlers$1(onMouseMove, itemHandleMouseMove),
      onMouseDown: callAllEventHandlers$1(onMouseDown, itemHandleMouseDown),
      ...rest
    };
  }, [dispatch, latest, shouldScrollRef, elementIds]);
  const getToggleButtonProps = useCallback(function (_temp4) {
    let {
      onClick,
      onPress,
      refKey = 'ref',
      ref,
      ...rest
    } = _temp4 === void 0 ? {} : _temp4;

    const toggleButtonHandleClick = () => {
      dispatch({
        type: ToggleButtonClick$2
      });

      if (!latest.current.state.isOpen && inputRef.current) {
        inputRef.current.focus();
      }
    };

    return {
      [refKey]: handleRefs$1(ref, toggleButtonNode => {
        toggleButtonRef.current = toggleButtonNode;
      }),
      id: elementIds.toggleButtonId,
      tabIndex: -1,
      ...(!rest.disabled && { ...({
          onClick: callAllEventHandlers$1(onClick, toggleButtonHandleClick)
        })
      }),
      ...rest
    };
  }, [dispatch, latest, elementIds]);
  const getInputProps = useCallback(function (_temp5, _temp6) {
    let {
      onKeyDown,
      onChange,
      onInput,
      onBlur,
      onChangeText,
      refKey = 'ref',
      ref,
      ...rest
    } = _temp5 === void 0 ? {} : _temp5;
    let {
      suppressRefError = false
    } = _temp6 === void 0 ? {} : _temp6;
    setGetterPropCallInfo('getInputProps', suppressRefError, refKey, inputRef);
    const latestState = latest.current.state;

    const inputHandleKeyDown = event => {
      const key = normalizeArrowKey$1(event);

      if (key && inputKeyDownHandlers[key]) {
        inputKeyDownHandlers[key](event);
      }
    };

    const inputHandleChange = event => {
      dispatch({
        type: InputChange$1,
        inputValue: event.target.value
      });
    };

    const inputHandleBlur = () => {
      /* istanbul ignore else */
      if (latestState.isOpen && !mouseAndTouchTrackersRef.current.isMouseDown) {
        dispatch({
          type: InputBlur$1,
          selectItem: true
        });
      }
    };
    /* istanbul ignore next (preact) */


    const onChangeKey = 'onChange';
    let eventHandlers = {};

    if (!rest.disabled) {
      eventHandlers = {
        [onChangeKey]: callAllEventHandlers$1(onChange, onInput, inputHandleChange),
        onKeyDown: callAllEventHandlers$1(onKeyDown, inputHandleKeyDown),
        onBlur: callAllEventHandlers$1(onBlur, inputHandleBlur)
      };
    }

    return {
      [refKey]: handleRefs$1(ref, inputNode => {
        inputRef.current = inputNode;
      }),
      id: elementIds.inputId,
      'aria-autocomplete': 'list',
      'aria-controls': elementIds.menuId,
      ...(latestState.isOpen && latestState.highlightedIndex > -1 && {
        'aria-activedescendant': elementIds.getItemId(latestState.highlightedIndex)
      }),
      'aria-labelledby': elementIds.labelId,
      // https://developer.mozilla.org/en-US/docs/Web/Security/Securing_your_site/Turning_off_form_autocompletion
      // revert back since autocomplete="nope" is ignored on latest Chrome and Opera
      autoComplete: 'off',
      value: latestState.inputValue,
      ...eventHandlers,
      ...rest
    };
  }, [dispatch, inputKeyDownHandlers, latest, mouseAndTouchTrackersRef, setGetterPropCallInfo, elementIds]);
  const getComboboxProps = useCallback(function (_temp7, _temp8) {
    let {
      refKey = 'ref',
      ref,
      ...rest
    } = _temp7 === void 0 ? {} : _temp7;
    let {
      suppressRefError = false
    } = _temp8 === void 0 ? {} : _temp8;
    setGetterPropCallInfo('getComboboxProps', suppressRefError, refKey, comboboxRef);
    return {
      [refKey]: handleRefs$1(ref, comboboxNode => {
        comboboxRef.current = comboboxNode;
      }),
      role: 'combobox',
      'aria-haspopup': 'listbox',
      'aria-owns': elementIds.menuId,
      'aria-expanded': latest.current.state.isOpen,
      ...rest
    };
  }, [latest, setGetterPropCallInfo, elementIds]); // returns

  const toggleMenu = useCallback(() => {
    dispatch({
      type: FunctionToggleMenu$2
    });
  }, [dispatch]);
  const closeMenu = useCallback(() => {
    dispatch({
      type: FunctionCloseMenu$2
    });
  }, [dispatch]);
  const openMenu = useCallback(() => {
    dispatch({
      type: FunctionOpenMenu$2
    });
  }, [dispatch]);
  const setHighlightedIndex = useCallback(newHighlightedIndex => {
    dispatch({
      type: FunctionSetHighlightedIndex$2,
      highlightedIndex: newHighlightedIndex
    });
  }, [dispatch]);
  const selectItem = useCallback(newSelectedItem => {
    dispatch({
      type: FunctionSelectItem$2,
      selectedItem: newSelectedItem
    });
  }, [dispatch]);
  const setInputValue = useCallback(newInputValue => {
    dispatch({
      type: FunctionSetInputValue$2,
      inputValue: newInputValue
    });
  }, [dispatch]);
  const reset = useCallback(() => {
    dispatch({
      type: FunctionReset$1$1
    });
  }, [dispatch]);
  return {
    // prop getters.
    getItemProps,
    getLabelProps,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    getToggleButtonProps,
    // actions.
    toggleMenu,
    openMenu,
    closeMenu,
    setHighlightedIndex,
    setInputValue,
    selectItem,
    reset,
    // state.
    highlightedIndex,
    isOpen,
    selectedItem,
    inputValue
  };
}

const defaultStateValues = {
  activeIndex: -1,
  selectedItems: []
};
/**
 * Returns the initial value for a state key in the following order:
 * 1. controlled prop, 2. initial prop, 3. default prop, 4. default
 * value from Downshift.
 *
 * @param {Object} props Props passed to the hook.
 * @param {string} propKey Props key to generate the value for.
 * @returns {any} The initial value for that prop.
 */

function getInitialValue(props, propKey) {
  return getInitialValue$1$1(props, propKey, defaultStateValues);
}
/**
 * Returns the default value for a state key in the following order:
 * 1. controlled prop, 2. default prop, 3. default value from Downshift.
 *
 * @param {Object} props Props passed to the hook.
 * @param {string} propKey Props key to generate the value for.
 * @returns {any} The initial value for that prop.
 */


function getDefaultValue(props, propKey) {
  return getDefaultValue$1$1(props, propKey, defaultStateValues);
}
/**
 * Gets the initial state based on the provided props. It uses initial, default
 * and controlled props related to state in order to compute the initial value.
 *
 * @param {Object} props Props passed to the hook.
 * @returns {Object} The initial state.
 */


function getInitialState(props) {
  const activeIndex = getInitialValue(props, 'activeIndex');
  const selectedItems = getInitialValue(props, 'selectedItems');
  return {
    activeIndex,
    selectedItems
  };
}
/**
 * Returns true if dropdown keydown operation is permitted. Should not be
 * allowed on keydown with modifier keys (ctrl, alt, shift, meta), on
 * input element with text content that is either highlighted or selection
 * cursor is not at the starting position.
 *
 * @param {KeyboardEvent} event The event from keydown.
 * @returns {boolean} Whether the operation is allowed.
 */


function isKeyDownOperationPermitted(event) {
  if (event.shiftKey || event.metaKey || event.ctrlKey || event.altKey) {
    return false;
  }

  const element = event.target;

  if (element instanceof HTMLInputElement && // if element is a text input
  element.value !== '' && ( // and we have text in it
  // and cursor is either not at the start or is currently highlighting text.
  element.selectionStart !== 0 || element.selectionEnd !== 0)) {
    return false;
  }

  return true;
}
/**
 * Returns a message to be added to aria-live region when item is removed.
 *
 * @param {Object} selectionParameters Parameters required to build the message.
 * @returns {string} The a11y message.
 */


function getA11yRemovalMessage$1(selectionParameters) {
  const {
    removedSelectedItem,
    itemToString: itemToStringLocal
  } = selectionParameters;
  return `${itemToStringLocal(removedSelectedItem)} has been removed.`;
}

const propTypes = {
  selectedItems: PropTypes.array,
  initialSelectedItems: PropTypes.array,
  defaultSelectedItems: PropTypes.array,
  itemToString: PropTypes.func,
  getA11yRemovalMessage: PropTypes.func,
  stateReducer: PropTypes.func,
  activeIndex: PropTypes.number,
  initialActiveIndex: PropTypes.number,
  defaultActiveIndex: PropTypes.number,
  onActiveIndexChange: PropTypes.func,
  onSelectedItemsChange: PropTypes.func,
  keyNavigationNext: PropTypes.string,
  keyNavigationPrevious: PropTypes.string,
  environment: PropTypes.shape({
    addEventListener: PropTypes.func,
    removeEventListener: PropTypes.func,
    document: PropTypes.shape({
      getElementById: PropTypes.func,
      activeElement: PropTypes.any,
      body: PropTypes.any
    })
  })
};
const defaultProps = {
  itemToString: defaultProps$3$1.itemToString,
  stateReducer: defaultProps$3$1.stateReducer,
  environment: defaultProps$3$1.environment,
  getA11yRemovalMessage: getA11yRemovalMessage$1,
  keyNavigationNext: 'ArrowRight',
  keyNavigationPrevious: 'ArrowLeft'
}; // eslint-disable-next-line import/no-mutable-exports

let validatePropTypes = noop$1;
/* istanbul ignore next */

if (process.env.NODE_ENV !== 'production') {
  validatePropTypes = (options, caller) => {
    PropTypes.checkPropTypes(propTypes, options, 'prop', caller.name);
  };
}

const SelectedItemClick = process.env.NODE_ENV !== "production" ? '__selected_item_click__' : 0;
const SelectedItemKeyDownDelete = process.env.NODE_ENV !== "production" ? '__selected_item_keydown_delete__' : 1;
const SelectedItemKeyDownBackspace = process.env.NODE_ENV !== "production" ? '__selected_item_keydown_backspace__' : 2;
const SelectedItemKeyDownNavigationNext = process.env.NODE_ENV !== "production" ? '__selected_item_keydown_navigation_next__' : 3;
const SelectedItemKeyDownNavigationPrevious = process.env.NODE_ENV !== "production" ? '__selected_item_keydown_navigation_previous__' : 4;
const DropdownKeyDownNavigationPrevious = process.env.NODE_ENV !== "production" ? '__dropdown_keydown_navigation_previous__' : 5;
const DropdownKeyDownBackspace = process.env.NODE_ENV !== "production" ? '__dropdown_keydown_backspace__' : 6;
const DropdownClick = process.env.NODE_ENV !== "production" ? '__dropdown_click__' : 7;
const FunctionAddSelectedItem = process.env.NODE_ENV !== "production" ? '__function_add_selected_item__' : 8;
const FunctionRemoveSelectedItem = process.env.NODE_ENV !== "production" ? '__function_remove_selected_item__' : 9;
const FunctionSetSelectedItems = process.env.NODE_ENV !== "production" ? '__function_set_selected_items__' : 10;
const FunctionSetActiveIndex = process.env.NODE_ENV !== "production" ? '__function_set_active_index__' : 11;
const FunctionReset = process.env.NODE_ENV !== "production" ? '__function_reset__' : 12;

var stateChangeTypes = /*#__PURE__*/Object.freeze({
  __proto__: null,
  SelectedItemClick: SelectedItemClick,
  SelectedItemKeyDownDelete: SelectedItemKeyDownDelete,
  SelectedItemKeyDownBackspace: SelectedItemKeyDownBackspace,
  SelectedItemKeyDownNavigationNext: SelectedItemKeyDownNavigationNext,
  SelectedItemKeyDownNavigationPrevious: SelectedItemKeyDownNavigationPrevious,
  DropdownKeyDownNavigationPrevious: DropdownKeyDownNavigationPrevious,
  DropdownKeyDownBackspace: DropdownKeyDownBackspace,
  DropdownClick: DropdownClick,
  FunctionAddSelectedItem: FunctionAddSelectedItem,
  FunctionRemoveSelectedItem: FunctionRemoveSelectedItem,
  FunctionSetSelectedItems: FunctionSetSelectedItems,
  FunctionSetActiveIndex: FunctionSetActiveIndex,
  FunctionReset: FunctionReset
});

/* eslint-disable complexity */

function downshiftMultipleSelectionReducer(state, action) {
  const {
    type,
    index,
    props,
    selectedItem
  } = action;
  const {
    activeIndex,
    selectedItems
  } = state;
  let changes;

  switch (type) {
    case SelectedItemClick:
      changes = {
        activeIndex: index
      };
      break;

    case SelectedItemKeyDownNavigationPrevious:
      changes = {
        activeIndex: activeIndex - 1 < 0 ? 0 : activeIndex - 1
      };
      break;

    case SelectedItemKeyDownNavigationNext:
      changes = {
        activeIndex: activeIndex + 1 >= selectedItems.length ? -1 : activeIndex + 1
      };
      break;

    case SelectedItemKeyDownBackspace:
    case SelectedItemKeyDownDelete:
      {
        let newActiveIndex = activeIndex;

        if (selectedItems.length === 1) {
          newActiveIndex = -1;
        } else if (activeIndex === selectedItems.length - 1) {
          newActiveIndex = selectedItems.length - 2;
        }

        changes = {
          selectedItems: [...selectedItems.slice(0, activeIndex), ...selectedItems.slice(activeIndex + 1)],
          ...{
            activeIndex: newActiveIndex
          }
        };
        break;
      }

    case DropdownKeyDownNavigationPrevious:
      changes = {
        activeIndex: selectedItems.length - 1
      };
      break;

    case DropdownKeyDownBackspace:
      changes = {
        selectedItems: selectedItems.slice(0, selectedItems.length - 1)
      };
      break;

    case FunctionAddSelectedItem:
      changes = {
        selectedItems: [...selectedItems, selectedItem]
      };
      break;

    case DropdownClick:
      changes = {
        activeIndex: -1
      };
      break;

    case FunctionRemoveSelectedItem:
      {
        let newActiveIndex = activeIndex;
        const selectedItemIndex = selectedItems.indexOf(selectedItem);

        if (selectedItemIndex >= 0) {
          if (selectedItems.length === 1) {
            newActiveIndex = -1;
          } else if (selectedItemIndex === selectedItems.length - 1) {
            newActiveIndex = selectedItems.length - 2;
          }

          changes = {
            selectedItems: [...selectedItems.slice(0, selectedItemIndex), ...selectedItems.slice(selectedItemIndex + 1)],
            activeIndex: newActiveIndex
          };
        }

        break;
      }

    case FunctionSetSelectedItems:
      {
        const {
          selectedItems: newSelectedItems
        } = action;
        changes = {
          selectedItems: newSelectedItems
        };
        break;
      }

    case FunctionSetActiveIndex:
      {
        const {
          activeIndex: newActiveIndex
        } = action;
        changes = {
          activeIndex: newActiveIndex
        };
        break;
      }

    case FunctionReset:
      changes = {
        activeIndex: getDefaultValue(props, 'activeIndex'),
        selectedItems: getDefaultValue(props, 'selectedItems')
      };
      break;

    default:
      throw new Error('Reducer called without proper action type.');
  }

  return { ...state,
    ...changes
  };
}

useMultipleSelection.stateChangeTypes = stateChangeTypes;

function useMultipleSelection(userProps) {
  if (userProps === void 0) {
    userProps = {};
  }

  validatePropTypes(userProps, useMultipleSelection); // Props defaults and destructuring.

  const props = { ...defaultProps,
    ...userProps
  };
  const {
    getA11yRemovalMessage,
    itemToString,
    environment,
    keyNavigationNext,
    keyNavigationPrevious
  } = props; // Reducer init.

  const [state, dispatch] = useControlledReducer$1(downshiftMultipleSelectionReducer, getInitialState(props), props);
  const {
    activeIndex,
    selectedItems
  } = state; // Refs.

  const isInitialMountRef = useRef(true);
  const dropdownRef = useRef(null);
  const previousSelectedItemsRef = useRef(selectedItems);
  const selectedItemRefs = useRef();
  selectedItemRefs.current = [];
  const latest = useLatestRef$1({
    state,
    props
  }); // Effects.

  /* Sets a11y status message on changes in selectedItem. */

  useEffect(() => {
    if (isInitialMountRef.current) {
      return;
    }

    if (selectedItems.length < previousSelectedItemsRef.current.length) {
      const removedSelectedItem = previousSelectedItemsRef.current.find(item => selectedItems.indexOf(item) < 0);
      setStatus$1(getA11yRemovalMessage({
        itemToString,
        resultCount: selectedItems.length,
        removedSelectedItem,
        activeIndex,
        activeSelectedItem: selectedItems[activeIndex]
      }), environment.document);
    }

    previousSelectedItemsRef.current = selectedItems; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItems.length]); // Sets focus on active item.

  useEffect(() => {
    if (isInitialMountRef.current) {
      return;
    }

    if (activeIndex === -1 && dropdownRef.current) {
      dropdownRef.current.focus();
    } else if (selectedItemRefs.current[activeIndex]) {
      selectedItemRefs.current[activeIndex].focus();
    }
  }, [activeIndex]);
  useControlPropsValidator$1({
    isInitialMount: isInitialMountRef.current,
    props,
    state
  });
  const setGetterPropCallInfo = useGetterPropsCalledChecker$1('getDropdownProps'); // Make initial ref false.

  useEffect(() => {
    isInitialMountRef.current = false;
  }, []); // Event handler functions.

  const selectedItemKeyDownHandlers = useMemo(() => ({
    [keyNavigationPrevious]() {
      dispatch({
        type: SelectedItemKeyDownNavigationPrevious
      });
    },

    [keyNavigationNext]() {
      dispatch({
        type: SelectedItemKeyDownNavigationNext
      });
    },

    Delete() {
      dispatch({
        type: SelectedItemKeyDownDelete
      });
    },

    Backspace() {
      dispatch({
        type: SelectedItemKeyDownBackspace
      });
    }

  }), [dispatch, keyNavigationNext, keyNavigationPrevious]);
  const dropdownKeyDownHandlers = useMemo(() => ({
    [keyNavigationPrevious](event) {
      if (isKeyDownOperationPermitted(event)) {
        dispatch({
          type: DropdownKeyDownNavigationPrevious
        });
      }
    },

    Backspace(event) {
      if (isKeyDownOperationPermitted(event)) {
        dispatch({
          type: DropdownKeyDownBackspace
        });
      }
    }

  }), [dispatch, keyNavigationPrevious]); // Getter props.

  const getSelectedItemProps = useCallback(function (_temp) {
    let {
      refKey = 'ref',
      ref,
      onClick,
      onKeyDown,
      selectedItem,
      index,
      ...rest
    } = _temp === void 0 ? {} : _temp;
    const {
      state: latestState
    } = latest.current;
    const itemIndex = getItemIndex$1(index, selectedItem, latestState.selectedItems);

    if (itemIndex < 0) {
      throw new Error('Pass either selectedItem or index in getSelectedItemProps!');
    }

    const selectedItemHandleClick = () => {
      dispatch({
        type: SelectedItemClick,
        index
      });
    };

    const selectedItemHandleKeyDown = event => {
      const key = normalizeArrowKey$1(event);

      if (key && selectedItemKeyDownHandlers[key]) {
        selectedItemKeyDownHandlers[key](event);
      }
    };

    return {
      [refKey]: handleRefs$1(ref, selectedItemNode => {
        if (selectedItemNode) {
          selectedItemRefs.current.push(selectedItemNode);
        }
      }),
      tabIndex: index === latestState.activeIndex ? 0 : -1,
      onClick: callAllEventHandlers$1(onClick, selectedItemHandleClick),
      onKeyDown: callAllEventHandlers$1(onKeyDown, selectedItemHandleKeyDown),
      ...rest
    };
  }, [dispatch, latest, selectedItemKeyDownHandlers]);
  const getDropdownProps = useCallback(function (_temp2, _temp3) {
    let {
      refKey = 'ref',
      ref,
      onKeyDown,
      onClick,
      preventKeyAction = false,
      ...rest
    } = _temp2 === void 0 ? {} : _temp2;
    let {
      suppressRefError = false
    } = _temp3 === void 0 ? {} : _temp3;
    setGetterPropCallInfo('getDropdownProps', suppressRefError, refKey, dropdownRef);

    const dropdownHandleKeyDown = event => {
      const key = normalizeArrowKey$1(event);

      if (key && dropdownKeyDownHandlers[key]) {
        dropdownKeyDownHandlers[key](event);
      }
    };

    const dropdownHandleClick = () => {
      dispatch({
        type: DropdownClick
      });
    };

    return {
      [refKey]: handleRefs$1(ref, dropdownNode => {
        if (dropdownNode) {
          dropdownRef.current = dropdownNode;
        }
      }),
      ...(!preventKeyAction && {
        onKeyDown: callAllEventHandlers$1(onKeyDown, dropdownHandleKeyDown),
        onClick: callAllEventHandlers$1(onClick, dropdownHandleClick)
      }),
      ...rest
    };
  }, [dispatch, dropdownKeyDownHandlers, setGetterPropCallInfo]); // returns

  const addSelectedItem = useCallback(selectedItem => {
    dispatch({
      type: FunctionAddSelectedItem,
      selectedItem
    });
  }, [dispatch]);
  const removeSelectedItem = useCallback(selectedItem => {
    dispatch({
      type: FunctionRemoveSelectedItem,
      selectedItem
    });
  }, [dispatch]);
  const setSelectedItems = useCallback(newSelectedItems => {
    dispatch({
      type: FunctionSetSelectedItems,
      selectedItems: newSelectedItems
    });
  }, [dispatch]);
  const setActiveIndex = useCallback(newActiveIndex => {
    dispatch({
      type: FunctionSetActiveIndex,
      activeIndex: newActiveIndex
    });
  }, [dispatch]);
  const reset = useCallback(() => {
    dispatch({
      type: FunctionReset
    });
  }, [dispatch]);
  return {
    getSelectedItemProps,
    getDropdownProps,
    addSelectedItem,
    removeSelectedItem,
    setSelectedItems,
    setActiveIndex,
    reset,
    selectedItems,
    activeIndex
  };
}

function _define_property$Q(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$Q(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$Q(target, key, source[key]);
        });
    }
    return target;
}
function _object_without_properties$H(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$H(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$H(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
/**
 * Used to extend Downshifts getter utils.
 *
 * @example
 * // Access the getter(s) you would like to extend
 * const {getItemProps: downShiftGetItemProps} = useSelect({...});
 *
 * // Extend the getter to include additional props
 * const getItemProps = extendGetter(downShiftGetItemProps, {"some-props-that": "matter-to-us"});
 *
 * // Use the getter
 * <li {...getItemProps({"add-more-specific": "list-item-props"})}>
 *     List item
 * </li>
 *
 * @param {function} getter
 * @param {object} controlledProps
 * @returns {function} Returns getter
 */ const extendGetter = (getter, controlledProps)=>(_param = {})=>{
        var { className: consumerClassName = "" } = _param, consumerProps = _object_without_properties$H(_param, [
            "className"
        ]);
        const _getter = getter(_object_spread$Q({}, controlledProps, consumerProps)), { className: classList } = _getter, rest = _object_without_properties$H(_getter, [
            "className"
        ]);
        return _object_spread$Q({
            className: cn(classList, consumerClassName)
        }, rest);
    };

const DropdownContext = /*#__PURE__*/ createContext(undefined);

const useDropdownContext = ()=>{
    const context = useContext(DropdownContext);
    if (!context) {
        throw new Error("Dropdown compound components cannot be rendered outside the Dropdown component");
    }
    return context;
};

const DropdownContextComponent = ({ children })=>{
    const context = useDropdownContext();
    return children(context);
};

function _define_property$P(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$P(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$P(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$I(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$I(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$I(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$G(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$G(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$G(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const DropdownLabel = (_param)=>{
    var { children } = _param, rest = _object_without_properties$G(_param, [
        "children"
    ]);
    const { getLabelProps } = useDropdownContext();
    if (!getLabelProps) {
        throw new Error("Dropdown.Label component requires getLabelProps to be defined");
    }
    return /*#__PURE__*/ jsx("label", _object_spread_props$I(_object_spread$P({}, getLabelProps(_object_spread$P({}, rest))), {
        children: children
    }));
};

var Variant;
(function(Variant) {
    Variant["LISTBOX"] = "LISTBOX";
    Variant["MENUBUTTON"] = "MENUBUTTON";
})(Variant || (Variant = {}));
var StyleType;
(function(StyleType) {
    StyleType["standard"] = "standard";
    StyleType["primary"] = "primary";
})(StyleType || (StyleType = {}));

function _define_property$O(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$O(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$O(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$H(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$H(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$H(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$F(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$F(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$F(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const DropdownIcon = (_param)=>{
    var { icon, children } = _param, rest = _object_without_properties$F(_param, [
        "icon",
        "children"
    ]);
    const { getBlocksWith, getIconProps } = useDropdownContext();
    if (!getIconProps) {
        throw new Error("Dropdown.Icon component requires getIconProps to be defined");
    }
    return /*#__PURE__*/ jsxs("span", _object_spread_props$H(_object_spread$O({}, getIconProps(_object_spread$O({}, rest))), {
        children: [
            icon && /*#__PURE__*/ jsx(FontAwesomeIcon, {
                icon: icon,
                className: cn(getBlocksWith("__icon-svg"))
            }),
            children
        ]
    }));
};

function _define_property$N(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _extends$2() {
    _extends$2 = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends$2.apply(this, arguments);
}
function _object_destructuring_empty$1(o) {
    if (o === null || o === void 0) throw new TypeError("Cannot destructure " + o);
    return o;
}
function _object_spread$N(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$N(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$G(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$G(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$G(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
const DropdownToggle = (_param)=>{
    var rest = _extends$2({}, _object_destructuring_empty$1(_param));
    const { getBlocksWith, getToggleButtonProps, placeholder, selectedItem, variant, isLoading, announceLoadingStart, announceLoadingEnd, loadingPlaceholder, isMultiselect } = useDropdownContext();
    if (!getToggleButtonProps) {
        throw new Error("Dropdown.Toggle component requires getToggleButtonProps to be defined");
    }
    const defaultButtonLabel = !isMultiselect && selectedItem && selectedItem.label || placeholder;
    // Use placeholder to provide the default styling and dropdown indicator.
    const placeholderButtonLabel = variant === Variant.MENUBUTTON ? placeholder : defaultButtonLabel;
    return /*#__PURE__*/ jsxs(LoadingButton, _object_spread_props$G(_object_spread$N({
        isLoading: isLoading,
        loadingPlaceholder: loadingPlaceholder || placeholderButtonLabel,
        announceLoadingStart: announceLoadingStart,
        announceLoadingEnd: announceLoadingEnd,
        type: "button"
    }, getToggleButtonProps(_object_spread$N({}, rest))), {
        children: [
            /*#__PURE__*/ jsx("span", {
                className: getBlocksWith("__toggle-button-label"),
                children: placeholderButtonLabel
            }),
            /*#__PURE__*/ jsx(DropdownIcon, {
                className: getBlocksWith("__dropdown-indicator"),
                icon: faAngleDown
            })
        ]
    }));
};

function _define_property$M(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$M(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$M(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$F(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$F(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$F(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$E(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$E(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$E(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const DropdownMenu = (_param)=>{
    var { children } = _param, rest = _object_without_properties$E(_param, [
        "children"
    ]);
    const { getMenuProps, isOpen } = useDropdownContext();
    if (!getMenuProps) {
        throw new Error("Dropdown.Menu component requires getMenuProps to be defined");
    }
    return /*#__PURE__*/ jsx("ul", _object_spread_props$F(_object_spread$M({}, getMenuProps(_object_spread$M({}, rest))), {
        children: isOpen && children
    }));
};

function _define_property$L(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$L(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$L(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$E(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$E(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$E(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$D(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$D(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$D(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const DropdownItem = (_param)=>{
    var { item, index, children } = _param, rest = _object_without_properties$D(_param, [
        "item",
        "index",
        "children"
    ]);
    const { disabled, isDivider, isSelectable } = item;
    const { getItemProps, getDividerProps, getSelectableDividerProps, highlightedIndex, getBlocksWith, selectedItem, variant } = useDropdownContext();
    if (!isDivider && !getItemProps) {
        throw new Error("Using Dropdown.Item component as a 'list item' requires getItemProps to be defined");
    }
    if (isDivider && !getDividerProps) {
        throw new Error("Using the Dropdown.Item component as a 'divider' requires getDividerProps to be defined");
    }
    const renderDivider = ()=>/*#__PURE__*/ jsx("li", _object_spread_props$E(_object_spread$L({}, getDividerProps(_object_spread$L({
            item,
            index,
            className: cn(!item.label && getBlocksWith("__divider--line"), index === highlightedIndex && getBlocksWith("__divider--highlighted")),
            "aria-label": item.value
        }, rest))), {
            children: children
        }));
    const renderItem = ()=>/*#__PURE__*/ jsx("li", _object_spread_props$E(_object_spread$L({}, getItemProps(_object_spread$L({
            item,
            index,
            className: cn(index === highlightedIndex && getBlocksWith("__item--highlighted"), item === selectedItem && getBlocksWith("__item--selected"), item.isSelected && getBlocksWith("__item--multiselected")),
            disabled,
            "aria-selected": variant === Variant.LISTBOX ? Boolean(item === selectedItem || item.isSelected) : undefined
        }, rest))), {
            children: children
        }));
    const renderSelectableDivider = ()=>/*#__PURE__*/ jsx("li", _object_spread_props$E(_object_spread$L({}, getSelectableDividerProps(_object_spread$L({
            item,
            index,
            className: cn(!item.label && getBlocksWith("__divider--line"), index === highlightedIndex && getBlocksWith("__selectable_divider--highlighted"), item === selectedItem && getBlocksWith("__selectable_divider--selected"), item.isSelected && getBlocksWith("__selectable_divider--multiselected")),
            disabled,
            "aria-selected": variant === Variant.LISTBOX ? Boolean(item === selectedItem || item.isSelected) : undefined
        }, rest))), {
            children: children
        }));
    const getItem = ()=>{
        if (isSelectable && isDivider) {
            return renderSelectableDivider();
        } else {
            return isDivider ? renderDivider() : renderItem();
        }
    };
    return getItem();
};

function _define_property$K(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$K(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$K(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$D(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$D(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$D(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$C(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$C(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$C(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const Dropdown = /*#__PURE__*/ forwardRef(({ items, className: consumerBlockClassName = "", styleType = StyleType.primary, disabled = false, placeholder, downshiftOptions = {}, matchToggleWidth = false, showAllItems = false, hideLabel = false, variant = Variant.LISTBOX, children, isLoading = false, announceLoadingStart, announceLoadingEnd, loadingPlaceholder, isMultiselect = false }, ref)=>{
    const getBlocksWith = createBemBlockBuilder$1([
        "eb-dropdown",
        consumerBlockClassName
    ]);
    function stateReducer(state, actionAndChanges) {
        const { type, changes } = actionAndChanges;
        switch(type){
            case useSelect.stateChangeTypes.ToggleButtonKeyDownCharacter:
                return variant === Variant.MENUBUTTON ? state : changes; // ignore this event for menu button
            case useSelect.stateChangeTypes.ItemClick:
            case useSelect.stateChangeTypes.MenuKeyDownEnter:
            case useSelect.stateChangeTypes.MenuKeyDownSpaceButton:
                {
                    var _changes_selectedItem, _changes_selectedItem1;
                    //if item is divider and not selectable, we should ignore this event (mouse click/press enter/press space)
                    if (isMultiselect) {
                        var _changes_selectedItem2, _changes_selectedItem3;
                        return (changes === null || changes === void 0 ? void 0 : (_changes_selectedItem2 = changes.selectedItem) === null || _changes_selectedItem2 === void 0 ? void 0 : _changes_selectedItem2.isDivider) && !(changes === null || changes === void 0 ? void 0 : (_changes_selectedItem3 = changes.selectedItem) === null || _changes_selectedItem3 === void 0 ? void 0 : _changes_selectedItem3.isSelectable) ? state : _object_spread_props$D(_object_spread$K({}, changes), {
                            isOpen: true,
                            highlightedIndex: state.highlightedIndex
                        });
                    }
                    return (changes === null || changes === void 0 ? void 0 : (_changes_selectedItem = changes.selectedItem) === null || _changes_selectedItem === void 0 ? void 0 : _changes_selectedItem.isDivider) && !(changes === null || changes === void 0 ? void 0 : (_changes_selectedItem1 = changes.selectedItem) === null || _changes_selectedItem1 === void 0 ? void 0 : _changes_selectedItem1.isSelectable) ? state : changes;
                }
            case useSelect.stateChangeTypes.ItemMouseMove:
                {
                    //if item is divider and not selectable, we should ignore this event (mouse over)
                    // @ts-expect-error changes can never be null or undefined
                    const item = items[changes.highlightedIndex];
                    const isDivider = item && item.isDivider && !item.isSelectable;
                    return isDivider ? _object_spread_props$D(_object_spread$K({}, state), {
                        highlightedIndex: -1
                    }) : changes;
                }
            case useSelect.stateChangeTypes.MenuKeyDownArrowDown:
                {
                    //if an item is a divider, and it's the first item in the list,
                    //we should ignore them and go straight to the second element
                    //and if it is the last element, we should ignore it and not allow the user to click down unnecessarily
                    // @ts-expect-error changes can never be null or undefined
                    const item = items[changes.highlightedIndex];
                    const isDivider = item && item.isDivider && !item.isSelectable;
                    const elementIndex = items.findIndex((el)=>el === item);
                    const firstItem = elementIndex === 0;
                    const nextItem = items.slice(elementIndex).find((el)=>!el.isDivider);
                    const lastItem = !nextItem;
                    const nextItemIndex = items.findIndex((el)=>el === nextItem);
                    if (isDivider && firstItem) {
                        return _object_spread_props$D(_object_spread$K({}, changes), {
                            highlightedIndex: nextItemIndex
                        });
                    }
                    if (isDivider && lastItem) {
                        //and if divider is the last element, we should ignore it and not allow the user to click down unnecessarily
                        return state;
                    }
                    return changes;
                }
            case useSelect.stateChangeTypes.MenuKeyDownArrowUp:
                {
                    //if an item is a divider, and it's the first element and not selectable,
                    //we should ignore it and not allow the user to click up unnecessarily
                    // @ts-expect-error changes can never be null or undefined
                    const item = items[changes.highlightedIndex];
                    const isDivider = item && item.isDivider && !item.isSelectable;
                    const elementIndex = items.findIndex((el)=>el === item);
                    return isDivider && elementIndex === 0 ? state : changes; // ignore this events for dividers
                }
            default:
                return changes; // otherwise business as usual.
        }
    }
    const menuButtonFocus = variant === Variant.MENUBUTTON ? {
        selectedItem: null,
        defaultHighlightedIndex: items && items.length && items[0] ? 0 : -1
    } : {};
    const _useSelect = useSelect(_object_spread$K({
        items,
        stateReducer,
        itemToString: (item)=>item && item.label || "",
        initialSelectedItem: !placeholder && items && items.length && items[0] || undefined
    }, menuButtonFocus, downshiftOptions)), { getToggleButtonProps: downshiftGetToggleButtonProps, getItemProps: downshiftGetItemProps, getLabelProps: downshiftGetLabelProps, getMenuProps: downshiftGetMenuProps } = _useSelect, downshiftProps = _object_without_properties$C(_useSelect, [
        "getToggleButtonProps",
        "getItemProps",
        "getLabelProps",
        "getMenuProps"
    ]);
    const isOpen = downshiftProps.isOpen && !isLoading;
    const useMenuButtonAriaAttributes = (attributes)=>variant === Variant.MENUBUTTON ? attributes : null;
    const getToggleButtonProps = extendGetter(downshiftGetToggleButtonProps, _object_spread$K({
        className: cn(getBlocksWith("__toggle-button"), getBlocksWith(`__toggle-button--${styleType}`), disabled && getBlocksWith("__toggle-button--disabled"), isOpen && getBlocksWith("__toggle-button--open")),
        disabled
    }, useMenuButtonAriaAttributes({
        "aria-haspopup": "menu",
        "aria-controls": downshiftGetMenuProps().id
    })));
    const getItemProps = extendGetter(downshiftGetItemProps, _object_spread$K({
        className: cn(getBlocksWith("__item"), getBlocksWith(`__item--${styleType}`))
    }, useMenuButtonAriaAttributes({
        role: "menuitem",
        "aria-selected": undefined
    })));
    const getLabelProps = extendGetter(downshiftGetLabelProps, {
        className: cn(getBlocksWith("__label"), getBlocksWith(`__label--${styleType}`), hideLabel && getBlocksWith("__label--hidden"))
    });
    const getMenuProps = extendGetter(downshiftGetMenuProps, _object_spread$K({
        className: cn(getBlocksWith("__menu"), getBlocksWith(`__menu--${styleType}`), matchToggleWidth && getBlocksWith("__menu--narrow"), showAllItems && getBlocksWith("__menu--show-all"), isOpen && getBlocksWith("__menu--open"))
    }, useMenuButtonAriaAttributes({
        role: "menu"
    })));
    const genericGetter = (props)=>_object_spread$K({}, props);
    const getDividerProps = extendGetter(downshiftGetItemProps, {
        role: "separator",
        "aria-selected": undefined,
        "aria-disabled": true,
        "aria-labelledby": null,
        className: cn(getBlocksWith("__divider"), getBlocksWith(`__divider--${styleType}`))
    });
    const getSelectableDividerProps = extendGetter(downshiftGetItemProps, _object_spread$K({
        className: cn(getBlocksWith("__selectable_divider"), getBlocksWith(`__selectable_divider--${styleType}`))
    }, useMenuButtonAriaAttributes({
        role: "menuitem",
        "aria-disabled": false,
        "aria-selected": undefined
    })));
    const getIconProps = extendGetter(genericGetter, {
        className: cn(getBlocksWith("__icon"), getBlocksWith(`__icon--${styleType}`)),
        role: "presentation"
    });
    const dropdownProps = _object_spread_props$D(_object_spread$K({
        getBlocksWith,
        getToggleButtonProps,
        getItemProps,
        getDividerProps,
        getLabelProps,
        getSelectableDividerProps,
        getMenuProps,
        getIconProps,
        placeholder,
        variant,
        isLoading,
        isMultiselect,
        announceLoadingStart,
        announceLoadingEnd,
        loadingPlaceholder
    }, downshiftProps), {
        isOpen
    });
    return /*#__PURE__*/ jsx(DropdownContext.Provider, {
        value: dropdownProps,
        children: /*#__PURE__*/ jsx("div", {
            className: cn(getBlocksWith(), getBlocksWith(`--${styleType}`)),
            "data-auto": "dropdown",
            ref: ref,
            children: children
        })
    });
});
Dropdown.Context = DropdownContextComponent;
Dropdown.Label = DropdownLabel;
Dropdown.Toggle = DropdownToggle;
Dropdown.Icon = DropdownIcon;
Dropdown.Menu = DropdownMenu;
Dropdown.Item = DropdownItem;
Dropdown.displayName = "Dropdown";

const Error$1 = ({ children, className: consumerBlockClassName, title, description })=>{
    const getBemBlocksWith = createBemBlockBuilder$1([
        "eb-error",
        consumerBlockClassName
    ]);
    return /*#__PURE__*/ jsxs("div", {
        className: getBemBlocksWith(),
        children: [
            /*#__PURE__*/ jsx("div", {
                className: getBemBlocksWith("__icon"),
                children: /*#__PURE__*/ jsx(FontAwesomeIcon, {
                    icon: faFrown
                })
            }),
            /*#__PURE__*/ jsxs("div", {
                className: getBemBlocksWith("__message"),
                children: [
                    /*#__PURE__*/ jsxs("strong", {
                        className: getBemBlocksWith("__title"),
                        children: [
                            title,
                            " "
                        ]
                    }),
                    /*#__PURE__*/ jsxs("h2", {
                        className: getBemBlocksWith("__description"),
                        children: [
                            description,
                            " "
                        ]
                    }),
                    /*#__PURE__*/ jsx("p", {
                        className: getBemBlocksWith("__body"),
                        children: children
                    })
                ]
            })
        ]
    });
};

function _define_property$J(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$J(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$J(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$C(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$C(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$C(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$B(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$B(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$B(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const Hyperlink = /*#__PURE__*/ forwardRef((_param, ref)=>{
    var { children, onClick, showExternalLinkWarning = false, externalLinkWarningText = "(opens new window)", className = "", styleType = "underline" } = _param, props = _object_without_properties$B(_param, [
        "children",
        "onClick",
        "showExternalLinkWarning",
        "externalLinkWarningText",
        "className",
        "styleType"
    ]);
    return /*#__PURE__*/ jsxs("a", _object_spread_props$C(_object_spread$J({
        "data-auto": "eb-hyperlink",
        className: cn("eb-hyperlink", `eb-hyperlink--${styleType}`, className),
        rel: "noopener noreferrer",
        onClick: (event)=>onClick && onClick(event),
        ref: ref
    }, props), {
        children: [
            children,
            showExternalLinkWarning && `${" "}${externalLinkWarningText}`
        ]
    }));
});
Hyperlink.displayName = "Hyperlink";

var FILTERS_OFF = {
	elements: [
		{
			tag: "path",
			fill: "#3D3F42",
			d: "M4.7,6.5h14.6c0.4,0,0.7,0.3,0.7,0.7v1.6c0,0.4-0.3,0.7-0.7,0.7H4.7C4.3,9.5,4,9.2,4,8.8V7.2 C4,6.8,4.3,6.5,4.7,6.5z"
		},
		{
			tag: "path",
			fill: "#3D3F42",
			d: "M7.4,12.5h9.3c0.4,0,0.7,0.3,0.7,0.7v1.6c0,0.4-0.3,0.7-0.7,0.7H7.4 c-0.4,0-0.7-0.3-0.7-0.7v-1.6C6.7,12.8,7,12.5,7.4,12.5z"
		},
		{
			tag: "path",
			fill: "#3D3F42",
			d: "M10,18.5H14c0.4,0,0.7,0.3,0.7,0.7v1.6c0,0.4-0.3,0.7-0.7,0.7H10c-0.4,0-0.7-0.3-0.7-0.7v-1.6 C9.3,18.8,9.6,18.5,10,18.5z"
		}
	],
	"data-icon": "filters-off",
	width: 24,
	height: 24,
	viewBox: "0 0 24 24"
};
var FILTERS_ON = {
	elements: [
		{
			tag: "path",
			fill: "#3D3F42",
			d: "M4.7,6.5h14.6c0.4,0,0.7,0.3,0.7,0.7v1.6c0,0.4-0.3,0.7-0.7,0.7H4.7C4.3,9.5,4,9.2,4,8.8V7.2 C4,6.8,4.3,6.5,4.7,6.5z"
		},
		{
			tag: "path",
			fill: "#3D3F42",
			d: "M7.4,12.5h9.3c0.4,0,0.7,0.3,0.7,0.7v1.6c0,0.4-0.3,0.7-0.7,0.7H7.4 c-0.4,0-0.7-0.3-0.7-0.7v-1.6C6.7,12.8,7,12.5,7.4,12.5z"
		},
		{
			tag: "path",
			fill: "#3D3F42",
			d: "M10,18.5H14c0.4,0,0.7,0.3,0.7,0.7v1.6c0,0.4-0.3,0.7-0.7,0.7H10c-0.4,0-0.7-0.3-0.7-0.7v-1.6 C9.3,18.8,9.6,18.5,10,18.5z"
		},
		{
			tag: "circle",
			fill: "#3E75CF",
			stroke: "#fff",
			strokeWidth: "2",
			cx: "18",
			cy: "5.5",
			r: "5"
		}
	],
	"data-icon": "filters-on",
	width: 24,
	height: 24,
	viewBox: "0 0 24 24"
};
var DASHBOARD = {
	elements: [
		{
			tag: "path",
			fill: "#3D3F42",
			d: "m2,0l12,0c1.10457,0 2,0.89543 2,2l0,12c0,1.10457 -0.89543,2 -2,2l-12,0c-1.10457,0 -2,-0.89543 -2,-2l0,-12c0,-1.10457 0.89543,-2 2,-2zm9,9c-0.55228,0 -1,0.44772 -1,1l0,3c0,0.55228 0.44772,1 1,1l2,0c0.55228,0 1,-0.44772 1,-1l0,-3c0,-0.55228 -0.44772,-1 -1,-1l-2,0zm0,-7c-0.55228,0 -1,0.44772 -1,1l0,3c0,0.55228 0.44772,1 1,1l2,0c0.55228,0 1,-0.44772 1,-1l0,-3c0,-0.55228 -0.44772,-1 -1,-1l-2,0zm-8,0c-0.55228,0 -1,0.44772 -1,1l0,10c0,0.55228 0.44772,1 1,1l4,0c0.55228,0 1,-0.44772 1,-1l0,-10c0,-0.55228 -0.44772,-1 -1,-1l-4,0z"
		}
	],
	"data-icon": "dashboard",
	width: 16,
	height: 16,
	viewBox: "0 0 16 16"
};
var GOOGLE_CLASSROOM = {
	elements: [
		{
			tag: "path",
			fill: "#000000",
			d: "M 5 2 C 3.347656 2 2 3.347656 2 5 L 2 19 C 2 20.652344 3.347656 22 5 22 L 19 22 C 20.652344 22 22 20.652344 22 19 L 22 5 C 22 3.347656 20.652344 2 19 2 Z M 5 4 L 19 4 C 19.550781 4 20 4.449219 20 5 L 20 19 C 20 19.550781 19.550781 20 19 20 L 18 20 L 18 18 L 13 18 L 13 20 L 5 20 C 4.449219 20 4 19.550781 4 19 L 4 5 C 4 4.449219 4.449219 4 5 4 Z M 12 6 C 10.894531 6 10 6.894531 10 8 C 10 9.105469 10.894531 10 12 10 C 13.105469 10 14 9.105469 14 8 C 14 6.894531 13.105469 6 12 6 Z M 7.5 8 C 6.671875 8 6 8.671875 6 9.5 C 6 10.328125 6.671875 11 7.5 11 C 8.328125 11 9 10.328125 9 9.5 C 9 8.671875 8.328125 8 7.5 8 Z M 16.5 8 C 15.671875 8 15 8.671875 15 9.5 C 15 10.328125 15.671875 11 16.5 11 C 17.328125 11 18 10.328125 18 9.5 C 18 8.671875 17.328125 8 16.5 8 Z M 12 11 C 9.339844 11 8.5 13.5 8.5 13.5 C 8.5 12.652344 9 12 9 12 C 8.699219 11.8125 7.929688 11.6875 7.5625 11.6875 C 6.347656 11.6875 5 12.878906 5 13.6875 L 5 16 L 19 16 L 19 13.6875 C 19 12.878906 17.488281 11.6875 16.4375 11.6875 C 16.070313 11.6875 15.300781 11.8125 15 12 C 15 12 15.5 12.652344 15.5 13.5 C 15.5 13.5 14.660156 11 12 11 Z"
		}
	],
	"data-icon": "google-classroom",
	width: 24,
	height: 24,
	viewBox: "0 0 24 24"
};
var iconJSON = {
	FILTERS_OFF: FILTERS_OFF,
	FILTERS_ON: FILTERS_ON,
	DASHBOARD: DASHBOARD,
	GOOGLE_CLASSROOM: GOOGLE_CLASSROOM
};

function _define_property$I(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$I(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$I(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$B(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$B(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$B(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$A(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$A(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$A(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const Icon = (_param)=>{
    var { title, elements } = _param, rest = _object_without_properties$A(_param, [
        "title",
        "elements"
    ]);
    return /*#__PURE__*/ jsxs("svg", _object_spread_props$B(_object_spread$I({
        role: "img",
        focusable: "false",
        "aria-hidden": "true"
    }, rest), {
        children: [
            /*#__PURE__*/ jsx("title", {
                children: title
            }),
            elements.map(Icon.renderElement)
        ]
    }));
};
Icon.renderElement = (_param, index)=>{
    var { tag: TagName } = _param, rest = _object_without_properties$A(_param, [
        "tag"
    ]);
    return /*#__PURE__*/ jsx(TagName, _object_spread$I({}, rest), `${TagName}-${index}`);
};
Icon.ICONS = iconJSON;
Icon.propTypes = {
    elements: PropTypes.arrayOf(PropTypes.shape({
        tag: PropTypes.string.isRequired,
        d: PropTypes.string,
        fill: PropTypes.string
    })).isRequired,
    title: PropTypes.string,
    height: PropTypes.number,
    width: PropTypes.number,
    viewBox: PropTypes.string
};
Icon.defaultProps = {
    height: 24,
    width: 24,
    title: "",
    viewBox: "0 0 24 24"
};

const { useGenericContext: useInPageContext, GenericContextConsumer: InPageContextConsumer, GenericContextProvider: InPageContextProvider } = createGenericContext$1({
    errorMessage: "InPageNotification compound components cannot be rendered outside of the InPageNotification component"
});

function _define_property$H(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$H(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$H(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$A(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$A(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$A(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$z(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$z(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$z(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const InPageHeader = (_param)=>{
    var { children, className } = _param, rest = _object_without_properties$z(_param, [
        "children",
        "className"
    ]);
    const { getBlocksWith } = useInPageContext();
    return /*#__PURE__*/ jsx("h2", _object_spread_props$A(_object_spread$H({}, rest), {
        className: cn(className, getBlocksWith("__header")),
        children: children
    }));
};

function _define_property$G(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$G(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$G(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$z(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$z(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$z(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$y(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$y(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$y(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const InPageDescription = (_param)=>{
    var { children, className } = _param, rest = _object_without_properties$y(_param, [
        "children",
        "className"
    ]);
    const { getBlocksWith } = useInPageContext();
    return /*#__PURE__*/ jsx("p", _object_spread_props$z(_object_spread$G({}, rest), {
        className: cn(className, getBlocksWith("__description")),
        children: children
    }));
};

const ICONS$1 = {
    default: faExclamationCircle$1,
    error: faExclamationCircle$1,
    information: faExclamationCircle$1,
    success: faCheckCircle,
    warning: faExclamationTriangle
};
var VARIANT;
(function(VARIANT) {
    VARIANT["error"] = "error";
    VARIANT["information"] = "information";
    VARIANT["success"] = "success";
    VARIANT["warning"] = "warning";
})(VARIANT || (VARIANT = {}));
const InPageNotification = /*#__PURE__*/ forwardRef(({ ariaLabel = "", children = [], className = "", variant, icon, hideIcon = false }, ref)=>{
    const faIcon = icon || ICONS$1[variant] || ICONS$1.default;
    const getBlocksWith = createBemBlockBuilder$1([
        "eb-in-page-notification",
        className
    ]);
    const inPageContext = {
        getBlocksWith
    };
    return /*#__PURE__*/ jsx(InPageContextProvider, {
        value: inPageContext,
        children: /*#__PURE__*/ jsxs(Card, {
            ariaLabel: ariaLabel || variant,
            border: "all",
            className: cn(getBlocksWith(), getBlocksWith(`--${variant}`)),
            isRounded: true,
            isTransparent: false,
            ref: ref,
            children: [
                !hideIcon && /*#__PURE__*/ jsx(FontAwesomeIcon, {
                    "data-auto": "font-awesome-icon",
                    className: getBlocksWith("__icon"),
                    icon: faIcon
                }),
                /*#__PURE__*/ jsx("div", {
                    "data-auto": "content",
                    className: getBlocksWith("__content"),
                    children: children
                })
            ]
        })
    });
});
InPageNotification.displayName = "InPageNotification";
InPageNotification.ICONS = ICONS$1;
InPageNotification.VARIANT = VARIANT;
InPageNotification.Header = InPageHeader;
InPageNotification.Header.displayName = "InPageNotification.Header";
InPageNotification.Description = InPageDescription;
InPageNotification.Description.displayName = "InPageNotification.Description";

const { useGenericContext: useListboxContext, GenericContextConsumer: ListboxContextConsumer, GenericContextProvider: ListboxContextProvider } = createGenericContext$1({
    errorMessage: "Listbox compound components cannot be rendered outside of the Listbox component"
});

const ListboxLabel = ({ children })=>{
    const { getBemBlocksWith } = useListboxContext();
    return /*#__PURE__*/ jsx("div", {
        className: getBemBlocksWith("__label"),
        "data-auto": "listbox-label",
        children: children
    });
};

const keyCodes = {
    32: "SPACE_KEY",
    38: "UP_ARROW",
    40: "DOWN_ARROW",
    9: "TAB_KEY"
};
const keys = {
    DOWN_ARROW: "DOWN_ARROW",
    UP_ARROW: "UP_ARROW",
    SPACE_KEY: "SPACE_KEY",
    TAB_KEY: "TAB_KEY"
};

//To check whether the current element is a container for the list.
const isListContainer = (_id, currentElement)=>{
    var _currentElement_getAttribute;
    return currentElement === null || currentElement === void 0 ? void 0 : (_currentElement_getAttribute = currentElement.getAttribute("id")) === null || _currentElement_getAttribute === void 0 ? void 0 : _currentElement_getAttribute.includes("listbox-list");
};
// To focus the item
const focusItem = (item)=>{
    return item === null || item === void 0 ? void 0 : item.focus();
};
const activedescendant = "aria-activedescendant";
//To handle the keydown events for Listbox
const handleKeyDown = (event, id, item, isItemDisabled, selectOrUnselectItem)=>{
    event.stopPropagation();
    const key = keyCodes[event === null || event === void 0 ? void 0 : event.keyCode];
    const currentElement = event === null || event === void 0 ? void 0 : event.currentTarget;
    // eslint-disable-next-line default-case
    switch(key){
        case keys.DOWN_ARROW:
            const shouldFocusFirstItem = isListContainer(id, currentElement);
            //If the current element is a list container then focus the first item else focus the next item
            if (shouldFocusFirstItem) {
                var _firstItem_parentElement;
                const firstItem = currentElement === null || currentElement === void 0 ? void 0 : currentElement.firstChild;
                focusItem(firstItem);
                /*Setting "aria-activedescendant" property to parent element 
        because inorder to notify the current focused child element for the screen readers.*/ return firstItem === null || firstItem === void 0 ? void 0 : (_firstItem_parentElement = firstItem.parentElement) === null || _firstItem_parentElement === void 0 ? void 0 : _firstItem_parentElement.setAttribute(activedescendant, firstItem === null || firstItem === void 0 ? void 0 : firstItem.getAttribute("id"));
            } else {
                var _nextItem_parentElement;
                const nextItem = currentElement === null || currentElement === void 0 ? void 0 : currentElement.nextSibling;
                focusItem(nextItem);
                return nextItem === null || nextItem === void 0 ? void 0 : (_nextItem_parentElement = nextItem.parentElement) === null || _nextItem_parentElement === void 0 ? void 0 : _nextItem_parentElement.setAttribute(activedescendant, nextItem === null || nextItem === void 0 ? void 0 : nextItem.getAttribute("id"));
            }
        case keys.UP_ARROW:
            var _previousItem_parentElement;
            //To focus the previous item
            const previousItem = currentElement === null || currentElement === void 0 ? void 0 : currentElement.previousSibling;
            focusItem(previousItem);
            return previousItem === null || previousItem === void 0 ? void 0 : (_previousItem_parentElement = previousItem.parentElement) === null || _previousItem_parentElement === void 0 ? void 0 : _previousItem_parentElement.setAttribute(activedescendant, previousItem === null || previousItem === void 0 ? void 0 : previousItem.getAttribute("id"));
        case keys.SPACE_KEY:
            // If the list item is already selected then unselect it and vice versa..
            if (item && !isItemDisabled && selectOrUnselectItem) {
                selectOrUnselectItem(item);
            }
            event.preventDefault();
            break;
        case keys.TAB_KEY:
            const parentElement = currentElement === null || currentElement === void 0 ? void 0 : currentElement.parentElement;
            // To notify the screen reader that there is no active element inside the listbox
            return isListContainer(id, parentElement) && parentElement.setAttribute(activedescendant, "");
    }
};

const ListboxList = ({ children })=>{
    const { getBemBlocksWith, id = "" } = useListboxContext();
    return /*#__PURE__*/ jsx("div", {
        className: getBemBlocksWith("__list"),
        "data-auto": id,
        id: `listbox-list-${id}`,
        tabIndex: 0,
        "aria-multiselectable": true,
        role: "listbox",
        "aria-label": `listbox-list-${id}`,
        onKeyDown: (event)=>{
            handleKeyDown(event, id);
        },
        children: children
    });
};

const ListboxListItem = ({ item, disabled, handleSelection })=>{
    const { allowTwoColumns, firstColumnKey, secondColumnKey, getBemBlocksWith } = useListboxContext();
    return /*#__PURE__*/ jsx("div", {
        id: item[firstColumnKey],
        className: cn(getBemBlocksWith("__list-item"), `${disabled ? getBemBlocksWith("__list-item__disabled") : ""}`),
        tabIndex: -1,
        onClick: ()=>handleSelection(item),
        role: "option",
        "aria-selected": item.isSelected,
        "aria-disabled": disabled,
        onKeyDown: (event)=>{
            handleKeyDown(event, item[firstColumnKey], item, disabled, handleSelection);
        },
        children: /*#__PURE__*/ jsxs("div", {
            className: getBemBlocksWith("__checkbox-row"),
            children: [
                /*#__PURE__*/ jsx("div", {
                    className: getBemBlocksWith("__list-item__icon"),
                    children: item.isSelected && /*#__PURE__*/ jsx(FontAwesomeIcon, {
                        icon: faCheck$1,
                        size: "1x"
                    })
                }),
                /*#__PURE__*/ jsxs("div", {
                    className: getBemBlocksWith("__list-item__column__wrapper"),
                    "data-auto": "listbox-listitem-column-wrapper",
                    children: [
                        firstColumnKey && /*#__PURE__*/ jsx("div", {
                            className: getBemBlocksWith("__list-item__first-column"),
                            "data-auto": "listbox-listitem-first-column",
                            children: item[firstColumnKey]
                        }),
                        allowTwoColumns && secondColumnKey && /*#__PURE__*/ jsx("div", {
                            className: getBemBlocksWith("__list-item__second-column"),
                            "data-auto": "listbox-listitem-second-column",
                            children: item[secondColumnKey]
                        })
                    ]
                })
            ]
        })
    });
};

const ListboxEmpty = ({ children })=>{
    const { getBemBlocksWith } = useListboxContext();
    return /*#__PURE__*/ jsx("div", {
        tabIndex: 0,
        className: getBemBlocksWith("__empty"),
        "data-auto": "listbox-empty",
        children: children
    });
};

function _define_property$F(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$F(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$F(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$y(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$y(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$y(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
const Listbox = /*#__PURE__*/ forwardRef(({ allowTwoColumns = false, children, firstColumnKey, secondColumnKey = "", className = "", id = "" }, ref)=>{
    const getBemBlocksWith = createBemBlockBuilder$1([
        "eb-listbox",
        className
    ]);
    const listboxProps = {
        allowTwoColumns,
        children,
        firstColumnKey,
        secondColumnKey,
        id
    };
    const contextProviderValue = _object_spread_props$y(_object_spread$F({}, listboxProps), {
        getBemBlocksWith
    });
    return /*#__PURE__*/ jsx(ListboxContextProvider, {
        value: contextProviderValue,
        children: /*#__PURE__*/ jsx("div", {
            className: getBemBlocksWith(),
            "data-auto": "listbox",
            ref: ref,
            children: children
        })
    });
});
Listbox.Label = ListboxLabel;
Listbox.List = ListboxList;
Listbox.ListItem = ListboxListItem;
Listbox.Empty = ListboxEmpty;
Listbox.handleKeyDown = handleKeyDown;
Listbox.displayName = "Listbox";

function _define_property$E(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$E(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$E(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$x(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$x(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$x(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$x(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$x(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$x(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const Loading = /*#__PURE__*/ forwardRef((_param, ref)=>{
    var { className, direction = "vertical", loadingMessage, showMessage = true, size = "large" } = _param, rest = _object_without_properties$x(_param, [
        "className",
        "direction",
        "loadingMessage",
        "showMessage",
        "size"
    ]);
    const getBlocksWith = createBemBlockBuilder$1([
        "eb-loading",
        className
    ]);
    return /*#__PURE__*/ jsxs("div", _object_spread_props$x(_object_spread$E({
        className: cn(getBlocksWith(), getBlocksWith(`--${direction}`), getBlocksWith(`--${size}`)),
        role: "status",
        "aria-live": "polite",
        "aria-label": loadingMessage,
        ref: ref,
        tabIndex: -1
    }, rest), {
        children: [
            /*#__PURE__*/ jsx(IndeterminateSpinner, {
                className: getBlocksWith("__spinner"),
                level: size === "small" ? "component" : "page"
            }),
            showMessage && /*#__PURE__*/ jsx("p", {
                className: getBlocksWith("__message"),
                children: loadingMessage
            })
        ]
    }));
});
Loading.displayName = "Loading";

const MoreInfo = /*#__PURE__*/ forwardRef(({ label, className = "", collapseButtonContent, expandButtonContent, lineLimit = 7, children }, ref)=>{
    const [isButtonVisible, setIsButtonVisible] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const infoElementRef = useRef(null);
    const lineHeightElementRef = useRef(null);
    const infoElementRefCallback = useCallback((node)=>{
        if (ref) {
            if (typeof ref === "function") {
                ref(node);
            } else {
                ref.current = node;
            }
        }
        infoElementRef.current = node;
    }, [
        ref
    ]);
    const handleClick = ()=>{
        setIsExpanded(!isExpanded);
    };
    const lineHeight = lineHeightElementRef.current ? lineHeightElementRef.current.offsetHeight : 0;
    const truncatedHeight = lineLimit * lineHeight;
    const checkShouldShowButton = useCallback(()=>{
        const scrollheight = infoElementRef.current ? infoElementRef.current.scrollHeight : 0;
        const isTruncated = scrollheight > truncatedHeight + lineLimit;
        if (isTruncated && !isButtonVisible) {
            setIsButtonVisible(true);
            return;
        }
        if (!isTruncated && isButtonVisible) {
            setIsButtonVisible(false);
            setIsExpanded(false);
        }
    }, [
        infoElementRef,
        truncatedHeight,
        lineLimit,
        isButtonVisible
    ]);
    useEffect(()=>{
        checkShouldShowButton();
        window.addEventListener("resize", checkShouldShowButton);
        return ()=>window.removeEventListener("resize", checkShouldShowButton);
    }, [
        checkShouldShowButton
    ]);
    const getBlocksWith = createBemBlockBuilder$1([
        "eb-more-info",
        className
    ]);
    const infoClassNames = cn(getBlocksWith("__info"), getBlocksWith(`__info--${isExpanded ? "more" : "less"}`));
    const infoElementHeight = isExpanded || !isButtonVisible ? {
        maxHeight: "none"
    } : {
        maxHeight: `${truncatedHeight}px`
    };
    return /*#__PURE__*/ jsxs("div", {
        className: getBlocksWith(),
        "data-auto": "more-info",
        children: [
            label && /*#__PURE__*/ jsx("label", {
                className: getBlocksWith("__label"),
                children: label
            }),
            /*#__PURE__*/ jsxs("div", {
                className: getBlocksWith("__info-wrapper"),
                children: [
                    /*#__PURE__*/ jsxs("div", {
                        ref: infoElementRefCallback,
                        style: infoElementHeight,
                        className: infoClassNames,
                        tabIndex: isButtonVisible ? 0 : -1,
                        "aria-label": label,
                        children: [
                            children,
                            /*#__PURE__*/ jsx("div", {
                                "aria-hidden": "true",
                                ref: lineHeightElementRef,
                                className: getBlocksWith("__hidden-element-container"),
                                children: /*#__PURE__*/ jsx("span", {
                                    className: getBlocksWith("__hidden-element"),
                                    children: children
                                })
                            })
                        ]
                    }),
                    isButtonVisible && !isExpanded && /*#__PURE__*/ jsx("hr", {
                        className: getBlocksWith("__gradient"),
                        "data-auto": "separator",
                        "aria-hidden": "true"
                    }),
                    isButtonVisible && /*#__PURE__*/ jsx("div", {
                        className: getBlocksWith("__button-container"),
                        children: /*#__PURE__*/ jsx(Button$1, {
                            className: getBlocksWith("__button"),
                            tabIndex: 0,
                            onClick: handleClick,
                            styleType: "link",
                            children: isExpanded ? collapseButtonContent : expandButtonContent
                        })
                    })
                ]
            })
        ]
    });
});
MoreInfo.displayName = "MoreInfo";

const NativeDropdownOptions = ({ items, placeholder, className })=>/*#__PURE__*/ jsxs(Fragment, {
        children: [
            placeholder && /*#__PURE__*/ jsx("option", {
                hidden: true,
                value: "",
                className: className,
                children: placeholder
            }),
            items.map(({ id, value, label, lang })=>/*#__PURE__*/ jsx("option", {
                    value: value,
                    "data-auto": "eb-native-dropdown-option",
                    className: className,
                    lang: lang,
                    children: label
                }, id))
        ]
    });

const NativeDropdown = /*#__PURE__*/ forwardRef(({ items, id, label: { value: labelValue, display: displayLabel = true }, onChange, initialSelectedValue = "", placeholder, className: consumerClassName, styleType = "primary", onOpen, disabled = false, isLoading = false, loadingPlaceholder, announceLoadingStart, announceLoadingEnd }, ref)=>{
    var _filteredItems_find;
    const [renderLoadingComponent, setRenderLoadingComponent] = useState(false);
    const [selected, setSelected] = useState(initialSelectedValue);
    const getBlocksWith = createBemBlockBuilder$1([
        "eb-native-dropdown",
        consumerClassName
    ]);
    const filteredItems = items.filter((item)=>!item.isDivider);
    const selectedLabel = (_filteredItems_find = filteredItems.find(({ value })=>value === selected)) === null || _filteredItems_find === void 0 ? void 0 : _filteredItems_find.label.toString();
    useEffect(()=>{
        /* We don't want the loading component to be rendered
       * if the dropdown is not supposed to have a loading state.*/ if (isLoading && !renderLoadingComponent) {
            setRenderLoadingComponent(true);
        }
    }, [
        isLoading,
        renderLoadingComponent
    ]);
    const handleChange = ({ target: { value: selectedValue } })=>{
        const selectedOption = filteredItems.find(({ value })=>value === selectedValue);
        if (!selectedOption) {
            return;
        }
        onChange && onChange(selectedOption);
        setSelected(selectedValue);
    };
    const handleOnOpen = (event)=>{
        if (!onOpen) {
            return;
        }
        if (!event.key) {
            onOpen(event);
            return;
        }
        switch(event.key){
            case "Spacebar":
            case " ":
            case "Enter":
            case "ArrowUp":
            case "ArrowDown":
                onOpen(event);
        }
    };
    return /*#__PURE__*/ jsxs("div", {
        className: cn(getBlocksWith(), getBlocksWith(`--${styleType}`), disabled && getBlocksWith("--disabled")),
        children: [
            /*#__PURE__*/ jsx("label", {
                className: cn(getBlocksWith("__label"), getBlocksWith(`__label--${styleType}`), !displayLabel && getBlocksWith("__label--hidden")),
                htmlFor: id,
                children: labelValue
            }),
            /*#__PURE__*/ jsxs("div", {
                className: cn(getBlocksWith("__menu-wrapper"), getBlocksWith(`__menu-wrapper--${styleType}`)),
                "data-auto": "eb-menu-wrapper",
                children: [
                    renderLoadingComponent && /*#__PURE__*/ jsx("div", {
                        className: cn(getBlocksWith("__loader"), isLoading && getBlocksWith("__loader--loading")),
                        children: /*#__PURE__*/ jsx(LoadingButton, {
                            tabIndex: -1,
                            className: getBlocksWith("__loader-button"),
                            styleType: styleType,
                            loadingPlaceholder: loadingPlaceholder || selectedLabel || placeholder,
                            announceLoadingStart: announceLoadingStart,
                            announceLoadingEnd: announceLoadingEnd,
                            isLoading: isLoading,
                            disabled: disabled,
                            children: loadingPlaceholder || selectedLabel || placeholder
                        })
                    }),
                    /*#__PURE__*/ jsx("select", {
                        ref: ref,
                        className: cn(getBlocksWith("__menu"), getBlocksWith(`__menu--${styleType}`), isLoading && getBlocksWith("__menu--loading")),
                        id: id,
                        value: selected,
                        onChange: handleChange,
                        onClick: handleOnOpen,
                        onKeyDown: handleOnOpen,
                        "data-auto": "eb-native-dropdown",
                        disabled: isLoading || disabled,
                        children: /*#__PURE__*/ jsx(NativeDropdownOptions, {
                            items: filteredItems,
                            placeholder: placeholder,
                            className: cn(getBlocksWith("__option"), getBlocksWith(`__option--${styleType}`))
                        })
                    })
                ]
            })
        ]
    });
});
NativeDropdown.displayName = "NativeDropdown";

const PageDivider = ({ isVisuallyHidden, pageNumber, title, uniqueName })=>{
    const id = getPageID(uniqueName, pageNumber);
    return /*#__PURE__*/ jsxs(Fragment, {
        children: [
            /*#__PURE__*/ jsx("div", {
                className: "screen-reader-text",
                children: title
            }),
            !isVisuallyHidden && /*#__PURE__*/ jsxs("span", {
                className: "page-divider__wrapper",
                id: id,
                tabIndex: -1,
                "aria-hidden": true,
                "data-auto": "page-divider",
                children: [
                    /*#__PURE__*/ jsx("span", {
                        className: "page-divider__title",
                        title: title,
                        children: pageNumber
                    }),
                    /*#__PURE__*/ jsx("hr", {
                        className: "page-divider"
                    })
                ]
            })
        ]
    });
};
const getPageID = (uniqueName, pageNumber)=>`${uniqueName}-page-${pageNumber}`;

const Page = ({ children, number, title, uniqueName })=>{
    const isFirstPage = number === 0;
    return /*#__PURE__*/ jsxs(Fragment, {
        children: [
            /*#__PURE__*/ jsx(PageDivider, {
                isVisuallyHidden: isFirstPage,
                pageNumber: number + 1,
                title: title,
                uniqueName: uniqueName
            }),
            children
        ]
    });
};
Page.getPageID = getPageID;

const DefaultImage = ()=>{
    return /*#__PURE__*/ jsxs("svg", {
        viewBox: "0 0 122 76",
        width: "122px",
        height: "76px",
        "data-auto": "eb-page-error-message__default-image",
        children: [
            /*#__PURE__*/ jsx("path", {
                id: "case",
                fillRule: "evenodd",
                clipRule: "evenodd",
                fill: "#002F56",
                d: "M116.6,36L116.6,36L116.6,36c-0.1-0.1-0.2-0.2-0.3-0.3  L92.8,5.3C90.2,1.9,86.3,0,82.1,0c-8,0-14.2,6.8-13.5,14.8l0,0.2H52.8l0-0.1l0-0.1c0-0.4,0.1-0.8,0.1-1.3c0-0.8-0.1-1.6-0.2-2.4  C51.6,4.9,46.1,0,39.4,0c-4.2,0-8.1,1.9-10.7,5.3l-4.6,5.9l-18.6,24c0,0,0,0,0,0c0,0,0,0,0,0l-0.5,0.7c0,0,0,0,0,0c0,0,0,0,0,0l0,0  h0C2,39.8,0.3,44.4,0,49.5c0,0.4,0,0.8,0,1.2C0,63,8.9,73.2,20.6,75.1c1.3,0.2,2.7,0.3,4.2,0.3c13.7,0,24.8-11.1,24.8-24.8  c0,0,0,0,0,0l0.8-8.4v0h20.7v0l0.8,8.4l0,0c0,13.7,11.1,24.8,24.8,24.8c13.7,0,24.8-11.1,24.8-24.8C121.4,45.2,119.6,40.1,116.6,36z  "
            }),
            /*#__PURE__*/ jsx("polygon", {
                id: "case-bar",
                fillRule: "evenodd",
                clipRule: "evenodd",
                fill: "#002341",
                points: "50.8,37.3 70.5,37.3 68.6,14.9 52.8,14.9"
            }),
            /*#__PURE__*/ jsxs("g", {
                id: "lensRight",
                transform: "translate(84.000000, 30.000000)",
                children: [
                    /*#__PURE__*/ jsx("path", {
                        id: "glassRight",
                        fillRule: "evenodd",
                        clipRule: "evenodd",
                        fill: "#D8E3F5",
                        d: "M33.9,20.7c0,11.8-9.5,21.3-21.3,21.3   S-8.7,32.5-8.7,20.7S0.9-0.6,12.6-0.6S33.9,8.9,33.9,20.7"
                    }),
                    /*#__PURE__*/ jsx("polygon", {
                        id: "reflectRightLg",
                        fillRule: "evenodd",
                        clipRule: "evenodd",
                        fill: "#FFFFFF",
                        points: "26.1,39.4 -5.7,7.6 -0.5,2.4   31.3,34.2"
                    }),
                    /*#__PURE__*/ jsx("polygon", {
                        id: "reflectRightSm",
                        fillRule: "evenodd",
                        clipRule: "evenodd",
                        fill: "#FFFFFF",
                        points: "32.8,32.7 1,0.9 2.1,-0.2 33.9,31.6    "
                    }),
                    /*#__PURE__*/ jsx("path", {
                        id: "ringRight",
                        fillRule: "evenodd",
                        clipRule: "evenodd",
                        fill: "#A7AAAF",
                        d: "M28.9,29.8c-0.2,0.4-0.5,0.9-0.8,1.3   c-1.4,2.1-3.2,3.8-5.2,5.2c-3,2-6.5,3.1-10.3,3.1C2.3,39.4-6,31-6,20.7c0-0.7,0-1.4,0.1-2c0.3-3,1.4-5.8,3-8.3   c1.4-2.1,3.1-3.8,5.2-5.2c0.6-0.4,1.3-0.8,1.9-1.1c0.5-0.3,1-0.5,1.5-0.7c0.5-0.2,1-0.4,1.6-0.5C9,2.3,10.8,2,12.6,2   c5.4,0,10.3,2.3,13.7,6c3.1,3.3,5,7.8,5,12.7c0,2.5-0.5,4.8-1.3,6.9c-0.2,0.5-0.4,1-0.7,1.5C29.2,29.4,29.1,29.6,28.9,29.8 M32.6,6   c-0.2-0.3-0.4-0.6-0.6-0.8c-4.5-5.6-11.5-9.2-19.3-9.2c-3.3,0-6.5,0.7-9.4,1.9c-9,3.7-15.4,12.6-15.4,22.9   c0,13.7,11.1,24.8,24.8,24.8c8.4,0,15.9-4.2,20.3-10.6c2.8-4,4.4-8.9,4.4-14.2C37.4,15.2,35.6,10.1,32.6,6"
                    })
                ]
            }),
            /*#__PURE__*/ jsxs("g", {
                id: "lensLeft",
                transform: "translate(11.000000, 30.000000)",
                children: [
                    /*#__PURE__*/ jsx("path", {
                        id: "glassLeft",
                        fillRule: "evenodd",
                        clipRule: "evenodd",
                        fill: "#D8E3F5",
                        d: "M35.1,20.7c0,11.8-9.5,21.3-21.3,21.3   S-7.5,32.5-7.5,20.7S2-0.6,13.8-0.6S35.1,8.9,35.1,20.7"
                    }),
                    /*#__PURE__*/ jsx("polygon", {
                        id: "reflectLeftLg",
                        fillRule: "evenodd",
                        clipRule: "evenodd",
                        fill: "#FFFFFF",
                        points: "25.6,39.4 -6.3,7.6 -1.1,2.4   30.7,34.2  "
                    }),
                    /*#__PURE__*/ jsx("polygon", {
                        id: "reflectLeftSm",
                        fillRule: "evenodd",
                        clipRule: "evenodd",
                        fill: "#FFFFFF",
                        points: "32.3,32.7 0.4,0.9 1.6,-0.2   33.4,31.6  "
                    }),
                    /*#__PURE__*/ jsx("path", {
                        id: "ringLeft",
                        fillRule: "evenodd",
                        clipRule: "evenodd",
                        fill: "#A7AAAF",
                        d: "M29.8,30.3c-0.4,0.6-0.8,1.2-1.2,1.8   c-1.5,2-3.4,3.6-5.6,4.8c-2.7,1.6-5.9,2.5-9.3,2.5c-5,0-9.6-2-13-5.2c-1.1-1.1-2.1-2.3-3-3.7c-1.7-2.8-2.7-6.2-2.7-9.7   c0-3.4,0.9-6.5,2.5-9.3c0.7-1.2,1.6-2.4,2.5-3.4c0.7-0.8,1.5-1.5,2.3-2.1C3,5.5,3.6,5,4.2,4.7c0.5-0.3,1-0.5,1.5-0.8   C8.1,2.7,10.9,2,13.8,2c9.6,0,17.6,7.3,18.6,16.6c0.1,0.7,0.1,1.4,0.1,2c0,2.9-0.7,5.7-1.8,8.1C30.4,29.3,30.1,29.8,29.8,30.3   M13.8-4.1C6-4.1-1-0.5-5.5,5.2c0,0,0,0,0,0l-0.5,0.7c0,0,0,0,0,0c0,0,0,0,0,0h0C-9,9.8-10.7,14.4-11,19.5c0,0.4,0,0.8,0,1.2   C-11,33-2.1,43.2,9.6,45.1c1.3,0.2,2.7,0.3,4.1,0.3c13.7,0,24.8-11.1,24.8-24.8C38.6,7,27.5-4.1,13.8-4.1"
                    })
                ]
            })
        ]
    });
};

const IMAGE_PLACEMENT = {
    blockStart: "block-start",
    blockEnd: "block-end"
};
const DEFAULT_TITLE_TAG = "h2";
const BASE_CLASS_NAME$5 = "eb-page-error-message";
const PageErrorMessage = (props)=>{
    const { titleTag, buttonIcon = faRedo, buttonText = "", className: consumerBaseClassName, image = null, imagePlacement = IMAGE_PLACEMENT.blockStart, onClick = null, subTitle = "", text = "", title } = props;
    // We need unique IDs to support screen readers in rare case
    // of having multiple PageErrorMessages on the same page
    const uniquePart = v4();
    const titleId = `title-${uniquePart}`;
    const subTitleId = `sub-title-${uniquePart}`;
    const textId = `text-${uniquePart}`;
    const TitleTag = titleTag || DEFAULT_TITLE_TAG;
    const getBEMBlocksWith = createBemBlockBuilder$1([
        BASE_CLASS_NAME$5,
        consumerBaseClassName
    ]);
    const renderImage = (position)=>{
        return /*#__PURE__*/ jsx("div", {
            className: cn(getBEMBlocksWith("__image"), getBEMBlocksWith(`__image--${position}`)),
            "aria-hidden": "true",
            children: image || /*#__PURE__*/ jsx(DefaultImage, {})
        });
    };
    return /*#__PURE__*/ jsxs("div", {
        role: "alert",
        "aria-labelledby": `${titleId} ${subTitleId}`,
        "aria-describedby": textId,
        className: getBEMBlocksWith(),
        children: [
            imagePlacement === IMAGE_PLACEMENT.blockStart && renderImage(imagePlacement),
            /*#__PURE__*/ jsx(TitleTag, {
                id: titleId,
                className: getBEMBlocksWith("__title"),
                children: title
            }),
            subTitle && /*#__PURE__*/ jsx("p", {
                id: subTitleId,
                className: getBEMBlocksWith("__sub-title"),
                children: subTitle
            }),
            text && /*#__PURE__*/ jsx("p", {
                id: textId,
                className: getBEMBlocksWith("__text"),
                children: text
            }),
            buttonText && onClick && /*#__PURE__*/ jsxs(Button$1, {
                className: getBEMBlocksWith("__button"),
                onClick: onClick,
                children: [
                    buttonIcon && /*#__PURE__*/ jsx(FontAwesomeIcon, {
                        icon: buttonIcon,
                        className: getBEMBlocksWith("__button-icon"),
                        "aria-hidden": "true"
                    }),
                    buttonText
                ]
            }),
            imagePlacement === IMAGE_PLACEMENT.blockEnd && renderImage(imagePlacement)
        ]
    });
};

function _define_property$D(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$D(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$D(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$w(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$w(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$w(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$w(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$w(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$w(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const BASE_CLASS_NAME$4 = "adornment";
const Adornment = (_param)=>{
    var { className: consumerClassName = "", gutter = "small", placement, children } = _param, rest = _object_without_properties$w(_param, [
        "className",
        "gutter",
        "placement",
        "children"
    ]);
    return /*#__PURE__*/ jsx("span", _object_spread_props$w(_object_spread$D({}, rest), {
        className: cn(consumerClassName, BASE_CLASS_NAME$4, `${BASE_CLASS_NAME$4}--${placement}`, `${BASE_CLASS_NAME$4}--${gutter}`),
        children: children
    }));
};
Adornment.displayName = "Adornment";

const EYE_BUTTON_ICONS = {
    eye: faEye,
    eyeSlash: faEyeSlash
};

function _define_property$C(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$C(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$C(target, key, source[key]);
        });
    }
    return target;
}
function _object_without_properties$v(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$v(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$v(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const baseClassName$2 = "eb-password-input";
const PasswordInput = /*#__PURE__*/ forwardRef((_param, ref)=>{
    var { id, className, label, error, disabled, hidePasswordButtonText, showPasswordButtonText, descriptionId } = _param, rest = _object_without_properties$v(_param, [
        "id",
        "className",
        "label",
        "error",
        "disabled",
        "hidePasswordButtonText",
        "showPasswordButtonText",
        "descriptionId"
    ]);
    const [isHidden, setIsHidden] = useState(true);
    const toggleHidden = (e)=>{
        e.stopPropagation();
        setIsHidden(!isHidden);
    };
    const getBlocksWith = createBemBlockBuilder$1([
        className,
        baseClassName$2
    ]);
    const buttonMessage = isHidden ? `${hidePasswordButtonText}` : `${showPasswordButtonText}`;
    const hideIcon = EYE_BUTTON_ICONS["eyeSlash"];
    const showIcon = EYE_BUTTON_ICONS["eye"];
    return /*#__PURE__*/ jsx(TextControl, {
        className: getBlocksWith(),
        label: label,
        error: error,
        children: /*#__PURE__*/ jsxs("div", {
            className: getBlocksWith("__password"),
            id: id,
            children: [
                /*#__PURE__*/ jsx("span", {
                    className: getBlocksWith("__input"),
                    children: /*#__PURE__*/ jsx("input", _object_spread$C({
                        type: isHidden ? "password" : "text",
                        name: id,
                        disabled: disabled,
                        className: getBlocksWith("__input__box"),
                        "aria-invalid": Boolean(error),
                        "aria-describedby": cn(error && `${id}-error`, descriptionId),
                        "aria-label": error ? cn(error && `${id}-error`, descriptionId) : descriptionId !== null && descriptionId !== void 0 ? descriptionId : id,
                        ref: ref
                    }, rest))
                }),
                /*#__PURE__*/ jsx("span", {
                    className: getBlocksWith("__icon__box"),
                    children: /*#__PURE__*/ jsx(Adornment, {
                        placement: "end",
                        className: getBlocksWith("__adornment__box"),
                        gutter: "x-small",
                        children: /*#__PURE__*/ jsx(Button$1, {
                            "aria-label": buttonMessage,
                            onClick: (e)=>toggleHidden(e),
                            styleType: "flat-icon",
                            className: getBlocksWith("__button_icon"),
                            children: /*#__PURE__*/ jsx(FontAwesomeIcon, {
                                className: getBlocksWith("__icon"),
                                icon: isHidden ? showIcon : hideIcon
                            })
                        })
                    })
                })
            ]
        })
    });
});
PasswordInput.displayName = "PasswordInput";

/**
 * Helper function to wrap blur handler. It allows us to detect when the wrapper and children lose focus (deep blur)
 */ const createBlurHandler = (ref, callback)=>(event)=>{
        const newTargetElement = event.relatedTarget || document.activeElement;
        // IE-specific check, please refer to https://github.com/facebook/react/issues/3751
        if (ref.current && !ref.current.contains(newTargetElement)) {
            callback(event);
        }
    };

/**
 * Returns back a NodeList of focusable elements
 * that exist within the passed parent HTMLElement or doucment.body, or
 * an empty array if no parent passed.
 *
 * @param {HTMLElement} parent HTML element, doucument.body by default
 * @returns {(NodeList|Array)} The focusable elements that we can find
 */ const getAllFocusableElements = (parent = document.body)=>parent.querySelectorAll('button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled]), details:not([disabled]), summary:not(:disabled)');

const { useGenericContext: usePopoverContext, GenericContextConsumer: PopoverContextConsumer, GenericContextProvider: PopoverContextProvider } = createGenericContext$1({
    errorMessage: "Popover compound components cannot be rendered outside the Popover component"
});

const usePopoverInteraction = (panelRef, popoverSelector)=>{
    const getAvailablePopovers = ()=>Array.from(document.querySelectorAll(popoverSelector));
    /**
   * @param {Node} relatedTarget
   * @return {{withinCurrentPopoverRef: boolean, withinAnotherPopover: boolean, outsideOfPopovers: boolean}|undefined}
   */ const getUserInteraction = useCallback((relatedTarget)=>{
        if (!(panelRef === null || panelRef === void 0 ? void 0 : panelRef.current) || !popoverSelector) {
            return;
        }
        let withinCurrentPopoverRef = false, withinAnotherPopover = false;
        for (const popover of getAvailablePopovers()){
            if (!popover.contains(relatedTarget)) {
                continue;
            }
            if (popover.contains(panelRef.current)) {
                withinCurrentPopoverRef = true;
                break;
            }
            withinAnotherPopover = true;
            break;
        }
        return {
            withinCurrentPopoverRef,
            withinAnotherPopover,
            outsideOfPopovers: !withinCurrentPopoverRef && !withinAnotherPopover
        };
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [
        panelRef
    ]);
    return {
        getUserInteraction
    };
};

function _define_property$B(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$B(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$B(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$v(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$v(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$v(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$u(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$u(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$u(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const PopoverButton = (_param)=>{
    var { children, className } = _param, rest = _object_without_properties$u(_param, [
        "children",
        "className"
    ]);
    const { getBlocksWith, handleOnClick, isOpen, uniqueName, buttonRef } = usePopoverContext();
    return /*#__PURE__*/ jsx(Button$1, _object_spread_props$v(_object_spread$B({
        ref: buttonRef,
        onMouseDown: (ev)=>{
            ev.preventDefault();
            ev.target.focus();
        },
        className: cn(className, getBlocksWith("__button"), isOpen && getBlocksWith("__button--open")),
        "aria-haspopup": true,
        "aria-controls": `${uniqueName}-panel`,
        "aria-expanded": isOpen ? true : false,
        onClick: handleOnClick
    }, rest), {
        children: children
    }));
};

function _define_property$A(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$A(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$A(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$u(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$u(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$u(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$t(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$t(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$t(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const PopoverPanel = (_param)=>{
    var { children, className } = _param, rest = _object_without_properties$t(_param, [
        "children",
        "className"
    ]);
    const { getBlocksWith, isOpen, panelPlacement, uniqueName, panelRef, handleOnPanelBlur } = usePopoverContext();
    return /*#__PURE__*/ jsx("div", _object_spread_props$u(_object_spread$A({
        className: cn(className, getBlocksWith("__panel"), getBlocksWith(`__panel--${panelPlacement}`), isOpen && getBlocksWith("__panel--open")),
        id: `${uniqueName}-panel`,
        ref: panelRef,
        onBlur: handleOnPanelBlur
    }, isOpen && {
        tabIndex: 0
    }, rest), {
        children: isOpen && children
    }));
};

function _define_property$z(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$z(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$z(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$t(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$t(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$t(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$s(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$s(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$s(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const baseClassName$1 = "eb-popover";
const Popover = /*#__PURE__*/ forwardRef((_param, ref)=>{
    var { children, className: consumerClassName, panelPlacement = "top-end", onToggle, uniqueName, elementToFocus = "panel" } = _param, rest = _object_without_properties$s(_param, [
        "children",
        "className",
        "panelPlacement",
        "onToggle",
        "uniqueName",
        "elementToFocus"
    ]);
    const [isOpen, setIsOpen] = useState(false);
    const getBlocksWith = createBemBlockBuilder$1([
        baseClassName$1,
        consumerClassName
    ]);
    const isButtonClickableRef = useRef(true);
    const panelRef = useRef(null);
    const buttonRef = useRef(null);
    const shouldSetFocusButtonOnClose = useRef(false);
    const { getUserInteraction } = usePopoverInteraction(panelRef, `.${baseClassName$1}`);
    const togglePopover = useCallback(()=>{
        onToggle && onToggle(!isOpen);
        setIsOpen(!isOpen);
    }, [
        isOpen,
        onToggle
    ]);
    useEffect(()=>{
        if (isOpen && panelRef.current) {
            if (elementToFocus === "panel") {
                panelRef.current.focus();
            }
            if (elementToFocus === "first-focusable") {
                const firstFocusableElement = getAllFocusableElements(panelRef.current)[0];
                firstFocusableElement.focus();
            }
            return;
        }
        if (shouldSetFocusButtonOnClose.current && buttonRef.current) {
            buttonRef.current.focus();
        }
        shouldSetFocusButtonOnClose.current = true;
        setTimeout(()=>{
            isButtonClickableRef.current = true;
        }, 200);
    }, [
        elementToFocus,
        isOpen
    ]);
    const handleOnClick = ()=>{
        // ignore click when button was closed by panel blur event
        if (isButtonClickableRef.current) {
            togglePopover();
        }
    };
    const handleOnKeyPress = (event)=>{
        if (isOpen && event.key === "Escape") {
            event.preventDefault();
            togglePopover();
        }
    };
    const handleOnOutsideInteractions = useCallback((event)=>{
        if (!isOpen) {
            return;
        }
        if (event.relatedTarget === buttonRef.current) {
            isButtonClickableRef.current = false;
        }
        togglePopover();
        const userInteraction = getUserInteraction(event.relatedTarget);
        var _ref;
        // When the blur event is firing with a relatedTarget, the user has interacted with
        // either the current panelRef, another known popover, or an element outside any known popovers.
        shouldSetFocusButtonOnClose.current = (_ref = !(userInteraction === null || userInteraction === void 0 ? void 0 : userInteraction.withinAnotherPopover)) !== null && _ref !== void 0 ? _ref : false;
    }, [
        getUserInteraction,
        isOpen,
        togglePopover
    ]);
    const handleOnPanelBlur = createBlurHandler(panelRef, handleOnOutsideInteractions);
    const closePopover = ()=>{
        setIsOpen(false);
    };
    const popoverProps = {
        handleOnClick,
        getBlocksWith,
        isOpen,
        panelPlacement,
        panelRef,
        buttonRef,
        handleOnPanelBlur,
        uniqueName,
        closePopover
    };
    return /*#__PURE__*/ jsx(PopoverContextProvider, {
        value: popoverProps,
        children: /*#__PURE__*/ jsx("div", _object_spread_props$t(_object_spread$z({
            ref: ref,
            className: getBlocksWith(),
            onKeyDown: handleOnKeyPress
        }, rest), {
            children: children
        }))
    });
});
Popover.Button = PopoverButton;
Popover.Button.displayName = "Popover.Button";
Popover.Panel = PopoverPanel;
Popover.Panel.displayName = "Popover.Panel";
Popover.ContextConsumer = PopoverContextConsumer;
Popover.ContextConsumer.displayName = "Popover.ContextConsumer";
Popover.useContext = usePopoverContext;
Popover.displayName = "Popover";

const { useGenericContext: useRearrangeableMultiSelectContext, GenericContextConsumer: RearrangeableMultiSelectContextConsumer, GenericContextProvider: RearrangeableMultiSelectContextProvider } = createGenericContext$1({
    errorMessage: "RearrangeableMultiSelect compound components cannot be rendered outside of the RearrangeableMultiSelect component"
});

const MultiSelectBox = ({ children })=>{
    const { getBemBlocksWith } = useRearrangeableMultiSelectContext();
    return /*#__PURE__*/ jsx("div", {
        className: getBemBlocksWith("__box"),
        "data-auto": "rearrangeable-multi-select-box",
        children: children
    });
};

const MultiSelectBoxButtons = ({ children })=>{
    const { getBemBlocksWith } = useRearrangeableMultiSelectContext();
    return /*#__PURE__*/ jsx("div", {
        className: getBemBlocksWith("__box-buttons"),
        "data-auto": "rearrangeable-multi-select-box-buttons",
        children: children
    });
};

const handleSort = (itemA, itemB, sortKey)=>{
    if (itemA[sortKey].toLowerCase() < itemB[sortKey].toLowerCase()) {
        return -1;
    } else {
        return 1;
    }
};
const useMultiSelect = (lItems, rItems, sort = false, sortKey = "column1")=>{
    const [leftItems, setLeftitems] = useState(sort ? lItems.sort((itemA, itemB)=>handleSort(itemA, itemB, sortKey)) : lItems);
    const [rightItems, setRightitems] = useState(sort ? rItems.sort((itemA, itemB)=>handleSort(itemA, itemB, sortKey)) : rItems);
    const handleSelect = (item)=>{
        if (item && !(item === null || item === void 0 ? void 0 : item.isDisabled)) {
            item.isSelected = !item.isSelected;
            setLeftitems([
                ...leftItems
            ]);
            setRightitems([
                ...rightItems
            ]);
        }
    };
    const moveSelectedRight = ()=>{
        let selectedLeftItems = leftItems.filter((item)=>item.isSelected);
        const remainingLeftItems = leftItems.filter((item)=>!item.isSelected);
        selectedLeftItems = selectedLeftItems.map((item)=>{
            item.isSelected = false;
            return item;
        });
        const newRightItems = sort ? [
            ...rightItems,
            ...selectedLeftItems
        ].sort((itemA, itemB)=>handleSort(itemA, itemB, sortKey)) : [
            ...rightItems,
            ...selectedLeftItems
        ];
        setRightitems([
            ...newRightItems
        ]);
        setLeftitems([
            ...remainingLeftItems
        ]);
    };
    const moveSelectedLeft = ()=>{
        let selectedRightItems = rightItems.filter((item)=>item.isSelected);
        const remainingRightItems = rightItems.filter((item)=>!item.isSelected);
        selectedRightItems = selectedRightItems.map((item)=>{
            item.isSelected = false;
            return item;
        });
        const newLeftItems = sort ? [
            ...leftItems,
            ...selectedRightItems
        ].sort((itemA, itemB)=>handleSort(itemA, itemB, sortKey)) : [
            ...leftItems,
            ...selectedRightItems
        ];
        setLeftitems([
            ...newLeftItems
        ]);
        setRightitems([
            ...remainingRightItems
        ]);
    };
    const moveAllRight = ()=>{
        const activeLeftItems = leftItems.filter((item)=>!item.isDisabled);
        const disabledLeftItems = leftItems.filter((item)=>item.isDisabled);
        let newRightItems = sort ? [
            ...rightItems,
            ...activeLeftItems
        ].sort((itemA, itemB)=>handleSort(itemA, itemB, sortKey)) : [
            ...rightItems,
            ...activeLeftItems
        ];
        newRightItems = newRightItems.map((item)=>{
            item.isSelected = false;
            return item;
        });
        setRightitems([
            ...newRightItems
        ]);
        setLeftitems([
            ...disabledLeftItems
        ]);
    };
    const moveAllLeft = ()=>{
        const activeRightItems = rightItems.filter((item)=>!item.isDisabled);
        const disabledRightItems = rightItems.filter((item)=>item.isDisabled);
        let newLeftItems = sort ? [
            ...leftItems,
            ...activeRightItems
        ].sort((itemA, itemB)=>handleSort(itemA, itemB, sortKey)) : [
            ...leftItems,
            ...activeRightItems
        ];
        newLeftItems = newLeftItems.map((item)=>{
            item.isSelected = false;
            return item;
        });
        setLeftitems([
            ...newLeftItems
        ]);
        setRightitems([
            ...disabledRightItems
        ]);
    };
    return {
        leftItems,
        rightItems,
        handleSelect,
        moveAllRight,
        moveAllLeft,
        moveSelectedRight,
        moveSelectedLeft
    };
};

const RearrangeableMultiSelect = /*#__PURE__*/ forwardRef(({ className = "", children }, ref)=>{
    const getBemBlocksWith = createBemBlockBuilder$1([
        "eb-rearrangeable-multi-select",
        className
    ]);
    const contextProviderValue = {
        getBemBlocksWith
    };
    return /*#__PURE__*/ jsx(RearrangeableMultiSelectContextProvider, {
        value: contextProviderValue,
        children: /*#__PURE__*/ jsx("div", {
            className: getBemBlocksWith(),
            "data-auto": "rearrangeable-multi-select",
            ref: ref,
            children: children
        })
    });
});
RearrangeableMultiSelect.MultiSelectBox = MultiSelectBox;
RearrangeableMultiSelect.MultiSelectBoxButtons = MultiSelectBoxButtons;
RearrangeableMultiSelect.useMultiSelect = useMultiSelect;

const scrollEmitter = mitt();
const SCROLL_EVENT = "scroll";
const SCROLL_TO_EVENT = "scrollTo";
/**
 * eventManager - A manager for toggling an event on and off.
 * @see https://github.com/developit/mitt for more emitter API details.
 *
 * @example
 * // Define an event handler.
 * const onScroll = eventManager("scroll");
 *
 * // Pass the event handler a callback to handle the expected data stream.
 * // The event handler, when invoked, returns an optional handler to remove the event.
 * // Typically the event removal should happen in componentWillUnmount.
 * const scrollDepth = ({scrollDepth}) => console.log(`Current scroll depth - ${scrollDepth}`));
 * const removeOnScrollListener = onScroll(scrollDepth);
 *
 * // Cleanup the event listener when is it no longer needed.
 * removerOnScrollListener();
 */ const eventManager = (event)=>(cb)=>{
        scrollEmitter.on(event, cb);
        return ()=>scrollEmitter.off(event, cb);
    };
/**
 * emitOnScroll - publishes the scrolling event for subscribers to listen to.
 */ const emitOnScroll = (scrollDepth)=>{
    scrollEmitter.emit(SCROLL_EVENT, {
        scrollDepth
    });
};
/**
 * onScroll - subscribes to the scroll event.
 */ const onScroll = eventManager(SCROLL_EVENT);
/**
 * emitOnScrollTo - publishes the scrollTo event for subscribers to listen to.
 */ const emitScrollTo = (targetPosition, scrollRate)=>{
    scrollEmitter.emit(SCROLL_TO_EVENT, {
        targetPosition,
        scrollRate
    });
};
/**
 * onScrollTo - subscribes to the scrollTo event.
 */ const onScrollTo = eventManager(SCROLL_TO_EVENT);
/**
 * useScrollTo hook.
 */ const useOnScroll = ()=>{
    const [depth, setDepth] = useState(0);
    useEffect(()=>{
        const removeScrollListener = onScroll(({ scrollDepth })=>setDepth(scrollDepth));
        return function cleanup() {
            removeScrollListener();
        };
    }, []);
    return depth;
};

/**
 * @see https://stackoverflow.com/a/30527650/5219328
 */ const getScrollStep$1 = (targetPosition, currentPosition, scrollRate)=>Math.ceil((targetPosition - currentPosition) / scrollRate);
const getPositionsDiff$1 = (targetPosition, currentPosition)=>Math.abs(currentPosition - targetPosition);
const isTargetPositionReached$1 = (targetPosition, currentPosition)=>currentPosition === targetPosition || getPositionsDiff$1(targetPosition, currentPosition) < 1;
const smoothScroll$1 = (container, targetScrollPosition, scrollRate = 5, onAnimationEnd)=>{
    if (!container) {
        return;
    }
    const currentScrollPosition = container.scrollTop || 0;
    if (isTargetPositionReached$1(targetScrollPosition, currentScrollPosition)) {
        if (typeof onAnimationEnd === "function") {
            onAnimationEnd();
        }
        return;
    }
    if (getPositionsDiff$1(targetScrollPosition, currentScrollPosition) < scrollRate) {
        container.scrollTop = targetScrollPosition;
    } else {
        container.scrollTop = currentScrollPosition + getScrollStep$1(targetScrollPosition, currentScrollPosition, scrollRate);
    }
    window.requestAnimationFrame(()=>smoothScroll$1(container, targetScrollPosition, scrollRate, onAnimationEnd));
};

const getScrollInfo = (scrollContainer)=>{
    let hasScrollBehavior = false;
    if (scrollContainer && scrollContainer.style && "scrollBehavior" in scrollContainer.style) {
        hasScrollBehavior = true;
    }
    const disableScrollAnimation = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    return {
        disableScrollAnimation,
        hasScrollBehavior
    };
};

function _define_property$y(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$y(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$y(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$s(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$s(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$s(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
class ScrollContainer extends Component {
    componentDidMount() {
        this.removeOnScrollToListener = onScrollTo(this.scrollTo);
        this.setState(getScrollInfo(this.containerRef.current));
    }
    componentWillUnmount() {
        this.removeOnScrollToListener && this.removeOnScrollToListener();
    }
    render() {
        const { renderScrollContainer: RenderScrollContainer, onScroll, containerRef } = this;
        const { className: consumerClassName, children } = this.props;
        return /*#__PURE__*/ jsx(RenderScrollContainer, {
            className: cn(ScrollContainer.BASE_CLASS_NAME, consumerClassName),
            onScroll: onScroll,
            ref: containerRef,
            children: children
        });
    }
    constructor(...args){
        super(...args);
        _define_property$y(this, "removeOnScrollToListener", void 0);
        _define_property$y(this, "containerRef", /*#__PURE__*/ createRef());
        _define_property$y(this, "scrollHandler", (event)=>{
            emitOnScroll(event.target.scrollTop);
        });
        _define_property$y(this, "onScroll", (event)=>{
            event.persist();
            this.throttledScroll(event);
        });
        /**
   * throttle( cb, duration )
   * 100ms provides enough of a throttle without it being noticeable to the end user.
   * @see https://lodash.com/docs/4.17.15#throttle
   */ _define_property$y(this, "throttledScroll", throttle$1(this.scrollHandler, 100));
        /**
   * @param {number} targetPosition - Y Position to scroll to.
   * @param {number} scrollRate     - Speed of scroll for browsers that do not support scroll behavior.
   */ _define_property$y(this, "scrollTo", ({ targetPosition, scrollRate = 5 })=>{
            const { hasScrollBehavior, disableScrollAnimation } = this.state;
            const scrollContainer = this.containerRef.current;
            if (!scrollContainer) return;
            if (disableScrollAnimation || hasScrollBehavior) {
                scrollContainer.scrollTop = targetPosition;
                return;
            }
            scrollContainer.style.overflow = "hidden";
            smoothScroll$1(scrollContainer, targetPosition, scrollRate, ()=>{
                scrollContainer.style.overflow = "auto";
            });
        });
        _define_property$y(this, "renderScrollContainer", /*#__PURE__*/ forwardRef(this.props.renderContainer));
    }
}
_define_property$y(ScrollContainer, "defaultProps", {
    renderContainer: (props, ref)=>/*#__PURE__*/ jsx("div", _object_spread_props$s(_object_spread$y({}, props), {
            ref: ref
        }))
});
_define_property$y(ScrollContainer, "BASE_CLASS_NAME", "scroll-container");

function _define_property$x(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$x(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$x(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$r(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$r(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$r(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$r(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$r(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$r(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
var reactIs$2 = {
    exports: {}
};
var reactIs_production_min$1 = {};
/** @license React v17.0.2
 * react-is.production.min.js
 *
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */ var hasRequiredReactIs_production_min$1;
function requireReactIs_production_min$1() {
    if (hasRequiredReactIs_production_min$1) return reactIs_production_min$1;
    hasRequiredReactIs_production_min$1 = 1;
    var b = 60103, c = 60106, d = 60107, e = 60108, f = 60114, g = 60109, h = 60110, k = 60112, l = 60113, m = 60120, n = 60115, p = 60116, q = 60121, r = 60122, u = 60117, v = 60129, w = 60131;
    if ("function" === typeof Symbol && Symbol.for) {
        var x = Symbol.for;
        b = x("react.element");
        c = x("react.portal");
        d = x("react.fragment");
        e = x("react.strict_mode");
        f = x("react.profiler");
        g = x("react.provider");
        h = x("react.context");
        k = x("react.forward_ref");
        l = x("react.suspense");
        m = x("react.suspense_list");
        n = x("react.memo");
        p = x("react.lazy");
        q = x("react.block");
        r = x("react.server.block");
        u = x("react.fundamental");
        v = x("react.debug_trace_mode");
        w = x("react.legacy_hidden");
    }
    function y(a) {
        if ("object" === typeof a && null !== a) {
            var t = a.$$typeof;
            switch(t){
                case b:
                    switch(a = a.type, a){
                        case d:
                        case f:
                        case e:
                        case l:
                        case m:
                            return a;
                        default:
                            switch(a = a && a.$$typeof, a){
                                case h:
                                case k:
                                case p:
                                case n:
                                case g:
                                    return a;
                                default:
                                    return t;
                            }
                    }
                case c:
                    return t;
            }
        }
    }
    var z = g, A = b, B = k, C = d, D = p, E = n, F = c, G = f, H = e, I = l;
    reactIs_production_min$1.ContextConsumer = h;
    reactIs_production_min$1.ContextProvider = z;
    reactIs_production_min$1.Element = A;
    reactIs_production_min$1.ForwardRef = B;
    reactIs_production_min$1.Fragment = C;
    reactIs_production_min$1.Lazy = D;
    reactIs_production_min$1.Memo = E;
    reactIs_production_min$1.Portal = F;
    reactIs_production_min$1.Profiler = G;
    reactIs_production_min$1.StrictMode = H;
    reactIs_production_min$1.Suspense = I;
    reactIs_production_min$1.isAsyncMode = function() {
        return !1;
    };
    reactIs_production_min$1.isConcurrentMode = function() {
        return !1;
    };
    reactIs_production_min$1.isContextConsumer = function(a) {
        return y(a) === h;
    };
    reactIs_production_min$1.isContextProvider = function(a) {
        return y(a) === g;
    };
    reactIs_production_min$1.isElement = function(a) {
        return "object" === typeof a && null !== a && a.$$typeof === b;
    };
    reactIs_production_min$1.isForwardRef = function(a) {
        return y(a) === k;
    };
    reactIs_production_min$1.isFragment = function(a) {
        return y(a) === d;
    };
    reactIs_production_min$1.isLazy = function(a) {
        return y(a) === p;
    };
    reactIs_production_min$1.isMemo = function(a) {
        return y(a) === n;
    };
    reactIs_production_min$1.isPortal = function(a) {
        return y(a) === c;
    };
    reactIs_production_min$1.isProfiler = function(a) {
        return y(a) === f;
    };
    reactIs_production_min$1.isStrictMode = function(a) {
        return y(a) === e;
    };
    reactIs_production_min$1.isSuspense = function(a) {
        return y(a) === l;
    };
    reactIs_production_min$1.isValidElementType = function(a) {
        return "string" === typeof a || "function" === typeof a || a === d || a === f || a === v || a === e || a === l || a === m || a === w || "object" === typeof a && null !== a && (a.$$typeof === p || a.$$typeof === n || a.$$typeof === g || a.$$typeof === h || a.$$typeof === k || a.$$typeof === u || a.$$typeof === q || a[0] === r) ? !0 : !1;
    };
    reactIs_production_min$1.typeOf = y;
    return reactIs_production_min$1;
}
var reactIs_development$1 = {};
/** @license React v17.0.2
 * react-is.development.js
 *
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */ var hasRequiredReactIs_development$1;
function requireReactIs_development$1() {
    if (hasRequiredReactIs_development$1) return reactIs_development$1;
    hasRequiredReactIs_development$1 = 1;
    if (process.env.NODE_ENV !== "production") {
        (function() {
            // ATTENTION
            // When adding new symbols to this file,
            // Please consider also adding to 'react-devtools-shared/src/backend/ReactSymbols'
            // The Symbol used to tag the ReactElement-like types. If there is no native Symbol
            // nor polyfill, then a plain number is used for performance.
            var REACT_ELEMENT_TYPE = 0xeac7;
            var REACT_PORTAL_TYPE = 0xeaca;
            var REACT_FRAGMENT_TYPE = 0xeacb;
            var REACT_STRICT_MODE_TYPE = 0xeacc;
            var REACT_PROFILER_TYPE = 0xead2;
            var REACT_PROVIDER_TYPE = 0xeacd;
            var REACT_CONTEXT_TYPE = 0xeace;
            var REACT_FORWARD_REF_TYPE = 0xead0;
            var REACT_SUSPENSE_TYPE = 0xead1;
            var REACT_SUSPENSE_LIST_TYPE = 0xead8;
            var REACT_MEMO_TYPE = 0xead3;
            var REACT_LAZY_TYPE = 0xead4;
            var REACT_BLOCK_TYPE = 0xead9;
            var REACT_SERVER_BLOCK_TYPE = 0xeada;
            var REACT_FUNDAMENTAL_TYPE = 0xead5;
            var REACT_DEBUG_TRACING_MODE_TYPE = 0xeae1;
            var REACT_LEGACY_HIDDEN_TYPE = 0xeae3;
            if (typeof Symbol === 'function' && Symbol.for) {
                var symbolFor = Symbol.for;
                REACT_ELEMENT_TYPE = symbolFor('react.element');
                REACT_PORTAL_TYPE = symbolFor('react.portal');
                REACT_FRAGMENT_TYPE = symbolFor('react.fragment');
                REACT_STRICT_MODE_TYPE = symbolFor('react.strict_mode');
                REACT_PROFILER_TYPE = symbolFor('react.profiler');
                REACT_PROVIDER_TYPE = symbolFor('react.provider');
                REACT_CONTEXT_TYPE = symbolFor('react.context');
                REACT_FORWARD_REF_TYPE = symbolFor('react.forward_ref');
                REACT_SUSPENSE_TYPE = symbolFor('react.suspense');
                REACT_SUSPENSE_LIST_TYPE = symbolFor('react.suspense_list');
                REACT_MEMO_TYPE = symbolFor('react.memo');
                REACT_LAZY_TYPE = symbolFor('react.lazy');
                REACT_BLOCK_TYPE = symbolFor('react.block');
                REACT_SERVER_BLOCK_TYPE = symbolFor('react.server.block');
                REACT_FUNDAMENTAL_TYPE = symbolFor('react.fundamental');
                symbolFor('react.scope');
                symbolFor('react.opaque.id');
                REACT_DEBUG_TRACING_MODE_TYPE = symbolFor('react.debug_trace_mode');
                symbolFor('react.offscreen');
                REACT_LEGACY_HIDDEN_TYPE = symbolFor('react.legacy_hidden');
            }
            // Filter certain DOM attributes (e.g. src, href) if their values are empty strings.
            var enableScopeAPI = false; // Experimental Create Event Handle API.
            function isValidElementType(type) {
                if (typeof type === 'string' || typeof type === 'function') {
                    return true;
                } // Note: typeof might be other than 'symbol' or 'number' (e.g. if it's a polyfill).
                if (type === REACT_FRAGMENT_TYPE || type === REACT_PROFILER_TYPE || type === REACT_DEBUG_TRACING_MODE_TYPE || type === REACT_STRICT_MODE_TYPE || type === REACT_SUSPENSE_TYPE || type === REACT_SUSPENSE_LIST_TYPE || type === REACT_LEGACY_HIDDEN_TYPE || enableScopeAPI) {
                    return true;
                }
                if (typeof type === 'object' && type !== null) {
                    if (type.$$typeof === REACT_LAZY_TYPE || type.$$typeof === REACT_MEMO_TYPE || type.$$typeof === REACT_PROVIDER_TYPE || type.$$typeof === REACT_CONTEXT_TYPE || type.$$typeof === REACT_FORWARD_REF_TYPE || type.$$typeof === REACT_FUNDAMENTAL_TYPE || type.$$typeof === REACT_BLOCK_TYPE || type[0] === REACT_SERVER_BLOCK_TYPE) {
                        return true;
                    }
                }
                return false;
            }
            function typeOf(object) {
                if (typeof object === 'object' && object !== null) {
                    var $$typeof = object.$$typeof;
                    switch($$typeof){
                        case REACT_ELEMENT_TYPE:
                            var type = object.type;
                            switch(type){
                                case REACT_FRAGMENT_TYPE:
                                case REACT_PROFILER_TYPE:
                                case REACT_STRICT_MODE_TYPE:
                                case REACT_SUSPENSE_TYPE:
                                case REACT_SUSPENSE_LIST_TYPE:
                                    return type;
                                default:
                                    var $$typeofType = type && type.$$typeof;
                                    switch($$typeofType){
                                        case REACT_CONTEXT_TYPE:
                                        case REACT_FORWARD_REF_TYPE:
                                        case REACT_LAZY_TYPE:
                                        case REACT_MEMO_TYPE:
                                        case REACT_PROVIDER_TYPE:
                                            return $$typeofType;
                                        default:
                                            return $$typeof;
                                    }
                            }
                        case REACT_PORTAL_TYPE:
                            return $$typeof;
                    }
                }
                return undefined;
            }
            var ContextConsumer = REACT_CONTEXT_TYPE;
            var ContextProvider = REACT_PROVIDER_TYPE;
            var Element = REACT_ELEMENT_TYPE;
            var ForwardRef = REACT_FORWARD_REF_TYPE;
            var Fragment = REACT_FRAGMENT_TYPE;
            var Lazy = REACT_LAZY_TYPE;
            var Memo = REACT_MEMO_TYPE;
            var Portal = REACT_PORTAL_TYPE;
            var Profiler = REACT_PROFILER_TYPE;
            var StrictMode = REACT_STRICT_MODE_TYPE;
            var Suspense = REACT_SUSPENSE_TYPE;
            var hasWarnedAboutDeprecatedIsAsyncMode = false;
            var hasWarnedAboutDeprecatedIsConcurrentMode = false; // AsyncMode should be deprecated
            function isAsyncMode(object) {
                {
                    if (!hasWarnedAboutDeprecatedIsAsyncMode) {
                        hasWarnedAboutDeprecatedIsAsyncMode = true; // Using console['warn'] to evade Babel and ESLint
                        console['warn']('The ReactIs.isAsyncMode() alias has been deprecated, ' + 'and will be removed in React 18+.');
                    }
                }
                return false;
            }
            function isConcurrentMode(object) {
                {
                    if (!hasWarnedAboutDeprecatedIsConcurrentMode) {
                        hasWarnedAboutDeprecatedIsConcurrentMode = true; // Using console['warn'] to evade Babel and ESLint
                        console['warn']('The ReactIs.isConcurrentMode() alias has been deprecated, ' + 'and will be removed in React 18+.');
                    }
                }
                return false;
            }
            function isContextConsumer(object) {
                return typeOf(object) === REACT_CONTEXT_TYPE;
            }
            function isContextProvider(object) {
                return typeOf(object) === REACT_PROVIDER_TYPE;
            }
            function isElement(object) {
                return typeof object === 'object' && object !== null && object.$$typeof === REACT_ELEMENT_TYPE;
            }
            function isForwardRef(object) {
                return typeOf(object) === REACT_FORWARD_REF_TYPE;
            }
            function isFragment(object) {
                return typeOf(object) === REACT_FRAGMENT_TYPE;
            }
            function isLazy(object) {
                return typeOf(object) === REACT_LAZY_TYPE;
            }
            function isMemo(object) {
                return typeOf(object) === REACT_MEMO_TYPE;
            }
            function isPortal(object) {
                return typeOf(object) === REACT_PORTAL_TYPE;
            }
            function isProfiler(object) {
                return typeOf(object) === REACT_PROFILER_TYPE;
            }
            function isStrictMode(object) {
                return typeOf(object) === REACT_STRICT_MODE_TYPE;
            }
            function isSuspense(object) {
                return typeOf(object) === REACT_SUSPENSE_TYPE;
            }
            reactIs_development$1.ContextConsumer = ContextConsumer;
            reactIs_development$1.ContextProvider = ContextProvider;
            reactIs_development$1.Element = Element;
            reactIs_development$1.ForwardRef = ForwardRef;
            reactIs_development$1.Fragment = Fragment;
            reactIs_development$1.Lazy = Lazy;
            reactIs_development$1.Memo = Memo;
            reactIs_development$1.Portal = Portal;
            reactIs_development$1.Profiler = Profiler;
            reactIs_development$1.StrictMode = StrictMode;
            reactIs_development$1.Suspense = Suspense;
            reactIs_development$1.isAsyncMode = isAsyncMode;
            reactIs_development$1.isConcurrentMode = isConcurrentMode;
            reactIs_development$1.isContextConsumer = isContextConsumer;
            reactIs_development$1.isContextProvider = isContextProvider;
            reactIs_development$1.isElement = isElement;
            reactIs_development$1.isForwardRef = isForwardRef;
            reactIs_development$1.isFragment = isFragment;
            reactIs_development$1.isLazy = isLazy;
            reactIs_development$1.isMemo = isMemo;
            reactIs_development$1.isPortal = isPortal;
            reactIs_development$1.isProfiler = isProfiler;
            reactIs_development$1.isStrictMode = isStrictMode;
            reactIs_development$1.isSuspense = isSuspense;
            reactIs_development$1.isValidElementType = isValidElementType;
            reactIs_development$1.typeOf = typeOf;
        })();
    }
    return reactIs_development$1;
}
if (process.env.NODE_ENV === 'production') {
    reactIs$2.exports = requireReactIs_production_min$1();
} else {
    reactIs$2.exports = requireReactIs_development$1();
}
var reactIsExports$1 = reactIs$2.exports;
function t(t) {
    return "object" == typeof t && null != t && 1 === t.nodeType;
}
function e(t, e) {
    return (!e || "hidden" !== t) && "visible" !== t && "clip" !== t;
}
function n(t, n) {
    if (t.clientHeight < t.scrollHeight || t.clientWidth < t.scrollWidth) {
        var r = getComputedStyle(t, null);
        return e(r.overflowY, n) || e(r.overflowX, n) || function(t) {
            var e = function(t) {
                if (!t.ownerDocument || !t.ownerDocument.defaultView) return null;
                try {
                    return t.ownerDocument.defaultView.frameElement;
                } catch (t) {
                    return null;
                }
            }(t);
            return !!e && (e.clientHeight < t.scrollHeight || e.clientWidth < t.scrollWidth);
        }(t);
    }
    return !1;
}
function r(t, e, n, r, i, o, l, d) {
    return o < t && l > e || o > t && l < e ? 0 : o <= t && d <= n || l >= e && d >= n ? o - t - r : l > e && d < n || o < t && d > n ? l - e + i : 0;
}
var i = function(e, i) {
    var o = window, l = i.scrollMode, d = i.block, f = i.inline, h = i.boundary, u = i.skipOverflowHiddenElements, s = "function" == typeof h ? h : function(t) {
        return t !== h;
    };
    if (!t(e)) throw new TypeError("Invalid target");
    for(var a, c, g = document.scrollingElement || document.documentElement, p = [], m = e; t(m) && s(m);){
        if ((m = null == (c = (a = m).parentElement) ? a.getRootNode().host || null : c) === g) {
            p.push(m);
            break;
        }
        null != m && m === document.body && n(m) && !n(document.documentElement) || null != m && n(m, u) && p.push(m);
    }
    for(var w = o.visualViewport ? o.visualViewport.width : innerWidth, v = o.visualViewport ? o.visualViewport.height : innerHeight, W = window.scrollX || pageXOffset, H = window.scrollY || pageYOffset, b = e.getBoundingClientRect(), y = b.height, E = b.width, M = b.top, V = b.right, x = b.bottom, I = b.left, C = "start" === d || "nearest" === d ? M : "end" === d ? x : M + y / 2, R = "center" === f ? I + E / 2 : "end" === f ? V : I, T = [], k = 0; k < p.length; k++){
        var B = p[k], D = B.getBoundingClientRect(), O = D.height, X = D.width, Y = D.top, L = D.right, S = D.bottom, j = D.left;
        if ("if-needed" === l && M >= 0 && I >= 0 && x <= v && V <= w && M >= Y && x <= S && I >= j && V <= L) return T;
        var N = getComputedStyle(B), q = parseInt(N.borderLeftWidth, 10), z = parseInt(N.borderTopWidth, 10), A = parseInt(N.borderRightWidth, 10), F = parseInt(N.borderBottomWidth, 10), G = 0, J = 0, K = "offsetWidth" in B ? B.offsetWidth - B.clientWidth - q - A : 0, P = "offsetHeight" in B ? B.offsetHeight - B.clientHeight - z - F : 0, Q = "offsetWidth" in B ? 0 === B.offsetWidth ? 0 : X / B.offsetWidth : 0, U = "offsetHeight" in B ? 0 === B.offsetHeight ? 0 : O / B.offsetHeight : 0;
        if (g === B) G = "start" === d ? C : "end" === d ? C - v : "nearest" === d ? r(H, H + v, v, z, F, H + C, H + C + y, y) : C - v / 2, J = "start" === f ? R : "center" === f ? R - w / 2 : "end" === f ? R - w : r(W, W + w, w, q, A, W + R, W + R + E, E), G = Math.max(0, G + H), J = Math.max(0, J + W);
        else {
            G = "start" === d ? C - Y - z : "end" === d ? C - S + F + P : "nearest" === d ? r(Y, S, O, z, F + P, C, C + y, y) : C - (Y + O / 2) + P / 2, J = "start" === f ? R - j - q : "center" === f ? R - (j + X / 2) + K / 2 : "end" === f ? R - L + A + K : r(j, L, X, q, A + K, R, R + E, E);
            var Z = B.scrollLeft, $ = B.scrollTop;
            C += $ - (G = Math.max(0, Math.min($ + G / U, B.scrollHeight - O / U + P))), R += Z - (J = Math.max(0, Math.min(Z + J / Q, B.scrollWidth - X / Q + K)));
        }
        T.push({
            el: B,
            top: G,
            left: J
        });
    }
    return T;
};
/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */ /* global Reflect, Promise, SuppressedError, Symbol */ var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for(var s, i = 1, n = arguments.length; i < n; i++){
            s = arguments[i];
            for(var p in s)if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
typeof SuppressedError === "function" ? SuppressedError : function(error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};
let idCounter = 0;
/**
 * Accepts a parameter and returns it if it's a function
 * or a noop function if it's not. This allows us to
 * accept a callback, but not worry about it if it's not
 * passed.
 * @param {Function} cb the callback
 * @return {Function} a function
 */ function cbToCb(cb) {
    return typeof cb === 'function' ? cb : noop;
}
function noop() {}
/**
 * Scroll node into view if necessary
 * @param {HTMLElement} node the element that should scroll into view
 * @param {HTMLElement} menuNode the menu element of the component
 */ function scrollIntoView(node, menuNode) {
    if (!node) {
        return;
    }
    const actions = i(node, {
        boundary: menuNode,
        block: 'nearest',
        scrollMode: 'if-needed'
    });
    actions.forEach((_ref)=>{
        let { el, top, left } = _ref;
        el.scrollTop = top;
        el.scrollLeft = left;
    });
}
/**
 * @param {HTMLElement} parent the parent node
 * @param {HTMLElement} child the child node
 * @param {Window} environment The window context where downshift renders.
 * @return {Boolean} whether the parent is the child or the child is in the parent
 */ function isOrContainsNode(parent, child, environment) {
    const result = parent === child || child instanceof environment.Node && parent.contains && parent.contains(child);
    return result;
}
/**
 * Simple debounce implementation. Will call the given
 * function once after the time given has passed since
 * it was last called.
 * @param {Function} fn the function to call after the time
 * @param {Number} time the time to wait
 * @return {Function} the debounced function
 */ function debounce(fn, time) {
    let timeoutId;
    function cancel() {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
    }
    function wrapper() {
        for(var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++){
            args[_key] = arguments[_key];
        }
        cancel();
        timeoutId = setTimeout(()=>{
            timeoutId = null;
            fn(...args);
        }, time);
    }
    wrapper.cancel = cancel;
    return wrapper;
}
/**
 * This is intended to be used to compose event handlers.
 * They are executed in order until one of them sets
 * `event.preventDownshiftDefault = true`.
 * @param {...Function} fns the event handler functions
 * @return {Function} the event handler to add to an element
 */ function callAllEventHandlers() {
    for(var _len2 = arguments.length, fns = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++){
        fns[_key2] = arguments[_key2];
    }
    return function(event) {
        for(var _len3 = arguments.length, args = new Array(_len3 > 1 ? _len3 - 1 : 0), _key3 = 1; _key3 < _len3; _key3++){
            args[_key3 - 1] = arguments[_key3];
        }
        return fns.some((fn)=>{
            if (fn) {
                fn(event, ...args);
            }
            return event.preventDownshiftDefault || event.hasOwnProperty('nativeEvent') && event.nativeEvent.preventDownshiftDefault;
        });
    };
}
function handleRefs() {
    for(var _len4 = arguments.length, refs = new Array(_len4), _key4 = 0; _key4 < _len4; _key4++){
        refs[_key4] = arguments[_key4];
    }
    return (node)=>{
        refs.forEach((ref)=>{
            if (typeof ref === 'function') {
                ref(node);
            } else if (ref) {
                ref.current = node;
            }
        });
    };
}
/**
 * This generates a unique ID for an instance of Downshift
 * @return {String} the unique ID
 */ function generateId() {
    return String(idCounter++);
}
/**
 * Default implementation for status message. Only added when menu is open.
 * Will specify if there are results in the list, and if so, how many,
 * and what keys are relevant.
 *
 * @param {Object} param the downshift state and other relevant properties
 * @return {String} the a11y status message
 */ function getA11yStatusMessage$1(_ref2) {
    let { isOpen, resultCount, previousResultCount } = _ref2;
    if (!isOpen) {
        return '';
    }
    if (!resultCount) {
        return 'No results are available.';
    }
    if (resultCount !== previousResultCount) {
        return `${resultCount} result${resultCount === 1 ? ' is' : 's are'} available, use up and down arrow keys to navigate. Press Enter key to select.`;
    }
    return '';
}
/**
 * Takes an argument and if it's an array, returns the first item in the array
 * otherwise returns the argument
 * @param {*} arg the maybe-array
 * @param {*} defaultValue the value if arg is falsey not defined
 * @return {*} the arg or it's first item
 */ function unwrapArray(arg, defaultValue) {
    arg = Array.isArray(arg) ? /* istanbul ignore next (preact) */ arg[0] : arg;
    if (!arg && defaultValue) {
        return defaultValue;
    } else {
        return arg;
    }
}
/**
 * @param {Object} element (P)react element
 * @return {Boolean} whether it's a DOM element
 */ function isDOMElement(element) {
    return typeof element.type === 'string';
}
/**
 * @param {Object} element (P)react element
 * @return {Object} the props
 */ function getElementProps(element) {
    return element.props;
}
/**
 * Throws a helpful error message for required properties. Useful
 * to be used as a default in destructuring or object params.
 * @param {String} fnName the function name
 * @param {String} propName the prop name
 */ function requiredProp(fnName, propName) {
    // eslint-disable-next-line no-console
    console.error(`The property "${propName}" is required in "${fnName}"`);
}
const stateKeys = [
    'highlightedIndex',
    'inputValue',
    'isOpen',
    'selectedItem',
    'type'
];
/**
 * @param {Object} state the state object
 * @return {Object} state that is relevant to downshift
 */ function pickState(state) {
    if (state === void 0) {
        state = {};
    }
    const result = {};
    stateKeys.forEach((k)=>{
        if (state.hasOwnProperty(k)) {
            result[k] = state[k];
        }
    });
    return result;
}
/**
 * This will perform a shallow merge of the given state object
 * with the state coming from props
 * (for the controlled component scenario)
 * This is used in state updater functions so they're referencing
 * the right state regardless of where it comes from.
 *
 * @param {Object} state The state of the component/hook.
 * @param {Object} props The props that may contain controlled values.
 * @returns {Object} The merged controlled state.
 */ function getState(state, props) {
    return Object.keys(state).reduce((prevState, key)=>{
        prevState[key] = isControlledProp(props, key) ? props[key] : state[key];
        return prevState;
    }, {});
}
/**
 * This determines whether a prop is a "controlled prop" meaning it is
 * state which is controlled by the outside of this component rather
 * than within this component.
 *
 * @param {Object} props The props that may contain controlled values.
 * @param {String} key the key to check
 * @return {Boolean} whether it is a controlled controlled prop
 */ function isControlledProp(props, key) {
    return props[key] !== undefined;
}
/**
 * Normalizes the 'key' property of a KeyboardEvent in IE/Edge
 * @param {Object} event a keyboardEvent object
 * @return {String} keyboard key
 */ function normalizeArrowKey(event) {
    const { key, keyCode } = event;
    /* istanbul ignore next (ie) */ if (keyCode >= 37 && keyCode <= 40 && key.indexOf('Arrow') !== 0) {
        return `Arrow${key}`;
    }
    return key;
}
/**
 * Simple check if the value passed is object literal
 * @param {*} obj any things
 * @return {Boolean} whether it's object literal
 */ function isPlainObject(obj) {
    return Object.prototype.toString.call(obj) === '[object Object]';
}
/**
 * Returns the new index in the list, in a circular way. If next value is out of bonds from the total,
 * it will wrap to either 0 or itemCount - 1.
 *
 * @param {number} moveAmount Number of positions to move. Negative to move backwards, positive forwards.
 * @param {number} baseIndex The initial position to move from.
 * @param {number} itemCount The total number of items.
 * @param {Function} getItemNodeFromIndex Used to check if item is disabled.
 * @param {boolean} circular Specify if navigation is circular. Default is true.
 * @returns {number} The new index after the move.
 */ function getNextWrappingIndex(moveAmount, baseIndex, itemCount, getItemNodeFromIndex, circular) {
    if (circular === void 0) {
        circular = true;
    }
    if (itemCount === 0) {
        return -1;
    }
    const itemsLastIndex = itemCount - 1;
    if (typeof baseIndex !== 'number' || baseIndex < 0 || baseIndex >= itemCount) {
        baseIndex = moveAmount > 0 ? -1 : itemsLastIndex + 1;
    }
    let newIndex = baseIndex + moveAmount;
    if (newIndex < 0) {
        newIndex = circular ? itemsLastIndex : 0;
    } else if (newIndex > itemsLastIndex) {
        newIndex = circular ? 0 : itemsLastIndex;
    }
    const nonDisabledNewIndex = getNextNonDisabledIndex(moveAmount, newIndex, itemCount, getItemNodeFromIndex, circular);
    if (nonDisabledNewIndex === -1) {
        return baseIndex >= itemCount ? -1 : baseIndex;
    }
    return nonDisabledNewIndex;
}
/**
 * Returns the next index in the list of an item that is not disabled.
 *
 * @param {number} moveAmount Number of positions to move. Negative to move backwards, positive forwards.
 * @param {number} baseIndex The initial position to move from.
 * @param {number} itemCount The total number of items.
 * @param {Function} getItemNodeFromIndex Used to check if item is disabled.
 * @param {boolean} circular Specify if navigation is circular. Default is true.
 * @returns {number} The new index. Returns baseIndex if item is not disabled. Returns next non-disabled item otherwise. If no non-disabled found it will return -1.
 */ function getNextNonDisabledIndex(moveAmount, baseIndex, itemCount, getItemNodeFromIndex, circular) {
    const currentElementNode = getItemNodeFromIndex(baseIndex);
    if (!currentElementNode || !currentElementNode.hasAttribute('disabled')) {
        return baseIndex;
    }
    if (moveAmount > 0) {
        for(let index = baseIndex + 1; index < itemCount; index++){
            if (!getItemNodeFromIndex(index).hasAttribute('disabled')) {
                return index;
            }
        }
    } else {
        for(let index = baseIndex - 1; index >= 0; index--){
            if (!getItemNodeFromIndex(index).hasAttribute('disabled')) {
                return index;
            }
        }
    }
    if (circular) {
        return moveAmount > 0 ? getNextNonDisabledIndex(1, 0, itemCount, getItemNodeFromIndex, false) : getNextNonDisabledIndex(-1, itemCount - 1, itemCount, getItemNodeFromIndex, false);
    }
    return -1;
}
/**
 * Checks if event target is within the downshift elements.
 *
 * @param {EventTarget} target Target to check.
 * @param {HTMLElement[]} downshiftElements The elements that form downshift (list, toggle button etc).
 * @param {Window} environment The window context where downshift renders.
 * @param {boolean} checkActiveElement Whether to also check activeElement.
 *
 * @returns {boolean} Whether or not the target is within downshift elements.
 */ function targetWithinDownshift(target, downshiftElements, environment, checkActiveElement) {
    if (checkActiveElement === void 0) {
        checkActiveElement = true;
    }
    return downshiftElements.some((contextNode)=>contextNode && (isOrContainsNode(contextNode, target, environment) || checkActiveElement && isOrContainsNode(contextNode, environment.document.activeElement, environment)));
} // eslint-disable-next-line import/no-mutable-exports
let validateControlledUnchanged = noop;
/* istanbul ignore next */ if (process.env.NODE_ENV !== 'production') {
    validateControlledUnchanged = (state, prevProps, nextProps)=>{
        const warningDescription = `This prop should not switch from controlled to uncontrolled (or vice versa). Decide between using a controlled or uncontrolled Downshift element for the lifetime of the component. More info: https://github.com/downshift-js/downshift#control-props`;
        Object.keys(state).forEach((propKey)=>{
            if (prevProps[propKey] !== undefined && nextProps[propKey] === undefined) {
                // eslint-disable-next-line no-console
                console.error(`downshift: A component has changed the controlled prop "${propKey}" to be uncontrolled. ${warningDescription}`);
            } else if (prevProps[propKey] === undefined && nextProps[propKey] !== undefined) {
                // eslint-disable-next-line no-console
                console.error(`downshift: A component has changed the uncontrolled prop "${propKey}" to be controlled. ${warningDescription}`);
            }
        });
    };
}
const cleanupStatus = debounce((documentProp)=>{
    getStatusDiv(documentProp).textContent = '';
}, 500);
/**
 * @param {String} status the status message
 * @param {Object} documentProp document passed by the user.
 */ function setStatus(status, documentProp) {
    const div = getStatusDiv(documentProp);
    if (!status) {
        return;
    }
    div.textContent = status;
    cleanupStatus(documentProp);
}
/**
 * Get the status node or create it if it does not already exist.
 * @param {Object} documentProp document passed by the user.
 * @return {HTMLElement} the status node.
 */ function getStatusDiv(documentProp) {
    if (documentProp === void 0) {
        documentProp = document;
    }
    let statusDiv = documentProp.getElementById('a11y-status-message');
    if (statusDiv) {
        return statusDiv;
    }
    statusDiv = documentProp.createElement('div');
    statusDiv.setAttribute('id', 'a11y-status-message');
    statusDiv.setAttribute('role', 'status');
    statusDiv.setAttribute('aria-live', 'polite');
    statusDiv.setAttribute('aria-relevant', 'additions text');
    Object.assign(statusDiv.style, {
        border: '0',
        clip: 'rect(0 0 0 0)',
        height: '1px',
        margin: '-1px',
        overflow: 'hidden',
        padding: '0',
        position: 'absolute',
        width: '1px'
    });
    documentProp.body.appendChild(statusDiv);
    return statusDiv;
}
const unknown = process.env.NODE_ENV !== "production" ? '__autocomplete_unknown__' : 0;
const mouseUp = process.env.NODE_ENV !== "production" ? '__autocomplete_mouseup__' : 1;
const itemMouseEnter = process.env.NODE_ENV !== "production" ? '__autocomplete_item_mouseenter__' : 2;
const keyDownArrowUp = process.env.NODE_ENV !== "production" ? '__autocomplete_keydown_arrow_up__' : 3;
const keyDownArrowDown = process.env.NODE_ENV !== "production" ? '__autocomplete_keydown_arrow_down__' : 4;
const keyDownEscape = process.env.NODE_ENV !== "production" ? '__autocomplete_keydown_escape__' : 5;
const keyDownEnter = process.env.NODE_ENV !== "production" ? '__autocomplete_keydown_enter__' : 6;
const keyDownHome = process.env.NODE_ENV !== "production" ? '__autocomplete_keydown_home__' : 7;
const keyDownEnd = process.env.NODE_ENV !== "production" ? '__autocomplete_keydown_end__' : 8;
const clickItem = process.env.NODE_ENV !== "production" ? '__autocomplete_click_item__' : 9;
const blurInput = process.env.NODE_ENV !== "production" ? '__autocomplete_blur_input__' : 10;
const changeInput = process.env.NODE_ENV !== "production" ? '__autocomplete_change_input__' : 11;
const keyDownSpaceButton = process.env.NODE_ENV !== "production" ? '__autocomplete_keydown_space_button__' : 12;
const clickButton = process.env.NODE_ENV !== "production" ? '__autocomplete_click_button__' : 13;
const blurButton = process.env.NODE_ENV !== "production" ? '__autocomplete_blur_button__' : 14;
const controlledPropUpdatedSelectedItem = process.env.NODE_ENV !== "production" ? '__autocomplete_controlled_prop_updated_selected_item__' : 15;
const touchEnd = process.env.NODE_ENV !== "production" ? '__autocomplete_touchend__' : 16;
var stateChangeTypes$3 = /*#__PURE__*/ Object.freeze({
    __proto__: null,
    unknown: unknown,
    mouseUp: mouseUp,
    itemMouseEnter: itemMouseEnter,
    keyDownArrowUp: keyDownArrowUp,
    keyDownArrowDown: keyDownArrowDown,
    keyDownEscape: keyDownEscape,
    keyDownEnter: keyDownEnter,
    keyDownHome: keyDownHome,
    keyDownEnd: keyDownEnd,
    clickItem: clickItem,
    blurInput: blurInput,
    changeInput: changeInput,
    keyDownSpaceButton: keyDownSpaceButton,
    clickButton: clickButton,
    blurButton: blurButton,
    controlledPropUpdatedSelectedItem: controlledPropUpdatedSelectedItem,
    touchEnd: touchEnd
});
/* eslint camelcase:0 */ const Downshift = /*#__PURE__*/ (()=>{
    class Downshift extends Component {
        /**
     * Clear all running timeouts
     */ internalClearTimeouts() {
            this.timeoutIds.forEach((id)=>{
                clearTimeout(id);
            });
            this.timeoutIds = [];
        }
        /**
     * Gets the state based on internal state or props
     * If a state value is passed via props, then that
     * is the value given, otherwise it's retrieved from
     * stateToMerge
     *
     * @param {Object} stateToMerge defaults to this.state
     * @return {Object} the state
     */ getState(stateToMerge) {
            if (stateToMerge === void 0) {
                stateToMerge = this.state;
            }
            return getState(stateToMerge, this.props);
        }
        getItemCount() {
            // things read better this way. They're in priority order:
            // 1. `this.itemCount`
            // 2. `this.props.itemCount`
            // 3. `this.items.length`
            let itemCount = this.items.length;
            if (this.itemCount != null) {
                itemCount = this.itemCount;
            } else if (this.props.itemCount !== undefined) {
                itemCount = this.props.itemCount;
            }
            return itemCount;
        }
        getItemNodeFromIndex(index) {
            return this.props.environment.document.getElementById(this.getItemId(index));
        }
        scrollHighlightedItemIntoView() {
            /* istanbul ignore else (react-native) */ {
                const node = this.getItemNodeFromIndex(this.getState().highlightedIndex);
                this.props.scrollIntoView(node, this._menuNode);
            }
        }
        moveHighlightedIndex(amount, otherStateToSet) {
            const itemCount = this.getItemCount();
            const { highlightedIndex } = this.getState();
            if (itemCount > 0) {
                const nextHighlightedIndex = getNextWrappingIndex(amount, highlightedIndex, itemCount, (index)=>this.getItemNodeFromIndex(index));
                this.setHighlightedIndex(nextHighlightedIndex, otherStateToSet);
            }
        }
        getStateAndHelpers() {
            const { highlightedIndex, inputValue, selectedItem, isOpen } = this.getState();
            const { itemToString } = this.props;
            const { id } = this;
            const { getRootProps, getToggleButtonProps, getLabelProps, getMenuProps, getInputProps, getItemProps, openMenu, closeMenu, toggleMenu, selectItem, selectItemAtIndex, selectHighlightedItem, setHighlightedIndex, clearSelection, clearItems, reset, setItemCount, unsetItemCount, internalSetState: setState } = this;
            return {
                // prop getters
                getRootProps,
                getToggleButtonProps,
                getLabelProps,
                getMenuProps,
                getInputProps,
                getItemProps,
                // actions
                reset,
                openMenu,
                closeMenu,
                toggleMenu,
                selectItem,
                selectItemAtIndex,
                selectHighlightedItem,
                setHighlightedIndex,
                clearSelection,
                clearItems,
                setItemCount,
                unsetItemCount,
                setState,
                // props
                itemToString,
                // derived
                id,
                // state
                highlightedIndex,
                inputValue,
                isOpen,
                selectedItem
            };
        }
        componentDidMount() {
            /* istanbul ignore if (react-native) */ if (process.env.NODE_ENV !== 'production' && !false && this.getMenuProps.called && !this.getMenuProps.suppressRefError) {
                validateGetMenuPropsCalledCorrectly(this._menuNode, this.getMenuProps);
            }
            /* istanbul ignore if (react-native) */ {
                // this.isMouseDown helps us track whether the mouse is currently held down.
                // This is useful when the user clicks on an item in the list, but holds the mouse
                // down long enough for the list to disappear (because the blur event fires on the input)
                // this.isMouseDown is used in the blur handler on the input to determine whether the blur event should
                // trigger hiding the menu.
                const onMouseDown = ()=>{
                    this.isMouseDown = true;
                };
                const onMouseUp = (event)=>{
                    this.isMouseDown = false; // if the target element or the activeElement is within a downshift node
                    // then we don't want to reset downshift
                    const contextWithinDownshift = targetWithinDownshift(event.target, [
                        this._rootNode,
                        this._menuNode
                    ], this.props.environment);
                    if (!contextWithinDownshift && this.getState().isOpen) {
                        this.reset({
                            type: mouseUp
                        }, ()=>this.props.onOuterClick(this.getStateAndHelpers()));
                    }
                }; // Touching an element in iOS gives focus and hover states, but touching out of
                // the element will remove hover, and persist the focus state, resulting in the
                // blur event not being triggered.
                // this.isTouchMove helps us track whether the user is tapping or swiping on a touch screen.
                // If the user taps outside of Downshift, the component should be reset,
                // but not if the user is swiping
                const onTouchStart = ()=>{
                    this.isTouchMove = false;
                };
                const onTouchMove = ()=>{
                    this.isTouchMove = true;
                };
                const onTouchEnd = (event)=>{
                    const contextWithinDownshift = targetWithinDownshift(event.target, [
                        this._rootNode,
                        this._menuNode
                    ], this.props.environment, false);
                    if (!this.isTouchMove && !contextWithinDownshift && this.getState().isOpen) {
                        this.reset({
                            type: touchEnd
                        }, ()=>this.props.onOuterClick(this.getStateAndHelpers()));
                    }
                };
                const { environment } = this.props;
                environment.addEventListener('mousedown', onMouseDown);
                environment.addEventListener('mouseup', onMouseUp);
                environment.addEventListener('touchstart', onTouchStart);
                environment.addEventListener('touchmove', onTouchMove);
                environment.addEventListener('touchend', onTouchEnd);
                this.cleanup = ()=>{
                    this.internalClearTimeouts();
                    this.updateStatus.cancel();
                    environment.removeEventListener('mousedown', onMouseDown);
                    environment.removeEventListener('mouseup', onMouseUp);
                    environment.removeEventListener('touchstart', onTouchStart);
                    environment.removeEventListener('touchmove', onTouchMove);
                    environment.removeEventListener('touchend', onTouchEnd);
                };
            }
        }
        shouldScroll(prevState, prevProps) {
            const { highlightedIndex: currentHighlightedIndex } = this.props.highlightedIndex === undefined ? this.getState() : this.props;
            const { highlightedIndex: prevHighlightedIndex } = prevProps.highlightedIndex === undefined ? prevState : prevProps;
            const scrollWhenOpen = currentHighlightedIndex && this.getState().isOpen && !prevState.isOpen;
            const scrollWhenNavigating = currentHighlightedIndex !== prevHighlightedIndex;
            return scrollWhenOpen || scrollWhenNavigating;
        }
        componentDidUpdate(prevProps, prevState) {
            if (process.env.NODE_ENV !== 'production') {
                validateControlledUnchanged(this.state, prevProps, this.props);
                /* istanbul ignore if (react-native) */ if (this.getMenuProps.called && !this.getMenuProps.suppressRefError) {
                    validateGetMenuPropsCalledCorrectly(this._menuNode, this.getMenuProps);
                }
            }
            if (isControlledProp(this.props, 'selectedItem') && this.props.selectedItemChanged(prevProps.selectedItem, this.props.selectedItem)) {
                this.internalSetState({
                    type: controlledPropUpdatedSelectedItem,
                    inputValue: this.props.itemToString(this.props.selectedItem)
                });
            }
            if (!this.avoidScrolling && this.shouldScroll(prevState, prevProps)) {
                this.scrollHighlightedItemIntoView();
            }
            /* istanbul ignore else (react-native) */ {
                this.updateStatus();
            }
        }
        componentWillUnmount() {
            this.cleanup(); // avoids memory leak
        }
        render() {
            const children = unwrapArray(this.props.children, noop); // because the items are rerendered every time we call the children
            // we clear this out each render and it will be populated again as
            // getItemProps is called.
            this.clearItems(); // we reset this so we know whether the user calls getRootProps during
            // this render. If they do then we don't need to do anything,
            // if they don't then we need to clone the element they return and
            // apply the props for them.
            this.getRootProps.called = false;
            this.getRootProps.refKey = undefined;
            this.getRootProps.suppressRefError = undefined; // we do something similar for getMenuProps
            this.getMenuProps.called = false;
            this.getMenuProps.refKey = undefined;
            this.getMenuProps.suppressRefError = undefined; // we do something similar for getLabelProps
            this.getLabelProps.called = false; // and something similar for getInputProps
            this.getInputProps.called = false;
            const element = unwrapArray(children(this.getStateAndHelpers()));
            if (!element) {
                return null;
            }
            if (this.getRootProps.called || this.props.suppressRefError) {
                if (process.env.NODE_ENV !== 'production' && !this.getRootProps.suppressRefError && !this.props.suppressRefError) {
                    validateGetRootPropsCalledCorrectly(element, this.getRootProps);
                }
                return element;
            } else if (isDOMElement(element)) {
                // they didn't apply the root props, but we can clone
                // this and apply the props ourselves
                return /*#__PURE__*/ cloneElement(element, this.getRootProps(getElementProps(element)));
            }
            /* istanbul ignore else */ if (process.env.NODE_ENV !== 'production') {
                // they didn't apply the root props, but they need to
                // otherwise we can't query around the autocomplete
                throw new Error('downshift: If you return a non-DOM element, you must apply the getRootProps function');
            }
            /* istanbul ignore next */ return undefined;
        }
        constructor(_props){
            var _this;
            super(_props);
            _this = this;
            this.id = this.props.id || `downshift-${generateId()}`;
            this.menuId = this.props.menuId || `${this.id}-menu`;
            this.labelId = this.props.labelId || `${this.id}-label`;
            this.inputId = this.props.inputId || `${this.id}-input`;
            this.getItemId = this.props.getItemId || ((index)=>`${this.id}-item-${index}`);
            this.input = null;
            this.items = [];
            this.itemCount = null;
            this.previousResultCount = 0;
            this.timeoutIds = [];
            this.internalSetTimeout = (fn, time)=>{
                const id = setTimeout(()=>{
                    this.timeoutIds = this.timeoutIds.filter((i)=>i !== id);
                    fn();
                }, time);
                this.timeoutIds.push(id);
            };
            this.setItemCount = (count)=>{
                this.itemCount = count;
            };
            this.unsetItemCount = ()=>{
                this.itemCount = null;
            };
            this.setHighlightedIndex = function(highlightedIndex, otherStateToSet) {
                if (highlightedIndex === void 0) {
                    highlightedIndex = _this.props.defaultHighlightedIndex;
                }
                if (otherStateToSet === void 0) {
                    otherStateToSet = {};
                }
                otherStateToSet = pickState(otherStateToSet);
                _this.internalSetState(_object_spread$x({
                    highlightedIndex
                }, otherStateToSet));
            };
            this.clearSelection = (cb)=>{
                this.internalSetState({
                    selectedItem: null,
                    inputValue: '',
                    highlightedIndex: this.props.defaultHighlightedIndex,
                    isOpen: this.props.defaultIsOpen
                }, cb);
            };
            this.selectItem = (item, otherStateToSet, cb)=>{
                otherStateToSet = pickState(otherStateToSet);
                this.internalSetState(_object_spread$x({
                    isOpen: this.props.defaultIsOpen,
                    highlightedIndex: this.props.defaultHighlightedIndex,
                    selectedItem: item,
                    inputValue: this.props.itemToString(item)
                }, otherStateToSet), cb);
            };
            this.selectItemAtIndex = (itemIndex, otherStateToSet, cb)=>{
                const item = this.items[itemIndex];
                if (item == null) {
                    return;
                }
                this.selectItem(item, otherStateToSet, cb);
            };
            this.selectHighlightedItem = (otherStateToSet, cb)=>{
                return this.selectItemAtIndex(this.getState().highlightedIndex, otherStateToSet, cb);
            };
            this.internalSetState = (stateToSet, cb)=>{
                let isItemSelected, onChangeArg;
                const onStateChangeArg = {};
                const isStateToSetFunction = typeof stateToSet === 'function'; // we want to call `onInputValueChange` before the `setState` call
                // so someone controlling the `inputValue` state gets notified of
                // the input change as soon as possible. This avoids issues with
                // preserving the cursor position.
                // See https://github.com/downshift-js/downshift/issues/217 for more info.
                if (!isStateToSetFunction && stateToSet.hasOwnProperty('inputValue')) {
                    this.props.onInputValueChange(stateToSet.inputValue, _object_spread$x({}, this.getStateAndHelpers(), stateToSet));
                }
                return this.setState((state)=>{
                    state = this.getState(state);
                    let newStateToSet = isStateToSetFunction ? stateToSet(state) : stateToSet; // Your own function that could modify the state that will be set.
                    newStateToSet = this.props.stateReducer(state, newStateToSet); // checks if an item is selected, regardless of if it's different from
                    // what was selected before
                    // used to determine if onSelect and onChange callbacks should be called
                    isItemSelected = newStateToSet.hasOwnProperty('selectedItem'); // this keeps track of the object we want to call with setState
                    const nextState = {}; // this is just used to tell whether the state changed
                    // and we're trying to update that state. OR if the selection has changed and we're
                    // trying to update the selection
                    if (isItemSelected && newStateToSet.selectedItem !== state.selectedItem) {
                        onChangeArg = newStateToSet.selectedItem;
                    }
                    newStateToSet.type = newStateToSet.type || unknown;
                    Object.keys(newStateToSet).forEach((key)=>{
                        // onStateChangeArg should only have the state that is
                        // actually changing
                        if (state[key] !== newStateToSet[key]) {
                            onStateChangeArg[key] = newStateToSet[key];
                        } // the type is useful for the onStateChangeArg
                        // but we don't actually want to set it in internal state.
                        // this is an undocumented feature for now... Not all internalSetState
                        // calls support it and I'm not certain we want them to yet.
                        // But it enables users controlling the isOpen state to know when
                        // the isOpen state changes due to mouseup events which is quite handy.
                        if (key === 'type') {
                            return;
                        }
                        newStateToSet[key]; // if it's coming from props, then we don't care to set it internally
                        if (!isControlledProp(this.props, key)) {
                            nextState[key] = newStateToSet[key];
                        }
                    }); // if stateToSet is a function, then we weren't able to call onInputValueChange
                    // earlier, so we'll call it now that we know what the inputValue state will be.
                    if (isStateToSetFunction && newStateToSet.hasOwnProperty('inputValue')) {
                        this.props.onInputValueChange(newStateToSet.inputValue, _object_spread$x({}, this.getStateAndHelpers(), newStateToSet));
                    }
                    return nextState;
                }, ()=>{
                    // call the provided callback if it's a function
                    cbToCb(cb)(); // only call the onStateChange and onChange callbacks if
                    // we have relevant information to pass them.
                    const hasMoreStateThanType = Object.keys(onStateChangeArg).length > 1;
                    if (hasMoreStateThanType) {
                        this.props.onStateChange(onStateChangeArg, this.getStateAndHelpers());
                    }
                    if (isItemSelected) {
                        this.props.onSelect(stateToSet.selectedItem, this.getStateAndHelpers());
                    }
                    if (onChangeArg !== undefined) {
                        this.props.onChange(onChangeArg, this.getStateAndHelpers());
                    } // this is currently undocumented and therefore subject to change
                    // We'll try to not break it, but just be warned.
                    this.props.onUserAction(onStateChangeArg, this.getStateAndHelpers());
                });
            };
            this.rootRef = (node)=>this._rootNode = node;
            this.getRootProps = function(_temp, _temp2) {
                let _ref = _temp === void 0 ? {} : _temp, { refKey = 'ref', ref } = _ref, rest = _object_without_properties$r(_ref, [
                    "refKey",
                    "ref"
                ]);
                let { suppressRefError = false } = _temp2 === void 0 ? {} : _temp2;
                // this is used in the render to know whether the user has called getRootProps.
                // It uses that to know whether to apply the props automatically
                _this.getRootProps.called = true;
                _this.getRootProps.refKey = refKey;
                _this.getRootProps.suppressRefError = suppressRefError;
                const { isOpen } = _this.getState();
                return _object_spread$x({
                    [refKey]: handleRefs(ref, _this.rootRef),
                    role: 'combobox',
                    'aria-expanded': isOpen,
                    'aria-haspopup': 'listbox',
                    'aria-owns': isOpen ? _this.menuId : null,
                    'aria-labelledby': _this.labelId
                }, rest);
            };
            this.keyDownHandlers = {
                ArrowDown (event) {
                    event.preventDefault();
                    if (this.getState().isOpen) {
                        const amount = event.shiftKey ? 5 : 1;
                        this.moveHighlightedIndex(amount, {
                            type: keyDownArrowDown
                        });
                    } else {
                        this.internalSetState({
                            isOpen: true,
                            type: keyDownArrowDown
                        }, ()=>{
                            const itemCount = this.getItemCount();
                            if (itemCount > 0) {
                                const { highlightedIndex } = this.getState();
                                const nextHighlightedIndex = getNextWrappingIndex(1, highlightedIndex, itemCount, (index)=>this.getItemNodeFromIndex(index));
                                this.setHighlightedIndex(nextHighlightedIndex, {
                                    type: keyDownArrowDown
                                });
                            }
                        });
                    }
                },
                ArrowUp (event) {
                    event.preventDefault();
                    if (this.getState().isOpen) {
                        const amount = event.shiftKey ? -5 : -1;
                        this.moveHighlightedIndex(amount, {
                            type: keyDownArrowUp
                        });
                    } else {
                        this.internalSetState({
                            isOpen: true,
                            type: keyDownArrowUp
                        }, ()=>{
                            const itemCount = this.getItemCount();
                            if (itemCount > 0) {
                                const { highlightedIndex } = this.getState();
                                const nextHighlightedIndex = getNextWrappingIndex(-1, highlightedIndex, itemCount, (index)=>this.getItemNodeFromIndex(index));
                                this.setHighlightedIndex(nextHighlightedIndex, {
                                    type: keyDownArrowUp
                                });
                            }
                        });
                    }
                },
                Enter (event) {
                    if (event.which === 229) {
                        return;
                    }
                    const { isOpen, highlightedIndex } = this.getState();
                    if (isOpen && highlightedIndex != null) {
                        event.preventDefault();
                        const item = this.items[highlightedIndex];
                        const itemNode = this.getItemNodeFromIndex(highlightedIndex);
                        if (item == null || itemNode && itemNode.hasAttribute('disabled')) {
                            return;
                        }
                        this.selectHighlightedItem({
                            type: keyDownEnter
                        });
                    }
                },
                Escape (event) {
                    event.preventDefault();
                    this.reset(_object_spread$x({
                        type: keyDownEscape
                    }, !this.state.isOpen && {
                        selectedItem: null,
                        inputValue: ''
                    }));
                }
            };
            this.buttonKeyDownHandlers = _object_spread_props$r(_object_spread$x({}, this.keyDownHandlers), {
                ' ' (event) {
                    event.preventDefault();
                    this.toggleMenu({
                        type: keyDownSpaceButton
                    });
                }
            });
            this.inputKeyDownHandlers = _object_spread_props$r(_object_spread$x({}, this.keyDownHandlers), {
                Home (event) {
                    const { isOpen } = this.getState();
                    if (!isOpen) {
                        return;
                    }
                    event.preventDefault();
                    const itemCount = this.getItemCount();
                    if (itemCount <= 0 || !isOpen) {
                        return;
                    } // get next non-disabled starting downwards from 0 if that's disabled.
                    const newHighlightedIndex = getNextNonDisabledIndex(1, 0, itemCount, (index)=>this.getItemNodeFromIndex(index), false);
                    this.setHighlightedIndex(newHighlightedIndex, {
                        type: keyDownHome
                    });
                },
                End (event) {
                    const { isOpen } = this.getState();
                    if (!isOpen) {
                        return;
                    }
                    event.preventDefault();
                    const itemCount = this.getItemCount();
                    if (itemCount <= 0 || !isOpen) {
                        return;
                    } // get next non-disabled starting upwards from last index if that's disabled.
                    const newHighlightedIndex = getNextNonDisabledIndex(-1, itemCount - 1, itemCount, (index)=>this.getItemNodeFromIndex(index), false);
                    this.setHighlightedIndex(newHighlightedIndex, {
                        type: keyDownEnd
                    });
                }
            });
            this.getToggleButtonProps = function(_temp3) {
                let _ref = _temp3 === void 0 ? {} : _temp3, { onClick, onPress, onKeyDown, onKeyUp, onBlur } = _ref, rest = _object_without_properties$r(_ref, [
                    "onClick",
                    "onPress",
                    "onKeyDown",
                    "onKeyUp",
                    "onBlur"
                ]);
                const { isOpen } = _this.getState();
                const enabledEventHandlers = {
                    onClick: callAllEventHandlers(onClick, _this.buttonHandleClick),
                    onKeyDown: callAllEventHandlers(onKeyDown, _this.buttonHandleKeyDown),
                    onKeyUp: callAllEventHandlers(onKeyUp, _this.buttonHandleKeyUp),
                    onBlur: callAllEventHandlers(onBlur, _this.buttonHandleBlur)
                };
                const eventHandlers = rest.disabled ? {} : enabledEventHandlers;
                return _object_spread$x({
                    type: 'button',
                    role: 'button',
                    'aria-label': isOpen ? 'close menu' : 'open menu',
                    'aria-haspopup': true,
                    'data-toggle': true
                }, eventHandlers, rest);
            };
            this.buttonHandleKeyUp = (event)=>{
                // Prevent click event from emitting in Firefox
                event.preventDefault();
            };
            this.buttonHandleKeyDown = (event)=>{
                const key = normalizeArrowKey(event);
                if (this.buttonKeyDownHandlers[key]) {
                    this.buttonKeyDownHandlers[key].call(this, event);
                }
            };
            this.buttonHandleClick = (event)=>{
                event.preventDefault(); // handle odd case for Safari and Firefox which
                // don't give the button the focus properly.
                /* istanbul ignore if (can't reasonably test this) */ if (this.props.environment.document.activeElement === this.props.environment.document.body) {
                    event.target.focus();
                } // to simplify testing components that use downshift, we'll not wrap this in a setTimeout
                // if the NODE_ENV is test. With the proper build system, this should be dead code eliminated
                // when building for production and should therefore have no impact on production code.
                if (process.env.NODE_ENV === 'test') {
                    this.toggleMenu({
                        type: clickButton
                    });
                } else {
                    // Ensure that toggle of menu occurs after the potential blur event in iOS
                    this.internalSetTimeout(()=>this.toggleMenu({
                            type: clickButton
                        }));
                }
            };
            this.buttonHandleBlur = (event)=>{
                const blurTarget = event.target; // Save blur target for comparison with activeElement later
                // Need setTimeout, so that when the user presses Tab, the activeElement is the next focused element, not body element
                this.internalSetTimeout(()=>{
                    if (!this.isMouseDown && (this.props.environment.document.activeElement == null || this.props.environment.document.activeElement.id !== this.inputId) && this.props.environment.document.activeElement !== blurTarget // Do nothing if we refocus the same element again (to solve issue in Safari on iOS)
                    ) {
                        this.reset({
                            type: blurButton
                        });
                    }
                });
            };
            this.getLabelProps = (props)=>{
                return _object_spread$x({
                    htmlFor: this.inputId,
                    id: this.labelId
                }, props);
            };
            this.getInputProps = function(_temp4) {
                let _ref = _temp4 === void 0 ? {} : _temp4, { onKeyDown, onBlur, onChange, onInput, onChangeText } = _ref, rest = _object_without_properties$r(_ref, [
                    "onKeyDown",
                    "onBlur",
                    "onChange",
                    "onInput",
                    "onChangeText"
                ]);
                let onChangeKey;
                let eventHandlers = {};
                /* istanbul ignore next (preact) */ {
                    onChangeKey = 'onChange';
                }
                const { inputValue, isOpen, highlightedIndex } = _this.getState();
                if (!rest.disabled) {
                    eventHandlers = {
                        [onChangeKey]: callAllEventHandlers(onChange, onInput, _this.inputHandleChange),
                        onKeyDown: callAllEventHandlers(onKeyDown, _this.inputHandleKeyDown),
                        onBlur: callAllEventHandlers(onBlur, _this.inputHandleBlur)
                    };
                }
                return _object_spread$x({
                    'aria-autocomplete': 'list',
                    'aria-activedescendant': isOpen && typeof highlightedIndex === 'number' && highlightedIndex >= 0 ? _this.getItemId(highlightedIndex) : null,
                    'aria-controls': isOpen ? _this.menuId : null,
                    'aria-labelledby': _this.labelId,
                    // https://developer.mozilla.org/en-US/docs/Web/Security/Securing_your_site/Turning_off_form_autocompletion
                    // revert back since autocomplete="nope" is ignored on latest Chrome and Opera
                    autoComplete: 'off',
                    value: inputValue,
                    id: _this.inputId
                }, eventHandlers, rest);
            };
            this.inputHandleKeyDown = (event)=>{
                const key = normalizeArrowKey(event);
                if (key && this.inputKeyDownHandlers[key]) {
                    this.inputKeyDownHandlers[key].call(this, event);
                }
            };
            this.inputHandleChange = (event)=>{
                this.internalSetState({
                    type: changeInput,
                    isOpen: true,
                    inputValue: event.target.value,
                    highlightedIndex: this.props.defaultHighlightedIndex
                });
            };
            this.inputHandleBlur = ()=>{
                // Need setTimeout, so that when the user presses Tab, the activeElement is the next focused element, not the body element
                this.internalSetTimeout(()=>{
                    const downshiftButtonIsActive = this.props.environment.document && !!this.props.environment.document.activeElement && !!this.props.environment.document.activeElement.dataset && this.props.environment.document.activeElement.dataset.toggle && this._rootNode && this._rootNode.contains(this.props.environment.document.activeElement);
                    if (!this.isMouseDown && !downshiftButtonIsActive) {
                        this.reset({
                            type: blurInput
                        });
                    }
                });
            };
            this.menuRef = (node)=>{
                this._menuNode = node;
            };
            this.getMenuProps = function(_temp5, _temp6) {
                let _ref = _temp5 === void 0 ? {} : _temp5, { refKey = 'ref', ref } = _ref, props = _object_without_properties$r(_ref, [
                    "refKey",
                    "ref"
                ]);
                let { suppressRefError = false } = _temp6 === void 0 ? {} : _temp6;
                _this.getMenuProps.called = true;
                _this.getMenuProps.refKey = refKey;
                _this.getMenuProps.suppressRefError = suppressRefError;
                return _object_spread$x({
                    [refKey]: handleRefs(ref, _this.menuRef),
                    role: 'listbox',
                    'aria-labelledby': props && props['aria-label'] ? null : _this.labelId,
                    id: _this.menuId
                }, props);
            };
            this.getItemProps = function(_temp7) {
                let _ref = _temp7 === void 0 ? {} : _temp7, { onMouseMove, onMouseDown, onClick, onPress, index, item = process.env.NODE_ENV === 'production' ? /* istanbul ignore next */ undefined : requiredProp('getItemProps', 'item') } = _ref, rest = _object_without_properties$r(_ref, [
                    "onMouseMove",
                    "onMouseDown",
                    "onClick",
                    "onPress",
                    "index",
                    "item"
                ]);
                if (index === undefined) {
                    _this.items.push(item);
                    index = _this.items.indexOf(item);
                } else {
                    _this.items[index] = item;
                }
                const onSelectKey = 'onClick';
                const customClickHandler = onClick;
                const enabledEventHandlers = {
                    // onMouseMove is used over onMouseEnter here. onMouseMove
                    // is only triggered on actual mouse movement while onMouseEnter
                    // can fire on DOM changes, interrupting keyboard navigation
                    onMouseMove: callAllEventHandlers(onMouseMove, ()=>{
                        if (index === _this.getState().highlightedIndex) {
                            return;
                        }
                        _this.setHighlightedIndex(index, {
                            type: itemMouseEnter
                        }); // We never want to manually scroll when changing state based
                        // on `onMouseMove` because we will be moving the element out
                        // from under the user which is currently scrolling/moving the
                        // cursor
                        _this.avoidScrolling = true;
                        _this.internalSetTimeout(()=>_this.avoidScrolling = false, 250);
                    }),
                    onMouseDown: callAllEventHandlers(onMouseDown, (event)=>{
                        // This prevents the activeElement from being changed
                        // to the item so it can remain with the current activeElement
                        // which is a more common use case.
                        event.preventDefault();
                    }),
                    [onSelectKey]: callAllEventHandlers(customClickHandler, ()=>{
                        _this.selectItemAtIndex(index, {
                            type: clickItem
                        });
                    })
                }; // Passing down the onMouseDown handler to prevent redirect
                // of the activeElement if clicking on disabled items
                const eventHandlers = rest.disabled ? {
                    onMouseDown: enabledEventHandlers.onMouseDown
                } : enabledEventHandlers;
                return _object_spread$x({
                    id: _this.getItemId(index),
                    role: 'option',
                    'aria-selected': _this.getState().highlightedIndex === index
                }, eventHandlers, rest);
            };
            this.clearItems = ()=>{
                this.items = [];
            };
            this.reset = function(otherStateToSet, cb) {
                if (otherStateToSet === void 0) {
                    otherStateToSet = {};
                }
                otherStateToSet = pickState(otherStateToSet);
                _this.internalSetState((_ref)=>{
                    let { selectedItem } = _ref;
                    return _object_spread$x({
                        isOpen: _this.props.defaultIsOpen,
                        highlightedIndex: _this.props.defaultHighlightedIndex,
                        inputValue: _this.props.itemToString(selectedItem)
                    }, otherStateToSet);
                }, cb);
            };
            this.toggleMenu = function(otherStateToSet, cb) {
                if (otherStateToSet === void 0) {
                    otherStateToSet = {};
                }
                otherStateToSet = pickState(otherStateToSet);
                _this.internalSetState((_ref2)=>{
                    let { isOpen } = _ref2;
                    return _object_spread$x({
                        isOpen: !isOpen
                    }, isOpen && {
                        highlightedIndex: _this.props.defaultHighlightedIndex
                    }, otherStateToSet);
                }, ()=>{
                    const { isOpen, highlightedIndex } = _this.getState();
                    if (isOpen) {
                        if (_this.getItemCount() > 0 && typeof highlightedIndex === 'number') {
                            _this.setHighlightedIndex(highlightedIndex, otherStateToSet);
                        }
                    }
                    cbToCb(cb)();
                });
            };
            this.openMenu = (cb)=>{
                this.internalSetState({
                    isOpen: true
                }, cb);
            };
            this.closeMenu = (cb)=>{
                this.internalSetState({
                    isOpen: false
                }, cb);
            };
            this.updateStatus = debounce(()=>{
                const state = this.getState();
                const item = this.items[state.highlightedIndex];
                const resultCount = this.getItemCount();
                const status = this.props.getA11yStatusMessage(_object_spread$x({
                    itemToString: this.props.itemToString,
                    previousResultCount: this.previousResultCount,
                    resultCount,
                    highlightedItem: item
                }, state));
                this.previousResultCount = resultCount;
                setStatus(status, this.props.environment.document);
            }, 200);
            // fancy destructuring + defaults + aliases
            // this basically says each value of state should either be set to
            // the initial value or the default value if the initial value is not provided
            const { defaultHighlightedIndex, initialHighlightedIndex: _highlightedIndex = defaultHighlightedIndex, defaultIsOpen, initialIsOpen: _isOpen = defaultIsOpen, initialInputValue: _inputValue = '', initialSelectedItem: _selectedItem = null } = this.props;
            const _state = this.getState({
                highlightedIndex: _highlightedIndex,
                isOpen: _isOpen,
                inputValue: _inputValue,
                selectedItem: _selectedItem
            });
            if (_state.selectedItem != null && this.props.initialInputValue === undefined) {
                _state.inputValue = this.props.itemToString(_state.selectedItem);
            }
            this.state = _state;
        }
    }
    Downshift.defaultProps = {
        defaultHighlightedIndex: null,
        defaultIsOpen: false,
        getA11yStatusMessage: getA11yStatusMessage$1,
        itemToString: (i)=>{
            if (i == null) {
                return '';
            }
            if (process.env.NODE_ENV !== 'production' && isPlainObject(i) && !i.hasOwnProperty('toString')) {
                // eslint-disable-next-line no-console
                console.warn('downshift: An object was passed to the default implementation of `itemToString`. You should probably provide your own `itemToString` implementation. Please refer to the `itemToString` API documentation.', 'The object that was passed:', i);
            }
            return String(i);
        },
        onStateChange: noop,
        onInputValueChange: noop,
        onUserAction: noop,
        onChange: noop,
        onSelect: noop,
        onOuterClick: noop,
        selectedItemChanged: (prevItem, item)=>prevItem !== item,
        environment: /* istanbul ignore next (ssr) */ typeof window === 'undefined' ? {} : window,
        stateReducer: (state, stateToSet)=>stateToSet,
        suppressRefError: false,
        scrollIntoView
    };
    Downshift.stateChangeTypes = stateChangeTypes$3;
    return Downshift;
})();
process.env.NODE_ENV !== "production" ? Downshift.propTypes = {
    children: PropTypes.func,
    defaultHighlightedIndex: PropTypes.number,
    defaultIsOpen: PropTypes.bool,
    initialHighlightedIndex: PropTypes.number,
    initialSelectedItem: PropTypes.any,
    initialInputValue: PropTypes.string,
    initialIsOpen: PropTypes.bool,
    getA11yStatusMessage: PropTypes.func,
    itemToString: PropTypes.func,
    onChange: PropTypes.func,
    onSelect: PropTypes.func,
    onStateChange: PropTypes.func,
    onInputValueChange: PropTypes.func,
    onUserAction: PropTypes.func,
    onOuterClick: PropTypes.func,
    selectedItemChanged: PropTypes.func,
    stateReducer: PropTypes.func,
    itemCount: PropTypes.number,
    id: PropTypes.string,
    environment: PropTypes.shape({
        addEventListener: PropTypes.func,
        removeEventListener: PropTypes.func,
        document: PropTypes.shape({
            getElementById: PropTypes.func,
            activeElement: PropTypes.any,
            body: PropTypes.any
        })
    }),
    suppressRefError: PropTypes.bool,
    scrollIntoView: PropTypes.func,
    // things we keep in state for uncontrolled components
    // but can accept as props for controlled components
    /* eslint-disable react/no-unused-prop-types */ selectedItem: PropTypes.any,
    isOpen: PropTypes.bool,
    inputValue: PropTypes.string,
    highlightedIndex: PropTypes.number,
    labelId: PropTypes.string,
    inputId: PropTypes.string,
    menuId: PropTypes.string,
    getItemId: PropTypes.func
} : void 0;
function validateGetMenuPropsCalledCorrectly(node, _ref3) {
    let { refKey } = _ref3;
    if (!node) {
        // eslint-disable-next-line no-console
        console.error(`downshift: The ref prop "${refKey}" from getMenuProps was not applied correctly on your menu element.`);
    }
}
function validateGetRootPropsCalledCorrectly(element, _ref4) {
    let { refKey } = _ref4;
    const refKeySpecified = refKey !== 'ref';
    const isComposite = !isDOMElement(element);
    if (isComposite && !refKeySpecified && !reactIsExports$1.isForwardRef(element)) {
        // eslint-disable-next-line no-console
        console.error('downshift: You returned a non-DOM element. You must specify a refKey in getRootProps');
    } else if (!isComposite && refKeySpecified) {
        // eslint-disable-next-line no-console
        console.error(`downshift: You returned a DOM element. You should not specify a refKey in getRootProps. You specified "${refKey}"`);
    }
    if (!reactIsExports$1.isForwardRef(element) && !getElementProps(element)[refKey]) {
        // eslint-disable-next-line no-console
        console.error(`downshift: You must apply the ref prop "${refKey}" from getRootProps onto your root element.`);
    }
}
const dropdownDefaultStateValues = {
    highlightedIndex: -1,
    isOpen: false,
    selectedItem: null,
    inputValue: ''
};
function callOnChangeProps(action, state, newState) {
    const { props, type } = action;
    const changes = {};
    Object.keys(state).forEach((key)=>{
        invokeOnChangeHandler(key, action, state, newState);
        if (newState[key] !== state[key]) {
            changes[key] = newState[key];
        }
    });
    if (props.onStateChange && Object.keys(changes).length) {
        props.onStateChange(_object_spread$x({
            type
        }, changes));
    }
}
function invokeOnChangeHandler(key, action, state, newState) {
    const { props, type } = action;
    const handler = `on${capitalizeString(key)}Change`;
    if (props[handler] && newState[key] !== undefined && newState[key] !== state[key]) {
        props[handler](_object_spread$x({
            type
        }, newState));
    }
}
/**
 * Default state reducer that returns the changes.
 *
 * @param {Object} s state.
 * @param {Object} a action with changes.
 * @returns {Object} changes.
 */ function stateReducer(s, a) {
    return a.changes;
}
/**
 * Returns a message to be added to aria-live region when item is selected.
 *
 * @param {Object} selectionParameters Parameters required to build the message.
 * @returns {string} The a11y message.
 */ function getA11ySelectionMessage(selectionParameters) {
    const { selectedItem, itemToString: itemToStringLocal } = selectionParameters;
    return selectedItem ? `${itemToStringLocal(selectedItem)} has been selected.` : '';
}
/**
 * Debounced call for updating the a11y message.
 */ const updateA11yStatus = debounce((getA11yMessage, document1)=>{
    setStatus(getA11yMessage(), document1);
}, 200); // istanbul ignore next
const useIsomorphicLayoutEffect = typeof window !== 'undefined' && typeof window.document !== 'undefined' && typeof window.document.createElement !== 'undefined' ? useLayoutEffect : useEffect;
function useElementIds(_ref) {
    let { id = `downshift-${generateId()}`, labelId, menuId, getItemId, toggleButtonId, inputId } = _ref;
    const elementIdsRef = useRef({
        labelId: labelId || `${id}-label`,
        menuId: menuId || `${id}-menu`,
        getItemId: getItemId || ((index)=>`${id}-item-${index}`),
        toggleButtonId: toggleButtonId || `${id}-toggle-button`,
        inputId: inputId || `${id}-input`
    });
    return elementIdsRef.current;
}
function getItemIndex(index, item, items) {
    if (index !== undefined) {
        return index;
    }
    if (items.length === 0) {
        return -1;
    }
    return items.indexOf(item);
}
function itemToString(item) {
    return item ? String(item) : '';
}
function capitalizeString(string) {
    return `${string.slice(0, 1).toUpperCase()}${string.slice(1)}`;
}
function useLatestRef(val) {
    const ref = useRef(val); // technically this is not "concurrent mode safe" because we're manipulating
    // the value during render (so it's not idempotent). However, the places this
    // hook is used is to support memoizing callbacks which will be called
    // *during* render, so we need the latest values *during* render.
    // If not for this, then we'd probably want to use useLayoutEffect instead.
    ref.current = val;
    return ref;
}
/**
 * Computes the controlled state using a the previous state, props,
 * two reducers, one from downshift and an optional one from the user.
 * Also calls the onChange handlers for state values that have changed.
 *
 * @param {Function} reducer Reducer function from downshift.
 * @param {Object} initialState Initial state of the hook.
 * @param {Object} props The hook props.
 * @returns {Array} An array with the state and an action dispatcher.
 */ function useEnhancedReducer(reducer, initialState, props) {
    const prevStateRef = useRef();
    const actionRef = useRef();
    const enhancedReducer = useCallback((state, action)=>{
        actionRef.current = action;
        state = getState(state, action.props);
        const changes = reducer(state, action);
        const newState = action.props.stateReducer(state, _object_spread_props$r(_object_spread$x({}, action), {
            changes
        }));
        return newState;
    }, [
        reducer
    ]);
    const [state, dispatch] = useReducer(enhancedReducer, initialState);
    const propsRef = useLatestRef(props);
    const dispatchWithProps = useCallback((action)=>dispatch(_object_spread$x({
            props: propsRef.current
        }, action)), [
        propsRef
    ]);
    const action = actionRef.current;
    useEffect(()=>{
        if (action && prevStateRef.current && prevStateRef.current !== state) {
            callOnChangeProps(action, getState(prevStateRef.current, action.props), state);
        }
        prevStateRef.current = state;
    }, [
        state,
        props,
        action
    ]);
    return [
        state,
        dispatchWithProps
    ];
}
const defaultProps$3 = {
    itemToString,
    stateReducer,
    getA11ySelectionMessage,
    scrollIntoView,
    circularNavigation: false,
    environment: /* istanbul ignore next (ssr) */ typeof window === 'undefined' ? {} : window
};
function getDefaultValue$1(props, propKey, defaultStateValues) {
    if (defaultStateValues === void 0) {
        defaultStateValues = dropdownDefaultStateValues;
    }
    const defaultValue = props[`default${capitalizeString(propKey)}`];
    if (defaultValue !== undefined) {
        return defaultValue;
    }
    return defaultStateValues[propKey];
}
function getInitialValue$1(props, propKey, defaultStateValues) {
    if (defaultStateValues === void 0) {
        defaultStateValues = dropdownDefaultStateValues;
    }
    const value = props[propKey];
    if (value !== undefined) {
        return value;
    }
    const initialValue = props[`initial${capitalizeString(propKey)}`];
    if (initialValue !== undefined) {
        return initialValue;
    }
    return getDefaultValue$1(props, propKey, defaultStateValues);
}
function getInitialState$2(props) {
    const selectedItem = getInitialValue$1(props, 'selectedItem');
    const isOpen = getInitialValue$1(props, 'isOpen');
    const highlightedIndex = getInitialValue$1(props, 'highlightedIndex');
    const inputValue = getInitialValue$1(props, 'inputValue');
    return {
        highlightedIndex: highlightedIndex < 0 && selectedItem && isOpen ? props.items.indexOf(selectedItem) : highlightedIndex,
        isOpen,
        selectedItem,
        inputValue
    };
}
function getHighlightedIndexOnOpen(props, state, offset, getItemNodeFromIndex) {
    const { items, initialHighlightedIndex, defaultHighlightedIndex } = props;
    const { selectedItem, highlightedIndex } = state;
    if (items.length === 0) {
        return -1;
    } // initialHighlightedIndex will give value to highlightedIndex on initial state only.
    if (initialHighlightedIndex !== undefined && highlightedIndex === initialHighlightedIndex) {
        return initialHighlightedIndex;
    }
    if (defaultHighlightedIndex !== undefined) {
        return defaultHighlightedIndex;
    }
    if (selectedItem) {
        if (offset === 0) {
            return items.indexOf(selectedItem);
        }
        return getNextWrappingIndex(offset, items.indexOf(selectedItem), items.length, getItemNodeFromIndex, false);
    }
    if (offset === 0) {
        return -1;
    }
    return offset < 0 ? items.length - 1 : 0;
}
/**
 * Reuse the movement tracking of mouse and touch events.
 *
 * @param {boolean} isOpen Whether the dropdown is open or not.
 * @param {Array<Object>} downshiftElementRefs Downshift element refs to track movement (toggleButton, menu etc.)
 * @param {Object} environment Environment where component/hook exists.
 * @param {Function} handleBlur Handler on blur from mouse or touch.
 * @returns {Object} Ref containing whether mouseDown or touchMove event is happening
 */ function useMouseAndTouchTracker(isOpen, downshiftElementRefs, environment, handleBlur) {
    const mouseAndTouchTrackersRef = useRef({
        isMouseDown: false,
        isTouchMove: false
    });
    useEffect(()=>{
        // The same strategy for checking if a click occurred inside or outside downsift
        // as in downshift.js.
        const onMouseDown = ()=>{
            mouseAndTouchTrackersRef.current.isMouseDown = true;
        };
        const onMouseUp = (event)=>{
            mouseAndTouchTrackersRef.current.isMouseDown = false;
            if (isOpen && !targetWithinDownshift(event.target, downshiftElementRefs.map((ref)=>ref.current), environment)) {
                handleBlur();
            }
        };
        const onTouchStart = ()=>{
            mouseAndTouchTrackersRef.current.isTouchMove = false;
        };
        const onTouchMove = ()=>{
            mouseAndTouchTrackersRef.current.isTouchMove = true;
        };
        const onTouchEnd = (event)=>{
            if (isOpen && !mouseAndTouchTrackersRef.current.isTouchMove && !targetWithinDownshift(event.target, downshiftElementRefs.map((ref)=>ref.current), environment, false)) {
                handleBlur();
            }
        };
        environment.addEventListener('mousedown', onMouseDown);
        environment.addEventListener('mouseup', onMouseUp);
        environment.addEventListener('touchstart', onTouchStart);
        environment.addEventListener('touchmove', onTouchMove);
        environment.addEventListener('touchend', onTouchEnd);
        return function cleanup() {
            environment.removeEventListener('mousedown', onMouseDown);
            environment.removeEventListener('mouseup', onMouseUp);
            environment.removeEventListener('touchstart', onTouchStart);
            environment.removeEventListener('touchmove', onTouchMove);
            environment.removeEventListener('touchend', onTouchEnd);
        }; // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isOpen,
        environment
    ]);
    return mouseAndTouchTrackersRef;
}
/* istanbul ignore next */ // eslint-disable-next-line import/no-mutable-exports
let useGetterPropsCalledChecker = ()=>noop;
/**
 * Custom hook that checks if getter props are called correctly.
 *
 * @param  {...any} propKeys Getter prop names to be handled.
 * @returns {Function} Setter function called inside getter props to set call information.
 */ /* istanbul ignore next */ if (process.env.NODE_ENV !== 'production') {
    useGetterPropsCalledChecker = function() {
        const isInitialMountRef = useRef(true);
        for(var _len = arguments.length, propKeys = new Array(_len), _key = 0; _key < _len; _key++){
            propKeys[_key] = arguments[_key];
        }
        const getterPropsCalledRef = useRef(propKeys.reduce((acc, propKey)=>{
            acc[propKey] = {};
            return acc;
        }, {}));
        useEffect(()=>{
            Object.keys(getterPropsCalledRef.current).forEach((propKey)=>{
                const propCallInfo = getterPropsCalledRef.current[propKey];
                if (isInitialMountRef.current) {
                    if (!Object.keys(propCallInfo).length) {
                        // eslint-disable-next-line no-console
                        console.error(`downshift: You forgot to call the ${propKey} getter function on your component / element.`);
                        return;
                    }
                }
                const { suppressRefError, refKey, elementRef } = propCallInfo;
                if ((!elementRef || !elementRef.current) && !suppressRefError) {
                    // eslint-disable-next-line no-console
                    console.error(`downshift: The ref prop "${refKey}" from ${propKey} was not applied correctly on your element.`);
                }
            });
            isInitialMountRef.current = false;
        });
        const setGetterPropCallInfo = useCallback((propKey, suppressRefError, refKey, elementRef)=>{
            getterPropsCalledRef.current[propKey] = {
                suppressRefError,
                refKey,
                elementRef
            };
        }, []);
        return setGetterPropCallInfo;
    };
}
function useA11yMessageSetter(getA11yMessage, dependencyArray, _ref2) {
    let { isInitialMount, highlightedIndex, items, environment } = _ref2, rest = _object_without_properties$r(_ref2, [
        "isInitialMount",
        "highlightedIndex",
        "items",
        "environment"
    ]);
    // Sets a11y status message on changes in state.
    useEffect(()=>{
        if (isInitialMount || false) {
            return;
        }
        updateA11yStatus(()=>getA11yMessage(_object_spread$x({
                highlightedIndex,
                highlightedItem: items[highlightedIndex],
                resultCount: items.length
            }, rest)), environment.document); // eslint-disable-next-line react-hooks/exhaustive-deps
    }, dependencyArray);
}
function useScrollIntoView(_ref3) {
    let { highlightedIndex, isOpen, itemRefs, getItemNodeFromIndex, menuElement, scrollIntoView: scrollIntoViewProp } = _ref3;
    // used not to scroll on highlight by mouse.
    const shouldScrollRef = useRef(true); // Scroll on highlighted item if change comes from keyboard.
    useIsomorphicLayoutEffect(()=>{
        if (highlightedIndex < 0 || !isOpen || !Object.keys(itemRefs.current).length) {
            return;
        }
        if (shouldScrollRef.current === false) {
            shouldScrollRef.current = true;
        } else {
            scrollIntoViewProp(getItemNodeFromIndex(highlightedIndex), menuElement);
        } // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        highlightedIndex
    ]);
    return shouldScrollRef;
} // eslint-disable-next-line import/no-mutable-exports
let useControlPropsValidator = noop;
/* istanbul ignore next */ if (process.env.NODE_ENV !== 'production') {
    useControlPropsValidator = (_ref4)=>{
        let { isInitialMount, props, state } = _ref4;
        // used for checking when props are moving from controlled to uncontrolled.
        const prevPropsRef = useRef(props);
        useEffect(()=>{
            if (isInitialMount) {
                return;
            }
            validateControlledUnchanged(state, prevPropsRef.current, props);
            prevPropsRef.current = props;
        }, [
            state,
            props,
            isInitialMount
        ]);
    };
}
/* eslint-disable complexity */ function downshiftCommonReducer(state, action, stateChangeTypes) {
    const { type, props } = action;
    let changes;
    switch(type){
        case stateChangeTypes.ItemMouseMove:
            changes = {
                highlightedIndex: action.disabled ? -1 : action.index
            };
            break;
        case stateChangeTypes.MenuMouseLeave:
            changes = {
                highlightedIndex: -1
            };
            break;
        case stateChangeTypes.ToggleButtonClick:
        case stateChangeTypes.FunctionToggleMenu:
            changes = {
                isOpen: !state.isOpen,
                highlightedIndex: state.isOpen ? -1 : getHighlightedIndexOnOpen(props, state, 0)
            };
            break;
        case stateChangeTypes.FunctionOpenMenu:
            changes = {
                isOpen: true,
                highlightedIndex: getHighlightedIndexOnOpen(props, state, 0)
            };
            break;
        case stateChangeTypes.FunctionCloseMenu:
            changes = {
                isOpen: false
            };
            break;
        case stateChangeTypes.FunctionSetHighlightedIndex:
            changes = {
                highlightedIndex: action.highlightedIndex
            };
            break;
        case stateChangeTypes.FunctionSetInputValue:
            changes = {
                inputValue: action.inputValue
            };
            break;
        case stateChangeTypes.FunctionReset:
            changes = {
                highlightedIndex: getDefaultValue$1(props, 'highlightedIndex'),
                isOpen: getDefaultValue$1(props, 'isOpen'),
                selectedItem: getDefaultValue$1(props, 'selectedItem'),
                inputValue: getDefaultValue$1(props, 'inputValue')
            };
            break;
        default:
            throw new Error('Reducer called without proper action type.');
    }
    return _object_spread$x({}, state, changes);
}
({
    items: PropTypes.array.isRequired,
    itemToString: PropTypes.func,
    getA11yStatusMessage: PropTypes.func,
    getA11ySelectionMessage: PropTypes.func,
    circularNavigation: PropTypes.bool,
    highlightedIndex: PropTypes.number,
    defaultHighlightedIndex: PropTypes.number,
    initialHighlightedIndex: PropTypes.number,
    isOpen: PropTypes.bool,
    defaultIsOpen: PropTypes.bool,
    initialIsOpen: PropTypes.bool,
    selectedItem: PropTypes.any,
    initialSelectedItem: PropTypes.any,
    defaultSelectedItem: PropTypes.any,
    id: PropTypes.string,
    labelId: PropTypes.string,
    menuId: PropTypes.string,
    getItemId: PropTypes.func,
    toggleButtonId: PropTypes.string,
    stateReducer: PropTypes.func,
    onSelectedItemChange: PropTypes.func,
    onHighlightedIndexChange: PropTypes.func,
    onStateChange: PropTypes.func,
    onIsOpenChange: PropTypes.func,
    environment: PropTypes.shape({
        addEventListener: PropTypes.func,
        removeEventListener: PropTypes.func,
        document: PropTypes.shape({
            getElementById: PropTypes.func,
            activeElement: PropTypes.any,
            body: PropTypes.any
        })
    })
});
/**
 * Default implementation for status message. Only added when menu is open.
 * Will specift if there are results in the list, and if so, how many,
 * and what keys are relevant.
 *
 * @param {Object} param the downshift state and other relevant properties
 * @return {String} the a11y status message
 */ function getA11yStatusMessage(_a) {
    var isOpen = _a.isOpen, resultCount = _a.resultCount, previousResultCount = _a.previousResultCount;
    if (!isOpen) {
        return '';
    }
    if (!resultCount) {
        return 'No results are available.';
    }
    if (resultCount !== previousResultCount) {
        return "".concat(resultCount, " result").concat(resultCount === 1 ? ' is' : 's are', " available, use up and down arrow keys to navigate. Press Enter or Space Bar keys to select.");
    }
    return '';
}
__assign(__assign({}, defaultProps$3), {
    getA11yStatusMessage: getA11yStatusMessage
});
/* istanbul ignore next */ if (process.env.NODE_ENV !== 'production') ;
process.env.NODE_ENV !== "production" ? '__menu_keydown_arrow_down__' : 0;
process.env.NODE_ENV !== "production" ? '__menu_keydown_arrow_up__' : 1;
process.env.NODE_ENV !== "production" ? '__menu_keydown_escape__' : 2;
process.env.NODE_ENV !== "production" ? '__menu_keydown_home__' : 3;
process.env.NODE_ENV !== "production" ? '__menu_keydown_end__' : 4;
process.env.NODE_ENV !== "production" ? '__menu_keydown_enter__' : 5;
process.env.NODE_ENV !== "production" ? '__menu_keydown_space_button__' : 6;
process.env.NODE_ENV !== "production" ? '__menu_keydown_character__' : 7;
process.env.NODE_ENV !== "production" ? '__menu_blur__' : 8;
process.env.NODE_ENV !== "production" ? '__menu_mouse_leave__' : 9;
process.env.NODE_ENV !== "production" ? '__item_mouse_move__' : 10;
process.env.NODE_ENV !== "production" ? '__item_click__' : 11;
process.env.NODE_ENV !== "production" ? '__togglebutton_click__' : 12;
process.env.NODE_ENV !== "production" ? '__togglebutton_keydown_arrow_down__' : 13;
process.env.NODE_ENV !== "production" ? '__togglebutton_keydown_arrow_up__' : 14;
process.env.NODE_ENV !== "production" ? '__togglebutton_keydown_character__' : 15;
process.env.NODE_ENV !== "production" ? '__function_toggle_menu__' : 16;
process.env.NODE_ENV !== "production" ? '__function_open_menu__' : 17;
process.env.NODE_ENV !== "production" ? '__function_close_menu__' : 18;
process.env.NODE_ENV !== "production" ? '__function_set_highlighted_index__' : 19;
process.env.NODE_ENV !== "production" ? '__function_select_item__' : 20;
process.env.NODE_ENV !== "production" ? '__function_set_input_value__' : 21;
process.env.NODE_ENV !== "production" ? '__function_reset__' : 22;
const InputKeyDownArrowDown = process.env.NODE_ENV !== "production" ? '__input_keydown_arrow_down__' : 0;
const InputKeyDownArrowUp = process.env.NODE_ENV !== "production" ? '__input_keydown_arrow_up__' : 1;
const InputKeyDownEscape = process.env.NODE_ENV !== "production" ? '__input_keydown_escape__' : 2;
const InputKeyDownHome = process.env.NODE_ENV !== "production" ? '__input_keydown_home__' : 3;
const InputKeyDownEnd = process.env.NODE_ENV !== "production" ? '__input_keydown_end__' : 4;
const InputKeyDownEnter = process.env.NODE_ENV !== "production" ? '__input_keydown_enter__' : 5;
const InputChange = process.env.NODE_ENV !== "production" ? '__input_change__' : 6;
const InputBlur = process.env.NODE_ENV !== "production" ? '__input_blur__' : 7;
const MenuMouseLeave = process.env.NODE_ENV !== "production" ? '__menu_mouse_leave__' : 8;
const ItemMouseMove = process.env.NODE_ENV !== "production" ? '__item_mouse_move__' : 9;
const ItemClick = process.env.NODE_ENV !== "production" ? '__item_click__' : 10;
const ToggleButtonClick = process.env.NODE_ENV !== "production" ? '__togglebutton_click__' : 11;
const FunctionToggleMenu = process.env.NODE_ENV !== "production" ? '__function_toggle_menu__' : 12;
const FunctionOpenMenu = process.env.NODE_ENV !== "production" ? '__function_open_menu__' : 13;
const FunctionCloseMenu = process.env.NODE_ENV !== "production" ? '__function_close_menu__' : 14;
const FunctionSetHighlightedIndex = process.env.NODE_ENV !== "production" ? '__function_set_highlighted_index__' : 15;
const FunctionSelectItem = process.env.NODE_ENV !== "production" ? '__function_select_item__' : 16;
const FunctionSetInputValue = process.env.NODE_ENV !== "production" ? '__function_set_input_value__' : 17;
const FunctionReset$1 = process.env.NODE_ENV !== "production" ? '__function_reset__' : 18;
const ControlledPropUpdatedSelectedItem = process.env.NODE_ENV !== "production" ? '__controlled_prop_updated_selected_item__' : 19;
var stateChangeTypes$1 = /*#__PURE__*/ Object.freeze({
    __proto__: null,
    InputKeyDownArrowDown: InputKeyDownArrowDown,
    InputKeyDownArrowUp: InputKeyDownArrowUp,
    InputKeyDownEscape: InputKeyDownEscape,
    InputKeyDownHome: InputKeyDownHome,
    InputKeyDownEnd: InputKeyDownEnd,
    InputKeyDownEnter: InputKeyDownEnter,
    InputChange: InputChange,
    InputBlur: InputBlur,
    MenuMouseLeave: MenuMouseLeave,
    ItemMouseMove: ItemMouseMove,
    ItemClick: ItemClick,
    ToggleButtonClick: ToggleButtonClick,
    FunctionToggleMenu: FunctionToggleMenu,
    FunctionOpenMenu: FunctionOpenMenu,
    FunctionCloseMenu: FunctionCloseMenu,
    FunctionSetHighlightedIndex: FunctionSetHighlightedIndex,
    FunctionSelectItem: FunctionSelectItem,
    FunctionSetInputValue: FunctionSetInputValue,
    FunctionReset: FunctionReset$1,
    ControlledPropUpdatedSelectedItem: ControlledPropUpdatedSelectedItem
});
function getInitialState$1(props) {
    const initialState = getInitialState$2(props);
    const { selectedItem } = initialState;
    let { inputValue } = initialState;
    if (inputValue === '' && selectedItem && props.defaultInputValue === undefined && props.initialInputValue === undefined && props.inputValue === undefined) {
        inputValue = props.itemToString(selectedItem);
    }
    return _object_spread_props$r(_object_spread$x({}, initialState), {
        inputValue
    });
}
const propTypes$1 = {
    items: PropTypes.array.isRequired,
    itemToString: PropTypes.func,
    getA11yStatusMessage: PropTypes.func,
    getA11ySelectionMessage: PropTypes.func,
    circularNavigation: PropTypes.bool,
    highlightedIndex: PropTypes.number,
    defaultHighlightedIndex: PropTypes.number,
    initialHighlightedIndex: PropTypes.number,
    isOpen: PropTypes.bool,
    defaultIsOpen: PropTypes.bool,
    initialIsOpen: PropTypes.bool,
    selectedItem: PropTypes.any,
    initialSelectedItem: PropTypes.any,
    defaultSelectedItem: PropTypes.any,
    inputValue: PropTypes.string,
    defaultInputValue: PropTypes.string,
    initialInputValue: PropTypes.string,
    id: PropTypes.string,
    labelId: PropTypes.string,
    menuId: PropTypes.string,
    getItemId: PropTypes.func,
    inputId: PropTypes.string,
    toggleButtonId: PropTypes.string,
    stateReducer: PropTypes.func,
    onSelectedItemChange: PropTypes.func,
    onHighlightedIndexChange: PropTypes.func,
    onStateChange: PropTypes.func,
    onIsOpenChange: PropTypes.func,
    onInputValueChange: PropTypes.func,
    environment: PropTypes.shape({
        addEventListener: PropTypes.func,
        removeEventListener: PropTypes.func,
        document: PropTypes.shape({
            getElementById: PropTypes.func,
            activeElement: PropTypes.any,
            body: PropTypes.any
        })
    })
};
/**
 * The useCombobox version of useControlledReducer, which also
 * checks if the controlled prop selectedItem changed between
 * renders. If so, it will also update inputValue with its
 * string equivalent. It uses the common useEnhancedReducer to
 * compute the rest of the state.
 *
 * @param {Function} reducer Reducer function from downshift.
 * @param {Object} initialState Initial state of the hook.
 * @param {Object} props The hook props.
 * @returns {Array} An array with the state and an action dispatcher.
 */ function useControlledReducer(reducer, initialState, props) {
    const previousSelectedItemRef = useRef();
    const [state, dispatch] = useEnhancedReducer(reducer, initialState, props); // ToDo: if needed, make same approach as selectedItemChanged from Downshift.
    useEffect(()=>{
        if (isControlledProp(props, 'selectedItem')) {
            if (previousSelectedItemRef.current !== props.selectedItem) {
                dispatch({
                    type: ControlledPropUpdatedSelectedItem,
                    inputValue: props.itemToString(props.selectedItem)
                });
            }
            previousSelectedItemRef.current = state.selectedItem === previousSelectedItemRef.current ? props.selectedItem : state.selectedItem;
        }
    });
    return [
        getState(state, props),
        dispatch
    ];
} // eslint-disable-next-line import/no-mutable-exports
let validatePropTypes$1 = noop;
/* istanbul ignore next */ if (process.env.NODE_ENV !== 'production') {
    validatePropTypes$1 = (options, caller)=>{
        PropTypes.checkPropTypes(propTypes$1, options, 'prop', caller.name);
    };
}
const defaultProps$1 = _object_spread_props$r(_object_spread$x({}, defaultProps$3), {
    getA11yStatusMessage: getA11yStatusMessage$1,
    circularNavigation: true
});
/* eslint-disable complexity */ function downshiftUseComboboxReducer(state, action) {
    const { type, props, shiftKey } = action;
    let changes;
    switch(type){
        case ItemClick:
            changes = {
                isOpen: getDefaultValue$1(props, 'isOpen'),
                highlightedIndex: getDefaultValue$1(props, 'highlightedIndex'),
                selectedItem: props.items[action.index],
                inputValue: props.itemToString(props.items[action.index])
            };
            break;
        case InputKeyDownArrowDown:
            if (state.isOpen) {
                changes = {
                    highlightedIndex: getNextWrappingIndex(shiftKey ? 5 : 1, state.highlightedIndex, props.items.length, action.getItemNodeFromIndex, props.circularNavigation)
                };
            } else {
                changes = {
                    highlightedIndex: getHighlightedIndexOnOpen(props, state, 1, action.getItemNodeFromIndex),
                    isOpen: props.items.length >= 0
                };
            }
            break;
        case InputKeyDownArrowUp:
            if (state.isOpen) {
                changes = {
                    highlightedIndex: getNextWrappingIndex(shiftKey ? -5 : -1, state.highlightedIndex, props.items.length, action.getItemNodeFromIndex, props.circularNavigation)
                };
            } else {
                changes = {
                    highlightedIndex: getHighlightedIndexOnOpen(props, state, -1, action.getItemNodeFromIndex),
                    isOpen: props.items.length >= 0
                };
            }
            break;
        case InputKeyDownEnter:
            changes = _object_spread$x({}, state.isOpen && state.highlightedIndex >= 0 && {
                selectedItem: props.items[state.highlightedIndex],
                isOpen: getDefaultValue$1(props, 'isOpen'),
                highlightedIndex: getDefaultValue$1(props, 'highlightedIndex'),
                inputValue: props.itemToString(props.items[state.highlightedIndex])
            });
            break;
        case InputKeyDownEscape:
            changes = _object_spread$x({
                isOpen: false,
                highlightedIndex: -1
            }, !state.isOpen && {
                selectedItem: null,
                inputValue: ''
            });
            break;
        case InputKeyDownHome:
            changes = {
                highlightedIndex: getNextNonDisabledIndex(1, 0, props.items.length, action.getItemNodeFromIndex, false)
            };
            break;
        case InputKeyDownEnd:
            changes = {
                highlightedIndex: getNextNonDisabledIndex(-1, props.items.length - 1, props.items.length, action.getItemNodeFromIndex, false)
            };
            break;
        case InputBlur:
            changes = _object_spread$x({
                isOpen: false,
                highlightedIndex: -1
            }, state.highlightedIndex >= 0 && action.selectItem && {
                selectedItem: props.items[state.highlightedIndex],
                inputValue: props.itemToString(props.items[state.highlightedIndex])
            });
            break;
        case InputChange:
            changes = {
                isOpen: true,
                highlightedIndex: getDefaultValue$1(props, 'highlightedIndex'),
                inputValue: action.inputValue
            };
            break;
        case FunctionSelectItem:
            changes = {
                selectedItem: action.selectedItem,
                inputValue: props.itemToString(action.selectedItem)
            };
            break;
        case ControlledPropUpdatedSelectedItem:
            changes = {
                inputValue: action.inputValue
            };
            break;
        default:
            return downshiftCommonReducer(state, action, stateChangeTypes$1);
    }
    return _object_spread$x({}, state, changes);
}
/* eslint-enable complexity */ /* eslint-disable max-statements */ useCombobox.stateChangeTypes = stateChangeTypes$1;
function useCombobox(userProps) {
    if (userProps === void 0) {
        userProps = {};
    }
    validatePropTypes$1(userProps, useCombobox); // Props defaults and destructuring.
    const props = _object_spread$x({}, defaultProps$1, userProps);
    const { initialIsOpen, defaultIsOpen, items, scrollIntoView, environment, getA11yStatusMessage, getA11ySelectionMessage, itemToString } = props; // Initial state depending on controlled props.
    const initialState = getInitialState$1(props);
    const [state, dispatch] = useControlledReducer(downshiftUseComboboxReducer, initialState, props);
    const { isOpen, highlightedIndex, selectedItem, inputValue } = state; // Element refs.
    const menuRef = useRef(null);
    const itemRefs = useRef({});
    const inputRef = useRef(null);
    const toggleButtonRef = useRef(null);
    const comboboxRef = useRef(null);
    const isInitialMountRef = useRef(true); // prevent id re-generation between renders.
    const elementIds = useElementIds(props); // used to keep track of how many items we had on previous cycle.
    const previousResultCountRef = useRef(); // utility callback to get item element.
    const latest = useLatestRef({
        state,
        props
    });
    const getItemNodeFromIndex = useCallback((index)=>itemRefs.current[elementIds.getItemId(index)], [
        elementIds
    ]); // Effects.
    // Sets a11y status message on changes in state.
    useA11yMessageSetter(getA11yStatusMessage, [
        isOpen,
        highlightedIndex,
        inputValue,
        items
    ], _object_spread$x({
        isInitialMount: isInitialMountRef.current,
        previousResultCount: previousResultCountRef.current,
        items,
        environment,
        itemToString
    }, state)); // Sets a11y status message on changes in selectedItem.
    useA11yMessageSetter(getA11ySelectionMessage, [
        selectedItem
    ], _object_spread$x({
        isInitialMount: isInitialMountRef.current,
        previousResultCount: previousResultCountRef.current,
        items,
        environment,
        itemToString
    }, state)); // Scroll on highlighted item if change comes from keyboard.
    const shouldScrollRef = useScrollIntoView({
        menuElement: menuRef.current,
        highlightedIndex,
        isOpen,
        itemRefs,
        scrollIntoView,
        getItemNodeFromIndex
    });
    useControlPropsValidator({
        isInitialMount: isInitialMountRef.current,
        props,
        state
    }); // Focus the input on first render if required.
    useEffect(()=>{
        const focusOnOpen = initialIsOpen || defaultIsOpen || isOpen;
        if (focusOnOpen && inputRef.current) {
            inputRef.current.focus();
        } // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(()=>{
        if (isInitialMountRef.current) {
            return;
        }
        previousResultCountRef.current = items.length;
    }); // Add mouse/touch events to document.
    const mouseAndTouchTrackersRef = useMouseAndTouchTracker(isOpen, [
        comboboxRef,
        menuRef,
        toggleButtonRef
    ], environment, ()=>{
        dispatch({
            type: InputBlur,
            selectItem: false
        });
    });
    const setGetterPropCallInfo = useGetterPropsCalledChecker('getInputProps', 'getComboboxProps', 'getMenuProps'); // Make initial ref false.
    useEffect(()=>{
        isInitialMountRef.current = false;
    }, []); // Reset itemRefs on close.
    useEffect(()=>{
        if (!isOpen) {
            itemRefs.current = {};
        }
    }, [
        isOpen
    ]);
    /* Event handler functions */ const inputKeyDownHandlers = useMemo(()=>({
            ArrowDown (event) {
                event.preventDefault();
                dispatch({
                    type: InputKeyDownArrowDown,
                    shiftKey: event.shiftKey,
                    getItemNodeFromIndex
                });
            },
            ArrowUp (event) {
                event.preventDefault();
                dispatch({
                    type: InputKeyDownArrowUp,
                    shiftKey: event.shiftKey,
                    getItemNodeFromIndex
                });
            },
            Home (event) {
                if (!latest.current.state.isOpen) {
                    return;
                }
                event.preventDefault();
                dispatch({
                    type: InputKeyDownHome,
                    getItemNodeFromIndex
                });
            },
            End (event) {
                if (!latest.current.state.isOpen) {
                    return;
                }
                event.preventDefault();
                dispatch({
                    type: InputKeyDownEnd,
                    getItemNodeFromIndex
                });
            },
            Escape (event) {
                const latestState = latest.current.state;
                if (latestState.isOpen || latestState.inputValue || latestState.selectedItem || latestState.highlightedIndex > -1) {
                    event.preventDefault();
                    dispatch({
                        type: InputKeyDownEscape
                    });
                }
            },
            Enter (event) {
                const latestState = latest.current.state; // if closed or no highlighted index, do nothing.
                if (!latestState.isOpen || latestState.highlightedIndex < 0 || event.which === 229 // if IME composing, wait for next Enter keydown event.
                ) {
                    return;
                }
                event.preventDefault();
                dispatch({
                    type: InputKeyDownEnter,
                    getItemNodeFromIndex
                });
            }
        }), [
        dispatch,
        latest,
        getItemNodeFromIndex
    ]); // Getter props.
    const getLabelProps = useCallback((labelProps)=>_object_spread$x({
            id: elementIds.labelId,
            htmlFor: elementIds.inputId
        }, labelProps), [
        elementIds
    ]);
    const getMenuProps = useCallback(function(_temp, _temp2) {
        let _ref = _temp === void 0 ? {} : _temp, { onMouseLeave, refKey = 'ref', ref } = _ref, rest = _object_without_properties$r(_ref, [
            "onMouseLeave",
            "refKey",
            "ref"
        ]);
        let { suppressRefError = false } = _temp2 === void 0 ? {} : _temp2;
        setGetterPropCallInfo('getMenuProps', suppressRefError, refKey, menuRef);
        return _object_spread$x({
            [refKey]: handleRefs(ref, (menuNode)=>{
                menuRef.current = menuNode;
            }),
            id: elementIds.menuId,
            role: 'listbox',
            'aria-labelledby': elementIds.labelId,
            onMouseLeave: callAllEventHandlers(onMouseLeave, ()=>{
                dispatch({
                    type: MenuMouseLeave
                });
            })
        }, rest);
    }, [
        dispatch,
        setGetterPropCallInfo,
        elementIds
    ]);
    const getItemProps = useCallback(function(_temp3) {
        let _ref = _temp3 === void 0 ? {} : _temp3, { item, index, refKey = 'ref', ref, onMouseMove, onMouseDown, onClick, onPress, disabled } = _ref, rest = _object_without_properties$r(_ref, [
            "item",
            "index",
            "refKey",
            "ref",
            "onMouseMove",
            "onMouseDown",
            "onClick",
            "onPress",
            "disabled"
        ]);
        const { props: latestProps, state: latestState } = latest.current;
        const itemIndex = getItemIndex(index, item, latestProps.items);
        if (itemIndex < 0) {
            throw new Error('Pass either item or item index in getItemProps!');
        }
        const onSelectKey = 'onClick';
        const customClickHandler = onClick;
        const itemHandleMouseMove = ()=>{
            if (index === latestState.highlightedIndex) {
                return;
            }
            shouldScrollRef.current = false;
            dispatch({
                type: ItemMouseMove,
                index,
                disabled
            });
        };
        const itemHandleClick = ()=>{
            dispatch({
                type: ItemClick,
                index
            });
        };
        const itemHandleMouseDown = (e)=>e.preventDefault();
        return _object_spread$x(_object_spread_props$r(_object_spread$x({
            [refKey]: handleRefs(ref, (itemNode)=>{
                if (itemNode) {
                    itemRefs.current[elementIds.getItemId(itemIndex)] = itemNode;
                }
            }),
            disabled,
            role: 'option',
            'aria-selected': `${itemIndex === latestState.highlightedIndex}`,
            id: elementIds.getItemId(itemIndex)
        }, !disabled && {
            [onSelectKey]: callAllEventHandlers(customClickHandler, itemHandleClick)
        }), {
            onMouseMove: callAllEventHandlers(onMouseMove, itemHandleMouseMove),
            onMouseDown: callAllEventHandlers(onMouseDown, itemHandleMouseDown)
        }), rest);
    }, [
        dispatch,
        latest,
        shouldScrollRef,
        elementIds
    ]);
    const getToggleButtonProps = useCallback(function(_temp4) {
        let _ref = _temp4 === void 0 ? {} : _temp4, { onClick, onPress, refKey = 'ref', ref } = _ref, rest = _object_without_properties$r(_ref, [
            "onClick",
            "onPress",
            "refKey",
            "ref"
        ]);
        const toggleButtonHandleClick = ()=>{
            dispatch({
                type: ToggleButtonClick
            });
            if (!latest.current.state.isOpen && inputRef.current) {
                inputRef.current.focus();
            }
        };
        return _object_spread$x({
            [refKey]: handleRefs(ref, (toggleButtonNode)=>{
                toggleButtonRef.current = toggleButtonNode;
            }),
            id: elementIds.toggleButtonId,
            tabIndex: -1
        }, !rest.disabled && _object_spread$x({}, {
            onClick: callAllEventHandlers(onClick, toggleButtonHandleClick)
        }), rest);
    }, [
        dispatch,
        latest,
        elementIds
    ]);
    const getInputProps = useCallback(function(_temp5, _temp6) {
        let _ref = _temp5 === void 0 ? {} : _temp5, { onKeyDown, onChange, onInput, onBlur, onChangeText, refKey = 'ref', ref } = _ref, rest = _object_without_properties$r(_ref, [
            "onKeyDown",
            "onChange",
            "onInput",
            "onBlur",
            "onChangeText",
            "refKey",
            "ref"
        ]);
        let { suppressRefError = false } = _temp6 === void 0 ? {} : _temp6;
        setGetterPropCallInfo('getInputProps', suppressRefError, refKey, inputRef);
        const latestState = latest.current.state;
        const inputHandleKeyDown = (event)=>{
            const key = normalizeArrowKey(event);
            if (key && inputKeyDownHandlers[key]) {
                inputKeyDownHandlers[key](event);
            }
        };
        const inputHandleChange = (event)=>{
            dispatch({
                type: InputChange,
                inputValue: event.target.value
            });
        };
        const inputHandleBlur = ()=>{
            /* istanbul ignore else */ if (latestState.isOpen && !mouseAndTouchTrackersRef.current.isMouseDown) {
                dispatch({
                    type: InputBlur,
                    selectItem: true
                });
            }
        };
        /* istanbul ignore next (preact) */ const onChangeKey = 'onChange';
        let eventHandlers = {};
        if (!rest.disabled) {
            eventHandlers = {
                [onChangeKey]: callAllEventHandlers(onChange, onInput, inputHandleChange),
                onKeyDown: callAllEventHandlers(onKeyDown, inputHandleKeyDown),
                onBlur: callAllEventHandlers(onBlur, inputHandleBlur)
            };
        }
        return _object_spread$x(_object_spread_props$r(_object_spread$x({
            [refKey]: handleRefs(ref, (inputNode)=>{
                inputRef.current = inputNode;
            }),
            id: elementIds.inputId,
            'aria-autocomplete': 'list',
            'aria-controls': elementIds.menuId
        }, latestState.isOpen && latestState.highlightedIndex > -1 && {
            'aria-activedescendant': elementIds.getItemId(latestState.highlightedIndex)
        }), {
            'aria-labelledby': elementIds.labelId,
            // https://developer.mozilla.org/en-US/docs/Web/Security/Securing_your_site/Turning_off_form_autocompletion
            // revert back since autocomplete="nope" is ignored on latest Chrome and Opera
            autoComplete: 'off',
            value: latestState.inputValue
        }), eventHandlers, rest);
    }, [
        dispatch,
        inputKeyDownHandlers,
        latest,
        mouseAndTouchTrackersRef,
        setGetterPropCallInfo,
        elementIds
    ]);
    const getComboboxProps = useCallback(function(_temp7, _temp8) {
        let _ref = _temp7 === void 0 ? {} : _temp7, { refKey = 'ref', ref } = _ref, rest = _object_without_properties$r(_ref, [
            "refKey",
            "ref"
        ]);
        let { suppressRefError = false } = _temp8 === void 0 ? {} : _temp8;
        setGetterPropCallInfo('getComboboxProps', suppressRefError, refKey, comboboxRef);
        return _object_spread$x({
            [refKey]: handleRefs(ref, (comboboxNode)=>{
                comboboxRef.current = comboboxNode;
            }),
            role: 'combobox',
            'aria-haspopup': 'listbox',
            'aria-owns': elementIds.menuId,
            'aria-expanded': latest.current.state.isOpen
        }, rest);
    }, [
        latest,
        setGetterPropCallInfo,
        elementIds
    ]); // returns
    const toggleMenu = useCallback(()=>{
        dispatch({
            type: FunctionToggleMenu
        });
    }, [
        dispatch
    ]);
    const closeMenu = useCallback(()=>{
        dispatch({
            type: FunctionCloseMenu
        });
    }, [
        dispatch
    ]);
    const openMenu = useCallback(()=>{
        dispatch({
            type: FunctionOpenMenu
        });
    }, [
        dispatch
    ]);
    const setHighlightedIndex = useCallback((newHighlightedIndex)=>{
        dispatch({
            type: FunctionSetHighlightedIndex,
            highlightedIndex: newHighlightedIndex
        });
    }, [
        dispatch
    ]);
    const selectItem = useCallback((newSelectedItem)=>{
        dispatch({
            type: FunctionSelectItem,
            selectedItem: newSelectedItem
        });
    }, [
        dispatch
    ]);
    const setInputValue = useCallback((newInputValue)=>{
        dispatch({
            type: FunctionSetInputValue,
            inputValue: newInputValue
        });
    }, [
        dispatch
    ]);
    const reset = useCallback(()=>{
        dispatch({
            type: FunctionReset$1
        });
    }, [
        dispatch
    ]);
    return {
        // prop getters.
        getItemProps,
        getLabelProps,
        getMenuProps,
        getInputProps,
        getComboboxProps,
        getToggleButtonProps,
        // actions.
        toggleMenu,
        openMenu,
        closeMenu,
        setHighlightedIndex,
        setInputValue,
        selectItem,
        reset,
        // state.
        highlightedIndex,
        isOpen,
        selectedItem,
        inputValue
    };
}
/**
 * Returns a message to be added to aria-live region when item is removed.
 *
 * @param {Object} selectionParameters Parameters required to build the message.
 * @returns {string} The a11y message.
 */ function getA11yRemovalMessage(selectionParameters) {
    const { removedSelectedItem, itemToString: itemToStringLocal } = selectionParameters;
    return `${itemToStringLocal(removedSelectedItem)} has been removed.`;
}
({
    selectedItems: PropTypes.array,
    initialSelectedItems: PropTypes.array,
    defaultSelectedItems: PropTypes.array,
    itemToString: PropTypes.func,
    getA11yRemovalMessage: PropTypes.func,
    stateReducer: PropTypes.func,
    activeIndex: PropTypes.number,
    initialActiveIndex: PropTypes.number,
    defaultActiveIndex: PropTypes.number,
    onActiveIndexChange: PropTypes.func,
    onSelectedItemsChange: PropTypes.func,
    keyNavigationNext: PropTypes.string,
    keyNavigationPrevious: PropTypes.string,
    environment: PropTypes.shape({
        addEventListener: PropTypes.func,
        removeEventListener: PropTypes.func,
        document: PropTypes.shape({
            getElementById: PropTypes.func,
            activeElement: PropTypes.any,
            body: PropTypes.any
        })
    })
});
({
    itemToString: defaultProps$3.itemToString,
    stateReducer: defaultProps$3.stateReducer,
    environment: defaultProps$3.environment,
    getA11yRemovalMessage,
    keyNavigationNext: 'ArrowRight',
    keyNavigationPrevious: 'ArrowLeft'
}); // eslint-disable-next-line import/no-mutable-exports
/* istanbul ignore next */ if (process.env.NODE_ENV !== 'production') ;
process.env.NODE_ENV !== "production" ? '__selected_item_click__' : 0;
process.env.NODE_ENV !== "production" ? '__selected_item_keydown_delete__' : 1;
process.env.NODE_ENV !== "production" ? '__selected_item_keydown_backspace__' : 2;
process.env.NODE_ENV !== "production" ? '__selected_item_keydown_navigation_next__' : 3;
process.env.NODE_ENV !== "production" ? '__selected_item_keydown_navigation_previous__' : 4;
process.env.NODE_ENV !== "production" ? '__dropdown_keydown_navigation_previous__' : 5;
process.env.NODE_ENV !== "production" ? '__dropdown_keydown_backspace__' : 6;
process.env.NODE_ENV !== "production" ? '__dropdown_click__' : 7;
process.env.NODE_ENV !== "production" ? '__function_add_selected_item__' : 8;
process.env.NODE_ENV !== "production" ? '__function_remove_selected_item__' : 9;
process.env.NODE_ENV !== "production" ? '__function_set_selected_items__' : 10;
process.env.NODE_ENV !== "production" ? '__function_set_active_index__' : 11;
process.env.NODE_ENV !== "production" ? '__function_reset__' : 12;
const createBemBlockBuilder = (blocks)=>{
    const cleanedBlocks = blocks.reduce((reviewedBlocks, block)=>block ? reviewedBlocks.concat(block.trim().split(" ")) : reviewedBlocks, []);
    return (elementOrModifier = "")=>cleanedBlocks.reduce((classList, block)=>cn(classList, `${block}${elementOrModifier}`), "");
};
function createGenericContext({ initialContext, errorMessage }) {
    const GenericContext = /*#__PURE__*/ createContext(initialContext);
    const useGenericContext = ()=>{
        const expectedContext = useContext(GenericContext);
        if (!expectedContext) {
            const message = errorMessage ? errorMessage : "useGenericContext - Can not provide a context to use. Please make sure you have defined a context to be consumed.";
            throw new Error(message);
        }
        return expectedContext;
    };
    const GenericContextConsumer = ({ children })=>{
        const expectedContext = useGenericContext();
        return children(expectedContext);
    };
    return {
        useGenericContext,
        GenericContextProvider: GenericContext.Provider,
        GenericContextConsumer
    };
}
/**
 * @example
 * Wrapper class name as element:
 * getWrapperClassNames("base__wrapper", "base", ["--open"]) => "base__wrapper base__wrapper--open"
 *
 * Wrapper class name as block:
 * getWrapperClassNames(true, undefined, "base", ["--open"]) => "base base--open"
 *
 * Optional modifier:
 * getWrapperClassNames(true, undefined, "base", [isOpen && "--open"]) => "base base--open" or "base"
 */ const getWrapperClassNames = ({ consumerWrapperClassName, consumerBaseClassName, modifiers, defaultBaseClassName })=>{
    const getBEMBlocksWith = createBemBlockBuilder([
        defaultBaseClassName,
        consumerWrapperClassName !== null && consumerWrapperClassName !== void 0 ? consumerWrapperClassName : consumerBaseClassName
    ]);
    return cn(getBEMBlocksWith(), modifiers === null || modifiers === void 0 ? void 0 : modifiers.map((modifier)=>typeof modifier === "string" && getBEMBlocksWith(modifier)));
};
/** Takes multiple refs and returns a single ref-function that binds element instance to each ref */ const combineRefs = (...refs)=>(component)=>{
        refs.forEach((ref)=>{
            if (typeof ref === "function") {
                ref(component);
            }
            if (typeof ref === "object" && ref) {
                ref.current = component;
            }
        });
    };
const { GenericContextProvider: AutocompleteContextProvider, GenericContextConsumer: AutocompleteContextConsumer, useGenericContext: useAutocompleteContext } = createGenericContext({
    errorMessage: "Autocomplete compound components cannot be rendered outside of the Autocomplete component"
});
function _define_property$7$1(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _extends$1() {
    _extends$1 = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends$1.apply(this, arguments);
}
function _object_destructuring_empty(o) {
    if (o === null || o === void 0) throw new TypeError("Cannot destructure " + o);
    return o;
}
function _object_spread$7$1(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$7$1(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$4$1(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$4$1(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$4$1(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$7$1(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$7$1(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$7$1(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const AutocompleteInput = /*#__PURE__*/ forwardRef((_param, ref)=>{
    var { className: consumerClassname } = _param, rest = _object_without_properties$7$1(_param, [
        "className"
    ]);
    const { applyDisplayValue, getBEMBlocksWith, getInputGroupsFocusProps, getInputProps, inputRef, closeMenu, shouldShowMenuItems, highlightedIndex, isOpen, items, openMenu, onEnterPress } = useAutocompleteContext();
    const className = cn(getBEMBlocksWith("__input"), consumerClassname);
    const inputGroupFocusProps = _extends$1({}, _object_destructuring_empty(getInputGroupsFocusProps(_object_spread_props$4$1(_object_spread$7$1({}, rest), {
        onBlur: applyDisplayValue
    }))));
    const keyDownHandler = (event)=>{
        switch(event.key){
            case "Enter":
                /** highlightedIndex: is used bacause we want onEnterPress event to be called only when
           *                    there is no menu item selected or highlighted via keyboard down arrow key.
           *                    otherwise onEnterPress and onItemSelection will conflict, we can only fire one of these
           * Notice: If someone wants to use the onEnterPress event feature then highlightedIndex must be less than zero,
           *         because if menu items exist in the dropdown list and user highlights or focuses on any of the items via keyboard down arrow key
           *         then we need to call itemSelection event instead of onEnterPress.
           */ if (highlightedIndex < 0 && onEnterPress) {
                    onEnterPress(event);
                    return;
                }
                //   If nothing is highlighted, let the normal input event through.
                if (!isOpen || highlightedIndex === -1) {
                    closeMenu();
                    // (event.nativeEvent as any) https://github.com/downshift-js/downshift/issues/734
                    event.nativeEvent.preventDownshiftDefault = true;
                }
                break;
            case "Escape":
                applyDisplayValue();
                break;
            case "Home":
            case "End":
                // (event.nativeEvent as any) https://github.com/downshift-js/downshift/issues/734
                event.nativeEvent.preventDownshiftDefault = true;
                break;
        }
    };
    const inputProps = getInputProps(_object_spread$7$1({
        ref: combineRefs(inputRef, ref),
        className,
        autoCapitalize: "off",
        onClick () {
            if (items.length && shouldShowMenuItems) {
                openMenu();
            }
        },
        onKeyDown: keyDownHandler
    }, rest, inputGroupFocusProps));
    return /*#__PURE__*/ jsx("input", _object_spread$7$1({
        dir: "auto"
    }, inputProps));
});
AutocompleteInput.displayName = "AutocompleteInput";
function _define_property$6$1(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$6$1(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$6$1(target, key, source[key]);
        });
    }
    return target;
}
function _object_without_properties$6$1(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$6$1(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$6$1(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const AutocompleteInputGroup = (_param)=>{
    var { children, className: consumerClassName } = _param, rest = _object_without_properties$6$1(_param, [
        "children",
        "className"
    ]);
    const { getBEMBlocksWith, getComboboxProps, isOpen } = useAutocompleteContext();
    const className = cn(getBEMBlocksWith("__input-group"), consumerClassName);
    const autocompleteInputGroupProps = _object_spread$6$1({
        className,
        children
    }, getComboboxProps(), rest);
    const handleOnKeyDown = (event)=>{
        if (isOpen) {
            event.stopPropagation();
        }
    };
    return /*#__PURE__*/ jsx("div", _object_spread$6$1({
        onKeyDown: handleOnKeyDown
    }, autocompleteInputGroupProps));
};
AutocompleteInputGroup.displayName = "AutocompleteInputGroup";
/**
 * useShownItems
 *
 * @param {boolean} initialShownState
 * @param {array} items
 * @return {[[], (value: boolean) => void]}
 */ const useShownItems = (initialShownState, items)=>{
    const [hasShownItems, setShownItems] = useState(initialShownState);
    const shownItems = useMemo(()=>hasShownItems ? items : [], [
        hasShownItems,
        items
    ]);
    return [
        shownItems,
        setShownItems
    ];
};
const useAutocompleteInputGroupsFocusModifier = (classList)=>{
    const wrapperRef = useRef(null);
    const addInputGroupFocusModifier = ()=>{
        if (!(wrapperRef === null || wrapperRef === void 0 ? void 0 : wrapperRef.current)) return;
        // classList.add accepts one argument in IE.
        for (const classToToggle of classList.split(" ")){
            var _wrapperRef_current;
            (_wrapperRef_current = wrapperRef.current) === null || _wrapperRef_current === void 0 ? void 0 : _wrapperRef_current.classList.add(classToToggle);
        }
    };
    const removeInputGroupFocusModifier = ()=>{
        if (!(wrapperRef === null || wrapperRef === void 0 ? void 0 : wrapperRef.current)) return;
        // classList.remove accepts one argument in IE.
        for (const classToToggle of classList.split(" ")){
            wrapperRef.current.classList.remove(classToToggle);
        }
    };
    const getInputGroupsFocusProps = ({ onFocus, onBlur } = {})=>{
        try {
            document.querySelector(":focus-within");
        } catch (error) {
            // no support for :focus-within - using JS solution
            return {
                onFocus: (ev)=>{
                    addInputGroupFocusModifier();
                    onFocus && onFocus(ev);
                },
                onBlur: (ev)=>{
                    removeInputGroupFocusModifier();
                    onBlur && onBlur(ev);
                }
            };
        }
        return {
            onBlur: (ev)=>onBlur === null || onBlur === void 0 ? void 0 : onBlur(ev),
            onFocus: (ev)=>onFocus === null || onFocus === void 0 ? void 0 : onFocus(ev)
        };
    };
    return [
        getInputGroupsFocusProps,
        wrapperRef
    ];
};
function _define_property$5$1(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$5$1(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$5$1(target, key, source[key]);
        });
    }
    return target;
}
function _object_without_properties$5$1(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$5$1(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$5$1(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const Button = /*#__PURE__*/ forwardRef((_param, forwardedRef)=>{
    var { disabled = false, className = "", styleType = "default", type = "button" } = _param, rest = _object_without_properties$5$1(_param, [
        "disabled",
        "className",
        "styleType",
        "type"
    ]);
    return /*#__PURE__*/ jsx("button", _object_spread$5$1({
        ref: forwardedRef,
        "data-auto": "button",
        className: cn("eb-button", `eb-button--${styleType}`, className),
        disabled: disabled,
        type: type
    }, rest));
});
Button.displayName = "Button";
function _define_property$4$1(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$4$1(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$4$1(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$3$1(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$3$1(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$3$1(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$4$1(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$4$1(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$4$1(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const AutocompleteClearButton = (_param)=>{
    var { className: consumerClassName } = _param, rest = _object_without_properties$4$1(_param, [
        "className"
    ]);
    const { getInputProps, getBEMBlocksWith, getInputGroupsFocusProps, reset, inputRef, clearButtonProps } = useAutocompleteContext();
    const { value } = getInputProps();
    if (!value) {
        return null;
    }
    const handleOnClearClick = ()=>{
        var _clearButtonProps_onClick;
        reset();
        inputRef && inputRef.current && inputRef.current.focus();
        clearButtonProps === null || clearButtonProps === void 0 ? void 0 : (_clearButtonProps_onClick = clearButtonProps.onClick) === null || _clearButtonProps_onClick === void 0 ? void 0 : _clearButtonProps_onClick.call(clearButtonProps);
    };
    const buttonClassName = cn(getBEMBlocksWith("__clear-button"), consumerClassName);
    const iconClassName = cn("fa-sm", getBEMBlocksWith("__clear-button-icon"));
    return /*#__PURE__*/ jsx(Button, _object_spread_props$3$1(_object_spread$4$1({
        onClick: handleOnClearClick,
        className: buttonClassName,
        styleType: "flat-icon"
    }, getInputGroupsFocusProps({}), rest), {
        children: /*#__PURE__*/ jsx(FontAwesomeIcon, {
            className: iconClassName,
            icon: faTimesCircle
        })
    }));
};
const AutocompleteGroup = ({ className: consumerClassName, id, title, icon, shouldIndentContent, children })=>{
    const { getBEMBlocksWith } = useAutocompleteContext();
    const baseClassName = "__group";
    return /*#__PURE__*/ jsxs("ul", {
        className: cn(getBEMBlocksWith(baseClassName), shouldIndentContent && getBEMBlocksWith(`${baseClassName}--indent`), consumerClassName),
        "aria-labelledby": id,
        role: "group",
        children: [
            /*#__PURE__*/ jsxs("li", {
                role: "presentation",
                id: id,
                className: getBEMBlocksWith("__group-heading"),
                children: [
                    icon && /*#__PURE__*/ jsx("div", {
                        "data-auto": "autocomplete-group-icon",
                        className: getBEMBlocksWith(`${baseClassName}-icon`),
                        children: icon
                    }),
                    /*#__PURE__*/ jsx("span", {
                        className: cn(getBEMBlocksWith(`${baseClassName}-title`)),
                        children: title
                    })
                ]
            }),
            children
        ]
    });
};
function _define_property$3$1(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$3$1(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$3$1(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$2$1(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$2$1(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$2$1(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$3$1(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$3$1(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$3$1(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const AutocompleteMenu = (_param)=>{
    var { className: consumerClassName, isGrouped = false, children } = _param, rest = _object_without_properties$3$1(_param, [
        "className",
        "isGrouped",
        "children"
    ]);
    const { getMenuProps, getBEMBlocksWith, items, isOpen: comboboxIsOpen } = useAutocompleteContext();
    const isOpen = comboboxIsOpen && !!(items === null || items === void 0 ? void 0 : items.length);
    const className = cn(getBEMBlocksWith("__menu"), isOpen && getBEMBlocksWith("__menu--open"), consumerClassName);
    const Container = isGrouped ? "div" : "ul";
    return /*#__PURE__*/ jsx(Container, _object_spread_props$2$1(_object_spread$3$1({
        "data-auto": "autocomplete-menu"
    }, getMenuProps(_object_spread$3$1({
        className
    }, rest))), {
        children: isOpen && children
    }));
};
AutocompleteMenu.displayName = "AutocompleteMenu";
function _define_property$2$1(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$2$1(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$2$1(target, key, source[key]);
        });
    }
    return target;
}
function _object_without_properties$2$1(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$2$1(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$2$1(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const AutocompleteItem = (props)=>{
    const { className: consumerClassName, item, index } = props, rest = _object_without_properties$2$1(props, [
        "className",
        "item",
        "index"
    ]);
    const { getItemProps, getBEMBlocksWith, highlightedIndex, setHighlightedIndex } = useAutocompleteContext();
    const className = cn(getBEMBlocksWith("__item"), highlightedIndex === index && getBEMBlocksWith("__item--highlighted"), consumerClassName);
    return /*#__PURE__*/ jsx("li", _object_spread$2$1({}, getItemProps(_object_spread$2$1({
        item,
        index,
        className,
        // reset highlighted set when moving mouse outside the list item
        onMouseLeave: ()=>setHighlightedIndex(-1)
    }, rest))));
};
AutocompleteItem.displayName = "AutocompleteItem";
const BASE_CLASS_NAME$3 = "eb-autocomplete";
const MIN_SEARCH_LENGTH$1 = 3;
function _define_property$1$1(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$1$1(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$1$1(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$1$1(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$1$1(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$1$1(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$1$1(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$1$1(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$1$1(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const AutocompleteLabel = (props)=>{
    const { isHidden = false, className: consumerClassName, children } = props, rest = _object_without_properties$1$1(props, [
        "isHidden",
        "className",
        "children"
    ]);
    const { getLabelProps, getBEMBlocksWith } = useAutocompleteContext();
    const className = cn(getBEMBlocksWith("__label"), isHidden && "visually-hidden", consumerClassName);
    return /*#__PURE__*/ jsx("label", _object_spread_props$1$1(_object_spread$1$1({}, getLabelProps(_object_spread$1$1({
        className
    }, rest))), {
        children: children
    }));
};
AutocompleteLabel.displayName = "AutocompleteLabel";
function _define_property1(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread1(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property1(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys1(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props1(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys1(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties1(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose1(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose1(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const Autocomplete = /*#__PURE__*/ forwardRef((_param, ref)=>{
    var { children, items = [], value = "", onChange, itemToString = (item)=>{
        var _item_label;
        return (_item_label = item === null || item === void 0 ? void 0 : item.label) !== null && _item_label !== void 0 ? _item_label : "";
    }, baseClassName: consumerBaseClassName, className: consumerWrapperClassName, downshiftOptions, focusedByDefault = false, displayHighlightedValue = false, minSearchLength = MIN_SEARCH_LENGTH$1, forceMenuDisplay, onEnterPress, clearButtonProps } = _param, rest = _object_without_properties1(_param, [
        "children",
        "items",
        "value",
        "onChange",
        "itemToString",
        "baseClassName",
        "className",
        "downshiftOptions",
        "focusedByDefault",
        "displayHighlightedValue",
        "minSearchLength",
        "forceMenuDisplay",
        "onEnterPress",
        "clearButtonProps"
    ]);
    const [displayedValue, setDisplayedValue] = useState(value);
    const inputRef = useRef(null);
    useEffect(()=>{
        setDisplayedValue(value || "");
    }, [
        value
    ]);
    const getBEMBlocksWith = createBemBlockBuilder([
        BASE_CLASS_NAME$3,
        consumerBaseClassName
    ]);
    const shouldShowMenuItems = useMemo(()=>{
        if (forceMenuDisplay) {
            return true;
        }
        return value ? value.length >= minSearchLength : false;
    }, [
        value,
        minSearchLength,
        forceMenuDisplay
    ]);
    const [getInputGroupsFocusProps, wrapperRef] = useAutocompleteInputGroupsFocusModifier(getBEMBlocksWith("--focused"));
    const [shownItems, setShownItems] = useShownItems(shouldShowMenuItems, items);
    useEffect(()=>{
        setShownItems(shouldShowMenuItems);
    }, [
        shouldShowMenuItems,
        setShownItems
    ]);
    useEffect(()=>{
        if (focusedByDefault && inputRef.current) {
            setTimeout(()=>{
                if (inputRef.current) {
                    inputRef.current.focus();
                }
            }, 200);
        }
    }, [
        focusedByDefault
    ]);
    const stateReducer = (state, actionAndChanges)=>{
        const { type, changes } = actionAndChanges;
        var _changes_highlightedIndex;
        const highlightedIndex = (_changes_highlightedIndex = changes.highlightedIndex) !== null && _changes_highlightedIndex !== void 0 ? _changes_highlightedIndex : -1;
        switch(type){
            case useCombobox.stateChangeTypes.InputChange:
                return _object_spread_props1(_object_spread1({}, changes), {
                    // TODO: it doesn't exist in the types, what is it??
                    userInput: changes.inputValue
                });
            case useCombobox.stateChangeTypes.InputKeyDownArrowDown:
            case useCombobox.stateChangeTypes.InputKeyDownArrowUp:
                if (highlightedIndex > -1 && displayHighlightedValue) {
                    var _itemToString;
                    setDisplayedValue((_itemToString = itemToString === null || itemToString === void 0 ? void 0 : itemToString(items[highlightedIndex])) !== null && _itemToString !== void 0 ? _itemToString : "");
                }
                return _object_spread1({}, changes);
            case useCombobox.stateChangeTypes.InputKeyDownEscape:
            case useCombobox.stateChangeTypes.InputBlur:
                return _object_spread_props1(_object_spread1({}, state), {
                    isOpen: false
                });
            default:
                return changes;
        }
    };
    const applyDisplayValue = ()=>{
        if (displayHighlightedValue) {
            onChange && onChange({
                inputValue: displayedValue,
                type: useCombobox.stateChangeTypes.InputBlur
            });
        }
    };
    const comboboxProps = useCombobox(_object_spread1({
        itemToString,
        stateReducer,
        // used by the clear button
        onInputValueChange: (changes)=>onChange === null || onChange === void 0 ? void 0 : onChange(changes),
        items: shownItems,
        inputValue: displayedValue
    }, downshiftOptions));
    const { isOpen } = comboboxProps;
    const contextProviderValue = _object_spread_props1(_object_spread1({}, comboboxProps), {
        onChange,
        getBEMBlocksWith,
        inputRef,
        getInputGroupsFocusProps,
        items: shownItems,
        shouldShowMenuItems,
        applyDisplayValue,
        setDisplayedValue,
        onEnterPress,
        clearButtonProps,
        getInputProps: (props)=>{
            const inputProps = comboboxProps.getInputProps(props);
            return _object_spread_props1(_object_spread1({}, inputProps), {
                onChange: (ev)=>{
                    // workaround for controlled inputValue from Downshift https://github.com/downshift-js/downshift/issues/1108
                    onChange && onChange({
                        inputValue: ev.target.value,
                        type: useCombobox.stateChangeTypes.InputChange
                    });
                    setDisplayedValue(ev.target.value);
                    inputProps.onChange(ev);
                }
            });
        }
    });
    const wrapperClassName = getWrapperClassNames({
        consumerWrapperClassName,
        consumerBaseClassName,
        modifiers: [
            isOpen && "--open"
        ],
        defaultBaseClassName: BASE_CLASS_NAME$3
    });
    return /*#__PURE__*/ jsx(AutocompleteContextProvider, {
        value: contextProviderValue,
        children: /*#__PURE__*/ jsx("div", _object_spread_props1(_object_spread1({
            className: wrapperClassName,
            ref: combineRefs(wrapperRef, ref)
        }, rest), {
            children: children
        }))
    });
});
Autocomplete.ClearButton = AutocompleteClearButton;
Autocomplete.ContextConsumer = AutocompleteContextConsumer;
Autocomplete.useContext = useAutocompleteContext;
Autocomplete.Group = AutocompleteGroup;
Autocomplete.Input = AutocompleteInput;
Autocomplete.InputGroup = AutocompleteInputGroup;
Autocomplete.Item = AutocompleteItem;
Autocomplete.Label = AutocompleteLabel;
Autocomplete.Menu = AutocompleteMenu;
Autocomplete.displayName = "Autocomplete";

const SEARCH_BUTTON_ICONS = {
    search: faSearch,
    searchWithin: faFileSearch
};

function _define_property$w(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$w(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$w(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$q(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$q(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$q(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$q(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$q(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$q(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const BASE_CLASS_NAME$2 = "eb-search-button";
const SearchButton = (_param)=>{
    var { className: consumerBaseClassName, icon = "search", messages, focusedByDefault, onClick } = _param, rest = _object_without_properties$q(_param, [
        "className",
        "icon",
        "messages",
        "focusedByDefault",
        "onClick"
    ]);
    const searchButtonIcon = SEARCH_BUTTON_ICONS[icon];
    const searchButtonRef = useRef(null);
    const { openMenu } = Autocomplete.useContext();
    useEffect(()=>{
        if (focusedByDefault) {
            setTimeout(()=>{
                if (searchButtonRef.current) {
                    searchButtonRef.current.focus();
                }
            }, 200);
        }
    }, [
        focusedByDefault
    ]);
    const getBEMBlocksWith = createBemBlockBuilder$1([
        BASE_CLASS_NAME$2,
        consumerBaseClassName
    ]);
    const handleOnClick = (event)=>{
        if (onClick) {
            onClick(event);
            openMenu();
        }
    };
    return /*#__PURE__*/ jsx(Button$1, _object_spread_props$q(_object_spread$w(_object_spread_props$q(_object_spread$w({}, rest), {
        onClick: handleOnClick
    }), messages), {
        className: getBEMBlocksWith(),
        styleType: "flat-icon",
        type: "submit",
        ref: searchButtonRef,
        children: /*#__PURE__*/ jsx(FontAwesomeIcon, {
            className: getBEMBlocksWith("__icon"),
            icon: searchButtonIcon
        })
    }));
};
SearchButton.displayName = "SearchButton";

const MIN_SEARCH_LENGTH = 3;
const BASE_CLASS_NAME$1 = "eb-search-autocomplete";

var BOOLEAN_CHIP_VALUE;
(function(BOOLEAN_CHIP_VALUE) {
    BOOLEAN_CHIP_VALUE["And"] = "and";
    BOOLEAN_CHIP_VALUE["Or"] = "or";
    BOOLEAN_CHIP_VALUE["Not"] = "not";
})(BOOLEAN_CHIP_VALUE || (BOOLEAN_CHIP_VALUE = {}));
const BOOLEAN_ITEMS = [
    "and",
    "or",
    "not"
];

function _define_property$v(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$v(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$v(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$p(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$p(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$p(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$p(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$p(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$p(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const baseClassName = "eb-boolean-chip";
const BooleanChip = (_param)=>{
    var { className: consumerClassname, onChange, value, messages } = _param, props = _object_without_properties$p(_param, [
        "className",
        "onChange",
        "value",
        "messages"
    ]);
    const { getPosition, ariaRoledescription } = messages;
    const currentPosition = BOOLEAN_ITEMS.indexOf(value) + 1;
    const total = BOOLEAN_ITEMS.length;
    const onClick = ()=>{
        const nextIndex = currentPosition % total;
        onChange(BOOLEAN_ITEMS[nextIndex]);
    };
    const getBEMBlockWith = createBemBlockBuilder$1([
        baseClassName,
        consumerClassname
    ]);
    return /*#__PURE__*/ jsx("button", _object_spread_props$p(_object_spread$v({
        "aria-roledescription": ariaRoledescription,
        type: "button",
        className: getBEMBlockWith(),
        onClick: onClick
    }, props), {
        children: /*#__PURE__*/ jsxs("span", {
            "aria-live": "polite",
            className: getBEMBlockWith("__button-text"),
            children: [
                messages[value],
                /*#__PURE__*/ jsx("span", {
                    className: getBEMBlockWith("__order"),
                    children: `, ${getPosition(currentPosition, total)}`
                })
            ]
        })
    }));
};

/*
 * recursively splits provided **text** into fragments by highlighting the **term**
 * @example
 *
 * // 1st call (by consumer)
 * splitIntoFragments("all cats and dogs", "cat")
 * // 2nd call (by recursion)
 * splitIntoFragments("s and dogs", "cat", [
 *   { text: "all ", isHighlighted: false },
 *   { text: "cat", isHighlighted: true },
 * ])
 * // 3rd call (by recursion)
 * splitIntoFragments("", "cat", [
 *   { text: "all ", isHighlighted: false },
 *   { text: "cat", isHighlighted: true },
 *   { text: "s and dogs", isHighlighted: false },
 * ])
 *
 * returns [
 *   { text: "all ", isHighlighted: false },
 *   { text: "cat", isHighlighted: true },
 *   { text: "s and dogs", isHighlighted: false },
 * ]
 */ const splitIntoFragments = (text, term, result = [])=>{
    if (text.length === 0) {
        return result;
    }
    const index = text.toLowerCase().indexOf(term.toLowerCase());
    if (index === -1 || term.length === 0) {
        return splitIntoFragments("", term, [
            ...result,
            {
                text,
                isHighlighted: false
            }
        ]);
    }
    const fragments = [
        {
            text: text.slice(0, index),
            isHighlighted: false
        },
        {
            text: text.slice(index, index + term.length),
            isHighlighted: true
        }
    ];
    return splitIntoFragments(text.slice(index + term.length), term, [
        ...result,
        ...fragments
    ]);
};
const getOrCreateFragments = (item, term)=>{
    if (item.fragments && item.fragments.every((fragment)=>"isHighlighted" in fragment && "text" in fragment)) {
        return item.fragments;
    }
    return splitIntoFragments(item.term, term);
};

const ItemTerm = (props)=>{
    const { item, getBEMBlocksWith, shouldHighlightItems } = props;
    const { inputValue } = Autocomplete.useContext();
    if (shouldHighlightItems) {
        const fragments = getOrCreateFragments(item, inputValue);
        return /*#__PURE__*/ jsx(Fragment, {
            children: fragments.map((fragment, fragmentIndex)=>fragment.isHighlighted ? /*#__PURE__*/ jsx("mark", {
                    className: getBEMBlocksWith("__mark"),
                    children: fragment.text
                }, `${fragment.text}-${fragmentIndex}`) : fragment.text)
        });
    }
    return /*#__PURE__*/ jsx(Fragment, {
        children: item.term
    });
};
ItemTerm.displayName = "ItemTerm";

function _define_property$u(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$u(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$u(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$o(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$o(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$o(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
const ItemImage = (props)=>{
    const { image, className } = props;
    if (!image) {
        return null;
    }
    return /*#__PURE__*/ jsx("img", _object_spread_props$o(_object_spread$u({
        className: className
    }, image), {
        alt: "",
        tabIndex: -1,
        "aria-hidden": true
    }));
};
ItemImage.displayName = "ItemImage";

function _define_property$t(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$t(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$t(target, key, source[key]);
        });
    }
    return target;
}
function _object_without_properties$o(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$o(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$o(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const ItemContent = (props)=>{
    const { getBEMBlocksWith, item } = props, restProps = _object_without_properties$o(props, [
        "getBEMBlocksWith",
        "item"
    ]);
    return /*#__PURE__*/ jsxs(Fragment, {
        children: [
            /*#__PURE__*/ jsx(ItemImage, {
                image: item.image,
                className: getBEMBlocksWith("__image")
            }),
            /*#__PURE__*/ jsxs("div", {
                children: [
                    /*#__PURE__*/ jsx("div", {
                        className: getBEMBlocksWith("__content"),
                        children: /*#__PURE__*/ jsx(ItemTerm, _object_spread$t({
                            item: item,
                            getBEMBlocksWith: getBEMBlocksWith
                        }, restProps))
                    }),
                    item.description && /*#__PURE__*/ jsx("div", {
                        className: getBEMBlocksWith("__content-description"),
                        children: item.description
                    })
                ]
            })
        ]
    });
};
ItemContent.displayName = "ItemContent";

function _define_property$s(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$s(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$s(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$n(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$n(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$n(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
// Updated version of mappedItems pulled
// from search-autocomplete to included indexing
const useFormatItems = ()=>useCallback((items, initialIndex = 0)=>items.map((item, index)=>_object_spread$s({
                id: v4(),
                label: item.term,
                // assign sequential indexes to the items
                index: initialIndex + index
            }, item)), []);
const useGroupedBy = (items, groups, groupItemsBy)=>{
    const formatItems = useFormatItems();
    if (!groups || !groupItemsBy) {
        return {
            groupsToRender: undefined,
            itemsToRender: formatItems(items)
        };
    }
    const hasIcons = groups.some((group)=>group === null || group === void 0 ? void 0 : group.icon);
    const groupedItems = groups.reduce((groups, currentGroup)=>{
        const filteredItems = items.filter((item)=>{
            const groupingKey = groupItemsBy === null || groupItemsBy === void 0 ? void 0 : groupItemsBy(item);
            return groupingKey === currentGroup.id;
        });
        if (!filteredItems.length) {
            return groups;
        }
        const previousGroup = groups[groups.length - 1];
        const previousItem = previousGroup === null || previousGroup === void 0 ? void 0 : previousGroup.items[previousGroup.items.length - 1];
        // the index of the previous item (which is the last item in the previous group)
        const currentItemIndex = previousItem ? previousItem.index + 1 : 0;
        const formattedItems = formatItems(filteredItems, currentItemIndex);
        return [
            ...groups,
            _object_spread_props$n(_object_spread$s({}, currentGroup), {
                items: formattedItems,
                shouldIndentContent: hasIcons
            })
        ];
    }, []);
    return {
        groupsToRender: groupedItems.filter((group)=>!!group.items.length),
        itemsToRender: groupedItems.reduce((items, group)=>[
                ...items,
                ...group.items
            ], [])
    };
};

function _define_property$r(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$r(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$r(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$m(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$m(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$m(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$n(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$n(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$n(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const SearchAutocomplete = /*#__PURE__*/ forwardRef((_param, ref)=>{
    var { forceMenuDisplay, clearButtonProps, onEnterPress, baseClassName: consumerBaseClassName, className: consumerWrapperClassName, displayHighlightedValue, groups, groupItemsBy, fetchItems, itemToString = (item)=>{
        var _item_term;
        return (_item_term = item === null || item === void 0 ? void 0 : item.term) !== null && _item_term !== void 0 ? _item_term : "";
    }, items, minSearchLength = MIN_SEARCH_LENGTH, onChange, messages, value, maxLength, hasSearchButton, searchButtonProps, hasBooleanChip, booleanChipProps, shouldHighlightItems = true, onFocus, setFocusOn = "input", focusedByDefault } = _param, rest = _object_without_properties$n(_param, [
        "forceMenuDisplay",
        "clearButtonProps",
        "onEnterPress",
        "baseClassName",
        "className",
        "displayHighlightedValue",
        "groups",
        "groupItemsBy",
        "fetchItems",
        "itemToString",
        "items",
        "minSearchLength",
        "onChange",
        "messages",
        "value",
        "maxLength",
        "hasSearchButton",
        "searchButtonProps",
        "hasBooleanChip",
        "booleanChipProps",
        "shouldHighlightItems",
        "onFocus",
        "setFocusOn",
        "focusedByDefault"
    ]);
    const getBEMBlocksWith = createBemBlockBuilder$1([
        BASE_CLASS_NAME$1,
        consumerBaseClassName
    ]);
    const fetchSearchSuggestions = useThrottledDebouncedInput(fetchItems !== null && fetchItems !== void 0 ? fetchItems : ()=>{});
    const { groupsToRender, itemsToRender } = useGroupedBy(items, groups, groupItemsBy);
    const handleChange = (changes)=>{
        const { selectedItem } = changes, restChanges = _object_without_properties$n(changes, [
            "selectedItem"
        ]);
        onChange && onChange(_object_spread_props$m(_object_spread$r({}, restChanges), {
            selectedItem: selectedItem
        }));
        var _changes_inputValue;
        fetchSearchSuggestions((_changes_inputValue = changes.inputValue) !== null && _changes_inputValue !== void 0 ? _changes_inputValue : "");
    };
    const autocompleteProps = _object_spread$r({
        value,
        items: itemsToRender,
        baseClassName: getBEMBlocksWith(),
        className: getBEMBlocksWith("__autocomplete-wrapper"),
        onChange: handleChange,
        itemToString: itemToString,
        minSearchLength,
        displayHighlightedValue,
        focusedByDefault: focusedByDefault && setFocusOn === "input",
        forceMenuDisplay,
        onEnterPress,
        clearButtonProps
    }, rest);
    if (hasBooleanChip && !booleanChipProps) {
        throw new Error("You need to pass booleanChipProps (value, messages, onChange) to SearchAutocomplete to render BooleanChip");
    }
    const wrapperClassName = getWrapperClassNames$1({
        consumerWrapperClassName,
        consumerBaseClassName,
        defaultBaseClassName: BASE_CLASS_NAME$1
    });
    return /*#__PURE__*/ jsx("div", {
        className: wrapperClassName,
        children: /*#__PURE__*/ jsxs(Autocomplete, _object_spread_props$m(_object_spread$r({}, autocompleteProps), {
            ref: ref,
            children: [
                hasBooleanChip && booleanChipProps && /*#__PURE__*/ jsx(Adornment, {
                    placement: "start",
                    children: /*#__PURE__*/ jsx(BooleanChip, _object_spread$r({}, booleanChipProps))
                }),
                /*#__PURE__*/ jsx(Autocomplete.InputGroup, {
                    children: /*#__PURE__*/ jsx(Autocomplete.Input, _object_spread$r({
                        /** Override downshift's default label system */ "aria-labelledby": undefined,
                        onFocus: onFocus,
                        maxLength: maxLength
                    }, messages.autocompleteMessages.input))
                }),
                /*#__PURE__*/ jsx(Autocomplete.Menu, _object_spread_props$m(_object_spread$r({
                    isGrouped: Boolean(groupsToRender),
                    /** Override downshift's default label system */ "aria-labelledby": undefined
                }, messages.autocompleteMessages.menu), {
                    children: groupsToRender ? groupsToRender.map((_param, groupIndex)=>{
                        var { items: groupItems } = _param, groupProps = _object_without_properties$n(_param, [
                            "items"
                        ]);
                        return /*#__PURE__*/ createElement(Autocomplete.Group, _object_spread_props$m(_object_spread$r({}, groupProps), {
                            key: `${groupProps.id}-${groupIndex}`
                        }), groupItems.map((item)=>/*#__PURE__*/ jsx(Autocomplete.Item, {
                                item: item,
                                index: item.index,
                                children: /*#__PURE__*/ jsx(ItemContent, {
                                    item: item,
                                    shouldHighlightItems: shouldHighlightItems,
                                    getBEMBlocksWith: getBEMBlocksWith
                                })
                            }, `${item.term}-${item.index}`)));
                    }) : itemsToRender.map((item, index)=>/*#__PURE__*/ jsx(Autocomplete.Item, {
                            item: item,
                            index: index,
                            children: /*#__PURE__*/ jsx(ItemContent, {
                                item: item,
                                shouldHighlightItems: shouldHighlightItems,
                                getBEMBlocksWith: getBEMBlocksWith
                            })
                        }, `${item.term}-${index}`))
                })),
                /*#__PURE__*/ jsxs(Adornment, {
                    placement: "end",
                    children: [
                        /*#__PURE__*/ jsx(Autocomplete.ClearButton, _object_spread$r({}, messages.autocompleteMessages.clearButton)),
                        hasSearchButton && /*#__PURE__*/ jsx(SearchButton, _object_spread_props$m(_object_spread$r({}, searchButtonProps), {
                            focusedByDefault: focusedByDefault && setFocusOn === "searchButton"
                        }))
                    ]
                })
            ]
        }))
    });
});

const SectionNavigationContext = createContext({});

// TODO: use new asserts syntax when Typescript will be updated
const isContextPresent = (context)=>Object.keys(context).length !== 0;
const useSectionNavigationContext = ()=>{
    const context = useContext(SectionNavigationContext);
    if (!isContextPresent(context)) {
        throw new Error("SectionNavigation compound components cannot be rendered outside the SectionNavigation component");
    }
    return context;
};

const SectionNavigationContextComponent = ({ children })=>{
    const context = useSectionNavigationContext();
    return children(context);
};

function _define_property$q(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$q(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$q(target, key, source[key]);
        });
    }
    return target;
}
function _object_without_properties$m(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$m(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$m(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const spreadableProp$1 = (prop)=>Array.isArray(prop) ? prop : [
        prop
    ];
/**
 * buildGetToggleProps
 *
 * @param {string} id
 * @param {number | string} index
 * @param {Function} onClick
 * @param {Function} onKeyDown
 * @param {string | string[]} className
 */ const buildGetToggleProps = (_param)=>{
    var { registeredLists, openedLists, toggleOpenList, getBlocksWith, toggleCollapsedLabel, toggleExpandedLabel, uniqueName, index, id: itemId, onClick: consumerOnClick = ()=>{}, onKeyDown: consumerOnKeyDown = ()=>{}, className: consumerClassName } = _param, consumerProps = _object_without_properties$m(_param, [
        "registeredLists",
        "openedLists",
        "toggleOpenList",
        "getBlocksWith",
        "toggleCollapsedLabel",
        "toggleExpandedLabel",
        "uniqueName",
        "index",
        "id",
        "onClick",
        "onKeyDown",
        "className"
    ]);
    /**
   * getParentContainerByIndex
   *
   * The index lookup needs to happen after the parent container has been registered.
   * The parent containers are registered inside of a ref callback which has its own lifecycle.
   * Offloading the lookup to a callback should ensure the component has had
   * enough time to initialize and the desired container is available.
   *
   * @param {string} containerIndex
   * @returns { index: number | string; ref: ReactNode }
   */ const getParentContainerByIndex = (containerIndex)=>{
        const listContainer = registeredLists.current.find(({ index: listIndex })=>listIndex === containerIndex);
        if (!listContainer) {
            return {};
        }
        return listContainer;
    };
    const isOpen = openedLists.indexOf(index) !== -1;
    return _object_spread$q({
        onClick: (event)=>{
            event.persist();
            toggleOpenList(index);
            consumerOnClick({
                event,
                containerProps: _object_spread$q({}, getParentContainerByIndex(index))
            });
        },
        onKeyDown: (event)=>{
            const { key } = event;
            event.persist();
            let keyHandled = false;
            switch(key){
                case " ":
                case "Spacebar":
                case "Enter":
                    keyHandled = true;
                    break;
                case "Esc":
                case "Escape":
                    if (isOpen) {
                        keyHandled = true;
                    }
            }
            if (keyHandled) {
                event.preventDefault();
                toggleOpenList(index);
            }
            consumerOnKeyDown({
                event,
                containerProps: _object_spread$q({}, getParentContainerByIndex(index))
            });
        },
        className: cn(getBlocksWith("__toggle"), isOpen && getBlocksWith("__toggle--open"), ...spreadableProp$1(consumerClassName)),
        "aria-describedby": `${uniqueName}-${itemId}-label`,
        "aria-label": isOpen ? toggleExpandedLabel : toggleCollapsedLabel,
        "aria-expanded": isOpen,
        "aria-controls": `${uniqueName}-${index}`
    }, consumerProps);
};

function _define_property$p(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$p(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$p(target, key, source[key]);
        });
    }
    return target;
}
function _object_without_properties$l(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$l(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$l(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const BASE_CLASS = "eb-section-nav";
const spreadableProp = (prop)=>Array.isArray(prop) ? prop : [
        prop
    ];
const useList = ()=>{
    const initialList = [];
    const registeredLists = useRef(initialList);
    const registerList = useCallback((props)=>{
        const listIsRegistered = registeredLists.current.find(({ index: currentIndex })=>currentIndex === props.index);
        if (!listIsRegistered) {
            registeredLists.current = [
                ...registeredLists.current,
                props
            ];
        }
    }, []);
    return [
        registeredLists,
        registerList
    ];
};
const useOpenLists = (initialOpenLists = [])=>{
    const [openList, setOpenList] = useState(initialOpenLists);
    const toggleOpenList = useCallback((index)=>{
        const openIndex = openList.indexOf(index);
        if (openIndex === -1) {
            setOpenList([
                ...openList,
                index
            ]);
            return;
        }
        setOpenList([
            ...openList.slice(0, openIndex),
            ...openList.slice(openIndex + 1)
        ]);
    }, [
        openList
    ]);
    return [
        openList,
        toggleOpenList
    ];
};
const useSectionNavigationUtils = (controlledProps = {})=>{
    const { toggleExpandedLabel, toggleCollapsedLabel, currentItemId: newCurrentItemId, isFlat, uniqueName: consumerUniqueName, consumerBlockClassName } = controlledProps;
    const [uniqueName, setUniqueName] = useState(consumerUniqueName);
    const [isAutogenerated, setAutogenerated] = useState(false);
    const [registeredLists, registerList] = useList();
    const [openedLists, toggleOpenList] = useOpenLists();
    const [currentItemId, setCurrentItemId] = useState(isFlat ? null : newCurrentItemId || null);
    const getBlocksWith = useCallback((props)=>createBemBlockBuilder$1([
            BASE_CLASS,
            ...spreadableProp(consumerBlockClassName)
        ])(props), [
        consumerBlockClassName
    ]);
    useEffect(()=>{
        if (isFlat) {
            setCurrentItemId(null);
            return;
        }
        if (!isFlat && newCurrentItemId && currentItemId !== newCurrentItemId) {
            setCurrentItemId(newCurrentItemId);
        }
    }, [
        currentItemId,
        newCurrentItemId,
        isFlat
    ]);
    useEffect(()=>{
        // If consumer unique name is not blank and unique name is autogenerated, set it
        if (isAutogenerated && consumerUniqueName) {
            setUniqueName(consumerUniqueName);
            setAutogenerated(false);
        }
        // If unique name is not autogenerated and unique name does not equal consumer unique name, set it
        if (!isAutogenerated && consumerUniqueName !== uniqueName) {
            setUniqueName(consumerUniqueName);
            setAutogenerated(false);
        }
        // If unique name is blank and not autogenerated, randomize it
        if (!isAutogenerated && !uniqueName) {
            setUniqueName(`eb-${String(Math.random()).slice(2, 7)}`);
            setAutogenerated(true);
        }
    }, [
        consumerUniqueName,
        uniqueName,
        isAutogenerated
    ]);
    /**
   * refCallback
   * Provides a way to store a node with its current index.
   *
   * @param {number} index
   */ const refCallback = useCallback((index)=>{
        const ref = {
            current: null
        };
        return (node)=>{
            if (node) {
                ref.current = node;
                registerList({
                    index,
                    ref
                });
            }
        };
    }, [
        registerList
    ]);
    /**
   * getListProps
   *
   * @param {boolean} isSubsection
   * @param {boolean} isFlat
   * @param {number} index
   * @param {string|string[]} className
   * @param {object} consumerProps
   *
   * @returns Expected props for the list.
   */ const getListProps = useCallback((_param)=>{
        var { isSubsection, index, className: consumerClassName } = _param, consumerProps = _object_without_properties$l(_param, [
            "isSubsection",
            "index",
            "className"
        ]);
        const isOpen = openedLists.indexOf(index) !== -1;
        return _object_spread$p({
            className: cn(getBlocksWith("__list"), isSubsection && getBlocksWith("__list--subsection"), (isOpen || isFlat) && getBlocksWith("__list--open"), isFlat && getBlocksWith("__list--flat"), ...spreadableProp(consumerClassName)),
            ref: refCallback(index),
            id: isSubsection && `${uniqueName}-${index}` || undefined,
            role: "list"
        }, consumerProps);
    }, [
        openedLists,
        getBlocksWith,
        refCallback,
        uniqueName,
        isFlat
    ]);
    /**
   * getListItemProps
   *
   * @param {string} id
   * @param {string|string[]} className
   * @param {string} childListIndex
   * @param {boolean} hasSubsections
   * @param {object} consumerProps
   *
   * @returns Expected props for the list item.
   */ const getListItemProps = useCallback((_param)=>{
        var { hasSubsections, childListIndex, id, className: consumerClassName } = _param, consumerProps = _object_without_properties$l(_param, [
            "hasSubsections",
            "childListIndex",
            "id",
            "className"
        ]);
        const isCurrent = !isFlat && id === currentItemId;
        const isOpen = openedLists.indexOf(childListIndex) !== -1;
        return _object_spread$p({
            className: cn(getBlocksWith("__list-item"), hasSubsections && getBlocksWith("__list-item--has-subsections"), isOpen && getBlocksWith("__list-item--open"), isCurrent && getBlocksWith("__list-item--current"), ...spreadableProp(consumerClassName))
        }, consumerProps);
    }, [
        getBlocksWith,
        isFlat,
        currentItemId,
        openedLists
    ]);
    /**
   * getListItemLabelProps
   *
   * @param {string} id
   * @param {string|string[]} className
   * @param {string} dir
   * @param {string} locale
   * @param {function} onClick
   * @param {function} onKeyDown
   * @param {string} tabIndex
   * @param {object} consumerProps
   *
   * @returns {object} Expected props for the list item label.
   */ const getListItemLabelProps = useCallback((_param)=>{
        var { id: itemId, className: consumerClassName, locale, dir, onClick: consumerOnClick = ()=>{}, onKeyDown: consumerOnKeyDown = ()=>{}, tabIndex } = _param, consumerProps = _object_without_properties$l(_param, [
            "id",
            "className",
            "locale",
            "dir",
            "onClick",
            "onKeyDown",
            "tabIndex"
        ]);
        const isCurrent = !isFlat && itemId === currentItemId;
        return _object_spread$p({
            className: cn(getBlocksWith("__list-item-label"), ...spreadableProp(consumerClassName)),
            dir: "auto",
            id: `${uniqueName}-${itemId}-label`,
            lang: locale,
            onClick: (event)=>{
                !isFlat && setCurrentItemId(itemId);
                consumerOnClick({
                    event,
                    current: itemId
                });
            },
            onKeyDown: (event)=>{
                const { key } = event;
                switch(key){
                    case " ":
                    case "Spacebar":
                    case "Enter":
                        !isFlat && setCurrentItemId(itemId);
                }
                consumerOnKeyDown({
                    event,
                    current: itemId
                });
            },
            tabIndex: 0
        }, isCurrent ? {
            "aria-current": "true"
        } : {}, consumerProps);
    }, [
        currentItemId,
        getBlocksWith,
        isFlat,
        uniqueName
    ]);
    /**
   * getAuxiliaryWrapperProps
   *
   * @param {string|string[]} className
   * @param {object} consumerProps
   *
   * @returns Expected props for the AuxiliaryWrapper.
   */ const getAuxiliaryWrapperProps = useCallback((_param)=>{
        var { className: consumerClassName } = _param, consumerProps = _object_without_properties$l(_param, [
            "className"
        ]);
        return _object_spread$p({
            className: cn(getBlocksWith("__auxiliary-wrapper"), ...spreadableProp(consumerClassName))
        }, consumerProps);
    }, [
        getBlocksWith
    ]);
    /**
   * getAuxiliaryIconProps
   *
   * @param {string} id
   * @param {string|string[]} className
   * @param {object} consumerProps
   *
   * @returns {object} Expected props for the AuxiliaryIcon.
   */ const getAuxiliaryIconProps = useCallback((_param)=>{
        var { className: consumerClassName, id: itemId } = _param, consumerProps = _object_without_properties$l(_param, [
            "className",
            "id"
        ]);
        return _object_spread$p({
            className: cn(getBlocksWith("__auxiliary-icon"), ...spreadableProp(consumerClassName)),
            "aria-describedby": `${uniqueName}-${itemId}-label`
        }, consumerProps);
    }, [
        getBlocksWith,
        uniqueName
    ]);
    /**
   * getNavWrapperProps
   *
   * @param {string|string[]} className
   * @param {object} consumerProps
   *
   * @returns {object} Expected props for the NavWrapper.
   */ const getNavWrapperProps = useCallback((_param)=>{
        var { className: consumerClassName } = _param, consumerProps = _object_without_properties$l(_param, [
            "className"
        ]);
        return _object_spread$p({
            "aria-labelledby": `${uniqueName}-header`,
            className: cn(getBlocksWith("__nav-wrapper"), isFlat && getBlocksWith("__nav-wrapper--flat"), ...spreadableProp(consumerClassName))
        }, consumerProps);
    }, [
        getBlocksWith,
        isFlat,
        uniqueName
    ]);
    const getHeaderProps = useCallback((_param)=>{
        var { className: consumerClassName } = _param, consumerProps = _object_without_properties$l(_param, [
            "className"
        ]);
        return _object_spread$p({
            id: `${uniqueName}-header`,
            className: cn(getBlocksWith("__header"), ...spreadableProp(consumerClassName)),
            dir: "auto"
        }, consumerProps);
    }, [
        getBlocksWith,
        uniqueName
    ]);
    /**
   * getToggleProps
   *
   * @param {string} id
   * @param {number | string} index
   * @param {Function} onClick
   * @param {Function} onKeyDown
   * @param {string | string[]} className
   */ const getToggleProps = useCallback((_param)=>{
        var { index, onClick, onKeyDown, className, id } = _param, consumerProps = _object_without_properties$l(_param, [
            "index",
            "onClick",
            "onKeyDown",
            "className",
            "id"
        ]);
        return buildGetToggleProps(_object_spread$p({
            registeredLists,
            openedLists,
            getBlocksWith,
            toggleExpandedLabel,
            toggleCollapsedLabel,
            uniqueName,
            toggleOpenList,
            index,
            id,
            onClick,
            onKeyDown,
            className
        }, consumerProps));
    }, [
        registeredLists,
        openedLists,
        getBlocksWith,
        toggleExpandedLabel,
        toggleCollapsedLabel,
        uniqueName,
        toggleOpenList
    ]);
    return useMemo(()=>({
            getAuxiliaryIconProps,
            getAuxiliaryWrapperProps,
            getListProps,
            getListItemProps,
            getNavWrapperProps,
            getHeaderProps,
            getToggleProps,
            getListItemLabelProps,
            getBlocksWith,
            openedLists,
            toggleOpenList,
            registeredLists,
            registerList,
            setCurrentItemId,
            currentItemId,
            isFlat,
            uniqueName
        }), [
        currentItemId,
        getAuxiliaryIconProps,
        getAuxiliaryWrapperProps,
        getBlocksWith,
        getHeaderProps,
        getListItemLabelProps,
        getListItemProps,
        getListProps,
        getNavWrapperProps,
        getToggleProps,
        isFlat,
        openedLists,
        registerList,
        registeredLists,
        toggleOpenList,
        uniqueName
    ]);
};

function _define_property$o(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$o(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$o(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$l(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$l(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$l(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$k(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$k(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$k(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const SectionNavigationNavWrapper = (_param)=>{
    var { children, className = "" } = _param, rest = _object_without_properties$k(_param, [
        "children",
        "className"
    ]);
    const { getNavWrapperProps } = useSectionNavigationContext();
    if (!getNavWrapperProps) {
        throw new Error("SectionNavigation.NavWrapper component requires getNavWrapperProps to be defined");
    }
    return /*#__PURE__*/ jsx("nav", _object_spread_props$l(_object_spread$o({}, getNavWrapperProps(_object_spread$o({
        className
    }, rest))), {
        children: children
    }));
};

function _define_property$n(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$n(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$n(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$k(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$k(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$k(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$j(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$j(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$j(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const SectionNavigationHeader = (_param)=>{
    var { children, className, customHTMLTag: Wrapper = "h2" } = _param, rest = _object_without_properties$j(_param, [
        "children",
        "className",
        "customHTMLTag"
    ]);
    const { getHeaderProps } = useSectionNavigationContext();
    if (!getHeaderProps) {
        throw new Error("SectionNavigation.Header component requires getHeaderProps to be defined");
    }
    return /*#__PURE__*/ jsx(Wrapper, _object_spread_props$k(_object_spread$n({}, getHeaderProps(rest)), {
        children: children
    }));
};

function _define_property$m(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$m(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$m(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$j(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$j(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$j(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$i(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$i(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$i(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const SectionNavigationList = (_param)=>{
    var { index, isSubsection = false, children } = _param, rest = _object_without_properties$i(_param, [
        "index",
        "isSubsection",
        "children"
    ]);
    const { getListProps } = useSectionNavigationContext();
    if (!getListProps) {
        throw new Error("SectionNavigation.List component requires getListProps to be defined");
    }
    return /*#__PURE__*/ jsx("ul", _object_spread_props$j(_object_spread$m({}, getListProps(_object_spread$m({
        isSubsection,
        index
    }, rest))), {
        children: children
    }));
};
var SectionNavigationList$1 = /*#__PURE__*/ memo(SectionNavigationList);

function _define_property$l(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$l(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$l(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$i(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$i(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$i(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$h(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$h(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$h(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const SectionNavigationListItem = (_param)=>{
    var { children, hasSubsections = true, childListIndex = "", id } = _param, rest = _object_without_properties$h(_param, [
        "children",
        "hasSubsections",
        "childListIndex",
        "id"
    ]);
    const { getListItemProps } = useSectionNavigationContext();
    if (!getListItemProps) {
        throw new Error("SectionNavigation.ListItem component requires getListItemProps to be defined");
    }
    return /*#__PURE__*/ jsx("li", _object_spread_props$i(_object_spread$l({}, getListItemProps(_object_spread$l({
        hasSubsections,
        id,
        childListIndex
    }, rest))), {
        children: children
    }));
};
var SectionNavigationListItem$1 = /*#__PURE__*/ memo(SectionNavigationListItem);

function _define_property$k(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$k(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$k(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$h(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$h(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$h(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$g(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$g(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$g(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const SectionNavigationListItemContent = (_param)=>{
    var { children } = _param, rest = _object_without_properties$g(_param, [
        "children"
    ]);
    const { getBlocksWith } = useSectionNavigationContext();
    if (!getBlocksWith) {
        throw new Error("SectionNavigation component requires a class builder to be defined");
    }
    return /*#__PURE__*/ jsx("div", _object_spread_props$h(_object_spread$k({
        className: getBlocksWith("__list-item-content")
    }, rest), {
        children: children
    }));
};

function _define_property$j(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$j(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$j(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$g(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$g(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$g(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$f(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$f(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$f(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const SectionNavigationToggle = (_param)=>{
    var { index, className, icon, id } = _param, rest = _object_without_properties$f(_param, [
        "index",
        "className",
        "icon",
        "id"
    ]);
    const { getToggleProps, isFlat } = useSectionNavigationContext();
    if (!getToggleProps) {
        throw new Error("SectionNavigation.Toggle component requires getToggleProps to be defined");
    }
    if (isFlat) {
        return null;
    }
    const toggleIcon = icon ? icon : faAngleDown;
    return /*#__PURE__*/ jsx("button", _object_spread_props$g(_object_spread$j({}, getToggleProps(_object_spread$j({
        index,
        id,
        className
    }, rest))), {
        children: /*#__PURE__*/ jsx(FontAwesomeIcon, {
            className: "eb-section-nav__toggle-icon",
            icon: toggleIcon
        })
    }));
};

function _define_property$i(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$i(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$i(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$f(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$f(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$f(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$e(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$e(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$e(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const SectionNavigationAuxiliaryWrapper = (_param)=>{
    var { children } = _param, rest = _object_without_properties$e(_param, [
        "children"
    ]);
    const { getAuxiliaryWrapperProps } = useSectionNavigationContext();
    if (!getAuxiliaryWrapperProps) {
        throw new Error("SectionNavigation.AuxiliaryWrapper component requires getAuxiliaryWrapperProps to be defined");
    }
    return /*#__PURE__*/ jsx("div", _object_spread_props$f(_object_spread$i({}, getAuxiliaryWrapperProps(_object_spread$i({}, rest))), {
        children: children
    }));
};

const destructureItem = (item, getters)=>{
    const { getId, getLabel, getSubsections } = getters;
    if (!("id" in item) && !getId) {
        throw new Error("SectionNavigationItemsMapper: getId prop is required!");
    }
    if (!("label" in item) && !getLabel) {
        throw new Error("SectionNavigationItemsMapper: getLabel prop is required!");
    }
    const maybeDefaultItem = item;
    return {
        id: maybeDefaultItem.id || getId && getId(item) || "",
        label: maybeDefaultItem.label || getLabel && getLabel(item) || "",
        subsections: maybeDefaultItem.subsections || getSubsections && getSubsections(item) || []
    };
};
const countMaxLevel = (items, getters, level = 0)=>{
    return items.reduce((acc, item)=>{
        const { subsections } = destructureItem(item, getters);
        if (!subsections) {
            return Math.max(acc, level);
        }
        return Math.max(acc, level, countMaxLevel(subsections, getters, level + 1));
    }, level);
};

function _define_property$h(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$h(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$h(target, key, source[key]);
        });
    }
    return target;
}
function _object_without_properties$d(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$d(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$d(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const DefaultListItem = /*#__PURE__*/ memo((props)=>{
    const rest = _object_without_properties$d(props, [
        "item",
        "label",
        "subsections",
        "index"
    ]);
    return /*#__PURE__*/ jsx(SectionNavigationListItem$1, _object_spread$h({}, rest));
});
const ListBuilder = (props)=>{
    const { items, index: parentIndex = 1, isSubsection = false, ItemContent, ListItem = DefaultListItem, List = SectionNavigationList$1, getLabel, getId, getSubsections, level = 0, renderLevel, getters } = props, rest = _object_without_properties$d(props, [
        "items",
        "index",
        "isSubsection",
        "ItemContent",
        "ListItem",
        "List",
        "getLabel",
        "getId",
        "getSubsections",
        "level",
        "renderLevel",
        "getters"
    ]);
    if (level > renderLevel) {
        return null;
    }
    return /*#__PURE__*/ jsx(List, {
        index: parentIndex,
        isSubsection: isSubsection,
        children: items.map((item, itemIndex)=>{
            const { id, label, subsections } = destructureItem(item, getters);
            const hasSubsections = subsections.length > 0;
            return /*#__PURE__*/ jsxs(ListItem, {
                childListIndex: `${parentIndex}-${itemIndex + 1}`,
                index: `${parentIndex}-${itemIndex + 1}`,
                hasSubsections: hasSubsections,
                id: id,
                label: label,
                subsections: subsections,
                item: item,
                children: [
                    /*#__PURE__*/ jsx(ItemContent, _object_spread$h({
                        id: id,
                        label: label,
                        index: `${parentIndex}-${itemIndex + 1}`,
                        subsections: subsections,
                        item: item
                    }, rest)),
                    hasSubsections && /*#__PURE__*/ jsx(ListBuilder, _object_spread$h({
                        items: subsections,
                        index: `${parentIndex}-${itemIndex + 1}`,
                        ItemContent: ItemContent,
                        List: List,
                        ListItem: ListItem,
                        isSubsection: true,
                        getLabel: getLabel,
                        getId: getId,
                        getSubsections: getSubsections,
                        level: level + 1,
                        renderLevel: renderLevel,
                        getters: getters
                    }, rest))
                ]
            }, id);
        })
    });
};
// React.memo breaks generics
var ListBuilder$1 = /*#__PURE__*/ memo(ListBuilder);

function _define_property$g(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$g(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$g(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$e(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$e(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$e(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
const SectionNavigationNavListBuilder = (props)=>{
    const { items, getLabel, getId, getSubsections = (item)=>item.subsections, ItemContent } = props;
    const [renderLevel, setRenderLevel] = useState(0);
    const getters = useMemo(()=>({
            getId,
            getLabel,
            getSubsections
        }), [
        getId,
        getLabel,
        getSubsections
    ]);
    const maxLevel = useMemo(()=>countMaxLevel(items, getters), [
        getters,
        items
    ]);
    useLayoutEffect(()=>{
        if (renderLevel < maxLevel) {
            setRenderLevel((prevState)=>prevState + 1);
        }
    }, [
        maxLevel,
        renderLevel
    ]);
    return /*#__PURE__*/ jsx(ListBuilder$1, _object_spread_props$e(_object_spread$g({}, props), {
        renderLevel: renderLevel,
        getters: getters,
        ItemContent: ItemContent
    }));
};
var SectionNavigationNavListBuilder$1 = /*#__PURE__*/ memo(SectionNavigationNavListBuilder);

const SectionNavigation = /*#__PURE__*/ forwardRef(function SectionNavigation({ customHTMLTag: Wrapper = "div", toggleExpandedLabel = "Collapse", toggleCollapsedLabel = "Expand", currentItemId, isFlat, className: consumerBlockClassName, uniqueName, children }, forwardedRef) {
    const controlledProps = useMemo(()=>({
            toggleExpandedLabel,
            toggleCollapsedLabel,
            currentItemId,
            isFlat,
            consumerBlockClassName,
            uniqueName
        }), [
        consumerBlockClassName,
        currentItemId,
        isFlat,
        toggleCollapsedLabel,
        toggleExpandedLabel,
        uniqueName
    ]);
    const utils = useSectionNavigationUtils(controlledProps);
    return /*#__PURE__*/ jsx(SectionNavigationContext.Provider, {
        value: utils,
        children: /*#__PURE__*/ jsx(Wrapper, {
            ref: forwardedRef,
            className: utils.getBlocksWith(),
            children: children
        })
    });
});
SectionNavigationContextComponent.displayName = "SectionNavigation.Context";
SectionNavigation.Context = SectionNavigationContextComponent;
SectionNavigationHeader.displayName = "SectionNavigation.Header";
SectionNavigation.Header = SectionNavigationHeader;
SectionNavigationToggle.displayName = "SectionNavigation.Toggle";
SectionNavigation.Toggle = SectionNavigationToggle;
SectionNavigationNavWrapper.displayName = "SectionNavigation.NavWrapper";
SectionNavigation.NavWrapper = SectionNavigationNavWrapper;
SectionNavigationAuxiliaryWrapper.displayName = "SectionNavigation.AuxiliaryWrapper";
SectionNavigation.AuxiliaryWrapper = SectionNavigationAuxiliaryWrapper;
SectionNavigationList$1.displayName = "SectionNavigation.List";
SectionNavigation.List = SectionNavigationList$1;
SectionNavigationListItem$1.displayName = "SectionNavigation.ListItem";
SectionNavigation.ListItem = SectionNavigationListItem$1;
SectionNavigationListItemContent.displayName = "SectionNavigation.ListItemContent";
SectionNavigation.ListItemContent = SectionNavigationListItemContent;
SectionNavigationNavListBuilder$1.displayName = "SectionNavigation.NavListBuilder";
SectionNavigation.NavListBuilder = SectionNavigationNavListBuilder$1;
SectionNavigation.useSectionNavigationContext = useSectionNavigationContext;

const ShowMoreContext = /*#__PURE__*/ createContext(undefined);

const useShowMoreContext = ()=>{
    const context = useContext(ShowMoreContext);
    if (context === undefined) {
        throw new Error("ShowMore compound components cannot be rendered outside the ShowMore component");
    }
    return context;
};

const ShowMoreContextComponent = ({ children })=>{
    const context = useShowMoreContext();
    return children(context);
};

function _define_property$f(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$f(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$f(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$d(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$d(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$d(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$c(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$c(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$c(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const ShowMoreButton = /*#__PURE__*/ forwardRef((_param, forwardedRef)=>{
    var { expandContent, collapseContent, styleType = "inline", className } = _param, rest = _object_without_properties$c(_param, [
        "expandContent",
        "collapseContent",
        "styleType",
        "className"
    ]);
    const { isExpanded, toggleIsExpanded, totalItems, limit } = useShowMoreContext();
    if (totalItems <= limit) {
        return null;
    }
    const getClassNames = ()=>{
        const getBlocksWith = createBemBlockBuilder$1([
            "eb-show-more-button",
            className
        ]);
        return cn(getBlocksWith(), isExpanded ? getBlocksWith("--expanded") : getBlocksWith("--collapsed"));
    };
    return /*#__PURE__*/ jsx(Button$1, _object_spread_props$d(_object_spread$f({
        ref: forwardedRef,
        className: getClassNames(),
        onClick: toggleIsExpanded,
        styleType: styleType,
        type: "button"
    }, rest), {
        children: isExpanded ? collapseContent : expandContent
    }));
});
ShowMoreButton.displayName = "ShowMoreButton";

function _define_property$e(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$e(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$e(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$c(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$c(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$c(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
const ShowMoreItems = ({ children })=>{
    const showMoreItemRef = useRef(null);
    const { isExpanded, limit, setTotalItems } = useShowMoreContext();
    const allItems = Children.toArray(children);
    const itemsToRender = allItems.slice(0, isExpanded ? allItems.length : limit);
    const numberOfChildren = Children.count(children);
    useEffect(()=>{
        setTotalItems(numberOfChildren);
    }, [
        numberOfChildren,
        setTotalItems
    ]);
    useEffect(()=>{
        if (showMoreItemRef.current) {
            showMoreItemRef.current.focus();
        }
        if (isExpanded && !showMoreItemRef.current) {
            console.error("The ShowMore component provides a ref and tabindex to the components passed as items. Please make sure that these components can accept refs and tabindex as props.");
        }
    }, [
        isExpanded,
        showMoreItemRef
    ]);
    const getClassNames = (classNames)=>{
        const getBlocksWith = createBemBlockBuilder$1(classNames);
        return cn(getBlocksWith(), getBlocksWith(isExpanded ? "--expanded" : "--collapsed"));
    };
    const visibleItems = itemsToRender.map((child, index)=>{
        const baseProps = _object_spread_props$c(_object_spread$e({}, child.props), {
            className: getClassNames([
                child.props.className,
                "eb-show-more-item"
            ]),
            key: index
        });
        let focusableProps;
        if (index === limit) {
            focusableProps = {
                ref: showMoreItemRef,
                tabIndex: -1
            };
        }
        return /*#__PURE__*/ cloneElement(child, _object_spread$e({}, baseProps, focusableProps));
    });
    return /*#__PURE__*/ jsx(Fragment, {
        children: visibleItems
    });
};

const ShowMore = ({ limit = 10, children, className })=>{
    const [isExpanded, setIsExpanded] = useState(false);
    const [totalItems, setTotalItems] = useState(0);
    const setItems = useCallback((newState)=>{
        setTotalItems(newState);
    }, [
        setTotalItems
    ]);
    return /*#__PURE__*/ jsx(ShowMoreContext.Provider, {
        value: {
            limit,
            isExpanded,
            toggleIsExpanded: ()=>setIsExpanded(!isExpanded),
            totalItems,
            setTotalItems: setItems
        },
        children: /*#__PURE__*/ jsx("div", {
            className: cn("eb-show-more", className),
            "data-auto": "show-more",
            children: children
        })
    });
};
ShowMore.Context = ShowMoreContextComponent;
ShowMore.Button = ShowMoreButton;
ShowMore.Items = ShowMoreItems;

const ShowMorePagination = ({ children: renderPage, pagesContainerTag: PagesContainerTag = "section", className, loadingIndicator: LoadingIndicator, messages, error = false, isLoading = false, onLoadPage, pages, totalRecords, uniqueName })=>{
    const hasItemsToLoad = totalRecords > pages.flat().length;
    const getBlocksWith = createBemBlockBuilder$1([
        "eb-pagination",
        className
    ]);
    const loadingSpinnerRef = useRef(null);
    useEffect(()=>{
        if (isLoading && loadingSpinnerRef.current) {
            loadingSpinnerRef.current.focus();
        }
    }, [
        isLoading
    ]);
    const getFooterContent = ()=>{
        if (hasItemsToLoad) {
            if (isLoading) {
                return /*#__PURE__*/ jsx("div", {
                    className: getBlocksWith("__loading-indicator"),
                    tabIndex: -1,
                    ref: loadingSpinnerRef,
                    children: LoadingIndicator ? /*#__PURE__*/ jsx(LoadingIndicator, {}) : /*#__PURE__*/ jsx(Loading, {
                        direction: "horizontal",
                        size: "small",
                        loadingMessage: messages.loading
                    })
                });
            } else {
                return /*#__PURE__*/ jsxs(Button$1, {
                    "aria-controls": uniqueName,
                    className: getBlocksWith("__button"),
                    onClick: onLoadPage,
                    type: "button",
                    styleType: "default",
                    "data-auto": "show-more-button",
                    children: [
                        /*#__PURE__*/ jsx(FontAwesomeIcon, {
                            className: getBlocksWith("__button-icon"),
                            icon: faFileAlt
                        }),
                        messages.showMoreButtonTitle
                    ]
                });
            }
        } else {
            return /*#__PURE__*/ jsx("div", {
                className: "screen-reader-text",
                children: messages.endOfList
            });
        }
    };
    return /*#__PURE__*/ jsxs(Fragment, {
        children: [
            /*#__PURE__*/ jsx(PagesContainerTag, {
                id: uniqueName,
                className: getBlocksWith(),
                children: pages.map(renderPage)
            }),
            getFooterContent(),
            error && !isLoading && /*#__PURE__*/ jsx("div", {
                className: getBlocksWith("__error-message"),
                role: "alert",
                children: messages.error
            })
        ]
    });
};

var ScrollControlTypes;
(function(ScrollControlTypes) {
    ScrollControlTypes["start"] = "start";
    ScrollControlTypes["end"] = "end";
})(ScrollControlTypes || (ScrollControlTypes = {}));
const ICONS = {
    ltr: {
        start: faChevronLeft,
        end: faChevronRight
    },
    rtl: {
        start: faChevronRight,
        end: faChevronLeft
    }
};
const ScrollControl = ({ isEnabled, isRTL, title, type, onClick, className })=>{
    const icons = ICONS[isRTL ? "rtl" : "ltr"];
    const onControlClick = onClick.bind(null, type);
    return /*#__PURE__*/ jsx(Button$1, {
        disabled: !isEnabled,
        className: cn(className, `${className}--${type}`),
        onClick: onControlClick,
        "aria-hidden": true,
        tabIndex: -1,
        styleType: "flat-icon",
        type: "button",
        title: title,
        "data-auto": `tabs-scroll-${type}`,
        children: /*#__PURE__*/ jsx(FontAwesomeIcon, {
            icon: icons[type]
        })
    });
};
ScrollControl.defaultProps = {
    isRTL: false
};

/**
 * @see https://stackoverflow.com/a/30527650/5219328
 */ function _define_property$d(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$d(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$d(target, key, source[key]);
        });
    }
    return target;
}
function _object_without_properties$b(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$b(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$b(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const getScrollStep = (targetPosition, currentPosition, scrollRate)=>Math.ceil((targetPosition - currentPosition) / scrollRate);
const isTargetPositionReached = (targetPosition, currentPosition)=>currentPosition === targetPosition || getPositionsDiff(targetPosition, currentPosition) < 1;
const getPositionsDiff = (targetPosition, currentPosition)=>Math.abs(currentPosition - targetPosition);
var AXIS;
(function(AXIS) {
    AXIS["v"] = "v";
    AXIS["h"] = "h";
})(AXIS || (AXIS = {}));
const makeScroller = ()=>{
    let isScrolling = false;
    return (_param)=>{
        var { onAnimationEnd } = _param, restOptions = _object_without_properties$b(_param, [
            "onAnimationEnd"
        ]);
        if (isScrolling) {
            return;
        }
        isScrolling = true;
        const handleAnimationEnd = ()=>{
            isScrolling = false;
            if (typeof onAnimationEnd === "function") {
                onAnimationEnd();
            }
        };
        smoothScroll(_object_spread$d({
            onAnimationEnd: handleAnimationEnd
        }, restOptions));
    };
};
const smoothScroll = ({ container, targetScrollPosition, axis = "v", scrollRate = 5, onAnimationEnd })=>{
    if (!container) {
        return;
    }
    const scrollProperty = axis === "v" ? "scrollTop" : "scrollLeft";
    const currentScrollPosition = container[scrollProperty] || 0;
    if (isTargetPositionReached(targetScrollPosition, currentScrollPosition)) {
        onAnimationEnd();
        return;
    }
    const diff = getPositionsDiff(targetScrollPosition, currentScrollPosition);
    if (diff < scrollRate) {
        container[scrollProperty] = targetScrollPosition;
    } else {
        const step = getScrollStep(targetScrollPosition, currentScrollPosition, scrollRate);
        container[scrollProperty] = currentScrollPosition + step;
    }
    window.requestAnimationFrame(()=>{
        smoothScroll({
            container,
            targetScrollPosition,
            axis,
            scrollRate,
            onAnimationEnd
        });
    });
};

const isNegativeRTL = ()=>{
    const ua = navigator.userAgent.toLowerCase();
    // @ts-expect-error documentMode does only exist in IE so I don't see any alternative to ts-ignore
    const isIE = !!document.documentMode;
    const isFirefox = ua.indexOf("gecko") > -1;
    const isSafari = ua.indexOf("safari") > -1;
    const isChrome = ua.indexOf("chrome") > -1;
    return (isSafari || isFirefox) && !isChrome && !isIE;
};
// In IE sometimes because of inconvenient rounding we get wrong proportions
// between container and content widths, so scrolling becomes allowed when it shouldn't
// 1px buffer prevents it (though sometimes hides 1px from the real button,
// but it's not really visible.
const getContainerWidthForIE = (width)=>width + 1;
const isScrollingAllowedCases = {
    ltr: {
        all: ({ scrollLeft, scrollWidth, clientWidth })=>({
                start: scrollLeft > 0,
                end: Math.ceil(scrollLeft) < scrollWidth - clientWidth
            }),
        ie: ({ scrollLeft, scrollWidth, clientWidth })=>({
                start: scrollLeft > 0,
                end: Math.ceil(scrollLeft) < scrollWidth - getContainerWidthForIE(clientWidth)
            })
    },
    rtl: {
        chrome: ({ scrollLeft, scrollWidth, clientWidth })=>({
                start: Math.ceil(scrollLeft) < scrollWidth - clientWidth,
                end: scrollLeft > 0
            }),
        negativeRTL: ({ scrollLeft, scrollWidth, clientWidth })=>({
                start: scrollLeft < 0,
                end: Math.ceil(Math.abs(scrollLeft)) < scrollWidth - clientWidth
            }),
        ie: ({ scrollLeft, scrollWidth, clientWidth })=>({
                start: scrollLeft > 0,
                end: Math.ceil(scrollLeft) < scrollWidth - getContainerWidthForIE(clientWidth)
            })
    }
};
const getIsScrollingAllowed = ({ element, isRTL })=>{
    let isScrollingAllowed;
    if (isRTL) {
        if (isNegativeRTL()) {
            // Firefox and safari
            isScrollingAllowed = isScrollingAllowedCases.rtl.negativeRTL;
        } else if (!element.scrollTo) {
            isScrollingAllowed = isScrollingAllowedCases.rtl.ie;
        } else {
            isScrollingAllowed = isScrollingAllowedCases.rtl.chrome;
        }
    } else {
        if (!element.scrollTo) {
            isScrollingAllowed = isScrollingAllowedCases.ltr.ie;
        } else {
            isScrollingAllowed = isScrollingAllowedCases.ltr.all;
        }
    }
    return isScrollingAllowed(element);
};
const getNextScrollingPosition = ({ element, isRTL, type })=>{
    const { start, end } = ScrollControlTypes;
    const { scrollWidth, scrollLeft: currentScroll, clientWidth: tabListWidth } = element;
    const maxScroll = scrollWidth - tabListWidth;
    let direction = type === end ? 1 : -1;
    // In all browsers but IE horizontal scrolling is not inverted in RTL,
    // so initial value is equal to container's width
    // In IE it's 0, so we have to scroll in opposite direction
    if (isRTL && !!element.scrollTo) {
        direction = -1 * direction;
    }
    let nextScroll = currentScroll + tabListWidth * direction;
    if (isRTL && isNegativeRTL()) {
        // Safari and Firefox in RTL mode treat scrollLeft axis negatively,
        // so this entire IF branch is a workaround for this.
        // It is possible to combine calculations inside it with general ones,
        // but I believe that the nested IF, even if ugly, is more readable than
        // any sophisticated formula would be.
        if (type === start && nextScroll > 0) {
            nextScroll = 0;
        } else if (nextScroll < -maxScroll) {
            nextScroll = -maxScroll;
        }
    } else if (nextScroll > maxScroll) {
        nextScroll = maxScroll;
    } else if (nextScroll < 0) {
        nextScroll = 0;
    }
    return nextScroll;
};

const { useGenericContext: useTabsContext, GenericContextConsumer: TabsContextConsumer, GenericContextProvider: TabsContextProvider } = createGenericContext$1({
    errorMessage: "Tabs compound components cannot be rendered outside of the Tabs component"
});

function _define_property$c(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$c(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$c(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$b(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$b(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$b(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$a(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$a(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$a(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const DisabledTabPanel = (_param)=>{
    var { className, children } = _param, rest = _object_without_properties$a(_param, [
        "className",
        "children"
    ]);
    const { getBemBlocksWith } = useTabsContext();
    return /*#__PURE__*/ jsx("section", _object_spread_props$b(_object_spread$c({}, rest), {
        className: cn(className, getBemBlocksWith("__tab-panel"), getBemBlocksWith("__tab-panel--disabled")),
        children: children
    }));
};

function _define_property$b(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$b(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$b(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$a(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$a(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$a(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$9(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$9(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$9(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const TabPanel = (props)=>{
    const { children, className } = props, rest = _object_without_properties$9(props, [
        "children",
        "className"
    ]);
    const { getBemBlocksWith, isDisabled } = useTabsContext();
    if (isDisabled) {
        return /*#__PURE__*/ jsx(DisabledTabPanel, {
            "data-auto": "disabled-tab-panel",
            className: className,
            children: children
        });
    }
    return /*#__PURE__*/ jsx(TabPanel$1, _object_spread_props$a(_object_spread$b({
        "data-auto": "tab-panel",
        className: cn(className, getBemBlocksWith("__tab-panel")),
        selectedClassName: getBemBlocksWith("__tab-panel--selected")
    }, rest), {
        children: children
    }));
};
TabPanel.tabsRole = "TabPanel";

function _define_property$a(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$a(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$a(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$9(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$9(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$9(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$8(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$8(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$8(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const TAB_LIST_CLASSNAME = "eb-tabs__tab-list";
const ANIMATION_SCROLL_RATE = 5;
class Tabs extends Component {
    componentDidMount() {
        this.updateScrollPermissions();
        window.addEventListener("resize", this.updateScrollPermissions);
    }
    componentWillUnmount() {
        window.removeEventListener("resize", this.updateScrollPermissions);
    }
    render() {
        const _this_props = this.props, { children, className, disableTabs, i18n: { scrollControlStartTitle, scrollControlEndTitle }, isRTL, tabs, isTabRequired } = _this_props, rest = _object_without_properties$8(_this_props, [
            "children",
            "className",
            "disableTabs",
            "i18n",
            "isRTL",
            "tabs",
            "isTabRequired"
        ]);
        const { isScrollingAllowed: { start, end } } = this.state;
        const isDisabled = disableTabs || tabs.length < 2 && !isTabRequired;
        const tabsContext = {
            isDisabled,
            getBemBlocksWith: this.getBemBlocksWith
        };
        if (isDisabled) {
            return /*#__PURE__*/ jsx(TabsContextProvider, {
                value: tabsContext,
                children: /*#__PURE__*/ jsx("div", {
                    className: this.getBemBlocksWith(),
                    children: children
                })
            });
        }
        return /*#__PURE__*/ jsx(TabsContextProvider, {
            value: tabsContext,
            children: /*#__PURE__*/ jsxs(Tabs$1, _object_spread_props$9(_object_spread$a({}, rest), {
                className: this.getBemBlocksWith(),
                children: [
                    /*#__PURE__*/ jsxs("div", {
                        className: this.getBemBlocksWith("__tab-list-wrapper"),
                        children: [
                            /*#__PURE__*/ jsx(ScrollControl, {
                                className: this.getBemBlocksWith("__scroll-control"),
                                isEnabled: start,
                                isRTL: isRTL,
                                title: scrollControlStartTitle,
                                type: ScrollControlTypes.start,
                                onClick: this.handleScroll
                            }),
                            /*#__PURE__*/ jsx(TabList, {
                                "data-auto": "tab-list",
                                onScroll: this.updateScrollPermissions,
                                className: this.getBemBlocksWith("__tab-list"),
                                children: tabs.map(this.renderTab)
                            }),
                            /*#__PURE__*/ jsx(ScrollControl, {
                                className: this.getBemBlocksWith("__scroll-control"),
                                isEnabled: end,
                                isRTL: isRTL,
                                title: scrollControlEndTitle,
                                type: ScrollControlTypes.end,
                                onClick: this.handleScroll
                            })
                        ]
                    }),
                    children
                ]
            }))
        });
    }
    constructor(...args){
        super(...args);
        _define_property$a(this, "getBemBlocksWith", createBemBlockBuilder$1([
            Tabs.BASE_CLASSNAME,
            this.props.className
        ]));
        _define_property$a(this, "smoothScroll", makeScroller());
        _define_property$a(this, "state", {
            isScrollingAllowed: {
                start: false,
                end: false
            }
        });
        _define_property$a(this, "getTabList", ()=>{
            return document.getElementsByClassName(TAB_LIST_CLASSNAME)[0];
        });
        _define_property$a(this, "updateScrollPermissions", ()=>{
            this.setState({
                isScrollingAllowed: this.isScrollingAllowed()
            });
        });
        _define_property$a(this, "isScrollingAllowed", ()=>{
            const { isRTL } = this.props;
            const element = this.getTabList();
            // tablist may not exist when there is just one tab or tabs are disabled
            if (element) {
                return getIsScrollingAllowed({
                    element,
                    isRTL
                });
            } else {
                return {
                    start: false,
                    end: false
                };
            }
        });
        _define_property$a(this, "handleScroll", (type)=>{
            const { isRTL } = this.props;
            const tabList = this.getTabList();
            const nextScrollingPosition = getNextScrollingPosition({
                element: tabList,
                isRTL,
                type
            });
            this.smoothScroll({
                container: tabList,
                targetScrollPosition: nextScrollingPosition,
                axis: AXIS.h,
                scrollRate: ANIMATION_SCROLL_RATE
            });
        });
        _define_property$a(this, "renderTab", ({ icon, title, disabled, dataAuto }, index)=>{
            return /*#__PURE__*/ jsxs(Tab, {
                disabled: disabled,
                "data-auto": dataAuto,
                children: [
                    icon && /*#__PURE__*/ jsx(FontAwesomeIcon, {
                        icon: icon
                    }),
                    title
                ]
            }, `tab-${index}`);
        });
    }
}
_define_property$a(Tabs, "TabPanel", TabPanel);
_define_property$a(Tabs, "BASE_CLASSNAME", "eb-tabs");
_define_property$a(Tabs, "defaultProps", {
    defaultIndex: null,
    selectedIndex: null,
    disableTabs: false,
    isRTL: false,
    isTabRequired: false
});

function _define_property$9(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$9(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$9(target, key, source[key]);
        });
    }
    return target;
}
function _object_without_properties$7(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$7(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$7(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const TextArea = /*#__PURE__*/ forwardRef((_param, ref)=>{
    var { id, className: consumerBlockClassName, label, error, disabled } = _param, rest = _object_without_properties$7(_param, [
        "id",
        "className",
        "label",
        "error",
        "disabled"
    ]);
    const getBemBlocksWith = createBemBlockBuilder$1([
        "eb-text-area",
        consumerBlockClassName
    ]);
    return /*#__PURE__*/ jsx(TextControl, {
        className: getBemBlocksWith(),
        label: label,
        error: error,
        children: /*#__PURE__*/ jsx("textarea", _object_spread$9({
            id: id,
            disabled: disabled,
            className: getBemBlocksWith("__textarea"),
            ref: ref
        }, rest))
    });
});
TextArea.displayName = "TextArea";

const invokeCallback = (callback, param)=>{
    if (callback && typeof callback === "function") {
        callback(param);
    }
};

var ShowBylabelOrValue;
(function(ShowBylabelOrValue) {
    ShowBylabelOrValue["LABEL"] = "LABEL";
    ShowBylabelOrValue["VALUE"] = "VALUE";
    ShowBylabelOrValue["BOTH"] = "BOTH";
})(ShowBylabelOrValue || (ShowBylabelOrValue = {}));
var ArrowDirection;
(function(ArrowDirection) {
    ArrowDirection[ArrowDirection["Up"] = -1] = "Up";
    ArrowDirection[ArrowDirection["Down"] = 1] = "Down";
})(ArrowDirection || (ArrowDirection = {}));

const { GenericContextProvider: SearchMultiselectContextProvider, GenericContextConsumer: SearchMultiselectContextConsumer, useGenericContext: useSearchMultiselectContext } = createGenericContext$1({
    errorMessage: "Dropdown compound components cannot be rendered outside the Dropdown component"
});

function _define_property$8(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$8(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$8(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$8(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$8(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$8(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$6(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$6(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$6(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const SearchMultiSelectDropdownToggle = (_param)=>{
    var { children } = _param, rest = _object_without_properties$6(_param, [
        "children"
    ]);
    const { toggle, getBlocksWith, getMenuProps, messages, selectedItems, isMultiSelect, isSearchMultiselectDropdownDisabled, isOpen, options, showLabelOrValue } = useSearchMultiselectContext();
    const { getToggleButtonProps } = toggle;
    const { toggleBtnText, AllText, nItemSelectedText } = messages;
    const totalItemsLen = options.length;
    if (!getToggleButtonProps) {
        throw new Error("SearchMultiselectDropdown requires getToggleButtonProps to be defined");
    }
    const renderMenuPlaceHolder = ()=>{
        let placeholder = toggleBtnText;
        const selectedItemsLen = selectedItems.length;
        if (isMultiSelect && selectedItemsLen > 0) {
            if (totalItemsLen === selectedItemsLen) {
                placeholder = AllText;
            } else {
                placeholder = typeof nItemSelectedText === "function" ? nItemSelectedText(selectedItemsLen) : `${selectedItemsLen} ${nItemSelectedText}`;
            }
        }
        if (!isMultiSelect && selectedItemsLen > 0) {
            if (showLabelOrValue === ShowBylabelOrValue.BOTH) {
                var _selectedItems_;
                placeholder = `${selectedItems[0].label} (${(_selectedItems_ = selectedItems[0]) === null || _selectedItems_ === void 0 ? void 0 : _selectedItems_.value})`;
            } else if (showLabelOrValue === ShowBylabelOrValue.VALUE) {
                var _selectedItems_1;
                placeholder = (_selectedItems_1 = selectedItems[0]) === null || _selectedItems_1 === void 0 ? void 0 : _selectedItems_1.value;
            } else {
                var _selectedItems_2;
                placeholder = (_selectedItems_2 = selectedItems[0]) === null || _selectedItems_2 === void 0 ? void 0 : _selectedItems_2.label;
            }
        }
        return placeholder;
    };
    return /*#__PURE__*/ jsxs(Button$1, _object_spread_props$8(_object_spread$8({
        className: cn(getBlocksWith("__toggle-button"), isSearchMultiselectDropdownDisabled && getBlocksWith("__toggle-button--disabled"), isOpen && getBlocksWith("__toggle-button--open")),
        disabled: isSearchMultiselectDropdownDisabled,
        styleType: "primary",
        type: "button",
        "aria-haspopup": "menu",
        "aria-label": toggleBtnText,
        "aria-controls": getMenuProps().id,
        "aria-expanded": "true",
        "data-toggle": "dropdown-button"
    }, getToggleButtonProps(_object_spread$8({}, rest))), {
        tabIndex: 0,
        children: [
            children,
            /*#__PURE__*/ jsx("span", {
                "data-auto": "placeholder-text",
                className: getBlocksWith("__toggle-button-label"),
                children: renderMenuPlaceHolder()
            }),
            /*#__PURE__*/ jsx("span", {
                className: getBlocksWith("__toggle-button-indicator"),
                children: /*#__PURE__*/ jsx(FontAwesomeIcon, {
                    icon: faAngleDown,
                    className: getBlocksWith("__icon-svg")
                })
            })
        ]
    }));
};

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}

function _assertThisInitialized(self) {
  if (self === void 0) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }
  return self;
}

function _setPrototypeOf(o, p) {
  _setPrototypeOf = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };
  return _setPrototypeOf(o, p);
}

function _inheritsLoose(subClass, superClass) {
  subClass.prototype = Object.create(superClass.prototype);
  subClass.prototype.constructor = subClass;
  _setPrototypeOf(subClass, superClass);
}

var safeIsNaN = Number.isNaN ||
    function ponyfill(value) {
        return typeof value === 'number' && value !== value;
    };
function isEqual(first, second) {
    if (first === second) {
        return true;
    }
    if (safeIsNaN(first) && safeIsNaN(second)) {
        return true;
    }
    return false;
}
function areInputsEqual(newInputs, lastInputs) {
    if (newInputs.length !== lastInputs.length) {
        return false;
    }
    for (var i = 0; i < newInputs.length; i++) {
        if (!isEqual(newInputs[i], lastInputs[i])) {
            return false;
        }
    }
    return true;
}

function memoizeOne(resultFn, isEqual) {
    if (isEqual === void 0) { isEqual = areInputsEqual; }
    var lastThis;
    var lastArgs = [];
    var lastResult;
    var calledOnce = false;
    function memoized() {
        var newArgs = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            newArgs[_i] = arguments[_i];
        }
        if (calledOnce && lastThis === this && isEqual(newArgs, lastArgs)) {
            return lastResult;
        }
        lastResult = resultFn.apply(this, newArgs);
        calledOnce = true;
        lastThis = this;
        lastArgs = newArgs;
        return lastResult;
    }
    return memoized;
}

// Animation frame based implementation of setTimeout.
// Inspired by Joe Lambert, https://gist.github.com/joelambert/1002116#file-requesttimeout-js
var hasNativePerformanceNow = typeof performance === 'object' && typeof performance.now === 'function';
var now$1 = hasNativePerformanceNow ? function () {
  return performance.now();
} : function () {
  return Date.now();
};
function cancelTimeout(timeoutID) {
  cancelAnimationFrame(timeoutID.id);
}
function requestTimeout(callback, delay) {
  var start = now$1();

  function tick() {
    if (now$1() - start >= delay) {
      callback.call(null);
    } else {
      timeoutID.id = requestAnimationFrame(tick);
    }
  }

  var timeoutID = {
    id: requestAnimationFrame(tick)
  };
  return timeoutID;
}

var size = -1; // This utility copied from "dom-helpers" package.

function getScrollbarSize(recalculate) {
  if (recalculate === void 0) {
    recalculate = false;
  }

  if (size === -1 || recalculate) {
    var div = document.createElement('div');
    var style = div.style;
    style.width = '50px';
    style.height = '50px';
    style.overflow = 'scroll';
    document.body.appendChild(div);
    size = div.offsetWidth - div.clientWidth;
    document.body.removeChild(div);
  }

  return size;
}
var cachedRTLResult = null; // TRICKY According to the spec, scrollLeft should be negative for RTL aligned elements.
// Chrome does not seem to adhere; its scrollLeft values are positive (measured relative to the left).
// Safari's elastic bounce makes detecting this even more complicated wrt potential false positives.
// The safest way to check this is to intentionally set a negative offset,
// and then verify that the subsequent "scroll" event matches the negative offset.
// If it does not match, then we can assume a non-standard RTL scroll implementation.

function getRTLOffsetType(recalculate) {
  if (recalculate === void 0) {
    recalculate = false;
  }

  if (cachedRTLResult === null || recalculate) {
    var outerDiv = document.createElement('div');
    var outerStyle = outerDiv.style;
    outerStyle.width = '50px';
    outerStyle.height = '50px';
    outerStyle.overflow = 'scroll';
    outerStyle.direction = 'rtl';
    var innerDiv = document.createElement('div');
    var innerStyle = innerDiv.style;
    innerStyle.width = '100px';
    innerStyle.height = '100px';
    outerDiv.appendChild(innerDiv);
    document.body.appendChild(outerDiv);

    if (outerDiv.scrollLeft > 0) {
      cachedRTLResult = 'positive-descending';
    } else {
      outerDiv.scrollLeft = 1;

      if (outerDiv.scrollLeft === 0) {
        cachedRTLResult = 'negative';
      } else {
        cachedRTLResult = 'positive-ascending';
      }
    }

    document.body.removeChild(outerDiv);
    return cachedRTLResult;
  }

  return cachedRTLResult;
}

if (process.env.NODE_ENV !== 'production') ;

var IS_SCROLLING_DEBOUNCE_INTERVAL$1 = 150;

var defaultItemKey$1 = function defaultItemKey(index, data) {
  return index;
}; // In DEV mode, this Set helps us only log a warning once per component instance.
// This avoids spamming the console every time a render happens.


var devWarningsDirection = null;
var devWarningsTagName$1 = null;

if (process.env.NODE_ENV !== 'production') {
  if (typeof window !== 'undefined' && typeof window.WeakSet !== 'undefined') {
    devWarningsDirection = /*#__PURE__*/new WeakSet();
    devWarningsTagName$1 = /*#__PURE__*/new WeakSet();
  }
}

function createListComponent(_ref) {
  var _class;

  var getItemOffset = _ref.getItemOffset,
      getEstimatedTotalSize = _ref.getEstimatedTotalSize,
      getItemSize = _ref.getItemSize,
      getOffsetForIndexAndAlignment = _ref.getOffsetForIndexAndAlignment,
      getStartIndexForOffset = _ref.getStartIndexForOffset,
      getStopIndexForStartIndex = _ref.getStopIndexForStartIndex,
      initInstanceProps = _ref.initInstanceProps,
      shouldResetStyleCacheOnItemSizeChange = _ref.shouldResetStyleCacheOnItemSizeChange,
      validateProps = _ref.validateProps;
  return _class = /*#__PURE__*/function (_PureComponent) {
    _inheritsLoose(List, _PureComponent);

    // Always use explicit constructor for React components.
    // It produces less code after transpilation. (#26)
    // eslint-disable-next-line no-useless-constructor
    function List(props) {
      var _this;

      _this = _PureComponent.call(this, props) || this;
      _this._instanceProps = initInstanceProps(_this.props, _assertThisInitialized(_this));
      _this._outerRef = void 0;
      _this._resetIsScrollingTimeoutId = null;
      _this.state = {
        instance: _assertThisInitialized(_this),
        isScrolling: false,
        scrollDirection: 'forward',
        scrollOffset: typeof _this.props.initialScrollOffset === 'number' ? _this.props.initialScrollOffset : 0,
        scrollUpdateWasRequested: false
      };
      _this._callOnItemsRendered = void 0;
      _this._callOnItemsRendered = memoizeOne(function (overscanStartIndex, overscanStopIndex, visibleStartIndex, visibleStopIndex) {
        return _this.props.onItemsRendered({
          overscanStartIndex: overscanStartIndex,
          overscanStopIndex: overscanStopIndex,
          visibleStartIndex: visibleStartIndex,
          visibleStopIndex: visibleStopIndex
        });
      });
      _this._callOnScroll = void 0;
      _this._callOnScroll = memoizeOne(function (scrollDirection, scrollOffset, scrollUpdateWasRequested) {
        return _this.props.onScroll({
          scrollDirection: scrollDirection,
          scrollOffset: scrollOffset,
          scrollUpdateWasRequested: scrollUpdateWasRequested
        });
      });
      _this._getItemStyle = void 0;

      _this._getItemStyle = function (index) {
        var _this$props = _this.props,
            direction = _this$props.direction,
            itemSize = _this$props.itemSize,
            layout = _this$props.layout;

        var itemStyleCache = _this._getItemStyleCache(shouldResetStyleCacheOnItemSizeChange && itemSize, shouldResetStyleCacheOnItemSizeChange && layout, shouldResetStyleCacheOnItemSizeChange && direction);

        var style;

        if (itemStyleCache.hasOwnProperty(index)) {
          style = itemStyleCache[index];
        } else {
          var _offset = getItemOffset(_this.props, index, _this._instanceProps);

          var size = getItemSize(_this.props, index, _this._instanceProps); // TODO Deprecate direction "horizontal"

          var isHorizontal = direction === 'horizontal' || layout === 'horizontal';
          var isRtl = direction === 'rtl';
          var offsetHorizontal = isHorizontal ? _offset : 0;
          itemStyleCache[index] = style = {
            position: 'absolute',
            left: isRtl ? undefined : offsetHorizontal,
            right: isRtl ? offsetHorizontal : undefined,
            top: !isHorizontal ? _offset : 0,
            height: !isHorizontal ? size : '100%',
            width: isHorizontal ? size : '100%'
          };
        }

        return style;
      };

      _this._getItemStyleCache = void 0;
      _this._getItemStyleCache = memoizeOne(function (_, __, ___) {
        return {};
      });

      _this._onScrollHorizontal = function (event) {
        var _event$currentTarget = event.currentTarget,
            clientWidth = _event$currentTarget.clientWidth,
            scrollLeft = _event$currentTarget.scrollLeft,
            scrollWidth = _event$currentTarget.scrollWidth;

        _this.setState(function (prevState) {
          if (prevState.scrollOffset === scrollLeft) {
            // Scroll position may have been updated by cDM/cDU,
            // In which case we don't need to trigger another render,
            // And we don't want to update state.isScrolling.
            return null;
          }

          var direction = _this.props.direction;
          var scrollOffset = scrollLeft;

          if (direction === 'rtl') {
            // TRICKY According to the spec, scrollLeft should be negative for RTL aligned elements.
            // This is not the case for all browsers though (e.g. Chrome reports values as positive, measured relative to the left).
            // It's also easier for this component if we convert offsets to the same format as they would be in for ltr.
            // So the simplest solution is to determine which browser behavior we're dealing with, and convert based on it.
            switch (getRTLOffsetType()) {
              case 'negative':
                scrollOffset = -scrollLeft;
                break;

              case 'positive-descending':
                scrollOffset = scrollWidth - clientWidth - scrollLeft;
                break;
            }
          } // Prevent Safari's elastic scrolling from causing visual shaking when scrolling past bounds.


          scrollOffset = Math.max(0, Math.min(scrollOffset, scrollWidth - clientWidth));
          return {
            isScrolling: true,
            scrollDirection: prevState.scrollOffset < scrollOffset ? 'forward' : 'backward',
            scrollOffset: scrollOffset,
            scrollUpdateWasRequested: false
          };
        }, _this._resetIsScrollingDebounced);
      };

      _this._onScrollVertical = function (event) {
        var _event$currentTarget2 = event.currentTarget,
            clientHeight = _event$currentTarget2.clientHeight,
            scrollHeight = _event$currentTarget2.scrollHeight,
            scrollTop = _event$currentTarget2.scrollTop;

        _this.setState(function (prevState) {
          if (prevState.scrollOffset === scrollTop) {
            // Scroll position may have been updated by cDM/cDU,
            // In which case we don't need to trigger another render,
            // And we don't want to update state.isScrolling.
            return null;
          } // Prevent Safari's elastic scrolling from causing visual shaking when scrolling past bounds.


          var scrollOffset = Math.max(0, Math.min(scrollTop, scrollHeight - clientHeight));
          return {
            isScrolling: true,
            scrollDirection: prevState.scrollOffset < scrollOffset ? 'forward' : 'backward',
            scrollOffset: scrollOffset,
            scrollUpdateWasRequested: false
          };
        }, _this._resetIsScrollingDebounced);
      };

      _this._outerRefSetter = function (ref) {
        var outerRef = _this.props.outerRef;
        _this._outerRef = ref;

        if (typeof outerRef === 'function') {
          outerRef(ref);
        } else if (outerRef != null && typeof outerRef === 'object' && outerRef.hasOwnProperty('current')) {
          outerRef.current = ref;
        }
      };

      _this._resetIsScrollingDebounced = function () {
        if (_this._resetIsScrollingTimeoutId !== null) {
          cancelTimeout(_this._resetIsScrollingTimeoutId);
        }

        _this._resetIsScrollingTimeoutId = requestTimeout(_this._resetIsScrolling, IS_SCROLLING_DEBOUNCE_INTERVAL$1);
      };

      _this._resetIsScrolling = function () {
        _this._resetIsScrollingTimeoutId = null;

        _this.setState({
          isScrolling: false
        }, function () {
          // Clear style cache after state update has been committed.
          // This way we don't break pure sCU for items that don't use isScrolling param.
          _this._getItemStyleCache(-1, null);
        });
      };

      return _this;
    }

    List.getDerivedStateFromProps = function getDerivedStateFromProps(nextProps, prevState) {
      validateSharedProps$1(nextProps, prevState);
      validateProps(nextProps);
      return null;
    };

    var _proto = List.prototype;

    _proto.scrollTo = function scrollTo(scrollOffset) {
      scrollOffset = Math.max(0, scrollOffset);
      this.setState(function (prevState) {
        if (prevState.scrollOffset === scrollOffset) {
          return null;
        }

        return {
          scrollDirection: prevState.scrollOffset < scrollOffset ? 'forward' : 'backward',
          scrollOffset: scrollOffset,
          scrollUpdateWasRequested: true
        };
      }, this._resetIsScrollingDebounced);
    };

    _proto.scrollToItem = function scrollToItem(index, align) {
      if (align === void 0) {
        align = 'auto';
      }

      var _this$props2 = this.props,
          itemCount = _this$props2.itemCount,
          layout = _this$props2.layout;
      var scrollOffset = this.state.scrollOffset;
      index = Math.max(0, Math.min(index, itemCount - 1)); // The scrollbar size should be considered when scrolling an item into view, to ensure it's fully visible.
      // But we only need to account for its size when it's actually visible.
      // This is an edge case for lists; normally they only scroll in the dominant direction.

      var scrollbarSize = 0;

      if (this._outerRef) {
        var outerRef = this._outerRef;

        if (layout === 'vertical') {
          scrollbarSize = outerRef.scrollWidth > outerRef.clientWidth ? getScrollbarSize() : 0;
        } else {
          scrollbarSize = outerRef.scrollHeight > outerRef.clientHeight ? getScrollbarSize() : 0;
        }
      }

      this.scrollTo(getOffsetForIndexAndAlignment(this.props, index, align, scrollOffset, this._instanceProps, scrollbarSize));
    };

    _proto.componentDidMount = function componentDidMount() {
      var _this$props3 = this.props,
          direction = _this$props3.direction,
          initialScrollOffset = _this$props3.initialScrollOffset,
          layout = _this$props3.layout;

      if (typeof initialScrollOffset === 'number' && this._outerRef != null) {
        var outerRef = this._outerRef; // TODO Deprecate direction "horizontal"

        if (direction === 'horizontal' || layout === 'horizontal') {
          outerRef.scrollLeft = initialScrollOffset;
        } else {
          outerRef.scrollTop = initialScrollOffset;
        }
      }

      this._callPropsCallbacks();
    };

    _proto.componentDidUpdate = function componentDidUpdate() {
      var _this$props4 = this.props,
          direction = _this$props4.direction,
          layout = _this$props4.layout;
      var _this$state = this.state,
          scrollOffset = _this$state.scrollOffset,
          scrollUpdateWasRequested = _this$state.scrollUpdateWasRequested;

      if (scrollUpdateWasRequested && this._outerRef != null) {
        var outerRef = this._outerRef; // TODO Deprecate direction "horizontal"

        if (direction === 'horizontal' || layout === 'horizontal') {
          if (direction === 'rtl') {
            // TRICKY According to the spec, scrollLeft should be negative for RTL aligned elements.
            // This is not the case for all browsers though (e.g. Chrome reports values as positive, measured relative to the left).
            // So we need to determine which browser behavior we're dealing with, and mimic it.
            switch (getRTLOffsetType()) {
              case 'negative':
                outerRef.scrollLeft = -scrollOffset;
                break;

              case 'positive-ascending':
                outerRef.scrollLeft = scrollOffset;
                break;

              default:
                var clientWidth = outerRef.clientWidth,
                    scrollWidth = outerRef.scrollWidth;
                outerRef.scrollLeft = scrollWidth - clientWidth - scrollOffset;
                break;
            }
          } else {
            outerRef.scrollLeft = scrollOffset;
          }
        } else {
          outerRef.scrollTop = scrollOffset;
        }
      }

      this._callPropsCallbacks();
    };

    _proto.componentWillUnmount = function componentWillUnmount() {
      if (this._resetIsScrollingTimeoutId !== null) {
        cancelTimeout(this._resetIsScrollingTimeoutId);
      }
    };

    _proto.render = function render() {
      var _this$props5 = this.props,
          children = _this$props5.children,
          className = _this$props5.className,
          direction = _this$props5.direction,
          height = _this$props5.height,
          innerRef = _this$props5.innerRef,
          innerElementType = _this$props5.innerElementType,
          innerTagName = _this$props5.innerTagName,
          itemCount = _this$props5.itemCount,
          itemData = _this$props5.itemData,
          _this$props5$itemKey = _this$props5.itemKey,
          itemKey = _this$props5$itemKey === void 0 ? defaultItemKey$1 : _this$props5$itemKey,
          layout = _this$props5.layout,
          outerElementType = _this$props5.outerElementType,
          outerTagName = _this$props5.outerTagName,
          style = _this$props5.style,
          useIsScrolling = _this$props5.useIsScrolling,
          width = _this$props5.width;
      var isScrolling = this.state.isScrolling; // TODO Deprecate direction "horizontal"

      var isHorizontal = direction === 'horizontal' || layout === 'horizontal';
      var onScroll = isHorizontal ? this._onScrollHorizontal : this._onScrollVertical;

      var _this$_getRangeToRend = this._getRangeToRender(),
          startIndex = _this$_getRangeToRend[0],
          stopIndex = _this$_getRangeToRend[1];

      var items = [];

      if (itemCount > 0) {
        for (var _index = startIndex; _index <= stopIndex; _index++) {
          items.push(createElement(children, {
            data: itemData,
            key: itemKey(_index, itemData),
            index: _index,
            isScrolling: useIsScrolling ? isScrolling : undefined,
            style: this._getItemStyle(_index)
          }));
        }
      } // Read this value AFTER items have been created,
      // So their actual sizes (if variable) are taken into consideration.


      var estimatedTotalSize = getEstimatedTotalSize(this.props, this._instanceProps);
      return createElement(outerElementType || outerTagName || 'div', {
        className: className,
        onScroll: onScroll,
        ref: this._outerRefSetter,
        style: _extends({
          position: 'relative',
          height: height,
          width: width,
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch',
          willChange: 'transform',
          direction: direction
        }, style)
      }, createElement(innerElementType || innerTagName || 'div', {
        children: items,
        ref: innerRef,
        style: {
          height: isHorizontal ? '100%' : estimatedTotalSize,
          pointerEvents: isScrolling ? 'none' : undefined,
          width: isHorizontal ? estimatedTotalSize : '100%'
        }
      }));
    };

    _proto._callPropsCallbacks = function _callPropsCallbacks() {
      if (typeof this.props.onItemsRendered === 'function') {
        var itemCount = this.props.itemCount;

        if (itemCount > 0) {
          var _this$_getRangeToRend2 = this._getRangeToRender(),
              _overscanStartIndex = _this$_getRangeToRend2[0],
              _overscanStopIndex = _this$_getRangeToRend2[1],
              _visibleStartIndex = _this$_getRangeToRend2[2],
              _visibleStopIndex = _this$_getRangeToRend2[3];

          this._callOnItemsRendered(_overscanStartIndex, _overscanStopIndex, _visibleStartIndex, _visibleStopIndex);
        }
      }

      if (typeof this.props.onScroll === 'function') {
        var _this$state2 = this.state,
            _scrollDirection = _this$state2.scrollDirection,
            _scrollOffset = _this$state2.scrollOffset,
            _scrollUpdateWasRequested = _this$state2.scrollUpdateWasRequested;

        this._callOnScroll(_scrollDirection, _scrollOffset, _scrollUpdateWasRequested);
      }
    } // Lazily create and cache item styles while scrolling,
    // So that pure component sCU will prevent re-renders.
    // We maintain this cache, and pass a style prop rather than index,
    // So that List can clear cached styles and force item re-render if necessary.
    ;

    _proto._getRangeToRender = function _getRangeToRender() {
      var _this$props6 = this.props,
          itemCount = _this$props6.itemCount,
          overscanCount = _this$props6.overscanCount;
      var _this$state3 = this.state,
          isScrolling = _this$state3.isScrolling,
          scrollDirection = _this$state3.scrollDirection,
          scrollOffset = _this$state3.scrollOffset;

      if (itemCount === 0) {
        return [0, 0, 0, 0];
      }

      var startIndex = getStartIndexForOffset(this.props, scrollOffset, this._instanceProps);
      var stopIndex = getStopIndexForStartIndex(this.props, startIndex, scrollOffset, this._instanceProps); // Overscan by one item in each direction so that tab/focus works.
      // If there isn't at least one extra item, tab loops back around.

      var overscanBackward = !isScrolling || scrollDirection === 'backward' ? Math.max(1, overscanCount) : 1;
      var overscanForward = !isScrolling || scrollDirection === 'forward' ? Math.max(1, overscanCount) : 1;
      return [Math.max(0, startIndex - overscanBackward), Math.max(0, Math.min(itemCount - 1, stopIndex + overscanForward)), startIndex, stopIndex];
    };

    return List;
  }(PureComponent), _class.defaultProps = {
    direction: 'ltr',
    itemData: undefined,
    layout: 'vertical',
    overscanCount: 2,
    useIsScrolling: false
  }, _class;
} // NOTE: I considered further wrapping individual items with a pure ListItem component.
// This would avoid ever calling the render function for the same index more than once,
// But it would also add the overhead of a lot of components/fibers.
// I assume people already do this (render function returning a class component),
// So my doing it would just unnecessarily double the wrappers.

var validateSharedProps$1 = function validateSharedProps(_ref2, _ref3) {
  var children = _ref2.children,
      direction = _ref2.direction,
      height = _ref2.height,
      layout = _ref2.layout,
      innerTagName = _ref2.innerTagName,
      outerTagName = _ref2.outerTagName,
      width = _ref2.width;
  var instance = _ref3.instance;

  if (process.env.NODE_ENV !== 'production') {
    if (innerTagName != null || outerTagName != null) {
      if (devWarningsTagName$1 && !devWarningsTagName$1.has(instance)) {
        devWarningsTagName$1.add(instance);
        console.warn('The innerTagName and outerTagName props have been deprecated. ' + 'Please use the innerElementType and outerElementType props instead.');
      }
    } // TODO Deprecate direction "horizontal"


    var isHorizontal = direction === 'horizontal' || layout === 'horizontal';

    switch (direction) {
      case 'horizontal':
      case 'vertical':
        if (devWarningsDirection && !devWarningsDirection.has(instance)) {
          devWarningsDirection.add(instance);
          console.warn('The direction prop should be either "ltr" (default) or "rtl". ' + 'Please use the layout prop to specify "vertical" (default) or "horizontal" orientation.');
        }

        break;

      case 'ltr':
      case 'rtl':
        // Valid values
        break;

      default:
        throw Error('An invalid "direction" prop has been specified. ' + 'Value should be either "ltr" or "rtl". ' + ("\"" + direction + "\" was specified."));
    }

    switch (layout) {
      case 'horizontal':
      case 'vertical':
        // Valid values
        break;

      default:
        throw Error('An invalid "layout" prop has been specified. ' + 'Value should be either "horizontal" or "vertical". ' + ("\"" + layout + "\" was specified."));
    }

    if (children == null) {
      throw Error('An invalid "children" prop has been specified. ' + 'Value should be a React component. ' + ("\"" + (children === null ? 'null' : typeof children) + "\" was specified."));
    }

    if (isHorizontal && typeof width !== 'number') {
      throw Error('An invalid "width" prop has been specified. ' + 'Horizontal lists must specify a number for width. ' + ("\"" + (width === null ? 'null' : typeof width) + "\" was specified."));
    } else if (!isHorizontal && typeof height !== 'number') {
      throw Error('An invalid "height" prop has been specified. ' + 'Vertical lists must specify a number for height. ' + ("\"" + (height === null ? 'null' : typeof height) + "\" was specified."));
    }
  }
};

var DEFAULT_ESTIMATED_ITEM_SIZE$1 = 50;

var getItemMetadata$1 = function getItemMetadata(props, index, instanceProps) {
  var _ref = props,
      itemSize = _ref.itemSize;
  var itemMetadataMap = instanceProps.itemMetadataMap,
      lastMeasuredIndex = instanceProps.lastMeasuredIndex;

  if (index > lastMeasuredIndex) {
    var offset = 0;

    if (lastMeasuredIndex >= 0) {
      var itemMetadata = itemMetadataMap[lastMeasuredIndex];
      offset = itemMetadata.offset + itemMetadata.size;
    }

    for (var i = lastMeasuredIndex + 1; i <= index; i++) {
      var size = itemSize(i);
      itemMetadataMap[i] = {
        offset: offset,
        size: size
      };
      offset += size;
    }

    instanceProps.lastMeasuredIndex = index;
  }

  return itemMetadataMap[index];
};

var findNearestItem$1 = function findNearestItem(props, instanceProps, offset) {
  var itemMetadataMap = instanceProps.itemMetadataMap,
      lastMeasuredIndex = instanceProps.lastMeasuredIndex;
  var lastMeasuredItemOffset = lastMeasuredIndex > 0 ? itemMetadataMap[lastMeasuredIndex].offset : 0;

  if (lastMeasuredItemOffset >= offset) {
    // If we've already measured items within this range just use a binary search as it's faster.
    return findNearestItemBinarySearch$1(props, instanceProps, lastMeasuredIndex, 0, offset);
  } else {
    // If we haven't yet measured this high, fallback to an exponential search with an inner binary search.
    // The exponential search avoids pre-computing sizes for the full set of items as a binary search would.
    // The overall complexity for this approach is O(log n).
    return findNearestItemExponentialSearch$1(props, instanceProps, Math.max(0, lastMeasuredIndex), offset);
  }
};

var findNearestItemBinarySearch$1 = function findNearestItemBinarySearch(props, instanceProps, high, low, offset) {
  while (low <= high) {
    var middle = low + Math.floor((high - low) / 2);
    var currentOffset = getItemMetadata$1(props, middle, instanceProps).offset;

    if (currentOffset === offset) {
      return middle;
    } else if (currentOffset < offset) {
      low = middle + 1;
    } else if (currentOffset > offset) {
      high = middle - 1;
    }
  }

  if (low > 0) {
    return low - 1;
  } else {
    return 0;
  }
};

var findNearestItemExponentialSearch$1 = function findNearestItemExponentialSearch(props, instanceProps, index, offset) {
  var itemCount = props.itemCount;
  var interval = 1;

  while (index < itemCount && getItemMetadata$1(props, index, instanceProps).offset < offset) {
    index += interval;
    interval *= 2;
  }

  return findNearestItemBinarySearch$1(props, instanceProps, Math.min(index, itemCount - 1), Math.floor(index / 2), offset);
};

var getEstimatedTotalSize = function getEstimatedTotalSize(_ref2, _ref3) {
  var itemCount = _ref2.itemCount;
  var itemMetadataMap = _ref3.itemMetadataMap,
      estimatedItemSize = _ref3.estimatedItemSize,
      lastMeasuredIndex = _ref3.lastMeasuredIndex;
  var totalSizeOfMeasuredItems = 0; // Edge case check for when the number of items decreases while a scroll is in progress.
  // https://github.com/bvaughn/react-window/pull/138

  if (lastMeasuredIndex >= itemCount) {
    lastMeasuredIndex = itemCount - 1;
  }

  if (lastMeasuredIndex >= 0) {
    var itemMetadata = itemMetadataMap[lastMeasuredIndex];
    totalSizeOfMeasuredItems = itemMetadata.offset + itemMetadata.size;
  }

  var numUnmeasuredItems = itemCount - lastMeasuredIndex - 1;
  var totalSizeOfUnmeasuredItems = numUnmeasuredItems * estimatedItemSize;
  return totalSizeOfMeasuredItems + totalSizeOfUnmeasuredItems;
};

var VariableSizeList = /*#__PURE__*/createListComponent({
  getItemOffset: function getItemOffset(props, index, instanceProps) {
    return getItemMetadata$1(props, index, instanceProps).offset;
  },
  getItemSize: function getItemSize(props, index, instanceProps) {
    return instanceProps.itemMetadataMap[index].size;
  },
  getEstimatedTotalSize: getEstimatedTotalSize,
  getOffsetForIndexAndAlignment: function getOffsetForIndexAndAlignment(props, index, align, scrollOffset, instanceProps, scrollbarSize) {
    var direction = props.direction,
        height = props.height,
        layout = props.layout,
        width = props.width; // TODO Deprecate direction "horizontal"

    var isHorizontal = direction === 'horizontal' || layout === 'horizontal';
    var size = isHorizontal ? width : height;
    var itemMetadata = getItemMetadata$1(props, index, instanceProps); // Get estimated total size after ItemMetadata is computed,
    // To ensure it reflects actual measurements instead of just estimates.

    var estimatedTotalSize = getEstimatedTotalSize(props, instanceProps);
    var maxOffset = Math.max(0, Math.min(estimatedTotalSize - size, itemMetadata.offset));
    var minOffset = Math.max(0, itemMetadata.offset - size + itemMetadata.size + scrollbarSize);

    if (align === 'smart') {
      if (scrollOffset >= minOffset - size && scrollOffset <= maxOffset + size) {
        align = 'auto';
      } else {
        align = 'center';
      }
    }

    switch (align) {
      case 'start':
        return maxOffset;

      case 'end':
        return minOffset;

      case 'center':
        return Math.round(minOffset + (maxOffset - minOffset) / 2);

      case 'auto':
      default:
        if (scrollOffset >= minOffset && scrollOffset <= maxOffset) {
          return scrollOffset;
        } else if (scrollOffset < minOffset) {
          return minOffset;
        } else {
          return maxOffset;
        }

    }
  },
  getStartIndexForOffset: function getStartIndexForOffset(props, offset, instanceProps) {
    return findNearestItem$1(props, instanceProps, offset);
  },
  getStopIndexForStartIndex: function getStopIndexForStartIndex(props, startIndex, scrollOffset, instanceProps) {
    var direction = props.direction,
        height = props.height,
        itemCount = props.itemCount,
        layout = props.layout,
        width = props.width; // TODO Deprecate direction "horizontal"

    var isHorizontal = direction === 'horizontal' || layout === 'horizontal';
    var size = isHorizontal ? width : height;
    var itemMetadata = getItemMetadata$1(props, startIndex, instanceProps);
    var maxOffset = scrollOffset + size;
    var offset = itemMetadata.offset + itemMetadata.size;
    var stopIndex = startIndex;

    while (stopIndex < itemCount - 1 && offset < maxOffset) {
      stopIndex++;
      offset += getItemMetadata$1(props, stopIndex, instanceProps).size;
    }

    return stopIndex;
  },
  initInstanceProps: function initInstanceProps(props, instance) {
    var _ref4 = props,
        estimatedItemSize = _ref4.estimatedItemSize;
    var instanceProps = {
      itemMetadataMap: {},
      estimatedItemSize: estimatedItemSize || DEFAULT_ESTIMATED_ITEM_SIZE$1,
      lastMeasuredIndex: -1
    };

    instance.resetAfterIndex = function (index, shouldForceUpdate) {
      if (shouldForceUpdate === void 0) {
        shouldForceUpdate = true;
      }

      instanceProps.lastMeasuredIndex = Math.min(instanceProps.lastMeasuredIndex, index - 1); // We could potentially optimize further by only evicting styles after this index,
      // But since styles are only cached while scrolling is in progress-
      // It seems an unnecessary optimization.
      // It's unlikely that resetAfterIndex() will be called while a user is scrolling.

      instance._getItemStyleCache(-1);

      if (shouldForceUpdate) {
        instance.forceUpdate();
      }
    };

    return instanceProps;
  },
  shouldResetStyleCacheOnItemSizeChange: false,
  validateProps: function validateProps(_ref5) {
    var itemSize = _ref5.itemSize;

    if (process.env.NODE_ENV !== 'production') {
      if (typeof itemSize !== 'function') {
        throw Error('An invalid "itemSize" prop has been specified. ' + 'Value should be a function. ' + ("\"" + (itemSize === null ? 'null' : typeof itemSize) + "\" was specified."));
      }
    }
  }
});

var reactIs$1 = {exports: {}};

var reactIs_production_min = {};

/** @license React v16.13.1
 * react-is.production.min.js
 *
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

var hasRequiredReactIs_production_min;

function requireReactIs_production_min () {
	if (hasRequiredReactIs_production_min) return reactIs_production_min;
	hasRequiredReactIs_production_min = 1;
var b="function"===typeof Symbol&&Symbol.for,c=b?Symbol.for("react.element"):60103,d=b?Symbol.for("react.portal"):60106,e=b?Symbol.for("react.fragment"):60107,f=b?Symbol.for("react.strict_mode"):60108,g=b?Symbol.for("react.profiler"):60114,h=b?Symbol.for("react.provider"):60109,k=b?Symbol.for("react.context"):60110,l=b?Symbol.for("react.async_mode"):60111,m=b?Symbol.for("react.concurrent_mode"):60111,n=b?Symbol.for("react.forward_ref"):60112,p=b?Symbol.for("react.suspense"):60113,q=b?
	Symbol.for("react.suspense_list"):60120,r=b?Symbol.for("react.memo"):60115,t=b?Symbol.for("react.lazy"):60116,v=b?Symbol.for("react.block"):60121,w=b?Symbol.for("react.fundamental"):60117,x=b?Symbol.for("react.responder"):60118,y=b?Symbol.for("react.scope"):60119;
	function z(a){if("object"===typeof a&&null!==a){var u=a.$$typeof;switch(u){case c:switch(a=a.type,a){case l:case m:case e:case g:case f:case p:return a;default:switch(a=a&&a.$$typeof,a){case k:case n:case t:case r:case h:return a;default:return u}}case d:return u}}}function A(a){return z(a)===m}reactIs_production_min.AsyncMode=l;reactIs_production_min.ConcurrentMode=m;reactIs_production_min.ContextConsumer=k;reactIs_production_min.ContextProvider=h;reactIs_production_min.Element=c;reactIs_production_min.ForwardRef=n;reactIs_production_min.Fragment=e;reactIs_production_min.Lazy=t;reactIs_production_min.Memo=r;reactIs_production_min.Portal=d;
	reactIs_production_min.Profiler=g;reactIs_production_min.StrictMode=f;reactIs_production_min.Suspense=p;reactIs_production_min.isAsyncMode=function(a){return A(a)||z(a)===l};reactIs_production_min.isConcurrentMode=A;reactIs_production_min.isContextConsumer=function(a){return z(a)===k};reactIs_production_min.isContextProvider=function(a){return z(a)===h};reactIs_production_min.isElement=function(a){return "object"===typeof a&&null!==a&&a.$$typeof===c};reactIs_production_min.isForwardRef=function(a){return z(a)===n};reactIs_production_min.isFragment=function(a){return z(a)===e};reactIs_production_min.isLazy=function(a){return z(a)===t};
	reactIs_production_min.isMemo=function(a){return z(a)===r};reactIs_production_min.isPortal=function(a){return z(a)===d};reactIs_production_min.isProfiler=function(a){return z(a)===g};reactIs_production_min.isStrictMode=function(a){return z(a)===f};reactIs_production_min.isSuspense=function(a){return z(a)===p};
	reactIs_production_min.isValidElementType=function(a){return "string"===typeof a||"function"===typeof a||a===e||a===m||a===g||a===f||a===p||a===q||"object"===typeof a&&null!==a&&(a.$$typeof===t||a.$$typeof===r||a.$$typeof===h||a.$$typeof===k||a.$$typeof===n||a.$$typeof===w||a.$$typeof===x||a.$$typeof===y||a.$$typeof===v)};reactIs_production_min.typeOf=z;
	return reactIs_production_min;
}

var reactIs_development = {};

/** @license React v16.13.1
 * react-is.development.js
 *
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

var hasRequiredReactIs_development;

function requireReactIs_development () {
	if (hasRequiredReactIs_development) return reactIs_development;
	hasRequiredReactIs_development = 1;



	if (process.env.NODE_ENV !== "production") {
	  (function() {

	// The Symbol used to tag the ReactElement-like types. If there is no native Symbol
	// nor polyfill, then a plain number is used for performance.
	var hasSymbol = typeof Symbol === 'function' && Symbol.for;
	var REACT_ELEMENT_TYPE = hasSymbol ? Symbol.for('react.element') : 0xeac7;
	var REACT_PORTAL_TYPE = hasSymbol ? Symbol.for('react.portal') : 0xeaca;
	var REACT_FRAGMENT_TYPE = hasSymbol ? Symbol.for('react.fragment') : 0xeacb;
	var REACT_STRICT_MODE_TYPE = hasSymbol ? Symbol.for('react.strict_mode') : 0xeacc;
	var REACT_PROFILER_TYPE = hasSymbol ? Symbol.for('react.profiler') : 0xead2;
	var REACT_PROVIDER_TYPE = hasSymbol ? Symbol.for('react.provider') : 0xeacd;
	var REACT_CONTEXT_TYPE = hasSymbol ? Symbol.for('react.context') : 0xeace; // TODO: We don't use AsyncMode or ConcurrentMode anymore. They were temporary
	// (unstable) APIs that have been removed. Can we remove the symbols?

	var REACT_ASYNC_MODE_TYPE = hasSymbol ? Symbol.for('react.async_mode') : 0xeacf;
	var REACT_CONCURRENT_MODE_TYPE = hasSymbol ? Symbol.for('react.concurrent_mode') : 0xeacf;
	var REACT_FORWARD_REF_TYPE = hasSymbol ? Symbol.for('react.forward_ref') : 0xead0;
	var REACT_SUSPENSE_TYPE = hasSymbol ? Symbol.for('react.suspense') : 0xead1;
	var REACT_SUSPENSE_LIST_TYPE = hasSymbol ? Symbol.for('react.suspense_list') : 0xead8;
	var REACT_MEMO_TYPE = hasSymbol ? Symbol.for('react.memo') : 0xead3;
	var REACT_LAZY_TYPE = hasSymbol ? Symbol.for('react.lazy') : 0xead4;
	var REACT_BLOCK_TYPE = hasSymbol ? Symbol.for('react.block') : 0xead9;
	var REACT_FUNDAMENTAL_TYPE = hasSymbol ? Symbol.for('react.fundamental') : 0xead5;
	var REACT_RESPONDER_TYPE = hasSymbol ? Symbol.for('react.responder') : 0xead6;
	var REACT_SCOPE_TYPE = hasSymbol ? Symbol.for('react.scope') : 0xead7;

	function isValidElementType(type) {
	  return typeof type === 'string' || typeof type === 'function' || // Note: its typeof might be other than 'symbol' or 'number' if it's a polyfill.
	  type === REACT_FRAGMENT_TYPE || type === REACT_CONCURRENT_MODE_TYPE || type === REACT_PROFILER_TYPE || type === REACT_STRICT_MODE_TYPE || type === REACT_SUSPENSE_TYPE || type === REACT_SUSPENSE_LIST_TYPE || typeof type === 'object' && type !== null && (type.$$typeof === REACT_LAZY_TYPE || type.$$typeof === REACT_MEMO_TYPE || type.$$typeof === REACT_PROVIDER_TYPE || type.$$typeof === REACT_CONTEXT_TYPE || type.$$typeof === REACT_FORWARD_REF_TYPE || type.$$typeof === REACT_FUNDAMENTAL_TYPE || type.$$typeof === REACT_RESPONDER_TYPE || type.$$typeof === REACT_SCOPE_TYPE || type.$$typeof === REACT_BLOCK_TYPE);
	}

	function typeOf(object) {
	  if (typeof object === 'object' && object !== null) {
	    var $$typeof = object.$$typeof;

	    switch ($$typeof) {
	      case REACT_ELEMENT_TYPE:
	        var type = object.type;

	        switch (type) {
	          case REACT_ASYNC_MODE_TYPE:
	          case REACT_CONCURRENT_MODE_TYPE:
	          case REACT_FRAGMENT_TYPE:
	          case REACT_PROFILER_TYPE:
	          case REACT_STRICT_MODE_TYPE:
	          case REACT_SUSPENSE_TYPE:
	            return type;

	          default:
	            var $$typeofType = type && type.$$typeof;

	            switch ($$typeofType) {
	              case REACT_CONTEXT_TYPE:
	              case REACT_FORWARD_REF_TYPE:
	              case REACT_LAZY_TYPE:
	              case REACT_MEMO_TYPE:
	              case REACT_PROVIDER_TYPE:
	                return $$typeofType;

	              default:
	                return $$typeof;
	            }

	        }

	      case REACT_PORTAL_TYPE:
	        return $$typeof;
	    }
	  }

	  return undefined;
	} // AsyncMode is deprecated along with isAsyncMode

	var AsyncMode = REACT_ASYNC_MODE_TYPE;
	var ConcurrentMode = REACT_CONCURRENT_MODE_TYPE;
	var ContextConsumer = REACT_CONTEXT_TYPE;
	var ContextProvider = REACT_PROVIDER_TYPE;
	var Element = REACT_ELEMENT_TYPE;
	var ForwardRef = REACT_FORWARD_REF_TYPE;
	var Fragment = REACT_FRAGMENT_TYPE;
	var Lazy = REACT_LAZY_TYPE;
	var Memo = REACT_MEMO_TYPE;
	var Portal = REACT_PORTAL_TYPE;
	var Profiler = REACT_PROFILER_TYPE;
	var StrictMode = REACT_STRICT_MODE_TYPE;
	var Suspense = REACT_SUSPENSE_TYPE;
	var hasWarnedAboutDeprecatedIsAsyncMode = false; // AsyncMode should be deprecated

	function isAsyncMode(object) {
	  {
	    if (!hasWarnedAboutDeprecatedIsAsyncMode) {
	      hasWarnedAboutDeprecatedIsAsyncMode = true; // Using console['warn'] to evade Babel and ESLint

	      console['warn']('The ReactIs.isAsyncMode() alias has been deprecated, ' + 'and will be removed in React 17+. Update your code to use ' + 'ReactIs.isConcurrentMode() instead. It has the exact same API.');
	    }
	  }

	  return isConcurrentMode(object) || typeOf(object) === REACT_ASYNC_MODE_TYPE;
	}
	function isConcurrentMode(object) {
	  return typeOf(object) === REACT_CONCURRENT_MODE_TYPE;
	}
	function isContextConsumer(object) {
	  return typeOf(object) === REACT_CONTEXT_TYPE;
	}
	function isContextProvider(object) {
	  return typeOf(object) === REACT_PROVIDER_TYPE;
	}
	function isElement(object) {
	  return typeof object === 'object' && object !== null && object.$$typeof === REACT_ELEMENT_TYPE;
	}
	function isForwardRef(object) {
	  return typeOf(object) === REACT_FORWARD_REF_TYPE;
	}
	function isFragment(object) {
	  return typeOf(object) === REACT_FRAGMENT_TYPE;
	}
	function isLazy(object) {
	  return typeOf(object) === REACT_LAZY_TYPE;
	}
	function isMemo(object) {
	  return typeOf(object) === REACT_MEMO_TYPE;
	}
	function isPortal(object) {
	  return typeOf(object) === REACT_PORTAL_TYPE;
	}
	function isProfiler(object) {
	  return typeOf(object) === REACT_PROFILER_TYPE;
	}
	function isStrictMode(object) {
	  return typeOf(object) === REACT_STRICT_MODE_TYPE;
	}
	function isSuspense(object) {
	  return typeOf(object) === REACT_SUSPENSE_TYPE;
	}

	reactIs_development.AsyncMode = AsyncMode;
	reactIs_development.ConcurrentMode = ConcurrentMode;
	reactIs_development.ContextConsumer = ContextConsumer;
	reactIs_development.ContextProvider = ContextProvider;
	reactIs_development.Element = Element;
	reactIs_development.ForwardRef = ForwardRef;
	reactIs_development.Fragment = Fragment;
	reactIs_development.Lazy = Lazy;
	reactIs_development.Memo = Memo;
	reactIs_development.Portal = Portal;
	reactIs_development.Profiler = Profiler;
	reactIs_development.StrictMode = StrictMode;
	reactIs_development.Suspense = Suspense;
	reactIs_development.isAsyncMode = isAsyncMode;
	reactIs_development.isConcurrentMode = isConcurrentMode;
	reactIs_development.isContextConsumer = isContextConsumer;
	reactIs_development.isContextProvider = isContextProvider;
	reactIs_development.isElement = isElement;
	reactIs_development.isForwardRef = isForwardRef;
	reactIs_development.isFragment = isFragment;
	reactIs_development.isLazy = isLazy;
	reactIs_development.isMemo = isMemo;
	reactIs_development.isPortal = isPortal;
	reactIs_development.isProfiler = isProfiler;
	reactIs_development.isStrictMode = isStrictMode;
	reactIs_development.isSuspense = isSuspense;
	reactIs_development.isValidElementType = isValidElementType;
	reactIs_development.typeOf = typeOf;
	  })();
	}
	return reactIs_development;
}

if (process.env.NODE_ENV === 'production') {
  reactIs$1.exports = requireReactIs_production_min();
} else {
  reactIs$1.exports = requireReactIs_development();
}

var reactIsExports = reactIs$1.exports;

var reactIs = reactIsExports;
var FORWARD_REF_STATICS = {
  '$$typeof': true,
  render: true,
  defaultProps: true,
  displayName: true,
  propTypes: true
};
var MEMO_STATICS = {
  '$$typeof': true,
  compare: true,
  defaultProps: true,
  displayName: true,
  propTypes: true,
  type: true
};
var TYPE_STATICS = {};
TYPE_STATICS[reactIs.ForwardRef] = FORWARD_REF_STATICS;
TYPE_STATICS[reactIs.Memo] = MEMO_STATICS;

/**
 * Cannot do Math.log(x) / Math.log(10) bc if IEEE floating point issue
 * @param x number
 */
function invariant(condition, message, Err) {
    if (Err === void 0) { Err = Error; }
    if (!condition) {
        throw new Err(message);
    }
}

var ErrorKind;
(function (ErrorKind) {
    /** Argument is unclosed (e.g. `{0`) */
    ErrorKind[ErrorKind["EXPECT_ARGUMENT_CLOSING_BRACE"] = 1] = "EXPECT_ARGUMENT_CLOSING_BRACE";
    /** Argument is empty (e.g. `{}`). */
    ErrorKind[ErrorKind["EMPTY_ARGUMENT"] = 2] = "EMPTY_ARGUMENT";
    /** Argument is malformed (e.g. `{foo!}``) */
    ErrorKind[ErrorKind["MALFORMED_ARGUMENT"] = 3] = "MALFORMED_ARGUMENT";
    /** Expect an argument type (e.g. `{foo,}`) */
    ErrorKind[ErrorKind["EXPECT_ARGUMENT_TYPE"] = 4] = "EXPECT_ARGUMENT_TYPE";
    /** Unsupported argument type (e.g. `{foo,foo}`) */
    ErrorKind[ErrorKind["INVALID_ARGUMENT_TYPE"] = 5] = "INVALID_ARGUMENT_TYPE";
    /** Expect an argument style (e.g. `{foo, number, }`) */
    ErrorKind[ErrorKind["EXPECT_ARGUMENT_STYLE"] = 6] = "EXPECT_ARGUMENT_STYLE";
    /** The number skeleton is invalid. */
    ErrorKind[ErrorKind["INVALID_NUMBER_SKELETON"] = 7] = "INVALID_NUMBER_SKELETON";
    /** The date time skeleton is invalid. */
    ErrorKind[ErrorKind["INVALID_DATE_TIME_SKELETON"] = 8] = "INVALID_DATE_TIME_SKELETON";
    /** Exepct a number skeleton following the `::` (e.g. `{foo, number, ::}`) */
    ErrorKind[ErrorKind["EXPECT_NUMBER_SKELETON"] = 9] = "EXPECT_NUMBER_SKELETON";
    /** Exepct a date time skeleton following the `::` (e.g. `{foo, date, ::}`) */
    ErrorKind[ErrorKind["EXPECT_DATE_TIME_SKELETON"] = 10] = "EXPECT_DATE_TIME_SKELETON";
    /** Unmatched apostrophes in the argument style (e.g. `{foo, number, 'test`) */
    ErrorKind[ErrorKind["UNCLOSED_QUOTE_IN_ARGUMENT_STYLE"] = 11] = "UNCLOSED_QUOTE_IN_ARGUMENT_STYLE";
    /** Missing select argument options (e.g. `{foo, select}`) */
    ErrorKind[ErrorKind["EXPECT_SELECT_ARGUMENT_OPTIONS"] = 12] = "EXPECT_SELECT_ARGUMENT_OPTIONS";
    /** Expecting an offset value in `plural` or `selectordinal` argument (e.g `{foo, plural, offset}`) */
    ErrorKind[ErrorKind["EXPECT_PLURAL_ARGUMENT_OFFSET_VALUE"] = 13] = "EXPECT_PLURAL_ARGUMENT_OFFSET_VALUE";
    /** Offset value in `plural` or `selectordinal` is invalid (e.g. `{foo, plural, offset: x}`) */
    ErrorKind[ErrorKind["INVALID_PLURAL_ARGUMENT_OFFSET_VALUE"] = 14] = "INVALID_PLURAL_ARGUMENT_OFFSET_VALUE";
    /** Expecting a selector in `select` argument (e.g `{foo, select}`) */
    ErrorKind[ErrorKind["EXPECT_SELECT_ARGUMENT_SELECTOR"] = 15] = "EXPECT_SELECT_ARGUMENT_SELECTOR";
    /** Expecting a selector in `plural` or `selectordinal` argument (e.g `{foo, plural}`) */
    ErrorKind[ErrorKind["EXPECT_PLURAL_ARGUMENT_SELECTOR"] = 16] = "EXPECT_PLURAL_ARGUMENT_SELECTOR";
    /** Expecting a message fragment after the `select` selector (e.g. `{foo, select, apple}`) */
    ErrorKind[ErrorKind["EXPECT_SELECT_ARGUMENT_SELECTOR_FRAGMENT"] = 17] = "EXPECT_SELECT_ARGUMENT_SELECTOR_FRAGMENT";
    /**
     * Expecting a message fragment after the `plural` or `selectordinal` selector
     * (e.g. `{foo, plural, one}`)
     */
    ErrorKind[ErrorKind["EXPECT_PLURAL_ARGUMENT_SELECTOR_FRAGMENT"] = 18] = "EXPECT_PLURAL_ARGUMENT_SELECTOR_FRAGMENT";
    /** Selector in `plural` or `selectordinal` is malformed (e.g. `{foo, plural, =x {#}}`) */
    ErrorKind[ErrorKind["INVALID_PLURAL_ARGUMENT_SELECTOR"] = 19] = "INVALID_PLURAL_ARGUMENT_SELECTOR";
    /**
     * Duplicate selectors in `plural` or `selectordinal` argument.
     * (e.g. {foo, plural, one {#} one {#}})
     */
    ErrorKind[ErrorKind["DUPLICATE_PLURAL_ARGUMENT_SELECTOR"] = 20] = "DUPLICATE_PLURAL_ARGUMENT_SELECTOR";
    /** Duplicate selectors in `select` argument.
     * (e.g. {foo, select, apple {apple} apple {apple}})
     */
    ErrorKind[ErrorKind["DUPLICATE_SELECT_ARGUMENT_SELECTOR"] = 21] = "DUPLICATE_SELECT_ARGUMENT_SELECTOR";
    /** Plural or select argument option must have `other` clause. */
    ErrorKind[ErrorKind["MISSING_OTHER_CLAUSE"] = 22] = "MISSING_OTHER_CLAUSE";
    /** The tag is malformed. (e.g. `<bold!>foo</bold!>) */
    ErrorKind[ErrorKind["INVALID_TAG"] = 23] = "INVALID_TAG";
    /** The tag name is invalid. (e.g. `<123>foo</123>`) */
    ErrorKind[ErrorKind["INVALID_TAG_NAME"] = 25] = "INVALID_TAG_NAME";
    /** The closing tag does not match the opening tag. (e.g. `<bold>foo</italic>`) */
    ErrorKind[ErrorKind["UNMATCHED_CLOSING_TAG"] = 26] = "UNMATCHED_CLOSING_TAG";
    /** The opening tag has unmatched closing tag. (e.g. `<bold>foo`) */
    ErrorKind[ErrorKind["UNCLOSED_TAG"] = 27] = "UNCLOSED_TAG";
})(ErrorKind || (ErrorKind = {}));

var TYPE;
(function (TYPE) {
    /**
     * Raw text
     */
    TYPE[TYPE["literal"] = 0] = "literal";
    /**
     * Variable w/o any format, e.g `var` in `this is a {var}`
     */
    TYPE[TYPE["argument"] = 1] = "argument";
    /**
     * Variable w/ number format
     */
    TYPE[TYPE["number"] = 2] = "number";
    /**
     * Variable w/ date format
     */
    TYPE[TYPE["date"] = 3] = "date";
    /**
     * Variable w/ time format
     */
    TYPE[TYPE["time"] = 4] = "time";
    /**
     * Variable w/ select format
     */
    TYPE[TYPE["select"] = 5] = "select";
    /**
     * Variable w/ plural format
     */
    TYPE[TYPE["plural"] = 6] = "plural";
    /**
     * Only possible within plural argument.
     * This is the `#` symbol that will be substituted with the count.
     */
    TYPE[TYPE["pound"] = 7] = "pound";
    /**
     * XML-like tag
     */
    TYPE[TYPE["tag"] = 8] = "tag";
})(TYPE || (TYPE = {}));
var SKELETON_TYPE;
(function (SKELETON_TYPE) {
    SKELETON_TYPE[SKELETON_TYPE["number"] = 0] = "number";
    SKELETON_TYPE[SKELETON_TYPE["dateTime"] = 1] = "dateTime";
})(SKELETON_TYPE || (SKELETON_TYPE = {}));
/**
 * Type Guards
 */
function isLiteralElement(el) {
    return el.type === TYPE.literal;
}
function isArgumentElement(el) {
    return el.type === TYPE.argument;
}
function isNumberElement(el) {
    return el.type === TYPE.number;
}
function isDateElement(el) {
    return el.type === TYPE.date;
}
function isTimeElement(el) {
    return el.type === TYPE.time;
}
function isSelectElement(el) {
    return el.type === TYPE.select;
}
function isPluralElement(el) {
    return el.type === TYPE.plural;
}
function isPoundElement(el) {
    return el.type === TYPE.pound;
}
function isTagElement(el) {
    return el.type === TYPE.tag;
}
function isNumberSkeleton(el) {
    return !!(el && typeof el === 'object' && el.type === SKELETON_TYPE.number);
}
function isDateTimeSkeleton(el) {
    return !!(el && typeof el === 'object' && el.type === SKELETON_TYPE.dateTime);
}

// @generated from regex-gen.ts
var SPACE_SEPARATOR_REGEX = /[ \xA0\u1680\u2000-\u200A\u202F\u205F\u3000]/;

/**
 * https://unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table
 * Credit: https://github.com/caridy/intl-datetimeformat-pattern/blob/master/index.js
 * with some tweaks
 */
var DATE_TIME_REGEX = /(?:[Eec]{1,6}|G{1,5}|[Qq]{1,5}|(?:[yYur]+|U{1,5})|[ML]{1,5}|d{1,2}|D{1,3}|F{1}|[abB]{1,5}|[hkHK]{1,2}|w{1,2}|W{1}|m{1,2}|s{1,2}|[zZOvVxX]{1,4})(?=([^']*'[^']*')*[^']*$)/g;
/**
 * Parse Date time skeleton into Intl.DateTimeFormatOptions
 * Ref: https://unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table
 * @public
 * @param skeleton skeleton string
 */
function parseDateTimeSkeleton(skeleton) {
    var result = {};
    skeleton.replace(DATE_TIME_REGEX, function (match) {
        var len = match.length;
        switch (match[0]) {
            // Era
            case 'G':
                result.era = len === 4 ? 'long' : len === 5 ? 'narrow' : 'short';
                break;
            // Year
            case 'y':
                result.year = len === 2 ? '2-digit' : 'numeric';
                break;
            case 'Y':
            case 'u':
            case 'U':
            case 'r':
                throw new RangeError('`Y/u/U/r` (year) patterns are not supported, use `y` instead');
            // Quarter
            case 'q':
            case 'Q':
                throw new RangeError('`q/Q` (quarter) patterns are not supported');
            // Month
            case 'M':
            case 'L':
                result.month = ['numeric', '2-digit', 'short', 'long', 'narrow'][len - 1];
                break;
            // Week
            case 'w':
            case 'W':
                throw new RangeError('`w/W` (week) patterns are not supported');
            case 'd':
                result.day = ['numeric', '2-digit'][len - 1];
                break;
            case 'D':
            case 'F':
            case 'g':
                throw new RangeError('`D/F/g` (day) patterns are not supported, use `d` instead');
            // Weekday
            case 'E':
                result.weekday = len === 4 ? 'long' : len === 5 ? 'narrow' : 'short';
                break;
            case 'e':
                if (len < 4) {
                    throw new RangeError('`e..eee` (weekday) patterns are not supported');
                }
                result.weekday = ['short', 'long', 'narrow', 'short'][len - 4];
                break;
            case 'c':
                if (len < 4) {
                    throw new RangeError('`c..ccc` (weekday) patterns are not supported');
                }
                result.weekday = ['short', 'long', 'narrow', 'short'][len - 4];
                break;
            // Period
            case 'a': // AM, PM
                result.hour12 = true;
                break;
            case 'b': // am, pm, noon, midnight
            case 'B': // flexible day periods
                throw new RangeError('`b/B` (period) patterns are not supported, use `a` instead');
            // Hour
            case 'h':
                result.hourCycle = 'h12';
                result.hour = ['numeric', '2-digit'][len - 1];
                break;
            case 'H':
                result.hourCycle = 'h23';
                result.hour = ['numeric', '2-digit'][len - 1];
                break;
            case 'K':
                result.hourCycle = 'h11';
                result.hour = ['numeric', '2-digit'][len - 1];
                break;
            case 'k':
                result.hourCycle = 'h24';
                result.hour = ['numeric', '2-digit'][len - 1];
                break;
            case 'j':
            case 'J':
            case 'C':
                throw new RangeError('`j/J/C` (hour) patterns are not supported, use `h/H/K/k` instead');
            // Minute
            case 'm':
                result.minute = ['numeric', '2-digit'][len - 1];
                break;
            // Second
            case 's':
                result.second = ['numeric', '2-digit'][len - 1];
                break;
            case 'S':
            case 'A':
                throw new RangeError('`S/A` (second) patterns are not supported, use `s` instead');
            // Zone
            case 'z': // 1..3, 4: specific non-location format
                result.timeZoneName = len < 4 ? 'short' : 'long';
                break;
            case 'Z': // 1..3, 4, 5: The ISO8601 varios formats
            case 'O': // 1, 4: milliseconds in day short, long
            case 'v': // 1, 4: generic non-location format
            case 'V': // 1, 2, 3, 4: time zone ID or city
            case 'X': // 1, 2, 3, 4: The ISO8601 varios formats
            case 'x': // 1, 2, 3, 4: The ISO8601 varios formats
                throw new RangeError('`Z/O/v/V/X/x` (timeZone) patterns are not supported, use `z` instead');
        }
        return '';
    });
    return result;
}

// @generated from regex-gen.ts
var WHITE_SPACE_REGEX = /[\t-\r \x85\u200E\u200F\u2028\u2029]/i;

function parseNumberSkeletonFromString(skeleton) {
    if (skeleton.length === 0) {
        throw new Error('Number skeleton cannot be empty');
    }
    // Parse the skeleton
    var stringTokens = skeleton
        .split(WHITE_SPACE_REGEX)
        .filter(function (x) { return x.length > 0; });
    var tokens = [];
    for (var _i = 0, stringTokens_1 = stringTokens; _i < stringTokens_1.length; _i++) {
        var stringToken = stringTokens_1[_i];
        var stemAndOptions = stringToken.split('/');
        if (stemAndOptions.length === 0) {
            throw new Error('Invalid number skeleton');
        }
        var stem = stemAndOptions[0], options = stemAndOptions.slice(1);
        for (var _a = 0, options_1 = options; _a < options_1.length; _a++) {
            var option = options_1[_a];
            if (option.length === 0) {
                throw new Error('Invalid number skeleton');
            }
        }
        tokens.push({ stem: stem, options: options });
    }
    return tokens;
}
function icuUnitToEcma(unit) {
    return unit.replace(/^(.*?)-/, '');
}
var FRACTION_PRECISION_REGEX = /^\.(?:(0+)(\*)?|(#+)|(0+)(#+))$/g;
var SIGNIFICANT_PRECISION_REGEX = /^(@+)?(\+|#+)?[rs]?$/g;
var INTEGER_WIDTH_REGEX = /(\*)(0+)|(#+)(0+)|(0+)/g;
var CONCISE_INTEGER_WIDTH_REGEX = /^(0+)$/;
function parseSignificantPrecision(str) {
    var result = {};
    if (str[str.length - 1] === 'r') {
        result.roundingPriority = 'morePrecision';
    }
    else if (str[str.length - 1] === 's') {
        result.roundingPriority = 'lessPrecision';
    }
    str.replace(SIGNIFICANT_PRECISION_REGEX, function (_, g1, g2) {
        // @@@ case
        if (typeof g2 !== 'string') {
            result.minimumSignificantDigits = g1.length;
            result.maximumSignificantDigits = g1.length;
        }
        // @@@+ case
        else if (g2 === '+') {
            result.minimumSignificantDigits = g1.length;
        }
        // .### case
        else if (g1[0] === '#') {
            result.maximumSignificantDigits = g1.length;
        }
        // .@@## or .@@@ case
        else {
            result.minimumSignificantDigits = g1.length;
            result.maximumSignificantDigits =
                g1.length + (typeof g2 === 'string' ? g2.length : 0);
        }
        return '';
    });
    return result;
}
function parseSign(str) {
    switch (str) {
        case 'sign-auto':
            return {
                signDisplay: 'auto',
            };
        case 'sign-accounting':
        case '()':
            return {
                currencySign: 'accounting',
            };
        case 'sign-always':
        case '+!':
            return {
                signDisplay: 'always',
            };
        case 'sign-accounting-always':
        case '()!':
            return {
                signDisplay: 'always',
                currencySign: 'accounting',
            };
        case 'sign-except-zero':
        case '+?':
            return {
                signDisplay: 'exceptZero',
            };
        case 'sign-accounting-except-zero':
        case '()?':
            return {
                signDisplay: 'exceptZero',
                currencySign: 'accounting',
            };
        case 'sign-never':
        case '+_':
            return {
                signDisplay: 'never',
            };
    }
}
function parseConciseScientificAndEngineeringStem(stem) {
    // Engineering
    var result;
    if (stem[0] === 'E' && stem[1] === 'E') {
        result = {
            notation: 'engineering',
        };
        stem = stem.slice(2);
    }
    else if (stem[0] === 'E') {
        result = {
            notation: 'scientific',
        };
        stem = stem.slice(1);
    }
    if (result) {
        var signDisplay = stem.slice(0, 2);
        if (signDisplay === '+!') {
            result.signDisplay = 'always';
            stem = stem.slice(2);
        }
        else if (signDisplay === '+?') {
            result.signDisplay = 'exceptZero';
            stem = stem.slice(2);
        }
        if (!CONCISE_INTEGER_WIDTH_REGEX.test(stem)) {
            throw new Error('Malformed concise eng/scientific notation');
        }
        result.minimumIntegerDigits = stem.length;
    }
    return result;
}
function parseNotationOptions(opt) {
    var result = {};
    var signOpts = parseSign(opt);
    if (signOpts) {
        return signOpts;
    }
    return result;
}
/**
 * https://github.com/unicode-org/icu/blob/master/docs/userguide/format_parse/numbers/skeletons.md#skeleton-stems-and-options
 */
function parseNumberSkeleton(tokens) {
    var result = {};
    for (var _i = 0, tokens_1 = tokens; _i < tokens_1.length; _i++) {
        var token = tokens_1[_i];
        switch (token.stem) {
            case 'percent':
            case '%':
                result.style = 'percent';
                continue;
            case '%x100':
                result.style = 'percent';
                result.scale = 100;
                continue;
            case 'currency':
                result.style = 'currency';
                result.currency = token.options[0];
                continue;
            case 'group-off':
            case ',_':
                result.useGrouping = false;
                continue;
            case 'precision-integer':
            case '.':
                result.maximumFractionDigits = 0;
                continue;
            case 'measure-unit':
            case 'unit':
                result.style = 'unit';
                result.unit = icuUnitToEcma(token.options[0]);
                continue;
            case 'compact-short':
            case 'K':
                result.notation = 'compact';
                result.compactDisplay = 'short';
                continue;
            case 'compact-long':
            case 'KK':
                result.notation = 'compact';
                result.compactDisplay = 'long';
                continue;
            case 'scientific':
                result = __assign$1(__assign$1(__assign$1({}, result), { notation: 'scientific' }), token.options.reduce(function (all, opt) { return (__assign$1(__assign$1({}, all), parseNotationOptions(opt))); }, {}));
                continue;
            case 'engineering':
                result = __assign$1(__assign$1(__assign$1({}, result), { notation: 'engineering' }), token.options.reduce(function (all, opt) { return (__assign$1(__assign$1({}, all), parseNotationOptions(opt))); }, {}));
                continue;
            case 'notation-simple':
                result.notation = 'standard';
                continue;
            // https://github.com/unicode-org/icu/blob/master/icu4c/source/i18n/unicode/unumberformatter.h
            case 'unit-width-narrow':
                result.currencyDisplay = 'narrowSymbol';
                result.unitDisplay = 'narrow';
                continue;
            case 'unit-width-short':
                result.currencyDisplay = 'code';
                result.unitDisplay = 'short';
                continue;
            case 'unit-width-full-name':
                result.currencyDisplay = 'name';
                result.unitDisplay = 'long';
                continue;
            case 'unit-width-iso-code':
                result.currencyDisplay = 'symbol';
                continue;
            case 'scale':
                result.scale = parseFloat(token.options[0]);
                continue;
            case 'rounding-mode-floor':
                result.roundingMode = 'floor';
                continue;
            case 'rounding-mode-ceiling':
                result.roundingMode = 'ceil';
                continue;
            case 'rounding-mode-down':
                result.roundingMode = 'trunc';
                continue;
            case 'rounding-mode-up':
                result.roundingMode = 'expand';
                continue;
            case 'rounding-mode-half-even':
                result.roundingMode = 'halfEven';
                continue;
            case 'rounding-mode-half-down':
                result.roundingMode = 'halfTrunc';
                continue;
            case 'rounding-mode-half-up':
                result.roundingMode = 'halfExpand';
                continue;
            // https://unicode-org.github.io/icu/userguide/format_parse/numbers/skeletons.html#integer-width
            case 'integer-width':
                if (token.options.length > 1) {
                    throw new RangeError('integer-width stems only accept a single optional option');
                }
                token.options[0].replace(INTEGER_WIDTH_REGEX, function (_, g1, g2, g3, g4, g5) {
                    if (g1) {
                        result.minimumIntegerDigits = g2.length;
                    }
                    else if (g3 && g4) {
                        throw new Error('We currently do not support maximum integer digits');
                    }
                    else if (g5) {
                        throw new Error('We currently do not support exact integer digits');
                    }
                    return '';
                });
                continue;
        }
        // https://unicode-org.github.io/icu/userguide/format_parse/numbers/skeletons.html#integer-width
        if (CONCISE_INTEGER_WIDTH_REGEX.test(token.stem)) {
            result.minimumIntegerDigits = token.stem.length;
            continue;
        }
        if (FRACTION_PRECISION_REGEX.test(token.stem)) {
            // Precision
            // https://unicode-org.github.io/icu/userguide/format_parse/numbers/skeletons.html#fraction-precision
            // precision-integer case
            if (token.options.length > 1) {
                throw new RangeError('Fraction-precision stems only accept a single optional option');
            }
            token.stem.replace(FRACTION_PRECISION_REGEX, function (_, g1, g2, g3, g4, g5) {
                // .000* case (before ICU67 it was .000+)
                if (g2 === '*') {
                    result.minimumFractionDigits = g1.length;
                }
                // .### case
                else if (g3 && g3[0] === '#') {
                    result.maximumFractionDigits = g3.length;
                }
                // .00## case
                else if (g4 && g5) {
                    result.minimumFractionDigits = g4.length;
                    result.maximumFractionDigits = g4.length + g5.length;
                }
                else {
                    result.minimumFractionDigits = g1.length;
                    result.maximumFractionDigits = g1.length;
                }
                return '';
            });
            var opt = token.options[0];
            // https://unicode-org.github.io/icu/userguide/format_parse/numbers/skeletons.html#trailing-zero-display
            if (opt === 'w') {
                result = __assign$1(__assign$1({}, result), { trailingZeroDisplay: 'stripIfInteger' });
            }
            else if (opt) {
                result = __assign$1(__assign$1({}, result), parseSignificantPrecision(opt));
            }
            continue;
        }
        // https://unicode-org.github.io/icu/userguide/format_parse/numbers/skeletons.html#significant-digits-precision
        if (SIGNIFICANT_PRECISION_REGEX.test(token.stem)) {
            result = __assign$1(__assign$1({}, result), parseSignificantPrecision(token.stem));
            continue;
        }
        var signOpts = parseSign(token.stem);
        if (signOpts) {
            result = __assign$1(__assign$1({}, result), signOpts);
        }
        var conciseScientificAndEngineeringOpts = parseConciseScientificAndEngineeringStem(token.stem);
        if (conciseScientificAndEngineeringOpts) {
            result = __assign$1(__assign$1({}, result), conciseScientificAndEngineeringOpts);
        }
    }
    return result;
}

// @generated from time-data-gen.ts
// prettier-ignore  
var timeData = {
    "001": [
        "H",
        "h"
    ],
    "AC": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "AD": [
        "H",
        "hB"
    ],
    "AE": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "AF": [
        "H",
        "hb",
        "hB",
        "h"
    ],
    "AG": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "AI": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "AL": [
        "h",
        "H",
        "hB"
    ],
    "AM": [
        "H",
        "hB"
    ],
    "AO": [
        "H",
        "hB"
    ],
    "AR": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "AS": [
        "h",
        "H"
    ],
    "AT": [
        "H",
        "hB"
    ],
    "AU": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "AW": [
        "H",
        "hB"
    ],
    "AX": [
        "H"
    ],
    "AZ": [
        "H",
        "hB",
        "h"
    ],
    "BA": [
        "H",
        "hB",
        "h"
    ],
    "BB": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "BD": [
        "h",
        "hB",
        "H"
    ],
    "BE": [
        "H",
        "hB"
    ],
    "BF": [
        "H",
        "hB"
    ],
    "BG": [
        "H",
        "hB",
        "h"
    ],
    "BH": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "BI": [
        "H",
        "h"
    ],
    "BJ": [
        "H",
        "hB"
    ],
    "BL": [
        "H",
        "hB"
    ],
    "BM": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "BN": [
        "hb",
        "hB",
        "h",
        "H"
    ],
    "BO": [
        "H",
        "hB",
        "h",
        "hb"
    ],
    "BQ": [
        "H"
    ],
    "BR": [
        "H",
        "hB"
    ],
    "BS": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "BT": [
        "h",
        "H"
    ],
    "BW": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "BY": [
        "H",
        "h"
    ],
    "BZ": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "CA": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "CC": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "CD": [
        "hB",
        "H"
    ],
    "CF": [
        "H",
        "h",
        "hB"
    ],
    "CG": [
        "H",
        "hB"
    ],
    "CH": [
        "H",
        "hB",
        "h"
    ],
    "CI": [
        "H",
        "hB"
    ],
    "CK": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "CL": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "CM": [
        "H",
        "h",
        "hB"
    ],
    "CN": [
        "H",
        "hB",
        "hb",
        "h"
    ],
    "CO": [
        "h",
        "H",
        "hB",
        "hb"
    ],
    "CP": [
        "H"
    ],
    "CR": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "CU": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "CV": [
        "H",
        "hB"
    ],
    "CW": [
        "H",
        "hB"
    ],
    "CX": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "CY": [
        "h",
        "H",
        "hb",
        "hB"
    ],
    "CZ": [
        "H"
    ],
    "DE": [
        "H",
        "hB"
    ],
    "DG": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "DJ": [
        "h",
        "H"
    ],
    "DK": [
        "H"
    ],
    "DM": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "DO": [
        "h",
        "H",
        "hB",
        "hb"
    ],
    "DZ": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "EA": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "EC": [
        "H",
        "hB",
        "h",
        "hb"
    ],
    "EE": [
        "H",
        "hB"
    ],
    "EG": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "EH": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "ER": [
        "h",
        "H"
    ],
    "ES": [
        "H",
        "hB",
        "h",
        "hb"
    ],
    "ET": [
        "hB",
        "hb",
        "h",
        "H"
    ],
    "FI": [
        "H"
    ],
    "FJ": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "FK": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "FM": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "FO": [
        "H",
        "h"
    ],
    "FR": [
        "H",
        "hB"
    ],
    "GA": [
        "H",
        "hB"
    ],
    "GB": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "GD": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "GE": [
        "H",
        "hB",
        "h"
    ],
    "GF": [
        "H",
        "hB"
    ],
    "GG": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "GH": [
        "h",
        "H"
    ],
    "GI": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "GL": [
        "H",
        "h"
    ],
    "GM": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "GN": [
        "H",
        "hB"
    ],
    "GP": [
        "H",
        "hB"
    ],
    "GQ": [
        "H",
        "hB",
        "h",
        "hb"
    ],
    "GR": [
        "h",
        "H",
        "hb",
        "hB"
    ],
    "GT": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "GU": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "GW": [
        "H",
        "hB"
    ],
    "GY": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "HK": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "HN": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "HR": [
        "H",
        "hB"
    ],
    "HU": [
        "H",
        "h"
    ],
    "IC": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "ID": [
        "H"
    ],
    "IE": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "IL": [
        "H",
        "hB"
    ],
    "IM": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "IN": [
        "h",
        "H"
    ],
    "IO": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "IQ": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "IR": [
        "hB",
        "H"
    ],
    "IS": [
        "H"
    ],
    "IT": [
        "H",
        "hB"
    ],
    "JE": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "JM": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "JO": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "JP": [
        "H",
        "K",
        "h"
    ],
    "KE": [
        "hB",
        "hb",
        "H",
        "h"
    ],
    "KG": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "KH": [
        "hB",
        "h",
        "H",
        "hb"
    ],
    "KI": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "KM": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "KN": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "KP": [
        "h",
        "H",
        "hB",
        "hb"
    ],
    "KR": [
        "h",
        "H",
        "hB",
        "hb"
    ],
    "KW": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "KY": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "KZ": [
        "H",
        "hB"
    ],
    "LA": [
        "H",
        "hb",
        "hB",
        "h"
    ],
    "LB": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "LC": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "LI": [
        "H",
        "hB",
        "h"
    ],
    "LK": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "LR": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "LS": [
        "h",
        "H"
    ],
    "LT": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "LU": [
        "H",
        "h",
        "hB"
    ],
    "LV": [
        "H",
        "hB",
        "hb",
        "h"
    ],
    "LY": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "MA": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "MC": [
        "H",
        "hB"
    ],
    "MD": [
        "H",
        "hB"
    ],
    "ME": [
        "H",
        "hB",
        "h"
    ],
    "MF": [
        "H",
        "hB"
    ],
    "MG": [
        "H",
        "h"
    ],
    "MH": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "MK": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "ML": [
        "H"
    ],
    "MM": [
        "hB",
        "hb",
        "H",
        "h"
    ],
    "MN": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "MO": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "MP": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "MQ": [
        "H",
        "hB"
    ],
    "MR": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "MS": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "MT": [
        "H",
        "h"
    ],
    "MU": [
        "H",
        "h"
    ],
    "MV": [
        "H",
        "h"
    ],
    "MW": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "MX": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "MY": [
        "hb",
        "hB",
        "h",
        "H"
    ],
    "MZ": [
        "H",
        "hB"
    ],
    "NA": [
        "h",
        "H",
        "hB",
        "hb"
    ],
    "NC": [
        "H",
        "hB"
    ],
    "NE": [
        "H"
    ],
    "NF": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "NG": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "NI": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "NL": [
        "H",
        "hB"
    ],
    "NO": [
        "H",
        "h"
    ],
    "NP": [
        "H",
        "h",
        "hB"
    ],
    "NR": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "NU": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "NZ": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "OM": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "PA": [
        "h",
        "H",
        "hB",
        "hb"
    ],
    "PE": [
        "H",
        "hB",
        "h",
        "hb"
    ],
    "PF": [
        "H",
        "h",
        "hB"
    ],
    "PG": [
        "h",
        "H"
    ],
    "PH": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "PK": [
        "h",
        "hB",
        "H"
    ],
    "PL": [
        "H",
        "h"
    ],
    "PM": [
        "H",
        "hB"
    ],
    "PN": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "PR": [
        "h",
        "H",
        "hB",
        "hb"
    ],
    "PS": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "PT": [
        "H",
        "hB"
    ],
    "PW": [
        "h",
        "H"
    ],
    "PY": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "QA": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "RE": [
        "H",
        "hB"
    ],
    "RO": [
        "H",
        "hB"
    ],
    "RS": [
        "H",
        "hB",
        "h"
    ],
    "RU": [
        "H"
    ],
    "RW": [
        "H",
        "h"
    ],
    "SA": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "SB": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "SC": [
        "H",
        "h",
        "hB"
    ],
    "SD": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "SE": [
        "H"
    ],
    "SG": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "SH": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "SI": [
        "H",
        "hB"
    ],
    "SJ": [
        "H"
    ],
    "SK": [
        "H"
    ],
    "SL": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "SM": [
        "H",
        "h",
        "hB"
    ],
    "SN": [
        "H",
        "h",
        "hB"
    ],
    "SO": [
        "h",
        "H"
    ],
    "SR": [
        "H",
        "hB"
    ],
    "SS": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "ST": [
        "H",
        "hB"
    ],
    "SV": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "SX": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "SY": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "SZ": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "TA": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "TC": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "TD": [
        "h",
        "H",
        "hB"
    ],
    "TF": [
        "H",
        "h",
        "hB"
    ],
    "TG": [
        "H",
        "hB"
    ],
    "TH": [
        "H",
        "h"
    ],
    "TJ": [
        "H",
        "h"
    ],
    "TL": [
        "H",
        "hB",
        "hb",
        "h"
    ],
    "TM": [
        "H",
        "h"
    ],
    "TN": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "TO": [
        "h",
        "H"
    ],
    "TR": [
        "H",
        "hB"
    ],
    "TT": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "TW": [
        "hB",
        "hb",
        "h",
        "H"
    ],
    "TZ": [
        "hB",
        "hb",
        "H",
        "h"
    ],
    "UA": [
        "H",
        "hB",
        "h"
    ],
    "UG": [
        "hB",
        "hb",
        "H",
        "h"
    ],
    "UM": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "US": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "UY": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "UZ": [
        "H",
        "hB",
        "h"
    ],
    "VA": [
        "H",
        "h",
        "hB"
    ],
    "VC": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "VE": [
        "h",
        "H",
        "hB",
        "hb"
    ],
    "VG": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "VI": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "VN": [
        "H",
        "h"
    ],
    "VU": [
        "h",
        "H"
    ],
    "WF": [
        "H",
        "hB"
    ],
    "WS": [
        "h",
        "H"
    ],
    "XK": [
        "H",
        "hB",
        "h"
    ],
    "YE": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "YT": [
        "H",
        "hB"
    ],
    "ZA": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "ZM": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "ZW": [
        "H",
        "h"
    ],
    "af-ZA": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "ar-001": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "ca-ES": [
        "H",
        "h",
        "hB"
    ],
    "en-001": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "es-BO": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "es-BR": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "es-EC": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "es-ES": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "es-GQ": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "es-PE": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "fr-CA": [
        "H",
        "h",
        "hB"
    ],
    "gl-ES": [
        "H",
        "h",
        "hB"
    ],
    "gu-IN": [
        "hB",
        "hb",
        "h",
        "H"
    ],
    "hi-IN": [
        "hB",
        "h",
        "H"
    ],
    "it-CH": [
        "H",
        "h",
        "hB"
    ],
    "it-IT": [
        "H",
        "h",
        "hB"
    ],
    "kn-IN": [
        "hB",
        "h",
        "H"
    ],
    "ml-IN": [
        "hB",
        "h",
        "H"
    ],
    "mr-IN": [
        "hB",
        "hb",
        "h",
        "H"
    ],
    "pa-IN": [
        "hB",
        "hb",
        "h",
        "H"
    ],
    "ta-IN": [
        "hB",
        "h",
        "hb",
        "H"
    ],
    "te-IN": [
        "hB",
        "h",
        "H"
    ],
    "zu-ZA": [
        "H",
        "hB",
        "hb",
        "h"
    ]
};

/**
 * Returns the best matching date time pattern if a date time skeleton
 * pattern is provided with a locale. Follows the Unicode specification:
 * https://www.unicode.org/reports/tr35/tr35-dates.html#table-mapping-requested-time-skeletons-to-patterns
 * @param skeleton date time skeleton pattern that possibly includes j, J or C
 * @param locale
 */
function getBestPattern(skeleton, locale) {
    var skeletonCopy = '';
    for (var patternPos = 0; patternPos < skeleton.length; patternPos++) {
        var patternChar = skeleton.charAt(patternPos);
        if (patternChar === 'j') {
            var extraLength = 0;
            while (patternPos + 1 < skeleton.length &&
                skeleton.charAt(patternPos + 1) === patternChar) {
                extraLength++;
                patternPos++;
            }
            var hourLen = 1 + (extraLength & 1);
            var dayPeriodLen = extraLength < 2 ? 1 : 3 + (extraLength >> 1);
            var dayPeriodChar = 'a';
            var hourChar = getDefaultHourSymbolFromLocale(locale);
            if (hourChar == 'H' || hourChar == 'k') {
                dayPeriodLen = 0;
            }
            while (dayPeriodLen-- > 0) {
                skeletonCopy += dayPeriodChar;
            }
            while (hourLen-- > 0) {
                skeletonCopy = hourChar + skeletonCopy;
            }
        }
        else if (patternChar === 'J') {
            skeletonCopy += 'H';
        }
        else {
            skeletonCopy += patternChar;
        }
    }
    return skeletonCopy;
}
/**
 * Maps the [hour cycle type](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/Locale/hourCycle)
 * of the given `locale` to the corresponding time pattern.
 * @param locale
 */
function getDefaultHourSymbolFromLocale(locale) {
    var hourCycle = locale.hourCycle;
    if (hourCycle === undefined &&
        // @ts-ignore hourCycle(s) is not identified yet
        locale.hourCycles &&
        // @ts-ignore
        locale.hourCycles.length) {
        // @ts-ignore
        hourCycle = locale.hourCycles[0];
    }
    if (hourCycle) {
        switch (hourCycle) {
            case 'h24':
                return 'k';
            case 'h23':
                return 'H';
            case 'h12':
                return 'h';
            case 'h11':
                return 'K';
            default:
                throw new Error('Invalid hourCycle');
        }
    }
    // TODO: Once hourCycle is fully supported remove the following with data generation
    var languageTag = locale.language;
    var regionTag;
    if (languageTag !== 'root') {
        regionTag = locale.maximize().region;
    }
    var hourCycles = timeData[regionTag || ''] ||
        timeData[languageTag || ''] ||
        timeData["".concat(languageTag, "-001")] ||
        timeData['001'];
    return hourCycles[0];
}

var _a;
var SPACE_SEPARATOR_START_REGEX = new RegExp("^".concat(SPACE_SEPARATOR_REGEX.source, "*"));
var SPACE_SEPARATOR_END_REGEX = new RegExp("".concat(SPACE_SEPARATOR_REGEX.source, "*$"));
function createLocation(start, end) {
    return { start: start, end: end };
}
// #region Ponyfills
// Consolidate these variables up top for easier toggling during debugging
var hasNativeStartsWith = !!String.prototype.startsWith && '_a'.startsWith('a', 1);
var hasNativeFromCodePoint = !!String.fromCodePoint;
var hasNativeFromEntries = !!Object.fromEntries;
var hasNativeCodePointAt = !!String.prototype.codePointAt;
var hasTrimStart = !!String.prototype.trimStart;
var hasTrimEnd = !!String.prototype.trimEnd;
var hasNativeIsSafeInteger = !!Number.isSafeInteger;
var isSafeInteger = hasNativeIsSafeInteger
    ? Number.isSafeInteger
    : function (n) {
        return (typeof n === 'number' &&
            isFinite(n) &&
            Math.floor(n) === n &&
            Math.abs(n) <= 0x1fffffffffffff);
    };
// IE11 does not support y and u.
var REGEX_SUPPORTS_U_AND_Y = true;
try {
    var re = RE('([^\\p{White_Space}\\p{Pattern_Syntax}]*)', 'yu');
    /**
     * legacy Edge or Xbox One browser
     * Unicode flag support: supported
     * Pattern_Syntax support: not supported
     * See https://github.com/formatjs/formatjs/issues/2822
     */
    REGEX_SUPPORTS_U_AND_Y = ((_a = re.exec('a')) === null || _a === void 0 ? void 0 : _a[0]) === 'a';
}
catch (_) {
    REGEX_SUPPORTS_U_AND_Y = false;
}
var startsWith = hasNativeStartsWith
    ? // Native
        function startsWith(s, search, position) {
            return s.startsWith(search, position);
        }
    : // For IE11
        function startsWith(s, search, position) {
            return s.slice(position, position + search.length) === search;
        };
var fromCodePoint = hasNativeFromCodePoint
    ? String.fromCodePoint
    : // IE11
        function fromCodePoint() {
            var codePoints = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                codePoints[_i] = arguments[_i];
            }
            var elements = '';
            var length = codePoints.length;
            var i = 0;
            var code;
            while (length > i) {
                code = codePoints[i++];
                if (code > 0x10ffff)
                    throw RangeError(code + ' is not a valid code point');
                elements +=
                    code < 0x10000
                        ? String.fromCharCode(code)
                        : String.fromCharCode(((code -= 0x10000) >> 10) + 0xd800, (code % 0x400) + 0xdc00);
            }
            return elements;
        };
var fromEntries = 
// native
hasNativeFromEntries
    ? Object.fromEntries
    : // Ponyfill
        function fromEntries(entries) {
            var obj = {};
            for (var _i = 0, entries_1 = entries; _i < entries_1.length; _i++) {
                var _a = entries_1[_i], k = _a[0], v = _a[1];
                obj[k] = v;
            }
            return obj;
        };
var codePointAt = hasNativeCodePointAt
    ? // Native
        function codePointAt(s, index) {
            return s.codePointAt(index);
        }
    : // IE 11
        function codePointAt(s, index) {
            var size = s.length;
            if (index < 0 || index >= size) {
                return undefined;
            }
            var first = s.charCodeAt(index);
            var second;
            return first < 0xd800 ||
                first > 0xdbff ||
                index + 1 === size ||
                (second = s.charCodeAt(index + 1)) < 0xdc00 ||
                second > 0xdfff
                ? first
                : ((first - 0xd800) << 10) + (second - 0xdc00) + 0x10000;
        };
var trimStart = hasTrimStart
    ? // Native
        function trimStart(s) {
            return s.trimStart();
        }
    : // Ponyfill
        function trimStart(s) {
            return s.replace(SPACE_SEPARATOR_START_REGEX, '');
        };
var trimEnd = hasTrimEnd
    ? // Native
        function trimEnd(s) {
            return s.trimEnd();
        }
    : // Ponyfill
        function trimEnd(s) {
            return s.replace(SPACE_SEPARATOR_END_REGEX, '');
        };
// Prevent minifier to translate new RegExp to literal form that might cause syntax error on IE11.
function RE(s, flag) {
    return new RegExp(s, flag);
}
// #endregion
var matchIdentifierAtIndex;
if (REGEX_SUPPORTS_U_AND_Y) {
    // Native
    var IDENTIFIER_PREFIX_RE_1 = RE('([^\\p{White_Space}\\p{Pattern_Syntax}]*)', 'yu');
    matchIdentifierAtIndex = function matchIdentifierAtIndex(s, index) {
        var _a;
        IDENTIFIER_PREFIX_RE_1.lastIndex = index;
        var match = IDENTIFIER_PREFIX_RE_1.exec(s);
        return (_a = match[1]) !== null && _a !== void 0 ? _a : '';
    };
}
else {
    // IE11
    matchIdentifierAtIndex = function matchIdentifierAtIndex(s, index) {
        var match = [];
        while (true) {
            var c = codePointAt(s, index);
            if (c === undefined || _isWhiteSpace(c) || _isPatternSyntax(c)) {
                break;
            }
            match.push(c);
            index += c >= 0x10000 ? 2 : 1;
        }
        return fromCodePoint.apply(void 0, match);
    };
}
var Parser = /** @class */ (function () {
    function Parser(message, options) {
        if (options === void 0) { options = {}; }
        this.message = message;
        this.position = { offset: 0, line: 1, column: 1 };
        this.ignoreTag = !!options.ignoreTag;
        this.locale = options.locale;
        this.requiresOtherClause = !!options.requiresOtherClause;
        this.shouldParseSkeletons = !!options.shouldParseSkeletons;
    }
    Parser.prototype.parse = function () {
        if (this.offset() !== 0) {
            throw Error('parser can only be used once');
        }
        return this.parseMessage(0, '', false);
    };
    Parser.prototype.parseMessage = function (nestingLevel, parentArgType, expectingCloseTag) {
        var elements = [];
        while (!this.isEOF()) {
            var char = this.char();
            if (char === 123 /* `{` */) {
                var result = this.parseArgument(nestingLevel, expectingCloseTag);
                if (result.err) {
                    return result;
                }
                elements.push(result.val);
            }
            else if (char === 125 /* `}` */ && nestingLevel > 0) {
                break;
            }
            else if (char === 35 /* `#` */ &&
                (parentArgType === 'plural' || parentArgType === 'selectordinal')) {
                var position = this.clonePosition();
                this.bump();
                elements.push({
                    type: TYPE.pound,
                    location: createLocation(position, this.clonePosition()),
                });
            }
            else if (char === 60 /* `<` */ &&
                !this.ignoreTag &&
                this.peek() === 47 // char code for '/'
            ) {
                if (expectingCloseTag) {
                    break;
                }
                else {
                    return this.error(ErrorKind.UNMATCHED_CLOSING_TAG, createLocation(this.clonePosition(), this.clonePosition()));
                }
            }
            else if (char === 60 /* `<` */ &&
                !this.ignoreTag &&
                _isAlpha(this.peek() || 0)) {
                var result = this.parseTag(nestingLevel, parentArgType);
                if (result.err) {
                    return result;
                }
                elements.push(result.val);
            }
            else {
                var result = this.parseLiteral(nestingLevel, parentArgType);
                if (result.err) {
                    return result;
                }
                elements.push(result.val);
            }
        }
        return { val: elements, err: null };
    };
    /**
     * A tag name must start with an ASCII lower/upper case letter. The grammar is based on the
     * [custom element name][] except that a dash is NOT always mandatory and uppercase letters
     * are accepted:
     *
     * ```
     * tag ::= "<" tagName (whitespace)* "/>" | "<" tagName (whitespace)* ">" message "</" tagName (whitespace)* ">"
     * tagName ::= [a-z] (PENChar)*
     * PENChar ::=
     *     "-" | "." | [0-9] | "_" | [a-z] | [A-Z] | #xB7 | [#xC0-#xD6] | [#xD8-#xF6] | [#xF8-#x37D] |
     *     [#x37F-#x1FFF] | [#x200C-#x200D] | [#x203F-#x2040] | [#x2070-#x218F] | [#x2C00-#x2FEF] |
     *     [#x3001-#xD7FF] | [#xF900-#xFDCF] | [#xFDF0-#xFFFD] | [#x10000-#xEFFFF]
     * ```
     *
     * [custom element name]: https://html.spec.whatwg.org/multipage/custom-elements.html#valid-custom-element-name
     * NOTE: We're a bit more lax here since HTML technically does not allow uppercase HTML element but we do
     * since other tag-based engines like React allow it
     */
    Parser.prototype.parseTag = function (nestingLevel, parentArgType) {
        var startPosition = this.clonePosition();
        this.bump(); // `<`
        var tagName = this.parseTagName();
        this.bumpSpace();
        if (this.bumpIf('/>')) {
            // Self closing tag
            return {
                val: {
                    type: TYPE.literal,
                    value: "<".concat(tagName, "/>"),
                    location: createLocation(startPosition, this.clonePosition()),
                },
                err: null,
            };
        }
        else if (this.bumpIf('>')) {
            var childrenResult = this.parseMessage(nestingLevel + 1, parentArgType, true);
            if (childrenResult.err) {
                return childrenResult;
            }
            var children = childrenResult.val;
            // Expecting a close tag
            var endTagStartPosition = this.clonePosition();
            if (this.bumpIf('</')) {
                if (this.isEOF() || !_isAlpha(this.char())) {
                    return this.error(ErrorKind.INVALID_TAG, createLocation(endTagStartPosition, this.clonePosition()));
                }
                var closingTagNameStartPosition = this.clonePosition();
                var closingTagName = this.parseTagName();
                if (tagName !== closingTagName) {
                    return this.error(ErrorKind.UNMATCHED_CLOSING_TAG, createLocation(closingTagNameStartPosition, this.clonePosition()));
                }
                this.bumpSpace();
                if (!this.bumpIf('>')) {
                    return this.error(ErrorKind.INVALID_TAG, createLocation(endTagStartPosition, this.clonePosition()));
                }
                return {
                    val: {
                        type: TYPE.tag,
                        value: tagName,
                        children: children,
                        location: createLocation(startPosition, this.clonePosition()),
                    },
                    err: null,
                };
            }
            else {
                return this.error(ErrorKind.UNCLOSED_TAG, createLocation(startPosition, this.clonePosition()));
            }
        }
        else {
            return this.error(ErrorKind.INVALID_TAG, createLocation(startPosition, this.clonePosition()));
        }
    };
    /**
     * This method assumes that the caller has peeked ahead for the first tag character.
     */
    Parser.prototype.parseTagName = function () {
        var startOffset = this.offset();
        this.bump(); // the first tag name character
        while (!this.isEOF() && _isPotentialElementNameChar(this.char())) {
            this.bump();
        }
        return this.message.slice(startOffset, this.offset());
    };
    Parser.prototype.parseLiteral = function (nestingLevel, parentArgType) {
        var start = this.clonePosition();
        var value = '';
        while (true) {
            var parseQuoteResult = this.tryParseQuote(parentArgType);
            if (parseQuoteResult) {
                value += parseQuoteResult;
                continue;
            }
            var parseUnquotedResult = this.tryParseUnquoted(nestingLevel, parentArgType);
            if (parseUnquotedResult) {
                value += parseUnquotedResult;
                continue;
            }
            var parseLeftAngleResult = this.tryParseLeftAngleBracket();
            if (parseLeftAngleResult) {
                value += parseLeftAngleResult;
                continue;
            }
            break;
        }
        var location = createLocation(start, this.clonePosition());
        return {
            val: { type: TYPE.literal, value: value, location: location },
            err: null,
        };
    };
    Parser.prototype.tryParseLeftAngleBracket = function () {
        if (!this.isEOF() &&
            this.char() === 60 /* `<` */ &&
            (this.ignoreTag ||
                // If at the opening tag or closing tag position, bail.
                !_isAlphaOrSlash(this.peek() || 0))) {
            this.bump(); // `<`
            return '<';
        }
        return null;
    };
    /**
     * Starting with ICU 4.8, an ASCII apostrophe only starts quoted text if it immediately precedes
     * a character that requires quoting (that is, "only where needed"), and works the same in
     * nested messages as on the top level of the pattern. The new behavior is otherwise compatible.
     */
    Parser.prototype.tryParseQuote = function (parentArgType) {
        if (this.isEOF() || this.char() !== 39 /* `'` */) {
            return null;
        }
        // Parse escaped char following the apostrophe, or early return if there is no escaped char.
        // Check if is valid escaped character
        switch (this.peek()) {
            case 39 /* `'` */:
                // double quote, should return as a single quote.
                this.bump();
                this.bump();
                return "'";
            // '{', '<', '>', '}'
            case 123:
            case 60:
            case 62:
            case 125:
                break;
            case 35: // '#'
                if (parentArgType === 'plural' || parentArgType === 'selectordinal') {
                    break;
                }
                return null;
            default:
                return null;
        }
        this.bump(); // apostrophe
        var codePoints = [this.char()]; // escaped char
        this.bump();
        // read chars until the optional closing apostrophe is found
        while (!this.isEOF()) {
            var ch = this.char();
            if (ch === 39 /* `'` */) {
                if (this.peek() === 39 /* `'` */) {
                    codePoints.push(39);
                    // Bump one more time because we need to skip 2 characters.
                    this.bump();
                }
                else {
                    // Optional closing apostrophe.
                    this.bump();
                    break;
                }
            }
            else {
                codePoints.push(ch);
            }
            this.bump();
        }
        return fromCodePoint.apply(void 0, codePoints);
    };
    Parser.prototype.tryParseUnquoted = function (nestingLevel, parentArgType) {
        if (this.isEOF()) {
            return null;
        }
        var ch = this.char();
        if (ch === 60 /* `<` */ ||
            ch === 123 /* `{` */ ||
            (ch === 35 /* `#` */ &&
                (parentArgType === 'plural' || parentArgType === 'selectordinal')) ||
            (ch === 125 /* `}` */ && nestingLevel > 0)) {
            return null;
        }
        else {
            this.bump();
            return fromCodePoint(ch);
        }
    };
    Parser.prototype.parseArgument = function (nestingLevel, expectingCloseTag) {
        var openingBracePosition = this.clonePosition();
        this.bump(); // `{`
        this.bumpSpace();
        if (this.isEOF()) {
            return this.error(ErrorKind.EXPECT_ARGUMENT_CLOSING_BRACE, createLocation(openingBracePosition, this.clonePosition()));
        }
        if (this.char() === 125 /* `}` */) {
            this.bump();
            return this.error(ErrorKind.EMPTY_ARGUMENT, createLocation(openingBracePosition, this.clonePosition()));
        }
        // argument name
        var value = this.parseIdentifierIfPossible().value;
        if (!value) {
            return this.error(ErrorKind.MALFORMED_ARGUMENT, createLocation(openingBracePosition, this.clonePosition()));
        }
        this.bumpSpace();
        if (this.isEOF()) {
            return this.error(ErrorKind.EXPECT_ARGUMENT_CLOSING_BRACE, createLocation(openingBracePosition, this.clonePosition()));
        }
        switch (this.char()) {
            // Simple argument: `{name}`
            case 125 /* `}` */: {
                this.bump(); // `}`
                return {
                    val: {
                        type: TYPE.argument,
                        // value does not include the opening and closing braces.
                        value: value,
                        location: createLocation(openingBracePosition, this.clonePosition()),
                    },
                    err: null,
                };
            }
            // Argument with options: `{name, format, ...}`
            case 44 /* `,` */: {
                this.bump(); // `,`
                this.bumpSpace();
                if (this.isEOF()) {
                    return this.error(ErrorKind.EXPECT_ARGUMENT_CLOSING_BRACE, createLocation(openingBracePosition, this.clonePosition()));
                }
                return this.parseArgumentOptions(nestingLevel, expectingCloseTag, value, openingBracePosition);
            }
            default:
                return this.error(ErrorKind.MALFORMED_ARGUMENT, createLocation(openingBracePosition, this.clonePosition()));
        }
    };
    /**
     * Advance the parser until the end of the identifier, if it is currently on
     * an identifier character. Return an empty string otherwise.
     */
    Parser.prototype.parseIdentifierIfPossible = function () {
        var startingPosition = this.clonePosition();
        var startOffset = this.offset();
        var value = matchIdentifierAtIndex(this.message, startOffset);
        var endOffset = startOffset + value.length;
        this.bumpTo(endOffset);
        var endPosition = this.clonePosition();
        var location = createLocation(startingPosition, endPosition);
        return { value: value, location: location };
    };
    Parser.prototype.parseArgumentOptions = function (nestingLevel, expectingCloseTag, value, openingBracePosition) {
        var _a;
        // Parse this range:
        // {name, type, style}
        //        ^---^
        var typeStartPosition = this.clonePosition();
        var argType = this.parseIdentifierIfPossible().value;
        var typeEndPosition = this.clonePosition();
        switch (argType) {
            case '':
                // Expecting a style string number, date, time, plural, selectordinal, or select.
                return this.error(ErrorKind.EXPECT_ARGUMENT_TYPE, createLocation(typeStartPosition, typeEndPosition));
            case 'number':
            case 'date':
            case 'time': {
                // Parse this range:
                // {name, number, style}
                //              ^-------^
                this.bumpSpace();
                var styleAndLocation = null;
                if (this.bumpIf(',')) {
                    this.bumpSpace();
                    var styleStartPosition = this.clonePosition();
                    var result = this.parseSimpleArgStyleIfPossible();
                    if (result.err) {
                        return result;
                    }
                    var style = trimEnd(result.val);
                    if (style.length === 0) {
                        return this.error(ErrorKind.EXPECT_ARGUMENT_STYLE, createLocation(this.clonePosition(), this.clonePosition()));
                    }
                    var styleLocation = createLocation(styleStartPosition, this.clonePosition());
                    styleAndLocation = { style: style, styleLocation: styleLocation };
                }
                var argCloseResult = this.tryParseArgumentClose(openingBracePosition);
                if (argCloseResult.err) {
                    return argCloseResult;
                }
                var location_1 = createLocation(openingBracePosition, this.clonePosition());
                // Extract style or skeleton
                if (styleAndLocation && startsWith(styleAndLocation === null || styleAndLocation === void 0 ? void 0 : styleAndLocation.style, '::', 0)) {
                    // Skeleton starts with `::`.
                    var skeleton = trimStart(styleAndLocation.style.slice(2));
                    if (argType === 'number') {
                        var result = this.parseNumberSkeletonFromString(skeleton, styleAndLocation.styleLocation);
                        if (result.err) {
                            return result;
                        }
                        return {
                            val: { type: TYPE.number, value: value, location: location_1, style: result.val },
                            err: null,
                        };
                    }
                    else {
                        if (skeleton.length === 0) {
                            return this.error(ErrorKind.EXPECT_DATE_TIME_SKELETON, location_1);
                        }
                        var dateTimePattern = skeleton;
                        // Get "best match" pattern only if locale is passed, if not, let it
                        // pass as-is where `parseDateTimeSkeleton()` will throw an error
                        // for unsupported patterns.
                        if (this.locale) {
                            dateTimePattern = getBestPattern(skeleton, this.locale);
                        }
                        var style = {
                            type: SKELETON_TYPE.dateTime,
                            pattern: dateTimePattern,
                            location: styleAndLocation.styleLocation,
                            parsedOptions: this.shouldParseSkeletons
                                ? parseDateTimeSkeleton(dateTimePattern)
                                : {},
                        };
                        var type = argType === 'date' ? TYPE.date : TYPE.time;
                        return {
                            val: { type: type, value: value, location: location_1, style: style },
                            err: null,
                        };
                    }
                }
                // Regular style or no style.
                return {
                    val: {
                        type: argType === 'number'
                            ? TYPE.number
                            : argType === 'date'
                                ? TYPE.date
                                : TYPE.time,
                        value: value,
                        location: location_1,
                        style: (_a = styleAndLocation === null || styleAndLocation === void 0 ? void 0 : styleAndLocation.style) !== null && _a !== void 0 ? _a : null,
                    },
                    err: null,
                };
            }
            case 'plural':
            case 'selectordinal':
            case 'select': {
                // Parse this range:
                // {name, plural, options}
                //              ^---------^
                var typeEndPosition_1 = this.clonePosition();
                this.bumpSpace();
                if (!this.bumpIf(',')) {
                    return this.error(ErrorKind.EXPECT_SELECT_ARGUMENT_OPTIONS, createLocation(typeEndPosition_1, __assign$1({}, typeEndPosition_1)));
                }
                this.bumpSpace();
                // Parse offset:
                // {name, plural, offset:1, options}
                //                ^-----^
                //
                // or the first option:
                //
                // {name, plural, one {...} other {...}}
                //                ^--^
                var identifierAndLocation = this.parseIdentifierIfPossible();
                var pluralOffset = 0;
                if (argType !== 'select' && identifierAndLocation.value === 'offset') {
                    if (!this.bumpIf(':')) {
                        return this.error(ErrorKind.EXPECT_PLURAL_ARGUMENT_OFFSET_VALUE, createLocation(this.clonePosition(), this.clonePosition()));
                    }
                    this.bumpSpace();
                    var result = this.tryParseDecimalInteger(ErrorKind.EXPECT_PLURAL_ARGUMENT_OFFSET_VALUE, ErrorKind.INVALID_PLURAL_ARGUMENT_OFFSET_VALUE);
                    if (result.err) {
                        return result;
                    }
                    // Parse another identifier for option parsing
                    this.bumpSpace();
                    identifierAndLocation = this.parseIdentifierIfPossible();
                    pluralOffset = result.val;
                }
                var optionsResult = this.tryParsePluralOrSelectOptions(nestingLevel, argType, expectingCloseTag, identifierAndLocation);
                if (optionsResult.err) {
                    return optionsResult;
                }
                var argCloseResult = this.tryParseArgumentClose(openingBracePosition);
                if (argCloseResult.err) {
                    return argCloseResult;
                }
                var location_2 = createLocation(openingBracePosition, this.clonePosition());
                if (argType === 'select') {
                    return {
                        val: {
                            type: TYPE.select,
                            value: value,
                            options: fromEntries(optionsResult.val),
                            location: location_2,
                        },
                        err: null,
                    };
                }
                else {
                    return {
                        val: {
                            type: TYPE.plural,
                            value: value,
                            options: fromEntries(optionsResult.val),
                            offset: pluralOffset,
                            pluralType: argType === 'plural' ? 'cardinal' : 'ordinal',
                            location: location_2,
                        },
                        err: null,
                    };
                }
            }
            default:
                return this.error(ErrorKind.INVALID_ARGUMENT_TYPE, createLocation(typeStartPosition, typeEndPosition));
        }
    };
    Parser.prototype.tryParseArgumentClose = function (openingBracePosition) {
        // Parse: {value, number, ::currency/GBP }
        //
        if (this.isEOF() || this.char() !== 125 /* `}` */) {
            return this.error(ErrorKind.EXPECT_ARGUMENT_CLOSING_BRACE, createLocation(openingBracePosition, this.clonePosition()));
        }
        this.bump(); // `}`
        return { val: true, err: null };
    };
    /**
     * See: https://github.com/unicode-org/icu/blob/af7ed1f6d2298013dc303628438ec4abe1f16479/icu4c/source/common/messagepattern.cpp#L659
     */
    Parser.prototype.parseSimpleArgStyleIfPossible = function () {
        var nestedBraces = 0;
        var startPosition = this.clonePosition();
        while (!this.isEOF()) {
            var ch = this.char();
            switch (ch) {
                case 39 /* `'` */: {
                    // Treat apostrophe as quoting but include it in the style part.
                    // Find the end of the quoted literal text.
                    this.bump();
                    var apostrophePosition = this.clonePosition();
                    if (!this.bumpUntil("'")) {
                        return this.error(ErrorKind.UNCLOSED_QUOTE_IN_ARGUMENT_STYLE, createLocation(apostrophePosition, this.clonePosition()));
                    }
                    this.bump();
                    break;
                }
                case 123 /* `{` */: {
                    nestedBraces += 1;
                    this.bump();
                    break;
                }
                case 125 /* `}` */: {
                    if (nestedBraces > 0) {
                        nestedBraces -= 1;
                    }
                    else {
                        return {
                            val: this.message.slice(startPosition.offset, this.offset()),
                            err: null,
                        };
                    }
                    break;
                }
                default:
                    this.bump();
                    break;
            }
        }
        return {
            val: this.message.slice(startPosition.offset, this.offset()),
            err: null,
        };
    };
    Parser.prototype.parseNumberSkeletonFromString = function (skeleton, location) {
        var tokens = [];
        try {
            tokens = parseNumberSkeletonFromString(skeleton);
        }
        catch (e) {
            return this.error(ErrorKind.INVALID_NUMBER_SKELETON, location);
        }
        return {
            val: {
                type: SKELETON_TYPE.number,
                tokens: tokens,
                location: location,
                parsedOptions: this.shouldParseSkeletons
                    ? parseNumberSkeleton(tokens)
                    : {},
            },
            err: null,
        };
    };
    /**
     * @param nesting_level The current nesting level of messages.
     *     This can be positive when parsing message fragment in select or plural argument options.
     * @param parent_arg_type The parent argument's type.
     * @param parsed_first_identifier If provided, this is the first identifier-like selector of
     *     the argument. It is a by-product of a previous parsing attempt.
     * @param expecting_close_tag If true, this message is directly or indirectly nested inside
     *     between a pair of opening and closing tags. The nested message will not parse beyond
     *     the closing tag boundary.
     */
    Parser.prototype.tryParsePluralOrSelectOptions = function (nestingLevel, parentArgType, expectCloseTag, parsedFirstIdentifier) {
        var _a;
        var hasOtherClause = false;
        var options = [];
        var parsedSelectors = new Set();
        var selector = parsedFirstIdentifier.value, selectorLocation = parsedFirstIdentifier.location;
        // Parse:
        // one {one apple}
        // ^--^
        while (true) {
            if (selector.length === 0) {
                var startPosition = this.clonePosition();
                if (parentArgType !== 'select' && this.bumpIf('=')) {
                    // Try parse `={number}` selector
                    var result = this.tryParseDecimalInteger(ErrorKind.EXPECT_PLURAL_ARGUMENT_SELECTOR, ErrorKind.INVALID_PLURAL_ARGUMENT_SELECTOR);
                    if (result.err) {
                        return result;
                    }
                    selectorLocation = createLocation(startPosition, this.clonePosition());
                    selector = this.message.slice(startPosition.offset, this.offset());
                }
                else {
                    break;
                }
            }
            // Duplicate selector clauses
            if (parsedSelectors.has(selector)) {
                return this.error(parentArgType === 'select'
                    ? ErrorKind.DUPLICATE_SELECT_ARGUMENT_SELECTOR
                    : ErrorKind.DUPLICATE_PLURAL_ARGUMENT_SELECTOR, selectorLocation);
            }
            if (selector === 'other') {
                hasOtherClause = true;
            }
            // Parse:
            // one {one apple}
            //     ^----------^
            this.bumpSpace();
            var openingBracePosition = this.clonePosition();
            if (!this.bumpIf('{')) {
                return this.error(parentArgType === 'select'
                    ? ErrorKind.EXPECT_SELECT_ARGUMENT_SELECTOR_FRAGMENT
                    : ErrorKind.EXPECT_PLURAL_ARGUMENT_SELECTOR_FRAGMENT, createLocation(this.clonePosition(), this.clonePosition()));
            }
            var fragmentResult = this.parseMessage(nestingLevel + 1, parentArgType, expectCloseTag);
            if (fragmentResult.err) {
                return fragmentResult;
            }
            var argCloseResult = this.tryParseArgumentClose(openingBracePosition);
            if (argCloseResult.err) {
                return argCloseResult;
            }
            options.push([
                selector,
                {
                    value: fragmentResult.val,
                    location: createLocation(openingBracePosition, this.clonePosition()),
                },
            ]);
            // Keep track of the existing selectors
            parsedSelectors.add(selector);
            // Prep next selector clause.
            this.bumpSpace();
            (_a = this.parseIdentifierIfPossible(), selector = _a.value, selectorLocation = _a.location);
        }
        if (options.length === 0) {
            return this.error(parentArgType === 'select'
                ? ErrorKind.EXPECT_SELECT_ARGUMENT_SELECTOR
                : ErrorKind.EXPECT_PLURAL_ARGUMENT_SELECTOR, createLocation(this.clonePosition(), this.clonePosition()));
        }
        if (this.requiresOtherClause && !hasOtherClause) {
            return this.error(ErrorKind.MISSING_OTHER_CLAUSE, createLocation(this.clonePosition(), this.clonePosition()));
        }
        return { val: options, err: null };
    };
    Parser.prototype.tryParseDecimalInteger = function (expectNumberError, invalidNumberError) {
        var sign = 1;
        var startingPosition = this.clonePosition();
        if (this.bumpIf('+')) ;
        else if (this.bumpIf('-')) {
            sign = -1;
        }
        var hasDigits = false;
        var decimal = 0;
        while (!this.isEOF()) {
            var ch = this.char();
            if (ch >= 48 /* `0` */ && ch <= 57 /* `9` */) {
                hasDigits = true;
                decimal = decimal * 10 + (ch - 48);
                this.bump();
            }
            else {
                break;
            }
        }
        var location = createLocation(startingPosition, this.clonePosition());
        if (!hasDigits) {
            return this.error(expectNumberError, location);
        }
        decimal *= sign;
        if (!isSafeInteger(decimal)) {
            return this.error(invalidNumberError, location);
        }
        return { val: decimal, err: null };
    };
    Parser.prototype.offset = function () {
        return this.position.offset;
    };
    Parser.prototype.isEOF = function () {
        return this.offset() === this.message.length;
    };
    Parser.prototype.clonePosition = function () {
        // This is much faster than `Object.assign` or spread.
        return {
            offset: this.position.offset,
            line: this.position.line,
            column: this.position.column,
        };
    };
    /**
     * Return the code point at the current position of the parser.
     * Throws if the index is out of bound.
     */
    Parser.prototype.char = function () {
        var offset = this.position.offset;
        if (offset >= this.message.length) {
            throw Error('out of bound');
        }
        var code = codePointAt(this.message, offset);
        if (code === undefined) {
            throw Error("Offset ".concat(offset, " is at invalid UTF-16 code unit boundary"));
        }
        return code;
    };
    Parser.prototype.error = function (kind, location) {
        return {
            val: null,
            err: {
                kind: kind,
                message: this.message,
                location: location,
            },
        };
    };
    /** Bump the parser to the next UTF-16 code unit. */
    Parser.prototype.bump = function () {
        if (this.isEOF()) {
            return;
        }
        var code = this.char();
        if (code === 10 /* '\n' */) {
            this.position.line += 1;
            this.position.column = 1;
            this.position.offset += 1;
        }
        else {
            this.position.column += 1;
            // 0 ~ 0x10000 -> unicode BMP, otherwise skip the surrogate pair.
            this.position.offset += code < 0x10000 ? 1 : 2;
        }
    };
    /**
     * If the substring starting at the current position of the parser has
     * the given prefix, then bump the parser to the character immediately
     * following the prefix and return true. Otherwise, don't bump the parser
     * and return false.
     */
    Parser.prototype.bumpIf = function (prefix) {
        if (startsWith(this.message, prefix, this.offset())) {
            for (var i = 0; i < prefix.length; i++) {
                this.bump();
            }
            return true;
        }
        return false;
    };
    /**
     * Bump the parser until the pattern character is found and return `true`.
     * Otherwise bump to the end of the file and return `false`.
     */
    Parser.prototype.bumpUntil = function (pattern) {
        var currentOffset = this.offset();
        var index = this.message.indexOf(pattern, currentOffset);
        if (index >= 0) {
            this.bumpTo(index);
            return true;
        }
        else {
            this.bumpTo(this.message.length);
            return false;
        }
    };
    /**
     * Bump the parser to the target offset.
     * If target offset is beyond the end of the input, bump the parser to the end of the input.
     */
    Parser.prototype.bumpTo = function (targetOffset) {
        if (this.offset() > targetOffset) {
            throw Error("targetOffset ".concat(targetOffset, " must be greater than or equal to the current offset ").concat(this.offset()));
        }
        targetOffset = Math.min(targetOffset, this.message.length);
        while (true) {
            var offset = this.offset();
            if (offset === targetOffset) {
                break;
            }
            if (offset > targetOffset) {
                throw Error("targetOffset ".concat(targetOffset, " is at invalid UTF-16 code unit boundary"));
            }
            this.bump();
            if (this.isEOF()) {
                break;
            }
        }
    };
    /** advance the parser through all whitespace to the next non-whitespace code unit. */
    Parser.prototype.bumpSpace = function () {
        while (!this.isEOF() && _isWhiteSpace(this.char())) {
            this.bump();
        }
    };
    /**
     * Peek at the *next* Unicode codepoint in the input without advancing the parser.
     * If the input has been exhausted, then this returns null.
     */
    Parser.prototype.peek = function () {
        if (this.isEOF()) {
            return null;
        }
        var code = this.char();
        var offset = this.offset();
        var nextCode = this.message.charCodeAt(offset + (code >= 0x10000 ? 2 : 1));
        return nextCode !== null && nextCode !== void 0 ? nextCode : null;
    };
    return Parser;
}());
/**
 * This check if codepoint is alphabet (lower & uppercase)
 * @param codepoint
 * @returns
 */
function _isAlpha(codepoint) {
    return ((codepoint >= 97 && codepoint <= 122) ||
        (codepoint >= 65 && codepoint <= 90));
}
function _isAlphaOrSlash(codepoint) {
    return _isAlpha(codepoint) || codepoint === 47; /* '/' */
}
/** See `parseTag` function docs. */
function _isPotentialElementNameChar(c) {
    return (c === 45 /* '-' */ ||
        c === 46 /* '.' */ ||
        (c >= 48 && c <= 57) /* 0..9 */ ||
        c === 95 /* '_' */ ||
        (c >= 97 && c <= 122) /** a..z */ ||
        (c >= 65 && c <= 90) /* A..Z */ ||
        c == 0xb7 ||
        (c >= 0xc0 && c <= 0xd6) ||
        (c >= 0xd8 && c <= 0xf6) ||
        (c >= 0xf8 && c <= 0x37d) ||
        (c >= 0x37f && c <= 0x1fff) ||
        (c >= 0x200c && c <= 0x200d) ||
        (c >= 0x203f && c <= 0x2040) ||
        (c >= 0x2070 && c <= 0x218f) ||
        (c >= 0x2c00 && c <= 0x2fef) ||
        (c >= 0x3001 && c <= 0xd7ff) ||
        (c >= 0xf900 && c <= 0xfdcf) ||
        (c >= 0xfdf0 && c <= 0xfffd) ||
        (c >= 0x10000 && c <= 0xeffff));
}
/**
 * Code point equivalent of regex `\p{White_Space}`.
 * From: https://www.unicode.org/Public/UCD/latest/ucd/PropList.txt
 */
function _isWhiteSpace(c) {
    return ((c >= 0x0009 && c <= 0x000d) ||
        c === 0x0020 ||
        c === 0x0085 ||
        (c >= 0x200e && c <= 0x200f) ||
        c === 0x2028 ||
        c === 0x2029);
}
/**
 * Code point equivalent of regex `\p{Pattern_Syntax}`.
 * See https://www.unicode.org/Public/UCD/latest/ucd/PropList.txt
 */
function _isPatternSyntax(c) {
    return ((c >= 0x0021 && c <= 0x0023) ||
        c === 0x0024 ||
        (c >= 0x0025 && c <= 0x0027) ||
        c === 0x0028 ||
        c === 0x0029 ||
        c === 0x002a ||
        c === 0x002b ||
        c === 0x002c ||
        c === 0x002d ||
        (c >= 0x002e && c <= 0x002f) ||
        (c >= 0x003a && c <= 0x003b) ||
        (c >= 0x003c && c <= 0x003e) ||
        (c >= 0x003f && c <= 0x0040) ||
        c === 0x005b ||
        c === 0x005c ||
        c === 0x005d ||
        c === 0x005e ||
        c === 0x0060 ||
        c === 0x007b ||
        c === 0x007c ||
        c === 0x007d ||
        c === 0x007e ||
        c === 0x00a1 ||
        (c >= 0x00a2 && c <= 0x00a5) ||
        c === 0x00a6 ||
        c === 0x00a7 ||
        c === 0x00a9 ||
        c === 0x00ab ||
        c === 0x00ac ||
        c === 0x00ae ||
        c === 0x00b0 ||
        c === 0x00b1 ||
        c === 0x00b6 ||
        c === 0x00bb ||
        c === 0x00bf ||
        c === 0x00d7 ||
        c === 0x00f7 ||
        (c >= 0x2010 && c <= 0x2015) ||
        (c >= 0x2016 && c <= 0x2017) ||
        c === 0x2018 ||
        c === 0x2019 ||
        c === 0x201a ||
        (c >= 0x201b && c <= 0x201c) ||
        c === 0x201d ||
        c === 0x201e ||
        c === 0x201f ||
        (c >= 0x2020 && c <= 0x2027) ||
        (c >= 0x2030 && c <= 0x2038) ||
        c === 0x2039 ||
        c === 0x203a ||
        (c >= 0x203b && c <= 0x203e) ||
        (c >= 0x2041 && c <= 0x2043) ||
        c === 0x2044 ||
        c === 0x2045 ||
        c === 0x2046 ||
        (c >= 0x2047 && c <= 0x2051) ||
        c === 0x2052 ||
        c === 0x2053 ||
        (c >= 0x2055 && c <= 0x205e) ||
        (c >= 0x2190 && c <= 0x2194) ||
        (c >= 0x2195 && c <= 0x2199) ||
        (c >= 0x219a && c <= 0x219b) ||
        (c >= 0x219c && c <= 0x219f) ||
        c === 0x21a0 ||
        (c >= 0x21a1 && c <= 0x21a2) ||
        c === 0x21a3 ||
        (c >= 0x21a4 && c <= 0x21a5) ||
        c === 0x21a6 ||
        (c >= 0x21a7 && c <= 0x21ad) ||
        c === 0x21ae ||
        (c >= 0x21af && c <= 0x21cd) ||
        (c >= 0x21ce && c <= 0x21cf) ||
        (c >= 0x21d0 && c <= 0x21d1) ||
        c === 0x21d2 ||
        c === 0x21d3 ||
        c === 0x21d4 ||
        (c >= 0x21d5 && c <= 0x21f3) ||
        (c >= 0x21f4 && c <= 0x22ff) ||
        (c >= 0x2300 && c <= 0x2307) ||
        c === 0x2308 ||
        c === 0x2309 ||
        c === 0x230a ||
        c === 0x230b ||
        (c >= 0x230c && c <= 0x231f) ||
        (c >= 0x2320 && c <= 0x2321) ||
        (c >= 0x2322 && c <= 0x2328) ||
        c === 0x2329 ||
        c === 0x232a ||
        (c >= 0x232b && c <= 0x237b) ||
        c === 0x237c ||
        (c >= 0x237d && c <= 0x239a) ||
        (c >= 0x239b && c <= 0x23b3) ||
        (c >= 0x23b4 && c <= 0x23db) ||
        (c >= 0x23dc && c <= 0x23e1) ||
        (c >= 0x23e2 && c <= 0x2426) ||
        (c >= 0x2427 && c <= 0x243f) ||
        (c >= 0x2440 && c <= 0x244a) ||
        (c >= 0x244b && c <= 0x245f) ||
        (c >= 0x2500 && c <= 0x25b6) ||
        c === 0x25b7 ||
        (c >= 0x25b8 && c <= 0x25c0) ||
        c === 0x25c1 ||
        (c >= 0x25c2 && c <= 0x25f7) ||
        (c >= 0x25f8 && c <= 0x25ff) ||
        (c >= 0x2600 && c <= 0x266e) ||
        c === 0x266f ||
        (c >= 0x2670 && c <= 0x2767) ||
        c === 0x2768 ||
        c === 0x2769 ||
        c === 0x276a ||
        c === 0x276b ||
        c === 0x276c ||
        c === 0x276d ||
        c === 0x276e ||
        c === 0x276f ||
        c === 0x2770 ||
        c === 0x2771 ||
        c === 0x2772 ||
        c === 0x2773 ||
        c === 0x2774 ||
        c === 0x2775 ||
        (c >= 0x2794 && c <= 0x27bf) ||
        (c >= 0x27c0 && c <= 0x27c4) ||
        c === 0x27c5 ||
        c === 0x27c6 ||
        (c >= 0x27c7 && c <= 0x27e5) ||
        c === 0x27e6 ||
        c === 0x27e7 ||
        c === 0x27e8 ||
        c === 0x27e9 ||
        c === 0x27ea ||
        c === 0x27eb ||
        c === 0x27ec ||
        c === 0x27ed ||
        c === 0x27ee ||
        c === 0x27ef ||
        (c >= 0x27f0 && c <= 0x27ff) ||
        (c >= 0x2800 && c <= 0x28ff) ||
        (c >= 0x2900 && c <= 0x2982) ||
        c === 0x2983 ||
        c === 0x2984 ||
        c === 0x2985 ||
        c === 0x2986 ||
        c === 0x2987 ||
        c === 0x2988 ||
        c === 0x2989 ||
        c === 0x298a ||
        c === 0x298b ||
        c === 0x298c ||
        c === 0x298d ||
        c === 0x298e ||
        c === 0x298f ||
        c === 0x2990 ||
        c === 0x2991 ||
        c === 0x2992 ||
        c === 0x2993 ||
        c === 0x2994 ||
        c === 0x2995 ||
        c === 0x2996 ||
        c === 0x2997 ||
        c === 0x2998 ||
        (c >= 0x2999 && c <= 0x29d7) ||
        c === 0x29d8 ||
        c === 0x29d9 ||
        c === 0x29da ||
        c === 0x29db ||
        (c >= 0x29dc && c <= 0x29fb) ||
        c === 0x29fc ||
        c === 0x29fd ||
        (c >= 0x29fe && c <= 0x2aff) ||
        (c >= 0x2b00 && c <= 0x2b2f) ||
        (c >= 0x2b30 && c <= 0x2b44) ||
        (c >= 0x2b45 && c <= 0x2b46) ||
        (c >= 0x2b47 && c <= 0x2b4c) ||
        (c >= 0x2b4d && c <= 0x2b73) ||
        (c >= 0x2b74 && c <= 0x2b75) ||
        (c >= 0x2b76 && c <= 0x2b95) ||
        c === 0x2b96 ||
        (c >= 0x2b97 && c <= 0x2bff) ||
        (c >= 0x2e00 && c <= 0x2e01) ||
        c === 0x2e02 ||
        c === 0x2e03 ||
        c === 0x2e04 ||
        c === 0x2e05 ||
        (c >= 0x2e06 && c <= 0x2e08) ||
        c === 0x2e09 ||
        c === 0x2e0a ||
        c === 0x2e0b ||
        c === 0x2e0c ||
        c === 0x2e0d ||
        (c >= 0x2e0e && c <= 0x2e16) ||
        c === 0x2e17 ||
        (c >= 0x2e18 && c <= 0x2e19) ||
        c === 0x2e1a ||
        c === 0x2e1b ||
        c === 0x2e1c ||
        c === 0x2e1d ||
        (c >= 0x2e1e && c <= 0x2e1f) ||
        c === 0x2e20 ||
        c === 0x2e21 ||
        c === 0x2e22 ||
        c === 0x2e23 ||
        c === 0x2e24 ||
        c === 0x2e25 ||
        c === 0x2e26 ||
        c === 0x2e27 ||
        c === 0x2e28 ||
        c === 0x2e29 ||
        (c >= 0x2e2a && c <= 0x2e2e) ||
        c === 0x2e2f ||
        (c >= 0x2e30 && c <= 0x2e39) ||
        (c >= 0x2e3a && c <= 0x2e3b) ||
        (c >= 0x2e3c && c <= 0x2e3f) ||
        c === 0x2e40 ||
        c === 0x2e41 ||
        c === 0x2e42 ||
        (c >= 0x2e43 && c <= 0x2e4f) ||
        (c >= 0x2e50 && c <= 0x2e51) ||
        c === 0x2e52 ||
        (c >= 0x2e53 && c <= 0x2e7f) ||
        (c >= 0x3001 && c <= 0x3003) ||
        c === 0x3008 ||
        c === 0x3009 ||
        c === 0x300a ||
        c === 0x300b ||
        c === 0x300c ||
        c === 0x300d ||
        c === 0x300e ||
        c === 0x300f ||
        c === 0x3010 ||
        c === 0x3011 ||
        (c >= 0x3012 && c <= 0x3013) ||
        c === 0x3014 ||
        c === 0x3015 ||
        c === 0x3016 ||
        c === 0x3017 ||
        c === 0x3018 ||
        c === 0x3019 ||
        c === 0x301a ||
        c === 0x301b ||
        c === 0x301c ||
        c === 0x301d ||
        (c >= 0x301e && c <= 0x301f) ||
        c === 0x3020 ||
        c === 0x3030 ||
        c === 0xfd3e ||
        c === 0xfd3f ||
        (c >= 0xfe45 && c <= 0xfe46));
}

function pruneLocation(els) {
    els.forEach(function (el) {
        delete el.location;
        if (isSelectElement(el) || isPluralElement(el)) {
            for (var k in el.options) {
                delete el.options[k].location;
                pruneLocation(el.options[k].value);
            }
        }
        else if (isNumberElement(el) && isNumberSkeleton(el.style)) {
            delete el.style.location;
        }
        else if ((isDateElement(el) || isTimeElement(el)) &&
            isDateTimeSkeleton(el.style)) {
            delete el.style.location;
        }
        else if (isTagElement(el)) {
            pruneLocation(el.children);
        }
    });
}
function parse(message, opts) {
    if (opts === void 0) { opts = {}; }
    opts = __assign$1({ shouldParseSkeletons: true, requiresOtherClause: true }, opts);
    var result = new Parser(message, opts).parse();
    if (result.err) {
        var error = SyntaxError(ErrorKind[result.err.kind]);
        // @ts-expect-error Assign to error object
        error.location = result.err.location;
        // @ts-expect-error Assign to error object
        error.originalMessage = result.err.message;
        throw error;
    }
    if (!(opts === null || opts === void 0 ? void 0 : opts.captureLocation)) {
        pruneLocation(result.val);
    }
    return result.val;
}

//
// Main
//
function memoize(fn, options) {
    var cache = options && options.cache ? options.cache : cacheDefault;
    var serializer = options && options.serializer ? options.serializer : serializerDefault;
    var strategy = options && options.strategy ? options.strategy : strategyDefault;
    return strategy(fn, {
        cache: cache,
        serializer: serializer,
    });
}
//
// Strategy
//
function isPrimitive(value) {
    return (value == null || typeof value === 'number' || typeof value === 'boolean'); // || typeof value === "string" 'unsafe' primitive for our needs
}
function monadic(fn, cache, serializer, arg) {
    var cacheKey = isPrimitive(arg) ? arg : serializer(arg);
    var computedValue = cache.get(cacheKey);
    if (typeof computedValue === 'undefined') {
        computedValue = fn.call(this, arg);
        cache.set(cacheKey, computedValue);
    }
    return computedValue;
}
function variadic(fn, cache, serializer) {
    var args = Array.prototype.slice.call(arguments, 3);
    var cacheKey = serializer(args);
    var computedValue = cache.get(cacheKey);
    if (typeof computedValue === 'undefined') {
        computedValue = fn.apply(this, args);
        cache.set(cacheKey, computedValue);
    }
    return computedValue;
}
function assemble(fn, context, strategy, cache, serialize) {
    return strategy.bind(context, fn, cache, serialize);
}
function strategyDefault(fn, options) {
    var strategy = fn.length === 1 ? monadic : variadic;
    return assemble(fn, this, strategy, options.cache.create(), options.serializer);
}
function strategyVariadic(fn, options) {
    return assemble(fn, this, variadic, options.cache.create(), options.serializer);
}
function strategyMonadic(fn, options) {
    return assemble(fn, this, monadic, options.cache.create(), options.serializer);
}
//
// Serializer
//
var serializerDefault = function () {
    return JSON.stringify(arguments);
};
//
// Cache
//
function ObjectWithoutPrototypeCache() {
    this.cache = Object.create(null);
}
ObjectWithoutPrototypeCache.prototype.get = function (key) {
    return this.cache[key];
};
ObjectWithoutPrototypeCache.prototype.set = function (key, value) {
    this.cache[key] = value;
};
var cacheDefault = {
    create: function create() {
        // @ts-ignore
        return new ObjectWithoutPrototypeCache();
    },
};
var strategies = {
    variadic: strategyVariadic,
    monadic: strategyMonadic,
};

var ErrorCode;
(function (ErrorCode) {
    // When we have a placeholder but no value to format
    ErrorCode["MISSING_VALUE"] = "MISSING_VALUE";
    // When value supplied is invalid
    ErrorCode["INVALID_VALUE"] = "INVALID_VALUE";
    // When we need specific Intl API but it's not available
    ErrorCode["MISSING_INTL_API"] = "MISSING_INTL_API";
})(ErrorCode || (ErrorCode = {}));
var FormatError = /** @class */ (function (_super) {
    __extends(FormatError, _super);
    function FormatError(msg, code, originalMessage) {
        var _this = _super.call(this, msg) || this;
        _this.code = code;
        _this.originalMessage = originalMessage;
        return _this;
    }
    FormatError.prototype.toString = function () {
        return "[formatjs Error: ".concat(this.code, "] ").concat(this.message);
    };
    return FormatError;
}(Error));
var InvalidValueError = /** @class */ (function (_super) {
    __extends(InvalidValueError, _super);
    function InvalidValueError(variableId, value, options, originalMessage) {
        return _super.call(this, "Invalid values for \"".concat(variableId, "\": \"").concat(value, "\". Options are \"").concat(Object.keys(options).join('", "'), "\""), ErrorCode.INVALID_VALUE, originalMessage) || this;
    }
    return InvalidValueError;
}(FormatError));
var InvalidValueTypeError = /** @class */ (function (_super) {
    __extends(InvalidValueTypeError, _super);
    function InvalidValueTypeError(value, type, originalMessage) {
        return _super.call(this, "Value for \"".concat(value, "\" must be of type ").concat(type), ErrorCode.INVALID_VALUE, originalMessage) || this;
    }
    return InvalidValueTypeError;
}(FormatError));
var MissingValueError = /** @class */ (function (_super) {
    __extends(MissingValueError, _super);
    function MissingValueError(variableId, originalMessage) {
        return _super.call(this, "The intl string context variable \"".concat(variableId, "\" was not provided to the string \"").concat(originalMessage, "\""), ErrorCode.MISSING_VALUE, originalMessage) || this;
    }
    return MissingValueError;
}(FormatError));

var PART_TYPE;
(function (PART_TYPE) {
    PART_TYPE[PART_TYPE["literal"] = 0] = "literal";
    PART_TYPE[PART_TYPE["object"] = 1] = "object";
})(PART_TYPE || (PART_TYPE = {}));
function mergeLiteral(parts) {
    if (parts.length < 2) {
        return parts;
    }
    return parts.reduce(function (all, part) {
        var lastPart = all[all.length - 1];
        if (!lastPart ||
            lastPart.type !== PART_TYPE.literal ||
            part.type !== PART_TYPE.literal) {
            all.push(part);
        }
        else {
            lastPart.value += part.value;
        }
        return all;
    }, []);
}
function isFormatXMLElementFn(el) {
    return typeof el === 'function';
}
// TODO(skeleton): add skeleton support
function formatToParts(els, locales, formatters, formats, values, currentPluralValue, 
// For debugging
originalMessage) {
    // Hot path for straight simple msg translations
    if (els.length === 1 && isLiteralElement(els[0])) {
        return [
            {
                type: PART_TYPE.literal,
                value: els[0].value,
            },
        ];
    }
    var result = [];
    for (var _i = 0, els_1 = els; _i < els_1.length; _i++) {
        var el = els_1[_i];
        // Exit early for string parts.
        if (isLiteralElement(el)) {
            result.push({
                type: PART_TYPE.literal,
                value: el.value,
            });
            continue;
        }
        // TODO: should this part be literal type?
        // Replace `#` in plural rules with the actual numeric value.
        if (isPoundElement(el)) {
            if (typeof currentPluralValue === 'number') {
                result.push({
                    type: PART_TYPE.literal,
                    value: formatters.getNumberFormat(locales).format(currentPluralValue),
                });
            }
            continue;
        }
        var varName = el.value;
        // Enforce that all required values are provided by the caller.
        if (!(values && varName in values)) {
            throw new MissingValueError(varName, originalMessage);
        }
        var value = values[varName];
        if (isArgumentElement(el)) {
            if (!value || typeof value === 'string' || typeof value === 'number') {
                value =
                    typeof value === 'string' || typeof value === 'number'
                        ? String(value)
                        : '';
            }
            result.push({
                type: typeof value === 'string' ? PART_TYPE.literal : PART_TYPE.object,
                value: value,
            });
            continue;
        }
        // Recursively format plural and select parts' option — which can be a
        // nested pattern structure. The choosing of the option to use is
        // abstracted-by and delegated-to the part helper object.
        if (isDateElement(el)) {
            var style = typeof el.style === 'string'
                ? formats.date[el.style]
                : isDateTimeSkeleton(el.style)
                    ? el.style.parsedOptions
                    : undefined;
            result.push({
                type: PART_TYPE.literal,
                value: formatters
                    .getDateTimeFormat(locales, style)
                    .format(value),
            });
            continue;
        }
        if (isTimeElement(el)) {
            var style = typeof el.style === 'string'
                ? formats.time[el.style]
                : isDateTimeSkeleton(el.style)
                    ? el.style.parsedOptions
                    : formats.time.medium;
            result.push({
                type: PART_TYPE.literal,
                value: formatters
                    .getDateTimeFormat(locales, style)
                    .format(value),
            });
            continue;
        }
        if (isNumberElement(el)) {
            var style = typeof el.style === 'string'
                ? formats.number[el.style]
                : isNumberSkeleton(el.style)
                    ? el.style.parsedOptions
                    : undefined;
            if (style && style.scale) {
                value =
                    value *
                        (style.scale || 1);
            }
            result.push({
                type: PART_TYPE.literal,
                value: formatters
                    .getNumberFormat(locales, style)
                    .format(value),
            });
            continue;
        }
        if (isTagElement(el)) {
            var children = el.children, value_1 = el.value;
            var formatFn = values[value_1];
            if (!isFormatXMLElementFn(formatFn)) {
                throw new InvalidValueTypeError(value_1, 'function', originalMessage);
            }
            var parts = formatToParts(children, locales, formatters, formats, values, currentPluralValue);
            var chunks = formatFn(parts.map(function (p) { return p.value; }));
            if (!Array.isArray(chunks)) {
                chunks = [chunks];
            }
            result.push.apply(result, chunks.map(function (c) {
                return {
                    type: typeof c === 'string' ? PART_TYPE.literal : PART_TYPE.object,
                    value: c,
                };
            }));
        }
        if (isSelectElement(el)) {
            var opt = el.options[value] || el.options.other;
            if (!opt) {
                throw new InvalidValueError(el.value, value, Object.keys(el.options), originalMessage);
            }
            result.push.apply(result, formatToParts(opt.value, locales, formatters, formats, values));
            continue;
        }
        if (isPluralElement(el)) {
            var opt = el.options["=".concat(value)];
            if (!opt) {
                if (!Intl.PluralRules) {
                    throw new FormatError("Intl.PluralRules is not available in this environment.\nTry polyfilling it using \"@formatjs/intl-pluralrules\"\n", ErrorCode.MISSING_INTL_API, originalMessage);
                }
                var rule = formatters
                    .getPluralRules(locales, { type: el.pluralType })
                    .select(value - (el.offset || 0));
                opt = el.options[rule] || el.options.other;
            }
            if (!opt) {
                throw new InvalidValueError(el.value, value, Object.keys(el.options), originalMessage);
            }
            result.push.apply(result, formatToParts(opt.value, locales, formatters, formats, values, value - (el.offset || 0)));
            continue;
        }
    }
    return mergeLiteral(result);
}

/*
Copyright (c) 2014, Yahoo! Inc. All rights reserved.
Copyrights licensed under the New BSD License.
See the accompanying LICENSE file for terms.
*/
// -- MessageFormat --------------------------------------------------------
function mergeConfig(c1, c2) {
    if (!c2) {
        return c1;
    }
    return __assign$1(__assign$1(__assign$1({}, (c1 || {})), (c2 || {})), Object.keys(c1).reduce(function (all, k) {
        all[k] = __assign$1(__assign$1({}, c1[k]), (c2[k] || {}));
        return all;
    }, {}));
}
function mergeConfigs(defaultConfig, configs) {
    if (!configs) {
        return defaultConfig;
    }
    return Object.keys(defaultConfig).reduce(function (all, k) {
        all[k] = mergeConfig(defaultConfig[k], configs[k]);
        return all;
    }, __assign$1({}, defaultConfig));
}
function createFastMemoizeCache$1(store) {
    return {
        create: function () {
            return {
                get: function (key) {
                    return store[key];
                },
                set: function (key, value) {
                    store[key] = value;
                },
            };
        },
    };
}
function createDefaultFormatters(cache) {
    if (cache === void 0) { cache = {
        number: {},
        dateTime: {},
        pluralRules: {},
    }; }
    return {
        getNumberFormat: memoize(function () {
            var _a;
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            return new ((_a = Intl.NumberFormat).bind.apply(_a, __spreadArray([void 0], args, false)))();
        }, {
            cache: createFastMemoizeCache$1(cache.number),
            strategy: strategies.variadic,
        }),
        getDateTimeFormat: memoize(function () {
            var _a;
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            return new ((_a = Intl.DateTimeFormat).bind.apply(_a, __spreadArray([void 0], args, false)))();
        }, {
            cache: createFastMemoizeCache$1(cache.dateTime),
            strategy: strategies.variadic,
        }),
        getPluralRules: memoize(function () {
            var _a;
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            return new ((_a = Intl.PluralRules).bind.apply(_a, __spreadArray([void 0], args, false)))();
        }, {
            cache: createFastMemoizeCache$1(cache.pluralRules),
            strategy: strategies.variadic,
        }),
    };
}
var IntlMessageFormat = /** @class */ (function () {
    function IntlMessageFormat(message, locales, overrideFormats, opts) {
        var _this = this;
        if (locales === void 0) { locales = IntlMessageFormat.defaultLocale; }
        this.formatterCache = {
            number: {},
            dateTime: {},
            pluralRules: {},
        };
        this.format = function (values) {
            var parts = _this.formatToParts(values);
            // Hot path for straight simple msg translations
            if (parts.length === 1) {
                return parts[0].value;
            }
            var result = parts.reduce(function (all, part) {
                if (!all.length ||
                    part.type !== PART_TYPE.literal ||
                    typeof all[all.length - 1] !== 'string') {
                    all.push(part.value);
                }
                else {
                    all[all.length - 1] += part.value;
                }
                return all;
            }, []);
            if (result.length <= 1) {
                return result[0] || '';
            }
            return result;
        };
        this.formatToParts = function (values) {
            return formatToParts(_this.ast, _this.locales, _this.formatters, _this.formats, values, undefined, _this.message);
        };
        this.resolvedOptions = function () {
            var _a;
            return ({
                locale: ((_a = _this.resolvedLocale) === null || _a === void 0 ? void 0 : _a.toString()) ||
                    Intl.NumberFormat.supportedLocalesOf(_this.locales)[0],
            });
        };
        this.getAst = function () { return _this.ast; };
        // Defined first because it's used to build the format pattern.
        this.locales = locales;
        this.resolvedLocale = IntlMessageFormat.resolveLocale(locales);
        if (typeof message === 'string') {
            this.message = message;
            if (!IntlMessageFormat.__parse) {
                throw new TypeError('IntlMessageFormat.__parse must be set to process `message` of type `string`');
            }
            var _a = opts || {}; _a.formatters; var parseOpts = __rest(_a, ["formatters"]);
            // Parse string messages into an AST.
            this.ast = IntlMessageFormat.__parse(message, __assign$1(__assign$1({}, parseOpts), { locale: this.resolvedLocale }));
        }
        else {
            this.ast = message;
        }
        if (!Array.isArray(this.ast)) {
            throw new TypeError('A message must be provided as a String or AST.');
        }
        // Creates a new object with the specified `formats` merged with the default
        // formats.
        this.formats = mergeConfigs(IntlMessageFormat.formats, overrideFormats);
        this.formatters =
            (opts && opts.formatters) || createDefaultFormatters(this.formatterCache);
    }
    Object.defineProperty(IntlMessageFormat, "defaultLocale", {
        get: function () {
            if (!IntlMessageFormat.memoizedDefaultLocale) {
                IntlMessageFormat.memoizedDefaultLocale =
                    new Intl.NumberFormat().resolvedOptions().locale;
            }
            return IntlMessageFormat.memoizedDefaultLocale;
        },
        enumerable: false,
        configurable: true
    });
    IntlMessageFormat.memoizedDefaultLocale = null;
    IntlMessageFormat.resolveLocale = function (locales) {
        if (typeof Intl.Locale === 'undefined') {
            return;
        }
        var supportedLocales = Intl.NumberFormat.supportedLocalesOf(locales);
        if (supportedLocales.length > 0) {
            return new Intl.Locale(supportedLocales[0]);
        }
        return new Intl.Locale(typeof locales === 'string' ? locales : locales[0]);
    };
    IntlMessageFormat.__parse = parse;
    // Default format options used as the prototype of the `formats` provided to the
    // constructor. These are used when constructing the internal Intl.NumberFormat
    // and Intl.DateTimeFormat instances.
    IntlMessageFormat.formats = {
        number: {
            integer: {
                maximumFractionDigits: 0,
            },
            currency: {
                style: 'currency',
            },
            percent: {
                style: 'percent',
            },
        },
        date: {
            short: {
                month: 'numeric',
                day: 'numeric',
                year: '2-digit',
            },
            medium: {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
            },
            long: {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
            },
            full: {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                year: 'numeric',
            },
        },
        time: {
            short: {
                hour: 'numeric',
                minute: 'numeric',
            },
            medium: {
                hour: 'numeric',
                minute: 'numeric',
                second: 'numeric',
            },
            long: {
                hour: 'numeric',
                minute: 'numeric',
                second: 'numeric',
                timeZoneName: 'short',
            },
            full: {
                hour: 'numeric',
                minute: 'numeric',
                second: 'numeric',
                timeZoneName: 'short',
            },
        },
    };
    return IntlMessageFormat;
}());

var IntlErrorCode;
(function (IntlErrorCode) {
    IntlErrorCode["FORMAT_ERROR"] = "FORMAT_ERROR";
    IntlErrorCode["UNSUPPORTED_FORMATTER"] = "UNSUPPORTED_FORMATTER";
    IntlErrorCode["INVALID_CONFIG"] = "INVALID_CONFIG";
    IntlErrorCode["MISSING_DATA"] = "MISSING_DATA";
    IntlErrorCode["MISSING_TRANSLATION"] = "MISSING_TRANSLATION";
})(IntlErrorCode || (IntlErrorCode = {}));
var IntlError = /** @class */ (function (_super) {
    __extends(IntlError, _super);
    function IntlError(code, message, exception) {
        var _this = this;
        var err = exception
            ? exception instanceof Error
                ? exception
                : new Error(String(exception))
            : undefined;
        _this = _super.call(this, "[@formatjs/intl Error ".concat(code, "] ").concat(message, "\n").concat(err ? "\n".concat(err.message, "\n").concat(err.stack) : '')) || this;
        _this.code = code;
        // @ts-ignore just so we don't need to declare dep on @types/node
        if (typeof Error.captureStackTrace === 'function') {
            // @ts-ignore just so we don't need to declare dep on @types/node
            Error.captureStackTrace(_this, IntlError);
        }
        return _this;
    }
    return IntlError;
}(Error));
var UnsupportedFormatterError = /** @class */ (function (_super) {
    __extends(UnsupportedFormatterError, _super);
    function UnsupportedFormatterError(message, exception) {
        return _super.call(this, IntlErrorCode.UNSUPPORTED_FORMATTER, message, exception) || this;
    }
    return UnsupportedFormatterError;
}(IntlError));
var InvalidConfigError = /** @class */ (function (_super) {
    __extends(InvalidConfigError, _super);
    function InvalidConfigError(message, exception) {
        return _super.call(this, IntlErrorCode.INVALID_CONFIG, message, exception) || this;
    }
    return InvalidConfigError;
}(IntlError));
var MissingDataError = /** @class */ (function (_super) {
    __extends(MissingDataError, _super);
    function MissingDataError(message, exception) {
        return _super.call(this, IntlErrorCode.MISSING_DATA, message, exception) || this;
    }
    return MissingDataError;
}(IntlError));
var IntlFormatError = /** @class */ (function (_super) {
    __extends(IntlFormatError, _super);
    function IntlFormatError(message, locale, exception) {
        var _this = _super.call(this, IntlErrorCode.FORMAT_ERROR, "".concat(message, "\nLocale: ").concat(locale, "\n"), exception) || this;
        _this.locale = locale;
        return _this;
    }
    return IntlFormatError;
}(IntlError));
var MessageFormatError = /** @class */ (function (_super) {
    __extends(MessageFormatError, _super);
    function MessageFormatError(message, locale, descriptor, exception) {
        var _this = _super.call(this, "".concat(message, "\nMessageID: ").concat(descriptor === null || descriptor === void 0 ? void 0 : descriptor.id, "\nDefault Message: ").concat(descriptor === null || descriptor === void 0 ? void 0 : descriptor.defaultMessage, "\nDescription: ").concat(descriptor === null || descriptor === void 0 ? void 0 : descriptor.description, "\n"), locale, exception) || this;
        _this.descriptor = descriptor;
        _this.locale = locale;
        return _this;
    }
    return MessageFormatError;
}(IntlFormatError));
var MissingTranslationError = /** @class */ (function (_super) {
    __extends(MissingTranslationError, _super);
    function MissingTranslationError(descriptor, locale) {
        var _this = _super.call(this, IntlErrorCode.MISSING_TRANSLATION, "Missing message: \"".concat(descriptor.id, "\" for locale \"").concat(locale, "\", using ").concat(descriptor.defaultMessage
            ? "default message (".concat(typeof descriptor.defaultMessage === 'string'
                ? descriptor.defaultMessage
                : descriptor.defaultMessage
                    .map(function (e) { var _a; return (_a = e.value) !== null && _a !== void 0 ? _a : JSON.stringify(e); })
                    .join(), ")")
            : 'id', " as fallback.")) || this;
        _this.descriptor = descriptor;
        return _this;
    }
    return MissingTranslationError;
}(IntlError));

function filterProps(props, allowlist, defaults) {
    if (defaults === void 0) { defaults = {}; }
    return allowlist.reduce(function (filtered, name) {
        if (name in props) {
            filtered[name] = props[name];
        }
        else if (name in defaults) {
            filtered[name] = defaults[name];
        }
        return filtered;
    }, {});
}
var defaultErrorHandler = function (error) {
    // @ts-ignore just so we don't need to declare dep on @types/node
    if (process.env.NODE_ENV !== 'production') {
        console.error(error);
    }
};
var defaultWarnHandler = function (warning) {
    // @ts-ignore just so we don't need to declare dep on @types/node
    if (process.env.NODE_ENV !== 'production') {
        console.warn(warning);
    }
};
var DEFAULT_INTL_CONFIG$1 = {
    formats: {},
    messages: {},
    timeZone: undefined,
    defaultLocale: 'en',
    defaultFormats: {},
    fallbackOnEmptyString: true,
    onError: defaultErrorHandler,
    onWarn: defaultWarnHandler,
};
function createIntlCache() {
    return {
        dateTime: {},
        number: {},
        message: {},
        relativeTime: {},
        pluralRules: {},
        list: {},
        displayNames: {},
    };
}
function createFastMemoizeCache(store) {
    return {
        create: function () {
            return {
                get: function (key) {
                    return store[key];
                },
                set: function (key, value) {
                    store[key] = value;
                },
            };
        },
    };
}
/**
 * Create intl formatters and populate cache
 * @param cache explicit cache to prevent leaking memory
 */
function createFormatters(cache) {
    if (cache === void 0) { cache = createIntlCache(); }
    var RelativeTimeFormat = Intl.RelativeTimeFormat;
    var ListFormat = Intl.ListFormat;
    var DisplayNames = Intl.DisplayNames;
    var getDateTimeFormat = memoize(function () {
        var _a;
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        return new ((_a = Intl.DateTimeFormat).bind.apply(_a, __spreadArray([void 0], args, false)))();
    }, {
        cache: createFastMemoizeCache(cache.dateTime),
        strategy: strategies.variadic,
    });
    var getNumberFormat = memoize(function () {
        var _a;
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        return new ((_a = Intl.NumberFormat).bind.apply(_a, __spreadArray([void 0], args, false)))();
    }, {
        cache: createFastMemoizeCache(cache.number),
        strategy: strategies.variadic,
    });
    var getPluralRules = memoize(function () {
        var _a;
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        return new ((_a = Intl.PluralRules).bind.apply(_a, __spreadArray([void 0], args, false)))();
    }, {
        cache: createFastMemoizeCache(cache.pluralRules),
        strategy: strategies.variadic,
    });
    return {
        getDateTimeFormat: getDateTimeFormat,
        getNumberFormat: getNumberFormat,
        getMessageFormat: memoize(function (message, locales, overrideFormats, opts) {
            return new IntlMessageFormat(message, locales, overrideFormats, __assign$1({ formatters: {
                    getNumberFormat: getNumberFormat,
                    getDateTimeFormat: getDateTimeFormat,
                    getPluralRules: getPluralRules,
                } }, (opts || {})));
        }, {
            cache: createFastMemoizeCache(cache.message),
            strategy: strategies.variadic,
        }),
        getRelativeTimeFormat: memoize(function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            return new (RelativeTimeFormat.bind.apply(RelativeTimeFormat, __spreadArray([void 0], args, false)))();
        }, {
            cache: createFastMemoizeCache(cache.relativeTime),
            strategy: strategies.variadic,
        }),
        getPluralRules: getPluralRules,
        getListFormat: memoize(function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            return new (ListFormat.bind.apply(ListFormat, __spreadArray([void 0], args, false)))();
        }, {
            cache: createFastMemoizeCache(cache.list),
            strategy: strategies.variadic,
        }),
        getDisplayNames: memoize(function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            return new (DisplayNames.bind.apply(DisplayNames, __spreadArray([void 0], args, false)))();
        }, {
            cache: createFastMemoizeCache(cache.displayNames),
            strategy: strategies.variadic,
        }),
    };
}
function getNamedFormat(formats, type, name, onError) {
    var formatType = formats && formats[type];
    var format;
    if (formatType) {
        format = formatType[name];
    }
    if (format) {
        return format;
    }
    onError(new UnsupportedFormatterError("No ".concat(type, " format named: ").concat(name)));
}

function setTimeZoneInOptions(opts, timeZone) {
    return Object.keys(opts).reduce(function (all, k) {
        all[k] = __assign$1({ timeZone: timeZone }, opts[k]);
        return all;
    }, {});
}
function deepMergeOptions(opts1, opts2) {
    var keys = Object.keys(__assign$1(__assign$1({}, opts1), opts2));
    return keys.reduce(function (all, k) {
        all[k] = __assign$1(__assign$1({}, (opts1[k] || {})), (opts2[k] || {}));
        return all;
    }, {});
}
function deepMergeFormatsAndSetTimeZone(f1, timeZone) {
    if (!timeZone) {
        return f1;
    }
    var mfFormats = IntlMessageFormat.formats;
    return __assign$1(__assign$1(__assign$1({}, mfFormats), f1), { date: deepMergeOptions(setTimeZoneInOptions(mfFormats.date, timeZone), setTimeZoneInOptions(f1.date || {}, timeZone)), time: deepMergeOptions(setTimeZoneInOptions(mfFormats.time, timeZone), setTimeZoneInOptions(f1.time || {}, timeZone)) });
}
var formatMessage$1 = function (_a, state, messageDescriptor, values, opts) {
    var locale = _a.locale, formats = _a.formats, messages = _a.messages, defaultLocale = _a.defaultLocale, defaultFormats = _a.defaultFormats, fallbackOnEmptyString = _a.fallbackOnEmptyString, onError = _a.onError, timeZone = _a.timeZone, defaultRichTextElements = _a.defaultRichTextElements;
    if (messageDescriptor === void 0) { messageDescriptor = { id: '' }; }
    var msgId = messageDescriptor.id, defaultMessage = messageDescriptor.defaultMessage;
    // `id` is a required field of a Message Descriptor.
    invariant(!!msgId, "[@formatjs/intl] An `id` must be provided to format a message. You can either:\n1. Configure your build toolchain with [babel-plugin-formatjs](https://formatjs.io/docs/tooling/babel-plugin)\nor [@formatjs/ts-transformer](https://formatjs.io/docs/tooling/ts-transformer) OR\n2. Configure your `eslint` config to include [eslint-plugin-formatjs](https://formatjs.io/docs/tooling/linter#enforce-id)\nto autofix this issue");
    var id = String(msgId);
    var message = 
    // In case messages is Object.create(null)
    // e.g import('foo.json') from webpack)
    // See https://github.com/formatjs/formatjs/issues/1914
    messages &&
        Object.prototype.hasOwnProperty.call(messages, id) &&
        messages[id];
    // IMPORTANT: Hot path if `message` is AST with a single literal node
    if (Array.isArray(message) &&
        message.length === 1 &&
        message[0].type === TYPE.literal) {
        return message[0].value;
    }
    // IMPORTANT: Hot path straight lookup for performance
    if (!values &&
        message &&
        typeof message === 'string' &&
        !defaultRichTextElements) {
        return message.replace(/'\{(.*?)\}'/gi, "{$1}");
    }
    values = __assign$1(__assign$1({}, defaultRichTextElements), (values || {}));
    formats = deepMergeFormatsAndSetTimeZone(formats, timeZone);
    defaultFormats = deepMergeFormatsAndSetTimeZone(defaultFormats, timeZone);
    if (!message) {
        if (fallbackOnEmptyString === false && message === '') {
            return message;
        }
        if (!defaultMessage ||
            (locale && locale.toLowerCase() !== defaultLocale.toLowerCase())) {
            // This prevents warnings from littering the console in development
            // when no `messages` are passed into the <IntlProvider> for the
            // default locale.
            onError(new MissingTranslationError(messageDescriptor, locale));
        }
        if (defaultMessage) {
            try {
                var formatter = state.getMessageFormat(defaultMessage, defaultLocale, defaultFormats, opts);
                return formatter.format(values);
            }
            catch (e) {
                onError(new MessageFormatError("Error formatting default message for: \"".concat(id, "\", rendering default message verbatim"), locale, messageDescriptor, e));
                return typeof defaultMessage === 'string' ? defaultMessage : id;
            }
        }
        return id;
    }
    // We have the translated message
    try {
        var formatter = state.getMessageFormat(message, locale, formats, __assign$1({ formatters: state }, (opts || {})));
        return formatter.format(values);
    }
    catch (e) {
        onError(new MessageFormatError("Error formatting message: \"".concat(id, "\", using ").concat(defaultMessage ? 'default message' : 'id', " as fallback."), locale, messageDescriptor, e));
    }
    if (defaultMessage) {
        try {
            var formatter = state.getMessageFormat(defaultMessage, defaultLocale, defaultFormats, opts);
            return formatter.format(values);
        }
        catch (e) {
            onError(new MessageFormatError("Error formatting the default message for: \"".concat(id, "\", rendering message verbatim"), locale, messageDescriptor, e));
        }
    }
    if (typeof message === 'string') {
        return message;
    }
    if (typeof defaultMessage === 'string') {
        return defaultMessage;
    }
    return id;
};

var DATE_TIME_FORMAT_OPTIONS = [
    'formatMatcher',
    'timeZone',
    'hour12',
    'weekday',
    'era',
    'year',
    'month',
    'day',
    'hour',
    'minute',
    'second',
    'timeZoneName',
    'hourCycle',
    'dateStyle',
    'timeStyle',
    'calendar',
    // 'dayPeriod',
    'numberingSystem',
    'fractionalSecondDigits',
];
function getFormatter$2(_a, type, getDateTimeFormat, options) {
    var locale = _a.locale, formats = _a.formats, onError = _a.onError, timeZone = _a.timeZone;
    if (options === void 0) { options = {}; }
    var format = options.format;
    var defaults = __assign$1(__assign$1({}, (timeZone && { timeZone: timeZone })), (format && getNamedFormat(formats, type, format, onError)));
    var filteredOptions = filterProps(options, DATE_TIME_FORMAT_OPTIONS, defaults);
    if (type === 'time' &&
        !filteredOptions.hour &&
        !filteredOptions.minute &&
        !filteredOptions.second &&
        !filteredOptions.timeStyle &&
        !filteredOptions.dateStyle) {
        // Add default formatting options if hour, minute, or second isn't defined.
        filteredOptions = __assign$1(__assign$1({}, filteredOptions), { hour: 'numeric', minute: 'numeric' });
    }
    return getDateTimeFormat(locale, filteredOptions);
}
function formatDate(config, getDateTimeFormat) {
    var _a = [];
    for (var _i = 2; _i < arguments.length; _i++) {
        _a[_i - 2] = arguments[_i];
    }
    var value = _a[0], _b = _a[1], options = _b === void 0 ? {} : _b;
    var date = typeof value === 'string' ? new Date(value || 0) : value;
    try {
        return getFormatter$2(config, 'date', getDateTimeFormat, options).format(date);
    }
    catch (e) {
        config.onError(new IntlFormatError('Error formatting date.', config.locale, e));
    }
    return String(date);
}
function formatTime(config, getDateTimeFormat) {
    var _a = [];
    for (var _i = 2; _i < arguments.length; _i++) {
        _a[_i - 2] = arguments[_i];
    }
    var value = _a[0], _b = _a[1], options = _b === void 0 ? {} : _b;
    var date = typeof value === 'string' ? new Date(value || 0) : value;
    try {
        return getFormatter$2(config, 'time', getDateTimeFormat, options).format(date);
    }
    catch (e) {
        config.onError(new IntlFormatError('Error formatting time.', config.locale, e));
    }
    return String(date);
}
function formatDateTimeRange(config, getDateTimeFormat) {
    var _a = [];
    for (var _i = 2; _i < arguments.length; _i++) {
        _a[_i - 2] = arguments[_i];
    }
    var from = _a[0], to = _a[1], _b = _a[2], options = _b === void 0 ? {} : _b;
    var timeZone = config.timeZone, locale = config.locale, onError = config.onError;
    var filteredOptions = filterProps(options, DATE_TIME_FORMAT_OPTIONS, timeZone ? { timeZone: timeZone } : {});
    try {
        return getDateTimeFormat(locale, filteredOptions).formatRange(from, to);
    }
    catch (e) {
        onError(new IntlFormatError('Error formatting date time range.', config.locale, e));
    }
    return String(from);
}
function formatDateToParts(config, getDateTimeFormat) {
    var _a = [];
    for (var _i = 2; _i < arguments.length; _i++) {
        _a[_i - 2] = arguments[_i];
    }
    var value = _a[0], _b = _a[1], options = _b === void 0 ? {} : _b;
    var date = typeof value === 'string' ? new Date(value || 0) : value;
    try {
        return getFormatter$2(config, 'date', getDateTimeFormat, options).formatToParts(date); // TODO: remove this when https://github.com/microsoft/TypeScript/pull/50402 is merged
    }
    catch (e) {
        config.onError(new IntlFormatError('Error formatting date.', config.locale, e));
    }
    return [];
}
function formatTimeToParts(config, getDateTimeFormat) {
    var _a = [];
    for (var _i = 2; _i < arguments.length; _i++) {
        _a[_i - 2] = arguments[_i];
    }
    var value = _a[0], _b = _a[1], options = _b === void 0 ? {} : _b;
    var date = typeof value === 'string' ? new Date(value || 0) : value;
    try {
        return getFormatter$2(config, 'time', getDateTimeFormat, options).formatToParts(date); // TODO: remove this when https://github.com/microsoft/TypeScript/pull/50402 is merged
    }
    catch (e) {
        config.onError(new IntlFormatError('Error formatting time.', config.locale, e));
    }
    return [];
}

var DISPLAY_NAMES_OPTONS = [
    'style',
    'type',
    'fallback',
    'languageDisplay',
];
function formatDisplayName(_a, getDisplayNames, value, options) {
    var locale = _a.locale, onError = _a.onError;
    var DisplayNames = Intl.DisplayNames;
    if (!DisplayNames) {
        onError(new FormatError("Intl.DisplayNames is not available in this environment.\nTry polyfilling it using \"@formatjs/intl-displaynames\"\n", ErrorCode.MISSING_INTL_API));
    }
    var filteredOptions = filterProps(options, DISPLAY_NAMES_OPTONS);
    try {
        return getDisplayNames(locale, filteredOptions).of(value);
    }
    catch (e) {
        onError(new IntlFormatError('Error formatting display name.', locale, e));
    }
}

var LIST_FORMAT_OPTIONS = [
    'type',
    'style',
];
var now = Date.now();
function generateToken(i) {
    return "".concat(now, "_").concat(i, "_").concat(now);
}
function formatList(opts, getListFormat, values, options) {
    if (options === void 0) { options = {}; }
    var results = formatListToParts(opts, getListFormat, values, options).reduce(function (all, el) {
        var val = el.value;
        if (typeof val !== 'string') {
            all.push(val);
        }
        else if (typeof all[all.length - 1] === 'string') {
            all[all.length - 1] += val;
        }
        else {
            all.push(val);
        }
        return all;
    }, []);
    return results.length === 1 ? results[0] : results.length === 0 ? '' : results;
}
function formatListToParts(_a, getListFormat, values, options) {
    var locale = _a.locale, onError = _a.onError;
    if (options === void 0) { options = {}; }
    var ListFormat = Intl.ListFormat;
    if (!ListFormat) {
        onError(new FormatError("Intl.ListFormat is not available in this environment.\nTry polyfilling it using \"@formatjs/intl-listformat\"\n", ErrorCode.MISSING_INTL_API));
    }
    var filteredOptions = filterProps(options, LIST_FORMAT_OPTIONS);
    try {
        var richValues_1 = {};
        var serializedValues = values.map(function (v, i) {
            if (typeof v === 'object') {
                var id = generateToken(i);
                richValues_1[id] = v;
                return id;
            }
            return String(v);
        });
        return getListFormat(locale, filteredOptions)
            .formatToParts(serializedValues)
            .map(function (part) {
            return part.type === 'literal'
                ? part
                : __assign$1(__assign$1({}, part), { value: richValues_1[part.value] || part.value });
        });
    }
    catch (e) {
        onError(new IntlFormatError('Error formatting list.', locale, e));
    }
    // @ts-ignore
    return values;
}

var PLURAL_FORMAT_OPTIONS = ['type'];
function formatPlural(_a, getPluralRules, value, options) {
    var locale = _a.locale, onError = _a.onError;
    if (options === void 0) { options = {}; }
    if (!Intl.PluralRules) {
        onError(new FormatError("Intl.PluralRules is not available in this environment.\nTry polyfilling it using \"@formatjs/intl-pluralrules\"\n", ErrorCode.MISSING_INTL_API));
    }
    var filteredOptions = filterProps(options, PLURAL_FORMAT_OPTIONS);
    try {
        return getPluralRules(locale, filteredOptions).select(value);
    }
    catch (e) {
        onError(new IntlFormatError('Error formatting plural.', locale, e));
    }
    return 'other';
}

var RELATIVE_TIME_FORMAT_OPTIONS = ['numeric', 'style'];
function getFormatter$1(_a, getRelativeTimeFormat, options) {
    var locale = _a.locale, formats = _a.formats, onError = _a.onError;
    if (options === void 0) { options = {}; }
    var format = options.format;
    var defaults = (!!format && getNamedFormat(formats, 'relative', format, onError)) || {};
    var filteredOptions = filterProps(options, RELATIVE_TIME_FORMAT_OPTIONS, defaults);
    return getRelativeTimeFormat(locale, filteredOptions);
}
function formatRelativeTime(config, getRelativeTimeFormat, value, unit, options) {
    if (options === void 0) { options = {}; }
    if (!unit) {
        unit = 'second';
    }
    var RelativeTimeFormat = Intl.RelativeTimeFormat;
    if (!RelativeTimeFormat) {
        config.onError(new FormatError("Intl.RelativeTimeFormat is not available in this environment.\nTry polyfilling it using \"@formatjs/intl-relativetimeformat\"\n", ErrorCode.MISSING_INTL_API));
    }
    try {
        return getFormatter$1(config, getRelativeTimeFormat, options).format(value, unit);
    }
    catch (e) {
        config.onError(new IntlFormatError('Error formatting relative time.', config.locale, e));
    }
    return String(value);
}

var NUMBER_FORMAT_OPTIONS = [
    'style',
    'currency',
    'unit',
    'unitDisplay',
    'useGrouping',
    'minimumIntegerDigits',
    'minimumFractionDigits',
    'maximumFractionDigits',
    'minimumSignificantDigits',
    'maximumSignificantDigits',
    // ES2020 NumberFormat
    'compactDisplay',
    'currencyDisplay',
    'currencySign',
    'notation',
    'signDisplay',
    'unit',
    'unitDisplay',
    'numberingSystem',
    // ES2023 NumberFormat
    'trailingZeroDisplay',
    'roundingPriority',
    'roundingIncrement',
    'roundingMode',
];
function getFormatter(_a, getNumberFormat, options) {
    var locale = _a.locale, formats = _a.formats, onError = _a.onError;
    if (options === void 0) { options = {}; }
    var format = options.format;
    var defaults = ((format &&
        getNamedFormat(formats, 'number', format, onError)) ||
        {});
    var filteredOptions = filterProps(options, NUMBER_FORMAT_OPTIONS, defaults);
    return getNumberFormat(locale, filteredOptions);
}
function formatNumber(config, getNumberFormat, value, options) {
    if (options === void 0) { options = {}; }
    try {
        return getFormatter(config, getNumberFormat, options).format(value);
    }
    catch (e) {
        config.onError(new IntlFormatError('Error formatting number.', config.locale, e));
    }
    return String(value);
}
function formatNumberToParts(config, getNumberFormat, value, options) {
    if (options === void 0) { options = {}; }
    try {
        return getFormatter(config, getNumberFormat, options).formatToParts(value);
    }
    catch (e) {
        config.onError(new IntlFormatError('Error formatting number.', config.locale, e));
    }
    return [];
}

function messagesContainString(messages) {
    var firstMessage = messages ? messages[Object.keys(messages)[0]] : undefined;
    return typeof firstMessage === 'string';
}
function verifyConfigMessages(config) {
    if (config.onWarn &&
        config.defaultRichTextElements &&
        messagesContainString(config.messages || {})) {
        config.onWarn("[@formatjs/intl] \"defaultRichTextElements\" was specified but \"message\" was not pre-compiled. \nPlease consider using \"@formatjs/cli\" to pre-compile your messages for performance.\nFor more details see https://formatjs.io/docs/getting-started/message-distribution");
    }
}
/**
 * Create intl object
 * @param config intl config
 * @param cache cache for formatter instances to prevent memory leak
 */
function createIntl$1(config, cache) {
    var formatters = createFormatters(cache);
    var resolvedConfig = __assign$1(__assign$1({}, DEFAULT_INTL_CONFIG$1), config);
    var locale = resolvedConfig.locale, defaultLocale = resolvedConfig.defaultLocale, onError = resolvedConfig.onError;
    if (!locale) {
        if (onError) {
            onError(new InvalidConfigError("\"locale\" was not configured, using \"".concat(defaultLocale, "\" as fallback. See https://formatjs.io/docs/react-intl/api#intlshape for more details")));
        }
        // Since there's no registered locale data for `locale`, this will
        // fallback to the `defaultLocale` to make sure things can render.
        // The `messages` are overridden to the `defaultProps` empty object
        // to maintain referential equality across re-renders. It's assumed
        // each <FormattedMessage> contains a `defaultMessage` prop.
        resolvedConfig.locale = resolvedConfig.defaultLocale || 'en';
    }
    else if (!Intl.NumberFormat.supportedLocalesOf(locale).length && onError) {
        onError(new MissingDataError("Missing locale data for locale: \"".concat(locale, "\" in Intl.NumberFormat. Using default locale: \"").concat(defaultLocale, "\" as fallback. See https://formatjs.io/docs/react-intl#runtime-requirements for more details")));
    }
    else if (!Intl.DateTimeFormat.supportedLocalesOf(locale).length &&
        onError) {
        onError(new MissingDataError("Missing locale data for locale: \"".concat(locale, "\" in Intl.DateTimeFormat. Using default locale: \"").concat(defaultLocale, "\" as fallback. See https://formatjs.io/docs/react-intl#runtime-requirements for more details")));
    }
    verifyConfigMessages(resolvedConfig);
    return __assign$1(__assign$1({}, resolvedConfig), { formatters: formatters, formatNumber: formatNumber.bind(null, resolvedConfig, formatters.getNumberFormat), formatNumberToParts: formatNumberToParts.bind(null, resolvedConfig, formatters.getNumberFormat), formatRelativeTime: formatRelativeTime.bind(null, resolvedConfig, formatters.getRelativeTimeFormat), formatDate: formatDate.bind(null, resolvedConfig, formatters.getDateTimeFormat), formatDateToParts: formatDateToParts.bind(null, resolvedConfig, formatters.getDateTimeFormat), formatTime: formatTime.bind(null, resolvedConfig, formatters.getDateTimeFormat), formatDateTimeRange: formatDateTimeRange.bind(null, resolvedConfig, formatters.getDateTimeFormat), formatTimeToParts: formatTimeToParts.bind(null, resolvedConfig, formatters.getDateTimeFormat), formatPlural: formatPlural.bind(null, resolvedConfig, formatters.getPluralRules), 
        // @ts-expect-error TODO: will get to this later
        formatMessage: formatMessage$1.bind(null, resolvedConfig, formatters), 
        // @ts-expect-error TODO: will get to this later
        $t: formatMessage$1.bind(null, resolvedConfig, formatters), formatList: formatList.bind(null, resolvedConfig, formatters.getListFormat), formatListToParts: formatListToParts.bind(null, resolvedConfig, formatters.getListFormat), formatDisplayName: formatDisplayName.bind(null, resolvedConfig, formatters.getDisplayNames) });
}

function invariantIntlContext(intl) {
    invariant(intl, '[React Intl] Could not find required `intl` object. ' +
        '<IntlProvider> needs to exist in the component ancestry.');
}
var DEFAULT_INTL_CONFIG = __assign$1(__assign$1({}, DEFAULT_INTL_CONFIG$1), { textComponent: React.Fragment });
/**
 * Takes a `formatXMLElementFn`, and composes it in function, which passes
 * argument `parts` through, assigning unique key to each part, to prevent
 * "Each child in a list should have a unique "key"" React error.
 * @param formatXMLElementFn
 */
function assignUniqueKeysToParts(formatXMLElementFn) {
    return function (parts) {
        // eslint-disable-next-line prefer-rest-params
        return formatXMLElementFn(React.Children.toArray(parts));
    };
}
function shallowEqual(objA, objB) {
    if (objA === objB) {
        return true;
    }
    if (!objA || !objB) {
        return false;
    }
    var aKeys = Object.keys(objA);
    var bKeys = Object.keys(objB);
    var len = aKeys.length;
    if (bKeys.length !== len) {
        return false;
    }
    for (var i = 0; i < len; i++) {
        var key = aKeys[i];
        if (objA[key] !== objB[key] ||
            !Object.prototype.hasOwnProperty.call(objB, key)) {
            return false;
        }
    }
    return true;
}

// This is primarily dealing with packaging systems where multiple copies of react-intl
// might exist
var IntlContext = typeof window !== 'undefined' && !window.__REACT_INTL_BYPASS_GLOBAL_CONTEXT__
    ? window.__REACT_INTL_CONTEXT__ ||
        (window.__REACT_INTL_CONTEXT__ = React.createContext(null))
    : React.createContext(null);
IntlContext.Consumer; var IntlProvider = IntlContext.Provider;
var Provider = IntlProvider;
var Context = IntlContext;

function useIntl() {
    var intl = React.useContext(Context);
    invariantIntlContext(intl);
    return intl;
}

var DisplayName;
(function (DisplayName) {
    DisplayName["formatDate"] = "FormattedDate";
    DisplayName["formatTime"] = "FormattedTime";
    DisplayName["formatNumber"] = "FormattedNumber";
    DisplayName["formatList"] = "FormattedList";
    // Note that this DisplayName is the locale display name, not to be confused with
    // the name of the enum, which is for React component display name in dev tools.
    DisplayName["formatDisplayName"] = "FormattedDisplayName";
})(DisplayName || (DisplayName = {}));
var DisplayNameParts;
(function (DisplayNameParts) {
    DisplayNameParts["formatDate"] = "FormattedDateParts";
    DisplayNameParts["formatTime"] = "FormattedTimeParts";
    DisplayNameParts["formatNumber"] = "FormattedNumberParts";
    DisplayNameParts["formatList"] = "FormattedListParts";
})(DisplayNameParts || (DisplayNameParts = {}));
function createFormattedDateTimePartsComponent(name) {
    var ComponentParts = function (props) {
        var intl = useIntl();
        var value = props.value, children = props.children, formatProps = __rest(props, ["value", "children"]);
        var date = typeof value === 'string' ? new Date(value || 0) : value;
        var formattedParts = name === 'formatDate'
            ? intl.formatDateToParts(date, formatProps)
            : intl.formatTimeToParts(date, formatProps);
        return children(formattedParts);
    };
    ComponentParts.displayName = DisplayNameParts[name];
    return ComponentParts;
}
function createFormattedComponent(name) {
    var Component = function (props) {
        var intl = useIntl();
        var value = props.value, children = props.children, formatProps = __rest(props
        // TODO: fix TS type definition for localeMatcher upstream
        , ["value", "children"]);
        // TODO: fix TS type definition for localeMatcher upstream
        var formattedValue = intl[name](value, formatProps);
        if (typeof children === 'function') {
            return children(formattedValue);
        }
        var Text = intl.textComponent || React.Fragment;
        return React.createElement(Text, null, formattedValue);
    };
    Component.displayName = DisplayName[name];
    return Component;
}

/*
 * Copyright 2015, Yahoo Inc.
 * Copyrights licensed under the New BSD License.
 * See the accompanying LICENSE file for terms.
 */
function assignUniqueKeysToFormatXMLElementFnArgument(values) {
    if (!values) {
        return values;
    }
    return Object.keys(values).reduce(function (acc, k) {
        var v = values[k];
        acc[k] = isFormatXMLElementFn(v)
            ? assignUniqueKeysToParts(v)
            : v;
        return acc;
    }, {});
}
var formatMessage = function (config, formatters, descriptor, rawValues) {
    var rest = [];
    for (var _i = 4; _i < arguments.length; _i++) {
        rest[_i - 4] = arguments[_i];
    }
    var values = assignUniqueKeysToFormatXMLElementFnArgument(rawValues);
    var chunks = formatMessage$1.apply(void 0, __spreadArray([config,
        formatters,
        descriptor,
        values], rest, false));
    if (Array.isArray(chunks)) {
        return React.Children.toArray(chunks);
    }
    return chunks;
};
/**
 * Create intl object
 * @param config intl config
 * @param cache cache for formatter instances to prevent memory leak
 */
var createIntl = function (_a, cache) {
    var rawDefaultRichTextElements = _a.defaultRichTextElements, config = __rest(_a, ["defaultRichTextElements"]);
    var defaultRichTextElements = assignUniqueKeysToFormatXMLElementFnArgument(rawDefaultRichTextElements);
    var coreIntl = createIntl$1(__assign$1(__assign$1(__assign$1({}, DEFAULT_INTL_CONFIG), config), { defaultRichTextElements: defaultRichTextElements }), cache);
    var resolvedConfig = {
        locale: coreIntl.locale,
        timeZone: coreIntl.timeZone,
        fallbackOnEmptyString: coreIntl.fallbackOnEmptyString,
        formats: coreIntl.formats,
        defaultLocale: coreIntl.defaultLocale,
        defaultFormats: coreIntl.defaultFormats,
        messages: coreIntl.messages,
        onError: coreIntl.onError,
        defaultRichTextElements: defaultRichTextElements,
    };
    return __assign$1(__assign$1({}, coreIntl), { formatMessage: formatMessage.bind(null, resolvedConfig, 
        // @ts-expect-error fix this
        coreIntl.formatters), 
        // @ts-expect-error fix this
        $t: formatMessage.bind(null, resolvedConfig, coreIntl.formatters) });
};

/*
 * Copyright 2015, Yahoo Inc.
 * Copyrights licensed under the New BSD License.
 * See the accompanying LICENSE file for terms.
 */
function processIntlConfig(config) {
    return {
        locale: config.locale,
        timeZone: config.timeZone,
        fallbackOnEmptyString: config.fallbackOnEmptyString,
        formats: config.formats,
        textComponent: config.textComponent,
        messages: config.messages,
        defaultLocale: config.defaultLocale,
        defaultFormats: config.defaultFormats,
        onError: config.onError,
        onWarn: config.onWarn,
        wrapRichTextChunksInFragment: config.wrapRichTextChunksInFragment,
        defaultRichTextElements: config.defaultRichTextElements,
    };
}
/** @class */ ((function (_super) {
    __extends(IntlProvider, _super);
    function IntlProvider() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.cache = createIntlCache();
        _this.state = {
            cache: _this.cache,
            intl: createIntl(processIntlConfig(_this.props), _this.cache),
            prevConfig: processIntlConfig(_this.props),
        };
        return _this;
    }
    IntlProvider.getDerivedStateFromProps = function (props, _a) {
        var prevConfig = _a.prevConfig, cache = _a.cache;
        var config = processIntlConfig(props);
        if (!shallowEqual(prevConfig, config)) {
            return {
                intl: createIntl(config, cache),
                prevConfig: config,
            };
        }
        return null;
    };
    IntlProvider.prototype.render = function () {
        invariantIntlContext(this.state.intl);
        return React.createElement(Provider, { value: this.state.intl }, this.props.children);
    };
    IntlProvider.displayName = 'IntlProvider';
    IntlProvider.defaultProps = DEFAULT_INTL_CONFIG;
    return IntlProvider;
})(React.PureComponent));

/*
 * Copyright 2015, Yahoo Inc.
 * Copyrights licensed under the New BSD License.
 * See the accompanying LICENSE file for terms.
 */
function areEqual(prevProps, nextProps) {
    var values = prevProps.values, otherProps = __rest(prevProps, ["values"]);
    var nextValues = nextProps.values, nextOtherProps = __rest(nextProps, ["values"]);
    return (shallowEqual(nextValues, values) &&
        shallowEqual(otherProps, nextOtherProps));
}
function FormattedMessage(props) {
    var intl = useIntl();
    var formatMessage = intl.formatMessage, _a = intl.textComponent, Text = _a === void 0 ? React.Fragment : _a;
    var id = props.id, description = props.description, defaultMessage = props.defaultMessage, values = props.values, children = props.children, _b = props.tagName, Component = _b === void 0 ? Text : _b, ignoreTag = props.ignoreTag;
    var descriptor = { id: id, description: description, defaultMessage: defaultMessage };
    var nodes = formatMessage(descriptor, values, {
        ignoreTag: ignoreTag,
    });
    if (typeof children === 'function') {
        return children(Array.isArray(nodes) ? nodes : [nodes]);
    }
    if (Component) {
        return React.createElement(Component, null, React.Children.toArray(nodes));
    }
    return React.createElement(React.Fragment, null, nodes);
}
FormattedMessage.displayName = 'FormattedMessage';
var MemoizedFormattedMessage = React.memo(FormattedMessage, areEqual);
MemoizedFormattedMessage.displayName = 'MemoizedFormattedMessage';

function defineMessages(msgs) {
    return msgs;
}
// IMPORTANT: Explicit here to prevent api-extractor from outputing `import('./src/types').CustomFormatConfig`
createFormattedComponent('formatDate');
createFormattedComponent('formatTime');
createFormattedComponent('formatNumber');
createFormattedComponent('formatList');
createFormattedComponent('formatDisplayName');
createFormattedDateTimePartsComponent('formatDate');
createFormattedDateTimePartsComponent('formatTime');

const SearchMultiselectDropdownAction = ()=>{
    const { action, options, getBlocksWith, selectedItems, isMultiSelect, messages } = useSearchMultiselectContext();
    const { handleSelectAll, handleDeselectAll } = action;
    const { selectAllText } = messages;
    const handleOnClick = ()=>{
        const filteredOptions = options.filter((item)=>!item.disabled);
        if (filteredOptions.length > selectedItems.length) {
            handleSelectAll();
        } else {
            handleDeselectAll();
        }
    };
    return isMultiSelect ? /*#__PURE__*/ jsx("div", {
        className: getBlocksWith("__action-wrapper"),
        children: /*#__PURE__*/ jsx(Button$1, {
            "data-auto": "select-all-button",
            styleType: "inline",
            className: getBlocksWith("__action-wrapper-selectAll"),
            onClick: handleOnClick,
            tabIndex: 0,
            children: selectAllText
        })
    }) : null;
};

const isThisItemSelected = (item, selectedItems)=>{
    if (!item) {
        return false;
    }
    return selectedItems.some((o)=>o.label === item.label && o.value === item.value);
};
const isNullOrUndefined = (value)=>value === null || value === undefined;
const getMaxSelectionLimit = (maxSelections, options)=>{
    const maxLimit = isNullOrUndefined(maxSelections) ? options.length : maxSelections;
    return maxLimit !== null && maxLimit !== void 0 ? maxLimit : options.length;
};

const useWindowResize = ()=>{
    const [size, setSize] = useState([
        0,
        0
    ]);
    useLayoutEffect(()=>{
        function updateSize() {
            setSize([
                window.innerWidth,
                window.innerHeight
            ]);
        }
        window.addEventListener("resize", updateSize);
        updateSize();
        return ()=>window.removeEventListener("resize", updateSize);
    }, []);
    return size;
};

const messages = defineMessages({
    dropdownItemsContainer: {
        id: "searchMultiselectDropdown.containerMessage",
        defaultMessage: "Dropdown options"
    },
    dropdownItemDisabled: {
        id: "searchMultiselectDropdown.disabled",
        defaultMessage: "{itemLabel} is disabled"
    },
    dropdownItemSelected: {
        id: "searchMultiselectDropdown.selected",
        defaultMessage: "{itemLabel} is selected"
    },
    dropdownItemNotSelected: {
        id: "searchMultiselectDropdown.notSelected",
        defaultMessage: "{itemLabel} is not selected"
    }
});

function _define_property$7(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$7(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$7(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$7(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$7(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$7(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$5(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$5(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$5(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const SearchMultiselectDropdownItem = (_param)=>{
    var { children } = _param, rest = _object_without_properties$5(_param, [
        "children"
    ]);
    const intl = useIntl();
    const { item, getBlocksWith, isOpen, showItemLabelOrValue, messages: messages$1, isSearchMultiselectDropdownDisabled, selectedItems, isMultiSelect, selectItem } = useSearchMultiselectContext();
    const { isAdvanceActionsEnabled, isOptionsLoading, maxSelections, items, highlightedIndexItem = -1, getItemProps, setHighlightedIndexItem, handleItemClick } = item;
    const { loadingText } = messages$1;
    const [navIndex, setNavIndex] = useState();
    const listRef = useRef(null);
    const sizeMap = useRef({});
    const setSize = useCallback((index, size)=>{
        var _listRef_current;
        sizeMap.current = _object_spread_props$7(_object_spread$7({}, sizeMap.current), {
            [index]: size
        });
        (_listRef_current = listRef.current) === null || _listRef_current === void 0 ? void 0 : _listRef_current.resetAfterIndex(index);
    }, []);
    const getSize = (index)=>sizeMap.current[index] || 50;
    const [windowWidth] = useWindowResize();
    if (!getItemProps) {
        throw new Error("SearchMultiselectDropdown requires getItemProps to be defined");
    }
    const SPACE_KEY = " ";
    const handleKeyDown = (event)=>{
        var _items_highlightedIndexItem;
        if (event.key === "Tab") {
            !isMultiSelect && setNavIndex(undefined);
            if (highlightedIndexItem === -1) {
                event.preventDefault();
                setHighlightedIndexItem(0);
            } else {
                setHighlightedIndexItem(-1);
            }
        }
        if (event.key === "ArrowUp" || event.key === "ArrowDown") {
            let direction = 0;
            if (event.key === "ArrowUp") {
                direction = ArrowDirection.Up;
            } else if (event.key === "ArrowDown") {
                direction = ArrowDirection.Down;
            }
            if (direction === ArrowDirection.Up && highlightedIndexItem !== 0 || direction === ArrowDirection.Down && highlightedIndexItem !== items.length) {
                var _items_newIndex;
                const newIndex = highlightedIndexItem + direction;
                setNavIndex(newIndex);
                event.preventDefault();
                setHighlightedIndexItem(newIndex);
                if ((_items_newIndex = items[newIndex]) === null || _items_newIndex === void 0 ? void 0 : _items_newIndex.id) {
                    var _document_querySelector;
                    (_document_querySelector = document.querySelector(`[data-auto=items-wrapper-${items[newIndex].id}]`)) === null || _document_querySelector === void 0 ? void 0 : _document_querySelector.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                        inline: "nearest"
                    });
                }
            }
        }
        if ((event.key === "Enter" || event.key === SPACE_KEY) && highlightedIndexItem >= 0 && !((_items_highlightedIndexItem = items[highlightedIndexItem]) === null || _items_highlightedIndexItem === void 0 ? void 0 : _items_highlightedIndexItem.disabled)) {
            !isMultiSelect && setNavIndex(undefined);
            event.preventDefault();
            handleItemClick(items[highlightedIndexItem]);
            !isMultiSelect && selectItem(items[highlightedIndexItem]);
        }
    };
    const renderSelectedIcon = (item)=>{
        return isThisItemSelected(item, selectedItems) && /*#__PURE__*/ jsx("div", {
            className: getBlocksWith("__list-item-wrapper__item-icon"),
            children: /*#__PURE__*/ jsx(FontAwesomeIcon, {
                icon: faCheck$2,
                className: getBlocksWith("__list-item-wrapper__icon-svg")
            })
        });
    };
    const labelClass = getBlocksWith("__option-wrapper-content-label");
    const valueClass = showItemLabelOrValue === ShowBylabelOrValue.BOTH ? getBlocksWith("__option-wrapper-content-value") : getBlocksWith("__option-wrapper-content-label");
    const getAriaLabelForItemsWrapper = useCallback(()=>{
        if (navIndex === undefined) {
            return intl.formatMessage(messages.dropdownItemsContainer);
        } else {
            const itemIndex = Math.min(navIndex, items.length - 1);
            const item = items[itemIndex];
            if (item.disabled) {
                return intl.formatMessage(messages.dropdownItemDisabled, {
                    itemLabel: item.label,
                    itemIndex: itemIndex + 1,
                    totalItems: items.length
                });
            } else if (isThisItemSelected(item, selectedItems)) {
                return intl.formatMessage(messages.dropdownItemSelected, {
                    itemLabel: item.label,
                    itemIndex: itemIndex + 1,
                    totalItems: items.length
                });
            } else {
                return intl.formatMessage(messages.dropdownItemNotSelected, {
                    itemLabel: item.label,
                    itemIndex: itemIndex + 1,
                    totalItems: items.length
                });
            }
        }
    }, [
        navIndex,
        items,
        intl,
        selectedItems
    ]);
    const listItems = (item, index, rowRef)=>{
        return /*#__PURE__*/ jsx("li", _object_spread_props$7(_object_spread$7({}, getItemProps(_object_spread$7({
            item,
            index
        }, rest))), {
            className: cn(highlightedIndexItem === index && (item === null || item === void 0 ? void 0 : item.disabled) !== true && getBlocksWith("__option-list-item--highlighted"), isThisItemSelected(item, selectedItems) && getBlocksWith("__option-list-item--selected"), (item === null || item === void 0 ? void 0 : item.disabled) && getBlocksWith("__option-list-item--disabled")),
            "data-auto": `list-item-${item.id}`,
            ref: rowRef,
            children: /*#__PURE__*/ jsxs("div", {
                className: cn(getBlocksWith("__option-wrapper"), isThisItemSelected(item, selectedItems) && getBlocksWith("__option-wrapper-content--selected")),
                children: [
                    renderSelectedIcon(item),
                    /*#__PURE__*/ jsxs("div", {
                        className: getBlocksWith("__option-wrapper-content"),
                        children: [
                            /*#__PURE__*/ jsx("span", {
                                className: labelClass,
                                children: (showItemLabelOrValue === ShowBylabelOrValue.BOTH || showItemLabelOrValue === ShowBylabelOrValue.LABEL) && item.label
                            }),
                            /*#__PURE__*/ jsx("span", {
                                className: valueClass,
                                children: (showItemLabelOrValue === ShowBylabelOrValue.BOTH || showItemLabelOrValue === ShowBylabelOrValue.VALUE) && item.value
                            }),
                            children
                        ]
                    })
                ]
            })
        }));
    };
    const Row = ({ index, style })=>{
        const item = items[index];
        const rowRef = useRef();
        useEffect(()=>{
            var _rowRef_current;
            setSize(index, // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            rowRef.current ? (_rowRef_current = rowRef.current) === null || _rowRef_current === void 0 ? void 0 : _rowRef_current.getBoundingClientRect().height : 50);
        // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [
            setSize,
            index,
            windowWidth
        ]);
        return /*#__PURE__*/ jsx("div", {
            "data-auto": `items-wrapper-${item.id}`,
            className: getBlocksWith("__option"),
            style: style,
            children: listItems(item, index, rowRef)
        }, `${item.id}`);
    };
    return isOpen ? /*#__PURE__*/ jsxs(Fragment, {
        children: [
            isOptionsLoading && /*#__PURE__*/ jsx(Loading, {
                className: getBlocksWith("__loading"),
                loadingMessage: loadingText
            }),
            !isOptionsLoading && /*#__PURE__*/ jsxs(Fragment, {
                children: [
                    isAdvanceActionsEnabled && !isSearchMultiselectDropdownDisabled && isNullOrUndefined(maxSelections) && /*#__PURE__*/ jsx(SearchMultiselectDropdownAction, {}),
                    /*#__PURE__*/ jsx("div", {
                        tabIndex: 0,
                        onKeyDown: handleKeyDown,
                        className: getBlocksWith("__wrapper-list-items"),
                        "data-auto": "items-wrapper",
                        "aria-label": getAriaLabelForItemsWrapper(),
                        children: items.length > 50 ? /*#__PURE__*/ jsx(VariableSizeList, {
                            className: cn(getBlocksWith("__wrapper-list-virtualized"), "List"),
                            height: 500,
                            width: "100%",
                            itemCount: items === null || items === void 0 ? void 0 : items.length,
                            itemSize: getSize,
                            ref: listRef,
                            children: Row
                        }) : items.map((item, index)=>{
                            return /*#__PURE__*/ jsx("div", {
                                "data-auto": `items-wrapper-${item.id}`,
                                className: getBlocksWith("__option"),
                                children: listItems(item, index)
                            }, `${item.id}`);
                        })
                    })
                ]
            })
        ]
    }) : null;
};

function _define_property$6(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$6(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$6(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$6(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$6(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$6(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
const SearchMultiSelectDropdownSelectedChips = ()=>{
    const { selectedChips, getBlocksWith, selectedItems, showLabelOrValue, messages } = useSearchMultiselectContext();
    const { removeSelectedItem, getSelectedItemProps } = selectedChips;
    if (!getSelectedItemProps) {
        throw new Error("SearchMultiselectDropdown component requires getSelectedItemProps to be defined");
    }
    const { chipsDltBtnAriaText } = messages;
    const renderChipsText = (selectedItemForRender)=>{
        let placeholder = "";
        if (showLabelOrValue === ShowBylabelOrValue.BOTH) {
            placeholder = `${selectedItemForRender.label} (${selectedItemForRender === null || selectedItemForRender === void 0 ? void 0 : selectedItemForRender.value})`;
        } else {
            placeholder = showLabelOrValue === ShowBylabelOrValue.LABEL ? selectedItemForRender === null || selectedItemForRender === void 0 ? void 0 : selectedItemForRender.label : selectedItemForRender === null || selectedItemForRender === void 0 ? void 0 : selectedItemForRender.value;
        }
        return placeholder;
    };
    return /*#__PURE__*/ jsx("div", {
        className: getBlocksWith("__selected-chips"),
        "data-auto": "search-multiselect-dropdown-selected-chips",
        children: selectedItems === null || selectedItems === void 0 ? void 0 : selectedItems.map((selectedItemForRender, index)=>{
            return /*#__PURE__*/ jsxs("span", _object_spread_props$6(_object_spread$6({
                "data-auto": "chip-label",
                className: getBlocksWith("__selected-chips-text")
            }, getSelectedItemProps({
                selectedItem: selectedItemForRender,
                index
            })), {
                tabIndex: 0,
                children: [
                    renderChipsText(selectedItemForRender),
                    /*#__PURE__*/ jsx("button", {
                        className: getBlocksWith("__selected-chips-dltBtn"),
                        "data-auto": `remove-chips-btn-${selectedItemForRender.id}`,
                        onClick: (e)=>{
                            e.stopPropagation();
                            removeSelectedItem(selectedItemForRender);
                        },
                        type: "button",
                        tabIndex: 0,
                        "aria-label": chipsDltBtnAriaText,
                        children: /*#__PURE__*/ jsx(FontAwesomeIcon, {
                            icon: faTimes,
                            className: getBlocksWith("__dlt-icon-svg")
                        })
                    })
                ]
            }), `selected-item-${index}`);
        })
    });
};

function _define_property$5(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$5(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$5(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$5(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$5(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$5(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$4(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$4(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$4(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const SearchMultiselectDropdownLabel = (_param)=>{
    var { children } = _param, rest = _object_without_properties$4(_param, [
        "children"
    ]);
    const { label, getBlocksWith, selectedItems, isMultiSelect, isSearchMultiselectDropdownDisabled } = useSearchMultiselectContext();
    const { hideLabel, showSelectedItemAtTop, getLabelProps } = label;
    if (!getLabelProps) {
        throw new Error("SearchMultiselectDropdown requires getLabelProps to be defined");
    }
    const isSelectedChipsVisible = selectedItems.length > 0 && isMultiSelect && showSelectedItemAtTop && !isSearchMultiselectDropdownDisabled;
    return /*#__PURE__*/ jsxs("div", {
        className: getBlocksWith("__dropdown-label-wrapper"),
        children: [
            /*#__PURE__*/ jsx("label", _object_spread_props$5(_object_spread$5({
                className: cn(getBlocksWith("__dropdown-label"), hideLabel && getBlocksWith("__dropdown-label--hidden"))
            }, getLabelProps(_object_spread$5({}, rest))), {
                children: children
            })),
            isSelectedChipsVisible && /*#__PURE__*/ jsx(SearchMultiSelectDropdownSelectedChips, {})
        ]
    });
};

function _define_property$4(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$4(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$4(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$4(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$4(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$4(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
const SearchMultiselectDropdownInput = ()=>{
    const { input, getBlocksWith, isOpen, messages, getDropdownProps, options } = useSearchMultiselectContext();
    const { inputNotMatched, isSearchable, filterValue, showSearchButton, handleOnSearchBtnClick, clearSearch, getInputProps } = input;
    if (!getInputProps) {
        throw new Error("SearchMultiselectDropdown requires getInputProps to be defined");
    }
    const { inputBoxPlaceholder, clearBtnAriaText, searchBtnAriaText } = messages;
    const totalItemsLen = options.length;
    const [displayedValue, setDisplayedValue] = useState(filterValue);
    const inputRef = useRef(null);
    /**
   * totalItemsLen when you do not have options.
   * !inputNotMatched when no options(label, value) matching
   * with your filter keyword.
   */ const handleKeyDown = (event)=>{
        if (event.key === "Enter" && filterValue && (totalItemsLen === 0 || !inputNotMatched)) {
            event.preventDefault();
            handleOnSearchBtnClick && handleOnSearchBtnClick();
        }
    };
    const handleOnChange = (event)=>{
        setDisplayedValue(event.target.value);
    };
    return /*#__PURE__*/ jsx(Fragment, {
        children: /*#__PURE__*/ jsx("div", {
            className: cn(isOpen && isSearchable ? getBlocksWith("__search-wrapper") : getBlocksWith("__hidden")),
            children: /*#__PURE__*/ jsxs("div", {
                className: getBlocksWith("__search-wrapper-border"),
                children: [
                    /*#__PURE__*/ jsx("input", _object_spread_props$4(_object_spread$4({
                        className: cn(isOpen ? getBlocksWith("__search-wrapper-input") : getBlocksWith("__hidden")),
                        "data-auto": "multi-select-input",
                        placeholder: inputBoxPlaceholder,
                        autoFocus: true
                    }, getInputProps({
                        onKeyDown: handleKeyDown,
                        onChange: handleOnChange,
                        ref: inputRef,
                        value: displayedValue
                    }, _object_spread_props$4(_object_spread$4({}, getDropdownProps({
                        preventKeyAction: isOpen
                    })), {
                        suppressRefError: true
                    }))), {
                        "aria-autocomplete": "list",
                        name: "filterInput",
                        "aria-multiline": "false",
                        tabIndex: 0
                    })),
                    /*#__PURE__*/ jsx("button", {
                        className: cn(!!filterValue ? getBlocksWith("__search-wrapper-search-icon") : getBlocksWith("__search-wrapper-search-icon-hidden")),
                        onClick: ()=>{
                            setDisplayedValue("");
                            clearSearch();
                        },
                        "aria-label": clearBtnAriaText,
                        "data-auto": "clear-button",
                        tabIndex: 0,
                        children: /*#__PURE__*/ jsx(FontAwesomeIcon, {
                            icon: faTimes,
                            className: getBlocksWith("__clear-icon-svg")
                        })
                    }),
                    showSearchButton && /*#__PURE__*/ jsx("button", {
                        className: getBlocksWith("__search-wrapper-search-icon"),
                        onClick: ()=>{
                            handleOnSearchBtnClick && handleOnSearchBtnClick();
                            setDisplayedValue("");
                            clearSearch();
                        },
                        "aria-label": searchBtnAriaText,
                        "data-auto": "search-btn",
                        tabIndex: 0,
                        type: "button",
                        children: /*#__PURE__*/ jsx(FontAwesomeIcon, {
                            icon: faSearch$1,
                            className: getBlocksWith("__search-icon-svg")
                        })
                    })
                ]
            })
        })
    });
};

function _define_property$3(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$3(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$3(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$3(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$3(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$3(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$3(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$3(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$3(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const SearchMultiselectDropdownMenu = (_param)=>{
    var { children } = _param, rest = _object_without_properties$3(_param, [
        "children"
    ]);
    const { menu, getDropdownProps, getMenuProps, isOpen, selectedItems, getBlocksWith, messages } = useSearchMultiselectContext();
    const { dropdownRef, isClearBtnNeeded, onClearAll } = menu;
    const { clearAllBtnText } = messages;
    if (!getMenuProps || !getDropdownProps) {
        throw new Error("SearchMultiselectDropdown component requires getMenuProps and getDropdownProps be defined");
    }
    return /*#__PURE__*/ jsx(Fragment, {
        children: /*#__PURE__*/ jsxs("div", {
            ref: dropdownRef,
            children: [
                /*#__PURE__*/ jsx(SearchMultiselectDropdownInput, {}),
                /*#__PURE__*/ jsx("ul", _object_spread_props$3(_object_spread$3({
                    className: cn(getBlocksWith("__wrapper-list"), !isOpen && getBlocksWith("__hidden"))
                }, getMenuProps(_object_spread$3({}, rest), {
                    suppressRefError: true
                }), getDropdownProps()), {
                    "data-auto": "search_listbox",
                    "aria-hidden": !isOpen,
                    children: children
                })),
                isClearBtnNeeded && selectedItems.length > 0 && /*#__PURE__*/ jsx("div", {
                    className: getBlocksWith("__clear-all-btn"),
                    children: /*#__PURE__*/ jsx(Button$1, {
                        styleType: "inline",
                        onClick: onClearAll,
                        "data-auto": "clear-all-btn",
                        children: clearAllBtnText
                    })
                })
            ]
        })
    });
};

const searchMultiselectDropdownMessages = {
    inputBoxPlaceholder: "Please type here to filter/search",
    toggleBtnText: "Click here to open",
    AllText: "All",
    nItemSelectedText: "item(s) selected.",
    loadingText: "Loading...",
    selectAllText: "Select all",
    clearBtnAriaText: "Click here to clear the text",
    searchBtnAriaText: "Click here to search",
    comboboxAriaText: "Select the options",
    chipsDltBtnAriaText: "Click here to remove this item",
    clearAllBtnText: "Clear all"
};

const BASE_CLASS_NAME = "search-multiselect-dropdown";

function _define_property$2(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$2(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$2(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$2(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$2(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$2(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$2(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$2(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$2(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const getFilteredOptions = (options, enteredValue = "")=>{
    const lowerCasedInputValue = enteredValue.toLowerCase();
    return options.filter((o)=>{
        var _o_label;
        return o.value.toLowerCase().includes(lowerCasedInputValue) || ((_o_label = o.label) === null || _o_label === void 0 ? void 0 : _o_label.toLowerCase().includes(lowerCasedInputValue));
    });
};
const SearchMultiselectDropdown = /*#__PURE__*/ forwardRef(({ options = [], initialSelectedOptions = [], className: consumerBlockClassName = "", isMultiSelect = true, isAdvanceActionsEnabled = true, isSearchable = true, isSearchMultiselectDropdownDisabled = false, hideLabel = false, showSelectedItemAtTop = true, isOptionsLoading = false, showLabelOrValue = ShowBylabelOrValue.LABEL, showItemLabelOrValue = ShowBylabelOrValue.BOTH, maxSelections = options.length, isClearBtnNeeded = false, messages = searchMultiselectDropdownMessages, showSearchButton = false, handleSelectChange, handleSearchBtnClick, handleLimitExceed, handleSelectedItemChange, handleInputChange, handleClearAll, children }, ref)=>{
    const [filterValue, setFilterValue] = useState("");
    const [selectedItems, setSelectedItems] = useState(initialSelectedOptions !== null && initialSelectedOptions !== void 0 ? initialSelectedOptions : []);
    const items = useMemo(()=>getFilteredOptions(options, filterValue), [
        filterValue,
        options
    ]);
    const [inputNotMatched, setInputNotMatched] = useState(false);
    const [highlightedIndexItem, setHighlightedIndexItem] = useState(-1);
    const [isOpen, setIsOpen] = useState(false);
    const initialRender = useRef(true);
    /**
     * This is the work-around we found, in order to enable the search
     * or call the cb (handleSearchBtnClick) on enter as by default downshift
     * doesn't support this. isMatchAvailable used in the input comp.
     */ useEffect(()=>{
        const isMatchAvailable = items.length > 0;
        setInputNotMatched(isMatchAvailable);
    }, [
        items
    ]);
    /*
     * Invoking handleSelectChange callback when it has parent component state
     * together with setting internal state leads to "Cannot update a component
     * (`X`) while rendering a different component ("Y")" error. Therefore, it
     * is better invoke callback like below.
     * */ useEffect(()=>{
        if (!initialRender.current) {
            invokeCallback(handleSelectChange, selectedItems);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        selectedItems
    ]);
    const handleSelectAll = ()=>{
        if (options.length > 0) {
            const filterDisabledItems = options.filter((item)=>!item.disabled);
            setSelectedItems(filterDisabledItems);
        }
    };
    const handleDeselectAll = ()=>{
        setSelectedItems([]);
    };
    const onClearAll = ()=>{
        var _document_querySelector;
        setSelectedItems([]);
        invokeCallback(handleClearAll, []);
        invokeCallback(handleSelectedItemChange, []);
        (_document_querySelector = document.querySelector("[data-toggle=dropdown-button]")) === null || _document_querySelector === void 0 ? void 0 : _document_querySelector.focus();
    };
    const handleOnSearchBtnClick = ()=>{
        invokeCallback(handleSearchBtnClick, filterValue);
    };
    const handleInputValueChange = (inputStr)=>{
        setFilterValue(inputStr);
        invokeCallback(handleInputChange, inputStr);
    };
    const getBlocksWith = createBemBlockBuilder$1([
        BASE_CLASS_NAME,
        consumerBlockClassName
    ]);
    const { getSelectedItemProps, getDropdownProps, removeSelectedItem } = useMultipleSelection({
        selectedItems,
        itemToString: (item)=>{
            var _item_label;
            return item ? (_item_label = item.label) !== null && _item_label !== void 0 ? _item_label : item.value : "";
        },
        onStateChange ({ selectedItems: newSelectedItems = [], type }) {
            switch(type){
                case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownBackspace:
                case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownDelete:
                case useMultipleSelection.stateChangeTypes.FunctionRemoveSelectedItem:
                    /*
              ISSUE: https://github.com/downshift-js/downshift/issues/1138
              https://github.com/downshift-js/downshift/pull/1458
              */ const isValid = newSelectedItems.length > 0 && selectedItems.length > 0;
                    const otherItems = isValid ? newSelectedItems.filter((obj1)=>selectedItems.some((obj2)=>obj1.label !== obj2.label && obj1.value !== obj2.value)) : [];
                    // if we have any item left set it otherwise just []
                    setSelectedItems(otherItems);
                    break;
            }
        }
    });
    const _useCombobox = useCombobox$1({
        isOpen,
        items,
        itemToString: (item)=>{
            var _item_label;
            return item ? (_item_label = item.label) !== null && _item_label !== void 0 ? _item_label : item.value : "";
        },
        inputValue: filterValue,
        highlightedIndex: highlightedIndexItem,
        onInputValueChange: ({ inputValue })=>{
            return handleInputValueChange(inputValue);
        },
        onHighlightedIndexChange: ({ highlightedIndex })=>{
            if (highlightedIndex !== undefined && !isNaN(highlightedIndex)) {
                setHighlightedIndexItem(highlightedIndex);
            }
        },
        onSelectedItemChange: ({ selectedItem })=>{
            // handleSelectedItemChange will only trigger when previous selected item and current selected item are not same
            // selectedItemChanged: (prevItem, item) => prevItem !== item,
            invokeCallback(handleSelectedItemChange, selectedItem);
        },
        onIsOpenChange: ({ isOpen: newIsOpen })=>{
            setIsOpen(!!newIsOpen);
        },
        /**
       * defaultHighlightedIndex: after selection, highlight the first item.
       * this is important to keep other than nullish,
       * otherwise, onEnter after open menu will trough error
       */ defaultHighlightedIndex: -1,
        stateReducer (_, actionAndChanges) {
            const { changes, type } = actionAndChanges;
            switch(type){
                case useCombobox$1.stateChangeTypes.InputKeyDownArrowDown:
                case useCombobox$1.stateChangeTypes.InputKeyDownArrowUp:
                    return _object_spread_props$2(_object_spread$2({}, changes), {
                        highlightedIndex: highlightedIndexItem
                    });
                case useCombobox$1.stateChangeTypes.InputKeyDownEnter:
                case useCombobox$1.stateChangeTypes.ItemClick:
                    var _changes_selectedItem;
                    const isItemDisabled = changes === null || changes === void 0 ? void 0 : (_changes_selectedItem = changes.selectedItem) === null || _changes_selectedItem === void 0 ? void 0 : _changes_selectedItem.disabled;
                    if (!isItemDisabled && changes.selectedItem) {
                        handleItemClick(changes.selectedItem);
                        /**
               * we have to set the inputValue same as filterValue so the filtered items
               * will match the keyword available in the searchbox.
               */ const newChanges = _object_spread_props$2(_object_spread$2({}, changes), {
                            inputValue: filterValue
                        });
                        return _object_spread_props$2(_object_spread$2({}, newChanges), {
                            isOpen: isMultiSelect,
                            highlightedIndex: changes.selectedItem ? options.indexOf(changes === null || changes === void 0 ? void 0 : changes.selectedItem) : 0
                        });
                    } else {
                        /**
               * For disabled item, just keep the menu open and dont select it
               */ return {
                            isOpen: isMultiSelect,
                            highlightedIndex: changes.selectedItem ? options.indexOf(changes === null || changes === void 0 ? void 0 : changes.selectedItem) + 1 : 0
                        };
                    }
                case useCombobox$1.stateChangeTypes.InputBlur:
                    return {
                        isOpen: true
                    };
                case useCombobox$1.stateChangeTypes.ItemMouseMove:
                    return _object_spread_props$2(_object_spread$2({}, changes), {
                        highlightedIndex: highlightedIndexItem
                    });
                case useCombobox$1.stateChangeTypes.InputChange:
                    const { inputValue = "" } = changes;
                    setFilterValue(inputValue);
                    return changes;
                default:
                    return changes;
            }
        }
    }), { getToggleButtonProps, getLabelProps, getMenuProps, getInputProps, getComboboxProps, getItemProps, setInputValue, closeMenu, selectItem } = _useCombobox, downshiftRestComboboxProps = _object_without_properties$2(_useCombobox, [
        "getToggleButtonProps",
        "getLabelProps",
        "getMenuProps",
        "getInputProps",
        "getComboboxProps",
        "getItemProps",
        "setInputValue",
        "closeMenu",
        "selectItem"
    ]);
    const dropdownRef = useRef(null);
    const handleOutsideClick = useCallback((event)=>{
        var _event_target, _dropdownRef_current_textContent, _dropdownRef_current;
        if (dropdownRef.current && isOpen && !((_dropdownRef_current = dropdownRef.current) === null || _dropdownRef_current === void 0 ? void 0 : (_dropdownRef_current_textContent = _dropdownRef_current.textContent) === null || _dropdownRef_current_textContent === void 0 ? void 0 : _dropdownRef_current_textContent.includes((_event_target = event.target) === null || _event_target === void 0 ? void 0 : _event_target.textContent))) {
            closeMenu();
        }
    }, [
        isOpen,
        closeMenu
    ]);
    useEffect(()=>{
        document.addEventListener("click", handleOutsideClick);
        return ()=>{
            document.removeEventListener("click", handleOutsideClick);
        };
    }, [
        handleOutsideClick
    ]);
    useEffect(()=>{
        const dropdownButton = document.querySelector("[data-toggle=dropdown-button]");
        if (initialRender.current) {
            // On initial render, mark that initial render has occurred
            initialRender.current = false;
        } else {
            if (!isOpen && dropdownButton) {
                dropdownButton.focus();
            }
        }
        return ()=>{
            if (dropdownButton) {
                dropdownButton.blur(); // Unfocus the element when the effect is cleaned up
            }
        };
    }, [
        isOpen
    ]);
    const handleItemClick = (item)=>{
        if (isNullOrUndefined(item) || (item === null || item === void 0 ? void 0 : item.disabled)) {
            return;
        }
        /**
       * For first time selection, for both single and multiselect
       */ if (!isMultiSelect || (selectedItems === null || selectedItems === void 0 ? void 0 : selectedItems.length) === 0) {
            if (item) {
                setSelectedItems([
                    item
                ]);
            }
            if (!isMultiSelect) {
                setIsOpen(false);
            }
            return;
        }
        const isSameItemSelected = selectedItems.some((obj)=>obj.label === (item === null || item === void 0 ? void 0 : item.label) && obj.value === (item === null || item === void 0 ? void 0 : item.value));
        if (isSameItemSelected) {
            const otherItems = selectedItems.filter((o)=>o.label !== (item === null || item === void 0 ? void 0 : item.label) && o.value !== (item === null || item === void 0 ? void 0 : item.value));
            /**
         * for single item toggle selection and deselection
         */ if (otherItems.length === 0) {
                setSelectedItems([]);
            }
            /**
         * if multiple items selected then keep other item selected
         * and remove just the item from the selected list
         */ if (otherItems.length > 0) {
                setSelectedItems(otherItems);
            }
            return;
        }
        if (selectedItems.length < getMaxSelectionLimit(maxSelections, options)) {
            const newSelectedOptions = [
                ...selectedItems,
                item
            ];
            setSelectedItems(newSelectedOptions);
        } else {
            invokeCallback(handleLimitExceed, maxSelections);
        }
    };
    const clearSearch = ()=>{
        setInputValue("");
        setFilterValue("");
    };
    const dropdownProps = _object_spread_props$2(_object_spread$2({}, downshiftRestComboboxProps), {
        action: {
            handleSelectAll,
            handleDeselectAll
        },
        input: {
            inputNotMatched,
            isSearchable,
            filterValue,
            showSearchButton,
            handleOnSearchBtnClick,
            clearSearch,
            getInputProps
        },
        item: {
            isAdvanceActionsEnabled,
            isOptionsLoading,
            maxSelections,
            items,
            highlightedIndexItem,
            getItemProps,
            setHighlightedIndexItem,
            handleItemClick
        },
        label: {
            hideLabel,
            showSelectedItemAtTop,
            getLabelProps
        },
        menu: {
            dropdownRef,
            isClearBtnNeeded,
            onClearAll
        },
        selectedChips: {
            removeSelectedItem,
            getSelectedItemProps
        },
        toggle: {
            getToggleButtonProps
        },
        getDropdownProps,
        getMenuProps,
        closeMenu,
        selectItem,
        options,
        isMultiSelect,
        isSearchMultiselectDropdownDisabled,
        showLabelOrValue,
        showItemLabelOrValue,
        messages,
        getBlocksWith,
        selectedItems,
        isOpen,
        initialSelectedOptions,
        handleSelectChange,
        handleLimitExceed,
        handleSelectedItemChange,
        handleInputChange,
        handleClearAll,
        setFilterValue,
        setSelectedItems,
        setInputValue,
        handleOutsideClick,
        getComboboxProps
    });
    return /*#__PURE__*/ jsx(SearchMultiselectContextProvider, {
        value: dropdownProps,
        children: /*#__PURE__*/ jsx("div", _object_spread_props$2(_object_spread$2({
            className: getBlocksWith(),
            "data-auto": "multiselect-dropdown",
            "aria-label": messages.comboboxAriaText
        }, getComboboxProps({
            ref
        })), {
            children: children
        }))
    });
});
SearchMultiselectDropdown.Context = SearchMultiselectContextConsumer;
SearchMultiselectDropdown.Label = SearchMultiselectDropdownLabel;
SearchMultiselectDropdown.Toggle = SearchMultiSelectDropdownToggle;
SearchMultiselectDropdown.Menu = SearchMultiselectDropdownMenu;
SearchMultiselectDropdown.Item = SearchMultiselectDropdownItem;
SearchMultiselectDropdown.displayName = "SearchMultiselectDropdown";

const CloseButton = ({ closeToast, className, title })=>/*#__PURE__*/ jsx(Button$1, {
        "aria-label": title,
        className: cn(className, "toast__close-button"),
        onClick: closeToast,
        styleType: "link",
        children: /*#__PURE__*/ jsx(FontAwesomeIcon, {
            icon: faTimes,
            size: "lg",
            title: title
        })
    });

var success = "success";
var info = "info";
var error = "error";
var warning = "warning";
var ToastTypes = {
	success: success,
	info: info,
	error: error,
	warning: warning
};

const buildToast = (content, status, customIcon)=>{
    const ICONS = new Map([
        [
            ToastTypes.success,
            faCheckCircle$1
        ],
        [
            ToastTypes.error,
            faBan
        ],
        [
            ToastTypes.warning,
            faExclamationTriangle$1
        ],
        [
            ToastTypes.info,
            faInfoCircle
        ]
    ]);
    const icon = customIcon || ICONS.get(status);
    return /*#__PURE__*/ jsxs("div", {
        className: `toast__wrapper toast--${status}`,
        children: [
            /*#__PURE__*/ jsx(FontAwesomeIcon, {
                className: "toast__icon",
                icon: icon
            }),
            /*#__PURE__*/ jsx("div", {
                className: "toast__content-wrapper",
                children: content
            })
        ]
    });
};
function Toast(content = "", options = {
    type: ToastTypes.info,
    closeButton: true,
    closeButtonTitle: "Dismiss toast"
}) {
    toast(buildToast(content, options.type, options.icon), {
        className: `toast--${options.type}`,
        closeButton: options.closeButton && /*#__PURE__*/ jsx(CloseButton, {
            className: `toast--${options.type}`,
            title: options.closeButtonTitle
        })
    });
}

const ToastContainer = ({ rtl = false, autoClose = 3000, pauseOnHover = false })=>{
    return /*#__PURE__*/ jsx(ToastContainer$1, {
        autoClose: autoClose,
        closeOnClick: false,
        draggable: false,
        hideProgressBar: true,
        newestOnTop: true,
        pauseOnHover: pauseOnHover,
        pauseOnFocusLoss: false,
        position: rtl ? "top-left" : "top-right",
        rtl: rtl,
        transition: Slide
    });
};

function _define_property$1(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$1(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$1(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$1(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$1(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$1(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$1(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$1(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$1(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const TooltipWrapper = (_param)=>{
    var { title, trigger = "mouseenter focusin", zIndex = 12, delay = [
        200,
        10
    ], placement = "bottom", hideOnClick = false, styleType = "default", visible, className, children } = _param, rest = _object_without_properties$1(_param, [
        "title",
        "trigger",
        "zIndex",
        "delay",
        "placement",
        "hideOnClick",
        "styleType",
        "visible",
        "className",
        "children"
    ]);
    /**
   * If any boolean is passed for 'visible', then Tippy considers the tooltip as 'controlled'
   * and does not want values for 'hideOnClick' or 'trigger'. This only persists
   * values for 'hideOnClick' and 'trigger' if the tooltip is NOT controlled.
   */ let shouldHideOnClick;
    let triggerType;
    if (typeof visible !== "boolean") {
        shouldHideOnClick = hideOnClick;
        triggerType = trigger;
    }
    /**
   * Tippy does not allow null as a value for 'visible'. However, allowing null
   * gives consumers more flexibilty when toggling between controlled and uncontrolled states.
   * This allows null as the 'visible' prop for the Tooltip component, but removes it for Tippy.
   */ const isVisible = visible !== null && visible !== void 0 ? visible : undefined;
    const hideOnEscapeKeyClick = {
        name: "hideOnEscapeKeyClick",
        defaultValue: true,
        fn () {
            const hideTooltipInstance = ({ hide })=>(event)=>{
                    if (event.code === "Escape" || event.keyCode === 27) {
                        hide();
                    }
                };
            return {
                onShow (instance) {
                    document.addEventListener("keydown", hideTooltipInstance(instance));
                },
                onHide (instance) {
                    document.removeEventListener("keydown", hideTooltipInstance(instance));
                }
            };
        }
    };
    return /*#__PURE__*/ jsx(Tippy, _object_spread_props$1(_object_spread$1({
        content: title,
        trigger: triggerType,
        zIndex: zIndex,
        delay: delay,
        placement: placement,
        hideOnClick: shouldHideOnClick,
        plugins: [
            hideOnEscapeKeyClick
        ],
        visible: isVisible,
        className: cn("eb-tooltip", `eb-tooltip--${styleType}`, className),
        interactive: true
    }, rest), {
        children: children
    }));
};

function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const ToolButton = /*#__PURE__*/ forwardRef((_param, ref)=>{
    var { title, selectedTitle, onClick, isSelected = false, defaultIcon, selectedIcon, className, tooltipProps = {} } = _param, rest = _object_without_properties(_param, [
        "title",
        "selectedTitle",
        "onClick",
        "isSelected",
        "defaultIcon",
        "selectedIcon",
        "className",
        "tooltipProps"
    ]);
    const getBlocksWith = createBemBlockBuilder$1([
        "eb-tool-button",
        className
    ]);
    const toolIcon = isSelected && selectedIcon || defaultIcon;
    const toolTitle = isSelected && selectedTitle || title;
    return /*#__PURE__*/ jsx("div", {
        className: getBlocksWith(),
        children: /*#__PURE__*/ jsx(TooltipWrapper, _object_spread_props(_object_spread({
            className: getBlocksWith("__tooltip"),
            title: toolTitle
        }, tooltipProps), {
            children: /*#__PURE__*/ jsx(Button$1, _object_spread_props(_object_spread({
                className: cn(getBlocksWith("__button"), isSelected && getBlocksWith("__button--selected")),
                styleType: "flat-icon",
                onClick: onClick,
                "data-auto": "tool-button",
                "aria-label": toolTitle,
                ref: ref
            }, rest), {
                children: /*#__PURE__*/ jsx(FontAwesomeIcon, {
                    className: getBlocksWith("__icon"),
                    icon: toolIcon,
                    "data-auto": "font-awesome-icon"
                })
            }))
        }))
    });
});
ToolButton.displayName = "ToolButton";

const Truncate = ({ maxLines = 3, content })=>{
    const ignoreTagAttr = [
        "class"
    ];
    return /*#__PURE__*/ jsx("div", {
        className: "eb-truncate",
        style: {
            WebkitLineClamp: maxLines,
            padding: 0
        },
        dangerouslySetInnerHTML: {
            __html: xss(content, {
                onIgnoreTagAttr: function(_, name, value) {
                    return ignoreTagAttr.includes(name) ? `${name}="${escapeAttrValue(value)}"` : undefined;
                }
            })
        }
    });
};
var truncate = /*#__PURE__*/ memo(Truncate);

export { Accordion, Button$1 as Button, Card, Checkbox, CloseButton$1 as CloseButton, Control, DataTable, DateFilters, DateRange, DialogNotification, Drawer, Dropdown, Error$1 as Error, Hyperlink, Icon, InPageNotification, IndeterminateSpinner, Listbox, Loading, LoadingButton, Modal, MoreInfo, NativeDropdown, Page, PageErrorMessage, PasswordInput, Popover, RadioButton, RearrangeableMultiSelect, SCROLL_EVENT, SCROLL_TO_EVENT, ScrollContainer, SearchAutocomplete, SearchMultiselectDropdown, SectionNavigation, ShowMore, ShowMorePagination, Tabs, TextArea, TextControl, TextInput, Toast, ToastContainer, ToolButton, TooltipWrapper as Tooltip, truncate as Truncate, createBemBlockBuilder$1 as createBemBlockBuilder, createGenericContext$1 as createGenericContext, emitOnScroll, emitScrollTo, eventManager, onScroll, onScrollTo, scrollEmitter, useOnScroll, useThrottledDebouncedInput };
//# sourceMappingURL=ebsco-ui-esm.js.map
